/**
 * THREE.js 渲染器管理类
 * 负责3D场景的创建、渲染和交互控制
 */
import * as THREE from 'three/webgpu';
import type {Observable, Subscription} from 'rxjs';
import {EMPTY, filter, map, merge, ReplaySubject, Subject} from 'rxjs';
import {CSS2DRenderer} from 'three/examples/jsm/renderers/CSS2DRenderer.js';
import {Inspector} from 'three/addons/inspector/Inspector.js';

import {ViewerMode} from './ViewerMode';
import {ViewMode} from './ViewMode';
import { ImportedModelRaycastCandidateIndex } from '../imported-model/internal';
import { MeshRaycastQuery, SceneMeshRaycastIndex } from '../raycast-acceleration/internal';
import type {
    DatumPlaneOptions, SketchEntityReferenceAllInnerLoopsResolveInput,
    SketchEntityReferenceAutomaticLifecycleResult, SketchEntityReferenceControllerHost,
    SketchEntityReferenceHistoryDeleteInput, SketchEntityReferenceHistoryRestoreInput,
    ReferencePlane, SketchEntityReferenceMoveDetachPort, SketchEntityReferencePreviewSession,
    SketchEntityReferenceRemoveRelationInput, SketchEntityReferenceRemoveRelationResult,
    SketchEntityReferenceSelectionCandidate,
    SketchEntityReferenceSelectionResolveInput, SketchEntityReferenceTopologyOwnerQueryInput,
    SketchEntityReferenceTopologyOwnerRebuildInput, SketchEntityReferenceTopologyOwnerRemovedEvent,
    SketchEntityReferenceWarningSummary, CreateGeometryReferencesInput,
    CreateSketchEntityReferencesResult, RebuildSketchEntityReferencesResult,
    RemoveGeometryReferencesResult, SketchEntityReferenceRecord,
} from '../reference';
import {
    DATUM_AXIS_HISTORY_ENTITY_ID, DATUM_PLANE_HISTORY_ENTITY_ID, DATUM_POINT_HISTORY_ENTITY_ID,
    getDatumAxisIdFromObject, getDatumPlaneIdFromObject, getDatumPointIdFromObject,
    recomputeAssemblyDatumReferences, ReferenceAxisController, ReferenceCreationContext,
    ReferencePlaneController, ReferencePointController,
    SceneTopologyOwnerRuntimeResolver, SketchEntityReferenceTopologyRebuildOwnerPolicy,
} from '../reference';
import type {
    PuppyDatumExtension, PuppyDatumExtensionInput, PuppyFeatureExtension,
    PuppyFeatureExtensionInput, PuppyPackageDocumentType, PuppySketchExtension,
    PuppySketchExtensionEntry, PuppySketchEntityReferenceExtension,
} from '../export';
import type {
    CreateSketchDimensionInput,
    SketchActiveParametricStateSolveInput, SketchCornerControllerSnapshot,
    SketchDimensionDisplayState, SketchDimensionHistoryTransactionPort,
    SketchDimensionInBatchCreationResult,
    SketchDimensionReferenceRecord, SketchDimensionRepository, SketchDimensionSnapshot,
    SketchDimensionSolverSession, SketchEntitiesTransformControllerSnapshot,
    SketchEntitiesTransformInputMode, SketchEntitiesTransformRelationRetentionMode,
    SketchEntitiesTransformSelectionRole,
    SketchGroupDuplicationResult, SketchParametricOperationIntent,
    SketchParametricSolveCoordinatorResult,
    SketchCircularPatternControllerSnapshot, SketchCircularPatternParameters,
    SketchCircularPatternSelectionRole,
    SketchPlanarFaceSelection, SketchPlanarFaceSessionStartResult,
    SmartDimensionCommandHost, SmartDimensionCommandSnapshot, SmartDimensionSketchContext,
    SmartDimensionSnapshotListener, SmartDimensionStartOptions, SmartDimensionVec2,
} from '../sketch';
import { SketchDimensionInstant2dController } from '../sketch/internal';
import {
    notifySectionableModelRootContentChanged,
    registerSectionableModelRoot,
    SectionViewController,
    SectionViewRenderController,
    SectionViewRotationHandleManager,
    unregisterSectionableModelRoot,
} from '../section-view';
import type {SectionViewDocumentSnapshot} from '../section-view';
import {
    createSketchDimensionSolverSupplementProvider, createSketchRuntimeMeasureSource,
    measureSketchDimensionFromSource, rehearseSketchDimensionDrivingSolve,
    resolveChainAlignedLabelPositions, resolveSketchDimensionInstant2dValue,
    SketchCopyEntitiesController, SketchCornerDrawingController,
    CurveOffsetDerivedSyncService,
    SketchDimensionAnnotationRenderer, SketchDimensionApplicationService,
    SketchDimensionIdentityService, SketchDimensionRelationViewSynchronizer,
    SketchDimensionValidationService,
    SketchEntityClipboardController, SketchFaceViewFrameBuilder, SketchMoveEntitiesController,
    SketchMirrorAxisReferenceReconcileService, SketchMirrorEntitiesController,
    OffsetChainDerivedSyncService,
    SketchOffsetController, SketchParametricDragSession, SketchParametricSolveCoordinator,
    SketchPlanarFaceResolver, SketchRelationCreationService, SketchRelationStateService,
    SketchRotateEntitiesController, SketchCircularPatternController, SketchSmartDimensionController,
    SketchSmartDimensionPreviewRenderer, SketchTrimController,
} from '../sketch';
import type {
    EntityPatch, SketchGroupRuntimeHistoryRecord,
    SketchGroupRuntimeHistorySnapshot, SketchGroupRuntimeSnapshotRegistry,
} from '../history';
import {
    areSketchGroupRuntimeSnapshotsEqual, createSketchGroupRuntimeSnapshotRegistry,
} from '../history';
import type {
    ChamferFeatureDefinition, FilletFeatureDefinition,
    FindSketchRegionsPayload, SketchRegionPayload,
    ImportedModelTopologyHostTransformProjectionPort,
    ImportedModelInstancedUnitSource,
    SketchRuntimeHistorySession, SketchRuntimeHistorySessionInput,
} from '../../types';
export type {
    SketchRuntimeHistorySession, SketchRuntimeHistorySessionInput,
} from '../../types';
import type {
    AbstractSketchEdgeObject, FeatureLine, SketchCollectionObject, SketchGroup, SketchPointObject,
} from '../mesh';
import {
    clearSketchContourDisplays, FeatureResultMesh,
    isFeatureFace, isFeatureLine, isFeaturePoint, isFeatureResultMesh,
    isSketchCircleObject, isSketchCollectionObject, isSketchContour,
    isSketchEllipseObject, isSketchObject, isSketchPointObject, isSketchSlotObject,
    isSketchSplineObject, isSketchStyleSplineObject,
} from '../mesh';
import type {
    ComposedAssemblyResult, GlbDatumSlotTargetDescriptor,
    ImportedModelInstancedMeshUnit, ImportedModelSelectionSummary, SelectionInteractionMeta,
    ImportedModelSourceMeshVisibilityController,
} from '../imported-model';
import {
    buildImportedModelSelectionSummary, collectImportedModelTopologyRepositories, GlbSelectionExtension,
    getImportedModelTopologySemanticRevision,
    ImportedModelInstancedHitResolver,
    ImportedModelInstancedMeshRegistry,
    ImportedModelInstancedRenderCoordinator,
    ImportedModelObjectMetadataService,
    ImportedModelSelectionHighlighter, isOriginalImportedBrepBaseline,
    observeImportedModelTopologyOwnerRemoved, releaseFeatureOwnedImportedBodies,
    resolveImportedModelTopologyFeatureLine, resolveImportedModelTopologyPickHost,
    syncImportedModelBodyRepresentations,
} from '../imported-model';
import type {
    AssemblyComponentPlacementSnapInput, AssemblyComponentPlacementSnapResult,
    AssemblyMateDocumentChangeContext, AssemblyMateDocumentModel,
    AssemblyReferenceDependencyResolverHost, AssemblyReferenceDependencySnapshot,
    AssemblyTransformHistoryCommitResult, AssemblyTransformHistoryRecord,
} from '../assembly';
import {
    AssemblyComponentEditController, AssemblyComponentPlacementSnapResolver,
    AssemblyComponentTransformEffectsCoordinator,
    ComponentInstanceTransformRootIndex,
    resolveComponentInstanceTransformTarget,
    AssemblyMateController, MoveComponentController, resolveAssemblyReferenceDependencySnapshot,
} from '../assembly';
import type {
    LinearComponentPatternController, LinearComponentPatternPersistenceDocument,
} from '../pattern';
import {LinearComponentPatternManager} from '../pattern';
import type {
    ContextMenuActionType, ContextMenuOpenEventPayload, RawPointerEventType,
    SketchGroupEndResult, SketchGroupLifecycleEvent, SketchSessionTerminationResult,
} from './moudles';
import {
    ContextMenuController, DebugTool, EventManager,
    FeatureTimelineRebuildRuntime, HelperManager, LightManager, MeshManager,
    PipelineManager, RevolveRegionSelectionManager,
    SketchDisplayState, SketchEditingVisualLayering, SketchEntityReferenceDocumentRuntime,
    SketchFeaturePrefixEditRuntime, SketchGroupLifecycleEventType,
    SketchGroupManager, SketchSessionTerminator, ViewportCursorManager,
} from './moudles';
import type {
    MeasureEntityRef, MeasureSelectionUpdatedPayload,
    MeasureTreeNodeVisualSelectionDescriptor, MeasureTreePartSelectionDescriptor,
} from '../measure';
import {
    buildMeasureSelectionTreeHint, buildMeasureSelectionTreeHintFromEntityRef, MeasureController,
} from '../measure';
import type {
    CameraProjectionType, StandardViewPreviewFrameOptions, StandardViewType,
} from '../camera';
import {
    CameraManager, ViewOrientationPreviewRenderer,
} from '../camera';
import type {
    EntityInteractionEventType, EntitySelectedEventPayload, EntitySelectionModeOptions,
    FeatureVisualScope, PointSnapSettings, QuickSnapMode, SelectionUpdateIntent,
    SketchGeometryRelationLabelManager,
} from '../manager';
import {
    DatumAxisManager, DatumPlaneManager, DatumPointManager,
    EntitySelectionManager, InteractionStateManager,
    PatternDirectionArrowManager, PointSnapManager, PointerNearbyLabelManager,
    PointerNearbyLabelPosition, SketchDimensionManager, SketchDrawingManager,
    SketchSessionReferencePointManager,
} from '../manager';
import type {ModelDisplayMode} from './render';
import {ModelDisplayModeController, ViewerFrameScheduler, VisibleModelEdgeProjectionSourceCollector} from './render';
import type {ModelBodySceneIdentity, ModelBodySceneRegistry} from '../model-body';
import {disposeModelBodySceneRegistry, resolveModelBodySceneRegistry} from '../model-body';
import type {RectangleConstructionLineSource} from '../draw';
import {SketchDrawingTool} from '../draw';
import {
    addObject3DToParent, doesGeometryObjectReferenceAnyFeature, normalizePointSnapSettings,
} from '../utils';
import type {
    BossExtrudeSceneContour, BossExtrudeSelectionResolution, BossExtrudeSessionSelection,
    BossRevolveAxisResolution, BossRevolveEditSessionPayload, BossRevolveSelectionResolution,
    BossRevolveSessionSelection, ChamferEditSessionPayload, CutExtrudePreviewSeed,
    ExtrudeFeatureEditDefinition, FeatureDefinition, FeatureDeletionExecutionResult,
    FeatureDeletionPlan, FeatureEditSession, FeaturePreviewPresentationData,
    FeaturePreviewRequestQueueResult, FeaturePrototype, FeatureRollbackHistorySnapshot,
    FeatureRollbackMoveInput, FeatureRollbackProjectionState, FeatureRollbackState,
    FeatureRollbackTimelineItem, FilletEditSessionPayload, ResolveFeatureDeletionPlanInput,
    RevolveAngleDragChangePayload, RevolveFeatureEditDefinition, SketchFeaturePrefixEditInput,
} from '../feature';
import {
    assertFeatureDeletionPlanMatchesConfirmedPlan, BossExtrudeFeaturePrototype,
    BossExtrudeFeatureSessionBridge, BossExtrudeSceneResolver, BossRevolveFeaturePrototype,
    BossRevolveFeatureSessionBridge, BossRevolveSceneResolver,
    buildPartFeatureDocument, buildResultBodyDisplayNameMap,
    ChamferCommitExecution, ChamferCommitExecutionHandler, ChamferFeaturePrototype,
    ChamferFeatureSessionBridge, ChamferPreviewExecution, ChamferPreviewExecutionHandler,
    createFeatureCommandSeq, createFeatureReferenceBinding, CutExtrudeFeaturePrototype,
    CutExtrudeFeatureSessionBridge, CutExtrudePreviewSeedResolver, CutRevolveFeaturePrototype,
    ExtrudeCommitExecution, ExtrudeCommitExecutionHandler,
    ExtrudeFeatureDefinitionModel, ExtrudeEndTargetResolver,
    ExtrudePreviewExecution, ExtrudePreviewExecutionHandler,
    FEATURE_DELETION_PLAN_CHANGED_MESSAGE, FeatureCommitExecutionRegistry,
    FeatureDeletionPlanner, FeatureEditOrchestrator, FeatureExecutionRuntime,
    FeaturePreviewExecutionRegistry, FeaturePreviewRequestQueue, FeatureRebuildOrchestrator,
    featureCommandAdapterRegistry, FeatureRollbackController,
    FeatureRollbackCursorPlacement, FeatureRollbackTimelineItemKind,
    FilletCommitExecution, FilletCommitExecutionHandler, FilletFeaturePrototype,
    FilletFeatureSessionBridge, FilletPreviewExecution, FilletPreviewExecutionHandler,
    resolveFeatureReplayAffectedCommandIds, resolveFeatureReplayAffectedCommandIdsFromSummaries,
    RevolveAngleDragController, RevolveCommitExecution, RevolveCommitExecutionHandler,
    RevolvePreviewExecution, RevolvePreviewExecutionHandler,
    ShellCommitExecution, ShellCommitExecutionHandler, ShellFeatureSessionBridge,
    ShellPreviewExecution, ShellPreviewExecutionHandler,
    ShellFeatureController,
    RibCommitExecution, RibCommitExecutionHandler, RibFeatureSessionBridge,
    RibPreviewExecution, RibPreviewExecutionHandler,
    RibFeatureController,
    syncFeatureRollbackDatumVisibility,
} from '../feature';
import type {
    BRepRegistrar, CadDocumentRuntimeSwitchScope,
    WorkerGeometryRuntimeSwitchCommitInput, WorkerManager,
} from '../worker';
import {isStaleWorkerResponseError, workerManager} from '../worker';
import type {ViewerWorkerRuntimeLease} from './ViewerWorkerRuntimeLease';
import {viewerWorkerRuntimeLease} from './ViewerWorkerRuntimeLease';
import {normalizeArcAngles} from '../tool';
import {GeometryType} from '../geometry';

export {ViewerMode} from './ViewerMode';

//------ Viewer 调试挂载 开始------
/**
 * 扩展浏览器 Window 类型，声明调试场景下挂载的 Viewer 实例引用。
 */
declare global {
    interface Window {
        /** 当前调试挂载的 Viewer 实例。 */
        viewer?: Viewer;
    }
}
//------ Viewer 调试挂载 结束------

//------ 特征消费草图自动隐藏 开始------
/**
 * 需要在提交成功后自动隐藏来源草图的特征种类集合。
 *
 * 设计说明：
 * 1. 仅对会“消费草图”的实体特征启用自动隐藏；
 * 2. 当前仓库已落地拉伸凸台、拉伸切除、旋转凸台；
 * 3. 预留 `cut-revolve` 以兼容后续旋转切除正式接入后的同类行为。
 */
const AUTO_HIDE_SOURCE_SKETCH_FEATURE_KINDS: ReadonlySet<string> = new Set([
    'boss-extrude',
    'cut-extrude',
    'boss-revolve',
    'cut-revolve',
]);
//------ 特征消费草图自动隐藏 结束------

/**
 * Viewer 渲染选项。
 */
export interface RendererOptions {
    /** 是否启用抗锯齿。 */
    antialias?: boolean;
    /** 视口背景色。 */
    backgroundColor?: number;
    /** 网格尺寸。 */
    gridSize?: number;
    /** 网格分段数。 */
    gridDivisions?: number;
    /** 相机初始位置。 */
    cameraPosition?: [number, number, number];
    /** 透视相机视角。 */
    fov?: number;
    /** 点捕捉设置。 */
    snapSettings?: PointSnapSettings | null;
    /** 装配体移动/旋转/固定/浮动提交后的历史记录回调。 */
    onAssemblyTransformCommitted?: (record: AssemblyTransformHistoryRecord) => AssemblyTransformHistoryCommitResult;
    /**
     * 标准配合文档发生变化后的投影刷新回调。
     *
     * @param document cad-core 持有的标准配合文档快照。
     * @param context 触发本次变化的命令上下文。
     */
    onAssemblyMateDocumentChanged?: (
        document: AssemblyMateDocumentModel,
        context: AssemblyMateDocumentChangeContext,
    ) => void | Promise<void>;
    /** 草图组运行时历史记录提交回调。 */
    onSketchRuntimeHistoryCommitted?: (record: SketchGroupRuntimeHistoryRecord) => void | Promise<void>;
    /** 移动转换实体前解除外部引用的领域端口。 */
    sketchEntityReferenceMoveDetachPort?: SketchEntityReferenceMoveDetachPort | null;
}

/**
 * Viewer 资源释放选项。
 */
export interface ViewerDisposeOptions {
    /**
     * 是否同步终止 cad-core 共享 Worker runtime。
     *
     * 设计说明：页面卸载需要完整释放 Worker；根文档切换重建 Viewer 时只释放旧 Viewer
     * 的渲染与交互资源，后续 document runtime switch 会在同一个 WorkerManager 上接管新文档。
     */
    readonly terminateWorker?: boolean;
}

//------ 草图实体变换预选实体吸收 开始------
/**
 * 草图实体变换命令启动选项。
 */
export interface SketchEntitiesTransformModeStartOptions {
    /** 启动命令前已经在普通草图选择链路中选中的对象快照。 */
    readonly preselectedObjects?: readonly THREE.Object3D[];
}
//------ 草图实体变换预选实体吸收 结束------

/**
 * 草图运行时历史事务输入。
 */
export interface SketchRuntimeHistoryTransactionInput<TResult> {
    /** 用户可读标签。 */
    readonly label: string;
    /** 命令类型。 */
    readonly commandType: string;
    /** 实际执行的草图变更。 */
    readonly work: () => TResult;
    /**
     * 判断执行结果是否需要提交历史。
     *
     * @param result work 返回结果。
     * @returns 是否提交历史事务。
     */
    readonly shouldCommit?: (result: TResult) => boolean;
    /**
     * 解析随本笔运行时事务一同提交的领域补丁（如删除实体联动的尺寸悬空迁移，
     * HIST-001：dimension patch 与删除必须同一笔事务）。
     *
     * @param result work 返回结果。
     * @returns 附加补丁列表；无附加语义省略。
     */
    readonly resolveExtraPatches?: (result: TResult) => readonly EntityPatch[];
    /**
     * 历史提交成功后确认运行时快照之外的领域状态。
     *
     * Promise 型历史提交必须等待其兑现后再调用，避免先释放可回滚资源、随后提交失败却无法恢复。
     * @param result work 返回结果。
     */
    readonly commitExtraState?: (result: TResult) => void;
    /**
     * 历史提交失败时回滚运行时快照之外的领域主状态。
     *
     * 草图组 before/after 只覆盖 Three.js 运行态；尺寸仓库等额外领域状态必须由变更方
     * 提供对称回滚，防止出现“几何已恢复但尺寸仍残留”的半截事务。
     *
     * @param result work 返回结果。
     */
    readonly rollbackExtraState?: (result: TResult) => void;
}

/**
 * Viewer 文档运行时切换输入。
 */
export interface ViewerDocumentRuntimeSwitchInput {
    /** 目标文档运行时稳定键。 */
    readonly runtimeKey: string;
    /** 触发本次文档运行时切换的业务原因。 */
    readonly reason: 'project-context-switch' | 'offline-package-import' | 'editor-dispose';
    /** 本次运行时切换影响的 CAD 文档范围。 */
    readonly scope: CadDocumentRuntimeSwitchScope;
}

/**
 * Viewer 文档运行时切换内恢复项目状态的输入。
 */
export interface ViewerRestoreProjectStateInDocumentRuntimeSwitchInput extends ViewerDocumentRuntimeSwitchInput {
    /** 需要恢复的完整项目命令历史。 */
    readonly commands: CadCommand[];
    /** 项目保存时记录的退回控制棒插入索引。 */
    readonly rollbackCursorIndex?: number;
}

/**
 * Viewer 文档运行时切换句柄。
 */
export interface ViewerDocumentRuntimeSwitchHandle {
    /** 文档运行时切换会话 ID。 */
    readonly id: string;
    /** 指向 staged Worker runtime 的 BRep 注册入口。 */
    readonly brepRegistrar: BRepRegistrar;
    /**
     * 在 staged Worker runtime 中批量回放命令。
     *
     * @param payload 批量回放载荷。
     * @returns staged runtime 回放后的几何状态摘要。
     */
    batchExecute(payload: BatchExecutePayload): Promise<WorkerGeometryStateSummary>;
    /**
     * 提交 staged Worker runtime。
     *
     * @param input 提交阶段的补充边界信息。
     * @returns 提交后的 Worker 几何状态摘要。
     */
    commit(input?: WorkerGeometryRuntimeSwitchCommitInput): Promise<WorkerGeometryStateSummary>;
    /**
     * 放弃 staged Worker runtime。
     *
     * @returns 放弃完成后的异步结果。
     */
    abort(): Promise<void>;
}

/**
 * 判断值是否为 PromiseLike。
 *
 * @param value 待判断值。
 * @returns 输入是否可被 await。
 */
function isPromiseLike(value: unknown): value is PromiseLike<unknown> {
    return typeof (value as {then?: unknown} | null)?.then === 'function';
}

/**
 * Viewer 对外暴露的特征预览输入。
 */
export interface ViewerFeaturePreviewInput {
    /** 特征种类。 */
    featureKind: string;
    /** 编辑态特征 ID。 */
    featureId?: string | null;
    /** 预览定义。 */
    definition: FeatureDefinition;
    /** 已序列化的引用绑定。 */
    bindings: SerializedFeatureReferenceBinding[];
    /** 预览附加表达优先绑定的 UI 当前选中项引用。 */
    preferredItemRef?: string | null;
}

/**
 * Viewer 对外暴露的特征预览结果。
 */
export interface ViewerFeaturePreviewResult {
    /** 预览 mesh 数据。 */
    meshData: MeshData;
    /** 预览附加表达数据。 */
    presentation?: FeaturePreviewPresentationData | null;
    /** 本次预览对应的特征原型。 */
    prototype: FeaturePrototype;
    /** 本次预览实际使用的草稿定义。 */
    draftDefinition: FeatureDefinition;
    /** 旋转切除保留实体高亮网格。 */
    fragmentMeshes?: RevolveCutPreviewFragmentMesh[];
}

/**
 * 设计树投影的原子快照。
 *
 * 设计说明：
 * 1. 回退控制棒处于中间位置时，Worker 的 history 只是当前激活前缀；
 * 2. 设计树必须同时消费完整 FeatureManager history 与同一时刻的 rollbackState；
 * 3. 将两者收口到 Viewer 单一门面，避免 web-client 分两次读取造成状态错配。
 */
export interface FeatureTreeProjectionSnapshot {
    /** 已替换为完整 FeatureManager history 的设计树几何摘要。 */
    readonly state: WorkerGeometryStateSummary;
    /** 与 state 同步生成的回退控制棒投影状态。 */
    readonly rollbackState: FeatureRollbackProjectionState;
}

/**
 * 显式草图驱动特征预览的会话状态。
 */
interface EditSketchFeaturePreviewState {
    /** 正在编辑的草图 ID。 */
    sketchId: string;
    /** 需要保持真实编辑预览态的特征 ID。 */
    featureId: string;
    /** 当前接入的特征种类。 */
    featureKind: string;
    /** 拉伸族编辑定义；当前编辑草图预览优先覆盖拉伸族。 */
    extrudeDefinition?: ExtrudeFeatureEditDefinition;
}

/**
 * Viewer 特征删除计划选项。
 */
export interface ViewerFeatureDeletionPlanOptions {
    /** 设计树名称覆盖表，用于让确认框和删除计划沿用用户重命名后的显示名称。 */
    readonly objectNameOverrides?: ReadonlyMap<string, string>;
}

/**
 * Viewer 特征删除执行选项。
 */
export interface ViewerFeatureDeletionExecuteOptions extends ViewerFeatureDeletionPlanOptions {
    /** 用户确认过的删除计划；执行前会与当前计划做一致性校验。 */
    readonly confirmedPlan?: FeatureDeletionPlan;
}

/**
 * Viewer 结果体显示名映射选项。
 */
export interface ViewerResultBodyDisplayNameMapOptions {
    /** 设计树名称覆盖表，用于让导出名称沿用用户重命名后的显示名称。 */
    readonly objectNameOverrides?: ReadonlyMap<string, string>;
}

/**
 * Viewer 渲染统计数据。
 */
export interface ViewerRenderStats {
    /** 当前可见三角网格顶点数量。 */
    vertices: number;
    /** 当前可见三角面数量。 */
    triangles: number;
}

/** 交互式特征预览的默认合并等待时间，单位毫秒。 */
const INTERACTIVE_FEATURE_PREVIEW_DELAY_MS = 80;

/** 尺寸箭头的目标屏幕长度（像素，屏幕定长渲染基准）。 */
const SKETCH_DIMENSION_ARROW_PIXELS = 12;

/**
 * Viewer 事件映射。
 */
export type ViewerEventMap = THREE.Object3DEventMap;

/**
 * THREE.js 渲染器管理类
 */
export class Viewer extends THREE.EventDispatcher<ViewerEventMap> {
    //------ 调试对象中文命名 开始------
    /**
     * 主渲染场景调试名称。
     *
     * 设计说明：
     * 1. Three.js DevTools 与控制台遍历场景树时会直接展示该名称；
     * 2. 名称只用于调试定位，不参与任何业务判断。
     */
    private static readonly MAIN_SCENE_DEBUG_NAME = 'PuppyCAD主场景';

    /**
     * 辅助渲染场景调试名称。
     *
     * 设计说明：
     * 1. 用于区分主模型场景与辅助叠加场景；
     * 2. 名称只用于排查渲染管线与 layer 问题，不影响渲染行为。
     */
    private static readonly HELPER_SCENE_DEBUG_NAME = 'PuppyCAD辅助场景';
    //------ 调试对象中文命名 结束------

    //------ Viewer 静态创建事件 开始------
    /**
     * Viewer 创建完成事件流。
     *
     * 设计说明：
     * 1. 使用 `ReplaySubject(1)`，保证晚于创建时机订阅的调用方也能拿到最近一次已创建实例；
     * 2. 每次新建 Viewer 并完成初始化后都会 `next` 一次；
     * 3. 该静态事件流常驻进程，不在实例 `dispose` 时 `complete`。
     */
    public static readonly viewerCreated$: ReplaySubject<Viewer> = new ReplaySubject<Viewer>(1);
    //------ Viewer 静态创建事件 结束------

    /**
     * topology owner 删除事件流。
     *
     * 说明：
     * 1. 由显式删除入口与当前 Viewer 场景内的 imported topology 释放入口触发；
     * 2. 重建刷新使用 syncFeatureMeshesFromState/clearFeatureMeshes，不应触发该事件；
     * 3. 事件只携带稳定 owner 身份，避免订阅者依赖即将销毁的 THREE 对象。
     */
    private readonly topologyOwnerRemovedSubject = new Subject<SketchEntityReferenceTopologyOwnerRemovedEvent>();

    /** 当前 Viewer 文档唯一的转换实体引用深模块。 */
    private sketchEntityReferenceDocumentRuntime:
        SketchEntityReferenceDocumentRuntime | null = null;

    // 核心对象
    /**
     * Viewer 的主 Three.js 场景。
     *
     * 该场景在构造阶段立即创建，承载模型、草图、基准几何与交互对象；其内容由 Viewer
     * 及各领域管理器共同维护。场景中的 GPU 资源必须通过对应管理器或 `dispose()` 释放，
     * 不能仅通过移除对象来假设资源已经销毁。
     */
    public scene: THREE.Scene = new THREE.Scene();
    /** 辅助场景（用于叠加渲染辅助对象与扩展通道）。 */
    public sceneHelpers: THREE.Scene = new THREE.Scene();
    /**
     * Viewer 持有的 WebGPU 主渲染器。
     *
     * 构造阶段创建后会把画布挂载到 `container`；底层异步初始化完成前，宿主应先等待
     * `whenReady()`。`dispose()` 会释放渲染器、移除画布并把该属性重置为 `null`。
     */
    public renderer: THREE.WebGPURenderer | null = null;
    /** WebGPU Inspector 全局调试入口。 */
    public inspector: Inspector | null = null;
    /**
     * 用于基准标签和其它 CSS2D 对象的叠加渲染器。
     *
     * 其 DOM 元素由 Viewer 创建并挂载到 `container`，尺寸与 WebGPU 画布同步；
     * `dispose()` 会移除该 DOM 元素并把属性重置为 `null`。
     */
    public labelRenderer: CSS2DRenderer | null = null;
    /**
     * 监听 Viewer 容器尺寸变化的观察器。
     *
     * 回调会同步两个渲染器的尺寸并请求重绘；`dispose()` 会调用 `disconnect()`，
     * 因此 Viewer 销毁后不得继续复用该观察器。
     */
    public resizeObserver: ResizeObserver | null = null;
    /** WebGPU 管线管理器。 */
    public pipelineManager: PipelineManager | null = null;

    /** 相机管理器。 */
    public cameraManager: CameraManager | null = null;

    /** Viewer 自有帧循环的唯一调度器；不另建输入或视觉 RAF。 */
    private readonly frameScheduler = new ViewerFrameScheduler({
        prepareFrame: () => this.prepareRenderFrame(),
        renderFrame: () => this.renderFrame(),
    });

    //------ 视图定向预览渲染器 开始------
    /** 视图定向预览渲染器（在 Viewer 初始化完成后自动启动）。 */
    private viewOrientationPreviewRenderer: ViewOrientationPreviewRenderer | null = null;
    //------ 视图定向预览渲染器 结束------

    /** 原始鼠标事件管理器。 */
    public eventManager: EventManager | null = null;
    /** 实体语义事件管理器。 */
    public entitySelectionManager: EntitySelectionManager | null = null;
    /** 导入模型选择高亮控制器。 */
    private importedModelSelectionHighlighter: ImportedModelSelectionHighlighter | null = null;
    /** 当前 Viewer 独占的导入模型 InstancedMeshUnit 注册表。 */
    private readonly importedModelInstancedMeshRegistry = new ImportedModelInstancedMeshRegistry();
    /** 多个选择 Handler 共享的 imported 候选投影，仅由 Viewer 拥有生命周期。 */
    private importedModelRaycastCandidateIndex: ImportedModelRaycastCandidateIndex | null = null;
    /** 普通网格共享空间粗筛，与 MeshManager 复用物理成员源。 */
    private sceneMeshRaycastIndex: SceneMeshRaycastIndex | null = null;
    /** 按完整求交成本选择普通 Mesh 粗筛路径，各选择所有者/通道独立校准。 */
    private meshRaycastQuery: MeshRaycastQuery | null = null;
    /** 组件身份查询共用 SDK 逻辑层级，历史不再遍历物理渲染树。 */
    private assemblyComponentObjectIndex: ComponentInstanceTransformRootIndex | null = null;
    /** 独立实例领域注册的 source Mesh 脱离控制器集合。 */
    private readonly importedModelExternalSourceMeshVisibilityControllers
        = new Set<ImportedModelSourceMeshVisibilityController>();
    /** 将实例射线命中还原为 source Mesh 命中的适配器。 */
    private readonly importedModelInstancedHitResolver = new ImportedModelInstancedHitResolver(
        this.importedModelInstancedMeshRegistry,
    );
    /** 装配加载完成后自动维护实例化派生投影的协调器。 */
    private readonly importedModelInstancedRenderCoordinator = new ImportedModelInstancedRenderCoordinator(
        this.importedModelInstancedMeshRegistry,
    );
    /** 装配组件位姿写回后的基准、实例矩阵和渲染副作用协调器。 */
    private readonly assemblyComponentTransformEffectsCoordinator = new AssemblyComponentTransformEffectsCoordinator({
        recomputeAssemblyReferences: () => this.recomputeAssemblyDatumReferences(),
        updateImportedModelInstanceMatrices: (changedObjects) => {
            this.updateImportedModelInstanceMatrices(changedObjects);
        },
        requestRender: () => this.requestRender(),
    });
    /** 交互状态管理器。 */
    public interactionStateManager: InteractionStateManager | null = null;

    // 新管理器（渐进式重构）
    /**
     * 主场景光照管理器。
     *
     * WebGPU 初始化完成后创建，`whenReady()` 之前可能为 `null`；Viewer 销毁时会释放
     * 管理器创建的光照资源并把该属性重置为 `null`。
     */
    public lightManager: LightManager | null = null;
    /**
     * 网格、坐标轴等辅助显示对象的管理器。
     *
     * WebGPU 初始化完成后创建，`whenReady()` 之前可能为 `null`；Viewer 销毁时由
     * `dispose()` 统一释放，调用方不应单独接管其资源所有权。
     */
    public helperManager: HelperManager | null = null;
    /**
     * 特征结果、预览网格与相关拓扑显示对象的管理器。
     *
     * WebGPU 初始化完成后创建，负责网格对象的增量更新与释放；Viewer 销毁后该属性
     * 会被重置为 `null`，此前取得的管理器引用也不得继续使用。
     */
    public meshManager: MeshManager | null = null;
    //------ 模型显示模式 开始------
    /** 模型显示模式控制器。 */
    private readonly modelDisplayModeController: ModelDisplayModeController;
    /** 普通与实例模型边共用的草图捕捉来源查询。 */
    private readonly visibleModelEdgeProjectionSourceCollector: VisibleModelEdgeProjectionSourceCollector;
    //------ 模型显示模式 结束------
    //------ DebugTool 接入 开始------
    /** 调试工具。 */
    public readonly debugTool: DebugTool;
    //------ DebugTool 接入 结束------
    /**
     * 草图绘制工具和绘制期预览的统一管理器。
     *
     * WebGPU 初始化完成后创建并启动；宿主调用其能力前应等待 `whenReady()`。
     * `dispose()` 会停止绘制事件链、释放预览资源并把该属性重置为 `null`。
     */
    public sketchDrawingManager: SketchDrawingManager | null = null;
    /**
     * 草图会话原点与模型投影参考点管理器。
     *
     * 生命周期绑定活动草图会话，不随具体绘制工具切换；这些点只参与显示与点捕捉，
     * 不进入正式草图对象集合。Viewer 释放时统一销毁。
     */
    public sketchSessionReferencePointManager: SketchSessionReferencePointManager | null = null;
    /**
     * 草图尺寸运行时管理器。
     *
     * WebGPU 初始化完成后创建，负责尺寸交互所需的运行时状态；Viewer 销毁时会释放
     * 其订阅和资源并把该属性重置为 `null`。
     */
    public sketchDimensionManager: SketchDimensionManager | null = null;
    /** 点捕捉管理器。 */
    public pointSnapManager: PointSnapManager | null = null;
    /** 鼠标周围标签管理器。 */
    public readonly pointerNearbyLabelManager: PointerNearbyLabelManager;
    /**
     * 草图圆角与倒角命令控制器。
     *
     * WebGPU 初始化完成后创建；宿主调用前应等待 `whenReady()`。Viewer 销毁时会取消
     * 在途预览、释放控制器资源并把该属性重置为 `null`。
     */
    public sketchCornerController: SketchCornerDrawingController | null = null;
    //------ 剪裁实体接入 开始------
    /** 草图剪裁控制器。 */
    public sketchTrimController: SketchTrimController | null = null;
    /** 草图等距实体控制器 */
    public sketchOffsetController: SketchOffsetController | null = null;
    //------ 剪裁实体接入 结束------
    //------ 智能尺寸 开始------
    /** 智能尺寸命令控制器。 */
    public sketchSmartDimensionController: SketchSmartDimensionController | null = null;
    /** 智能尺寸预览渲染器。 */
    private readonly sketchSmartDimensionPreviewRenderer = new SketchSmartDimensionPreviewRenderer();
    /** 持久尺寸标注渲染器（阶段 5：Renderer/Layout/标签 HitProxy）。 */
    private readonly sketchDimensionAnnotationRenderer = new SketchDimensionAnnotationRenderer();
    /** 草图定义提交 → 尺寸布局刷新订阅。 */
    private sketchDimensionRefreshSubscription: Subscription | null = null;
    /** 尺寸关系视图同步器（尺寸=关系，同一条约束的两个视图）。 */
    private readonly sketchDimensionRelationSynchronizer = new SketchDimensionRelationViewSynchronizer();
    /** 草图统一参数求解运行时协调器。 */
    private readonly sketchParametricSolveCoordinator = new SketchParametricSolveCoordinator();
    /** 草图关系抑制/恢复原子事务服务。 */
    private readonly sketchRelationStateService = new SketchRelationStateService();
    /** Viewer 生命周期内唯一的草图关系创建服务。 */
    public readonly sketchRelationCreationService: SketchRelationCreationService;
    /** 每个草图最近一次参数求解版本，用于丢弃重入或过期结果。 */
    private readonly sketchParametricSolveRevisionBySketchId = new Map<string, number>();
    /** 尺寸仓库事件 → 关系视图对账取消订阅。 */
    private disposeSketchDimensionRelationSubscription: (() => void) | null = null;

    /** 等距样条派生同步服务（求解写回后重算偏移对侧）。 */
    private curveOffsetDerivedSyncService: CurveOffsetDerivedSyncService | null = null;
    /** 等距链派生同步服务（改链首距离尺寸后整链重算）。 */
    private offsetChainDerivedSyncService: OffsetChainDerivedSyncService | null = null;
    /** 尺寸历史事务端口（编辑操作复用，与命令控制器共享注入）。 */
    private sketchDimensionTransactionPort: SketchDimensionHistoryTransactionPort | null = null;
    /** 尺寸编辑 UI 处理器（宿主注入：双击 Modify、选中变化）。 */
    private sketchDimensionUiHandlers: {
        readonly onRequestModify?: (payload: {
            readonly sketchId: string;
            readonly dimensionId: string;
            readonly screen: {readonly x: number; readonly y: number};
        }) => void;
        readonly onSelectionChange?: (payload: {readonly sketchId: string; readonly dimensionId: string} | null) => void;
    } | null = null;
    /** 上一次重算尺寸箭头时的相机缩放（屏幕定长箭头的缩放变化检测）。 */
    private lastSketchDimensionArrowZoom = 1;
    /** 首次拖动时创建的 Instant2D 控制器，统一拥有待处理输入和求解预览。 */
    private sketchDimensionInstant2dController: SketchDimensionInstant2dController | null = null;
    //------ 智能尺寸 结束------
    /** 草图移动实体控制器。 */
    public sketchMoveEntitiesController: SketchMoveEntitiesController | null = null;
    /** 草图复制实体控制器。 */
    public sketchCopyEntitiesController: SketchCopyEntitiesController | null = null;
    /** 草图旋转实体控制器。 */
    public sketchRotateEntitiesController: SketchRotateEntitiesController | null = null;
    /** 圆周草图阵列控制器。 */
    public sketchCircularPatternController: SketchCircularPatternController | null = null;
    /** 草图镜向实体控制器。 */
    public sketchMirrorEntitiesController: SketchMirrorEntitiesController | null = null;
    /** 外部模型或基准引用重建后的镜向派生关系统一重算器。 */
    private readonly sketchMirrorAxisReferenceReconcileService = new SketchMirrorAxisReferenceReconcileService();
    /** 草图实体快捷键剪贴板控制器。 */
    private sketchEntityClipboardController: SketchEntityClipboardController | null = null;
    /** 草图实体创建回调。 */
    private sketchEntityCreatedCallback: ((object: THREE.Object3D) => void) | null = null;
    /** 草图组运行时历史快照注册表。 */
    private readonly sketchRuntimeSnapshotRegistry: SketchGroupRuntimeSnapshotRegistry;
    /**
     * @description 草图矩形边关系标识管理器。
     */
    private rectangleRelationMarkerManager: SketchGeometryRelationLabelManager | null = null;
    // 基准面系统
    /**
     * 基准面集合、可见性和基准面视图状态的管理器。
     *
     * WebGPU 初始化完成后创建并建立默认基准面；宿主调用前应等待 `whenReady()`。
     * Viewer 销毁时会释放其场景对象并把该属性重置为 `null`。
     */
    public datumPlaneManager: DatumPlaneManager | null = null;
    /** 基准轴管理器。 */
    public datumAxisManager: DatumAxisManager | null = null;
    /** 基准点管理器。 */
    public datumPointManager: DatumPointManager | null = null;
    /** 基准面命令控制器。 */
    public referencePlaneController: ReferencePlaneController;
    /** 基准轴命令控制器。 */
    public referenceAxisController: ReferenceAxisController;
    /** 基准点命令控制器。 */
    public referencePointController: ReferencePointController;
    /** 测量命令控制器。 */
    public readonly measureController: MeasureController;
    /** 移动零部件命令控制器。 */
    public moveComponentController: MoveComponentController;
    /** 标准配合命令控制器，负责配合会话、预览、求解与文档同步。 */
    public assemblyMateController: AssemblyMateController;
    /** 线性零部件阵列管理器（统一持有控制器与预览渲染器）。 */
    public linearComponentPatternManager: LinearComponentPatternManager;
    /**
     * 线性零部件阵列命令控制器。
     *
     * 该引用由 `linearComponentPatternManager` 在 Viewer 构造阶段提供，用于启动、更新和
     * 提交阵列命令。其生命周期归阵列管理器所有，调用方不得单独释放该控制器。
     */
    public linearComponentPatternController: LinearComponentPatternController;
    /** 通用方向箭头管理器。 */
    public readonly patternDirectionArrowManager: PatternDirectionArrowManager;
    /** 剖面视图双半圆旋转手柄管理器。 */
    public readonly sectionViewRotationHandleManager: SectionViewRotationHandleManager;
    /** 剖面视图渲染核心，同时向主场景 Pass 提供稳定盖面子阶段。 */
    private readonly sectionViewRenderController: SectionViewRenderController;
    /**
     * 剖面视图应用控制器。
     *
     * 该控制器持有文档级剖切定义与编辑会话，渲染资源由其内部渲染端口统一释放；
     * web-client 只能通过此公开门面驱动功能，不能直接操作 Three.js 裁剪对象。
     */
    public readonly sectionViewController: SectionViewController;
    /**
     * 正式模型统一挂载的 WebGPU 局部裁剪根。
     *
     * 草图、基准几何和交互预览继续位于主场景其它层级，避免视图剖切污染建模辅助对象。
     */
    private readonly sectionableModelRoot: THREE.ClippingGroup;
    /** 视口右键菜单控制器。 */
    public contextMenuController: ContextMenuController | null = null;
    /** 视口鼠标光标管理器。 */
    public readonly cursorManager: ViewportCursorManager = new ViewportCursorManager();
    // 事件处理器保留（暂未迁移）
    /**
     * 草图线、样条等绘制工具记录的最近一次点击时间戳，单位为毫秒。
     *
     * 值由 `SketchDrawingManager` 读写，用于双击判定；`0` 表示尚未记录可比较的点击。
     */
    public lastClickTime: number = 0;
    /**
     * 草图绘制工具使用的双击最大间隔，单位为毫秒。
     *
     * 默认值为 `300`，`SketchDrawingManager` 在判断连续点击是否构成双击时读取该值。
     */
    public doubleClickThreshold: number = 300;
    /**
     * 当前草图绘制命令结束时调用的宿主回调。
     *
     * `onDrawingFinished()` 会替换该引用；回调不拥有 Viewer 或草图资源，宿主不得在
     * Viewer 销毁后继续通过旧实例触发绘制流程。
     */
    public drawingFinishedCallback: (() => void) | null = null;
    /**
     * cad-core 向宿主发布绘图、尺寸和选择提示的回调。
     *
     * `onDrawingNotice()` 会替换该引用；消息级别未提供时由接收方按普通提示处理。
     * 回调只传递文本状态，不转移任何场景或控制器资源的所有权。
     */
    public drawingNoticeCallback: ((message: string, level?: 'info' | 'warning' | 'error') => void) | null = null;
    /** 草图组管理器。 */
    public sketchGroupManager: SketchGroupManager | null = null;

    //------ 特征离线包定义持久化 开始------
    /** 离线包特征时间线恢复期间的 Worker 对账保护标记。 */
    private isPuppyFeatureTimelineReplayPending: boolean = false;
    /**
     * 离线包特征时间线处于“重放降级”状态的持久标记。
     *
     * 设计说明：
     * 1. 单条特征重放失败时，Worker history 只包含成功执行的前缀，不再代表文档的完整时间线；
     * 2. 若此时放开 Worker 对账，`reconcileWorkerHistory` 会把失败特征从完整时间线里抹掉，
     *    用户随后保存就会把特征永久写丢——这是数据丢失，不是显示问题；
     * 3. 因此降级标记必须跨越重放窗口持续生效，直到 Worker 重新覆盖全部命令为止（自愈）。
     */
    private isPuppyFeatureTimelineDegraded: boolean = false;
    /**
     * 零件项目打开时，从 Worker 原始导入状态冻结的 Feature replay 基线修订。
     *
     * 该集合不包含装配 occurrence，也不包含携带 commandId 的离线 Feature 最终快照。
     */
    private readonly importedModelFeatureReplayBaselineRevisionIds = new Set<string>();
    /** 当前 `.puppy` Feature 恢复任务代次；完整文档切换会使旧异步任务失效。 */
    private puppyFeatureRestoreGeneration: number = 0;
    //------ 特征离线包定义持久化 结束------
    /** 草图编辑视觉层级统一入口。 */
    private readonly sketchEditingVisualLayering: SketchEditingVisualLayering;
    /** 草图会话终止器。 */
    private sketchSessionTerminator: SketchSessionTerminator | null = null;
    /** 特征重建调度器。 */
    public featureRebuildOrchestrator: FeatureRebuildOrchestrator | null = null;
    /** 特征编辑调度器。 */
    public featureEditOrchestrator: FeatureEditOrchestrator | null = null;
    /** 显式草图驱动特征预览的活动会话。 */
    private editSketchFeaturePreviewState: EditSketchFeaturePreviewState | null = null;
    /** 显式草图驱动特征预览正在建立或刷新，用于与普通草图严格前缀互斥。 */
    private isEditSketchFeaturePreviewStarting: boolean = false;
    /** 特征执行运行时。 */
    private readonly featureExecutionRuntime: FeatureExecutionRuntime;

    /**
     * 引用发生时才从当前场景的 Body 记录取得共享原文和局部拓扑。
     * @param owner 本次引用 owner，含装配 occurrence 作用域。
     * @returns 当前 Body 的匹配输入；非共享导入来源不附加输入。
     */
    private resolveImportedBodyBrepQuerySource(
        owner: Parameters<FeatureExecutionRuntime['queryConvertEntitiesFaceBoundaries']>[0]['owner'],
    ): ImportedBodyBrepQuerySource | undefined {
        if (owner.ownerKind !== 'imported-model-body') return undefined;
        let source: ImportedBodyBrepQuerySource | undefined;
        for (const repository of collectImportedModelTopologyRepositories(this.scene)) {
            const record = repository.resolveBodyRecordByBindingRef(owner.bodyBindingId)
                ?? repository.resolveBodyRecordByRuntimeObjectId(owner.resultObjectId ?? owner.ownerId);
            const asset = record?.topologyAsset;
            const resource = asset?.referenceBrepResource;
            const topology = asset?.meshData.topologySnapshot;
            if (!record || !resource || !topology) continue;
            const runtimeObjectId = repository.resolveGeometryObjectIdForBodyRecord(record);
            if (owner.resultObjectId && owner.resultObjectId !== runtimeObjectId) continue;
            if (source && (source.runtimeObjectId !== runtimeObjectId
                || source.cacheKey !== resource.cacheKey || source.meshId !== record.bodyBinding.meshId
                || source.bodyId !== (record.bodyBinding.bodyId ?? record.bodyBindingId))) {
                throw new Error(`导入 Body 引用作用域不唯一: ${owner.bodyBindingId ?? owner.ownerId}`);
            }
            source = {
                ...resource,
                runtimeObjectId,
                bodyId: record.bodyBinding.bodyId ?? record.bodyBindingId,
                meshId: record.bodyBinding.meshId,
                topology: { faces: topology.faces, vertices: topology.vertices },
            };
        }
        return source;
    }
    /** 特征删除计划器。 */
    private readonly featureDeletionPlanner: FeatureDeletionPlanner;
    /** FeatureManager 退回控制棒时间线控制器。 */
    private readonly featureRollbackController: FeatureRollbackController = new FeatureRollbackController();
    /** 当前文档唯一的严格 Feature 前缀状态机。 */
    private sketchFeaturePrefixEditRuntime: SketchFeaturePrefixEditRuntime | null = null;
    /** 当前文档唯一的 Feature Timeline Viewer 侧运行时。 */
    private featureTimelineRebuildRuntime: FeatureTimelineRebuildRuntime | null = null;
    /** 退回控制棒临时隐藏顶级草图前的显示态快照。 */
    private readonly featureRollbackSketchDisplayStateSnapshot: Map<string, SketchDisplayState> = new Map();
    /** 退回控制棒临时隐藏用户基准面前的可见性快照。 */
    private readonly featureRollbackDatumPlaneVisibleSnapshot: Map<string, boolean> = new Map();
    /** 退回控制棒临时隐藏用户基准轴前的可见性快照。 */
    private readonly featureRollbackDatumAxisVisibleSnapshot: Map<string, boolean> = new Map();
    /** 退回控制棒临时隐藏用户基准点前的可见性快照。 */
    private readonly featureRollbackDatumPointVisibleSnapshot: Map<string, boolean> = new Map();
    /** 拉伸会话桥接层。 */
    private readonly bossExtrudeFeatureSessionBridge: BossExtrudeFeatureSessionBridge;
    /** 拉伸切除会话桥接层。 */
    private readonly cutExtrudeFeatureSessionBridge: CutExtrudeFeatureSessionBridge;
    /** 拉伸场景轮廓解析器。 */
    private readonly bossExtrudeSceneResolver: BossExtrudeSceneResolver;
    /** 拉伸切除默认预览种子解析器。 */
    private readonly cutExtrudePreviewSeedResolver: CutExtrudePreviewSeedResolver;
    /** 平面面选择解析器。 */
    private readonly sketchPlanarFaceResolver: SketchPlanarFaceResolver;
    /** 面草图视图帧构造器。 */
    private readonly sketchFaceViewFrameBuilder: SketchFaceViewFrameBuilder;
    /** 旋转会话桥接层。 */
    private readonly bossRevolveFeatureSessionBridge: BossRevolveFeatureSessionBridge;
    /** 旋转场景解析器。 */
    private readonly bossRevolveSceneResolver: BossRevolveSceneResolver;
    /** 旋转角度拖拽控制器。 */
    private readonly bossRevolveAngleDragController: RevolveAngleDragController;
    /** 旋转闭合区域选择管理器。 */
    private readonly revolveRegionSelectionManager: RevolveRegionSelectionManager;
    /** 圆角会话桥接层。 */
    private readonly filletFeatureSessionBridge: FilletFeatureSessionBridge;
    /** 抽壳特征编辑会话桥接层。 */
    private readonly shellFeatureSessionBridge: ShellFeatureSessionBridge;
    /** 抽壳功能控制器（进入/退出/预览/提交编排）。 */
    public readonly shellFeatureController: ShellFeatureController;
    /** 筋特征编辑会话桥接层。 */
    private readonly ribFeatureSessionBridge: RibFeatureSessionBridge;
    /** 筋功能控制器（进入/退出/预览/提交编排）。 */
    public readonly ribFeatureController: RibFeatureController;
    /** 倒角会话桥接层。 */
    private readonly chamferFeatureSessionBridge: ChamferFeatureSessionBridge;
    /** 特征预览执行注册表。 */
    private readonly featurePreviewExecutionRegistry: FeaturePreviewExecutionRegistry;
    /** 交互式特征预览请求队列。 */
    private readonly featurePreviewRequestQueue: FeaturePreviewRequestQueue<ViewerFeaturePreviewInput, ViewerFeaturePreviewResult>;
    /** 特征提交执行注册表。 */
    private readonly featureCommitExecutionRegistry: FeatureCommitExecutionRegistry;
    /** 草图编辑命令态额外预览根对象缓存。 */
    private sketchEditingVisualLayeringPreviewRoots: THREE.Object3D[] = [];
    /**
     * 草图编辑命令态预览子树对象 ID 索引。
     *
     * 该索引与 `sketchEditingVisualLayeringPreviewRoots` 同步更新，
     * 供交互视觉状态以 O(1) 判定对象是否由命令预览独占管理材质。
     */
    private readonly sketchEditingVisualPreviewObjectIds = new Set<string>();
    /** 草图编辑命令态外部高亮根对象缓存。 */
    private sketchEditingVisualLayeringHighlightedExternalRoots: THREE.Object3D[] = [];
    /**
     * 是否为草图闭合区域生成并显示上色轮廓。
     *
     * 该值只记录当前开关状态；需要同步既有场景对象时必须调用
     * `setShadedSketchContoursEnabled()`，直接改写属性不会自动重建或清理轮廓。
     */
    public shadedSketchContoursEnabled: boolean = true;
    /** Viewer 当前模式。 */
    private modeContext: ViewerMode = ViewerMode.Default;

    /**
     * 点绘制工具最近创建的点精灵临时引用。
     *
     * 绘制管理器在结束点工具时使用该引用同步交互材质状态并将其清空；Viewer 只持有
     * 场景对象引用，不据此取得精灵几何体或材质的独立所有权。
     */
    public latestCreatedPointObject: THREE.Sprite | null = null;
    /**
     * 矩形线段运行时 UUID 到所属草图集合对象的索引。
     *
     * `readonly` 仅保证 Map 引用不被替换，矩形绘制和调试链仍会写入条目。键和值都属于
     * 当前运行时场景，不是可持久化身份或跨 Viewer 共享数据。
     */
    public readonly rectangleSegmentGeometryMap = new Map<string, SketchCollectionObject>();
    /**
     * 线绘制期间是否暂时抑制草图端点交互层。
     *
     * 该状态由 `SketchDrawingManager` 在进入和退出直线工具时维护，并在 Viewer 销毁时
     * 重置；外部调用方不应把它作为持久设置保存。
     */
    public suppressSketchEndpointLayerDuringLineDrawing: boolean = false;

    // 容器和动画
    /**
     * Viewer 的宿主 DOM 容器。
     *
     * 容器由调用方传入并继续拥有；Viewer 只在其中挂载 WebGPU 画布与 CSS2D 叠加层，
     * `dispose()` 会移除这些子节点，但不会删除宿主容器本身。
     */
    public container: HTMLElement;
    /**
     * 当前 `requestAnimationFrame` 调度句柄。
     *
     * `null` 表示没有已记录的下一帧；渲染循环每帧更新该值，`dispose()` 会取消对应
     * 调度并恢复为 `null`。它是浏览器调度句柄，不是业务对象身份。
     */
    public animationFrameId: number | null = null;
    /** 异步初始化就绪回调。 */
    private resolveReadyPromise: (() => void) | null = null;
    /** 异步初始化失败回调。 */
    private rejectReadyPromise: ((error: unknown) => void) | null = null;
    /** Viewer 就绪 Promise。 */
    private readonly readyPromise: Promise<void>;
    /** Viewer 就绪 Promise 是否已经 resolve 或 reject。 */
    private readySettled = false;
    /** Viewer 是否已经开始资源释放；用于阻止异步初始化继续提交。 */
    private disposeStarted = false;

    // 配置
    /**
     * Viewer 构造参数应用默认值后的运行时配置。
     *
     * 该对象在构造阶段创建，包含渲染、相机、网格、捕捉和宿主回调配置；它描述当前
     * Viewer 的配置快照，不是新的资源所有者，回调和对象字段仍遵循各自类型的生命周期。
     */
    public options: Required<Omit<
        RendererOptions,
        'snapSettings'
        | 'onAssemblyTransformCommitted'
        | 'onAssemblyMateDocumentChanged'
        | 'onSketchRuntimeHistoryCommitted'
        | 'sketchEntityReferenceMoveDetachPort'
    >> & {
        snapSettings: PointSnapSettings | null;
        onAssemblyTransformCommitted?: RendererOptions['onAssemblyTransformCommitted'];
        onAssemblyMateDocumentChanged?: RendererOptions['onAssemblyMateDocumentChanged'];
        onSketchRuntimeHistoryCommitted?: RendererOptions['onSketchRuntimeHistoryCommitted'];
        sketchEntityReferenceMoveDetachPort?: RendererOptions['sketchEntityReferenceMoveDetachPort'];
    };
    /** Worker 管理器。 */
    public readonly workerManager: WorkerManager = workerManager;
    /** 全局 Worker runtime 的 Viewer 独占租约。 */
    private readonly workerRuntimeLease: ViewerWorkerRuntimeLease = viewerWorkerRuntimeLease;
    /** 当前 Viewer 是否已经获取全局 Worker runtime 使用权。 */
    private ownsWorkerRuntimeLease = false;

    /** 插入零部件放置吸附解析器，按需创建以避免污染 Viewer 初始化链。 */
    private assemblyComponentPlacementSnapResolver: AssemblyComponentPlacementSnapResolver | null = null;

    /** 编辑零部件控制器。 */
    private assemblyComponentEditController: AssemblyComponentEditController;

    /**
     * 构造 Viewer 实例并立即完成渲染初始化。
     * @param container 视口容器元素。
     * @param options 渲染器配置。
     * @throws 原样抛出 Worker 租约获取或 Viewer 渲染初始化产生的异常；补偿释放失败时抛出聚合异常。
     */
    constructor(container: HTMLElement, options: RendererOptions = {}) {
        super();
        this.sketchRelationCreationService = new SketchRelationCreationService(this);
        this.workerRuntimeLease.acquire(this);
        this.ownsWorkerRuntimeLease = true;
        let sectionableModelRoot: THREE.ClippingGroup | null = null;
        let sectionViewRenderController: SectionViewRenderController | null = null;
        let patternDirectionArrowManager: PatternDirectionArrowManager | null = null;
        let sectionViewRotationHandleManager: SectionViewRotationHandleManager | null = null;
        let sectionViewController: SectionViewController | null = null;

        try {

        //------ 调试对象中文命名 开始------
        // 在 Viewer 构造阶段立即命名场景，确保后续任何初始化异常都能通过场景名定位来源。
        this.scene.name = Viewer.MAIN_SCENE_DEBUG_NAME;
        this.sceneHelpers.name = Viewer.HELPER_SCENE_DEBUG_NAME;
        sectionableModelRoot = new THREE.ClippingGroup();
        this.sectionableModelRoot = sectionableModelRoot;
        this.sectionableModelRoot.name = 'PuppyCAD可剖切模型根';
        this.sectionableModelRoot.enabled = false;
        this.scene.add(this.sectionableModelRoot);
        //------ 调试对象中文命名 结束------

        //------ 模型显示模式 开始------
        this.modelDisplayModeController = new ModelDisplayModeController(this);
        this.visibleModelEdgeProjectionSourceCollector = new VisibleModelEdgeProjectionSourceCollector({
            getScene: () => this.scene,
            resolveInstancedSurface: (sourceMesh) => this.resolveImportedModelInstancedMeshUnit(sourceMesh)?.object ?? null,
        });
        //------ 模型显示模式 结束------

        this.container = container;
        this.readyPromise = new Promise<void>((resolve, reject) => {
            this.resolveReadyPromise = resolve;
            this.rejectReadyPromise = reject;
        });

        this.options = {
            antialias: options.antialias ?? true,
            backgroundColor: options.backgroundColor ?? 0xe0e3ed,
            //------ 统一相机位置与默认基准面尺寸 开始------
            // 默认网格大小由 100 修正为 0.1（100 mm），相机初始位置由 400 修正为 0.4（400 mm）
            gridSize: options.gridSize ?? 0.1,
            gridDivisions: options.gridDivisions ?? 50,
            cameraPosition: options.cameraPosition ?? [0.4, 0.4, 0.4],
            //------ 统一相机位置与默认基准面尺寸 结束------
            fov: options.fov ?? 45,
            // SDK 初始化入口必须执行产品级设置归一化，旧项目中的关闭值不能进入绘制运行时。
            snapSettings: options.snapSettings
                ? normalizePointSnapSettings(options.snapSettings)
                : null,
            onAssemblyTransformCommitted: options.onAssemblyTransformCommitted,
            onAssemblyMateDocumentChanged: options.onAssemblyMateDocumentChanged,
            onSketchRuntimeHistoryCommitted: options.onSketchRuntimeHistoryCommitted,
            sketchEntityReferenceMoveDetachPort: options.sketchEntityReferenceMoveDetachPort,
        };

        this.sketchRuntimeSnapshotRegistry = createSketchGroupRuntimeSnapshotRegistry({
            resolveSketchGroup: (sketchId) => this.sketchGroupManager?.getGroupById(sketchId) ?? null,
            resolveSketchGroupDisplayState: (sketchId) => this.sketchGroupManager?.getDisplayState(sketchId) ?? null,
            resolveSketchGroupOrder: (sketchId) => this.sketchGroupManager?.getGroupOrder(sketchId) ?? null,
            resolveSketchGroupCreatedAt: (sketchId) => this.sketchGroupManager?.getGroupCreatedAt(sketchId) ?? null,
            restoreMissingSketchGroup: ({sketchGroup, displayState, order, createdAt}) => (
                this.sketchGroupManager?.restoreGroup(sketchGroup, displayState as SketchDisplayState | null, {
                    order,
                    createdAt,
                }) ?? null
            ),
            requestRender: () => this.requestRender(),
        });

        this.debugTool = new DebugTool(this);

        this.sketchEditingVisualLayering = new SketchEditingVisualLayering();
        this.pointerNearbyLabelManager = new PointerNearbyLabelManager(this);

        this.featureExecutionRuntime = new FeatureExecutionRuntime(
            this.workerManager,
            (history) => {
                /**
                 * 该回调是“让 Worker history 成为时间线新权威”的显式入口，
                 * 但降级态下 Worker 永远重建不出失败特征，仍必须先过降级判定，
                 * 否则失败特征会经这条路被抹除并在保存时永久丢失。
                 */
                if (!this.canWorkerHistoryReplaceFullTimeline(history)) {
                    return;
                }
                this.applyWorkerHistoryProjection(history);
            },
            () => this.assemblyComponentEditController?.getDocumentScope() ?? null,
        );
        this.featureDeletionPlanner = new FeatureDeletionPlanner();
        this.bossExtrudeFeatureSessionBridge = new BossExtrudeFeatureSessionBridge();
        this.cutExtrudeFeatureSessionBridge = new CutExtrudeFeatureSessionBridge();
        this.bossExtrudeSceneResolver = new BossExtrudeSceneResolver();
        this.cutExtrudePreviewSeedResolver = new CutExtrudePreviewSeedResolver();
        this.sketchPlanarFaceResolver = new SketchPlanarFaceResolver();
        this.sketchFaceViewFrameBuilder = new SketchFaceViewFrameBuilder();
        this.bossRevolveFeatureSessionBridge = new BossRevolveFeatureSessionBridge();
        this.bossRevolveSceneResolver = new BossRevolveSceneResolver();
        this.bossRevolveAngleDragController = new RevolveAngleDragController();
        this.revolveRegionSelectionManager = new RevolveRegionSelectionManager({
            scene: this.scene,
            getCamera: () => this.camera,
            getContainer: () => this.container,
            resolveSketchGroup: (sketchId) => this.sketchGroupManager?.getGroupById(sketchId) ?? null,
            workerManager: this.workerManager,
            requestRender: () => this.requestRender(),
        });
        this.referencePlaneController = new ReferencePlaneController(this);
        this.referenceAxisController = new ReferenceAxisController(this);
        this.referencePointController = new ReferencePointController(this, {
            documentId: 'viewer-reference-point',
        });
        this.measureController = new MeasureController(this);
        this.filletFeatureSessionBridge = new FilletFeatureSessionBridge();
        this.chamferFeatureSessionBridge = new ChamferFeatureSessionBridge();
        this.shellFeatureSessionBridge = new ShellFeatureSessionBridge();
        this.shellFeatureController = new ShellFeatureController(this);
        this.ribFeatureSessionBridge = new RibFeatureSessionBridge();
        this.ribFeatureController = new RibFeatureController(this);
        const filletPreviewExecution = new FilletPreviewExecution(this.workerManager);
        const filletCommitExecution = new FilletCommitExecution(
            this,
            this.featureExecutionRuntime,
            async (featureId, command) => this.replaceFeatureCommandFromEditSession(featureId, command),
        );
        const shellPreviewExecution = new ShellPreviewExecution(this.workerManager);
        const shellCommitExecution = new ShellCommitExecution(
            this,
            this.featureExecutionRuntime,
            async (featureId, command) => this.replaceFeatureCommandFromEditSession(featureId, command),
        );
        const ribPreviewExecution = new RibPreviewExecution(this.workerManager);
        const ribCommitExecution = new RibCommitExecution(
            this,
            this.featureExecutionRuntime,
            async (featureId, command) => this.replaceFeatureCommandFromEditSession(featureId, command),
        );
        const chamferPreviewExecution = new ChamferPreviewExecution(this.workerManager);
        const chamferCommitExecution = new ChamferCommitExecution(
            this,
            this.featureExecutionRuntime,
            async (featureId, command) => this.replaceFeatureCommandFromEditSession(featureId, command),
        );
        this.moveComponentController = new MoveComponentController(this, {
            onAssemblyTransformCommitted: options.onAssemblyTransformCommitted,
        });
        this.assemblyMateController = new AssemblyMateController(this, {
            onStateChanged: () => {
                this.requestRender();
            },
            onMateDocumentChanged: options.onAssemblyMateDocumentChanged,
        });
        this.linearComponentPatternManager = new LinearComponentPatternManager(this);
        this.linearComponentPatternController = this.linearComponentPatternManager.controller;
        // 已提交阵列属于正式模型结果，必须与特征体、导入体共用局部裁剪根；预览仍由独立渲染器挂在主场景。
        this.addSectionableObject(this.linearComponentPatternManager);
        const extrudePreviewExecution = new ExtrudePreviewExecution(this.workerManager);
        const extrudeCommitExecution = new ExtrudeCommitExecution(
            this,
            this.featureExecutionRuntime,
            async (featureId, command) => this.replaceFeatureCommandFromEditSession(featureId, command),
        );
        const revolvePreviewExecution = new RevolvePreviewExecution(this.workerManager);
        const revolveCommitExecution = new RevolveCommitExecution(
            this,
            this.featureExecutionRuntime,
            async (featureId, command) => this.replaceFeatureCommandFromEditSession(featureId, command),
        );
        this.featurePreviewExecutionRegistry = new FeaturePreviewExecutionRegistry([
            new ChamferPreviewExecutionHandler(chamferPreviewExecution),
            new ExtrudePreviewExecutionHandler(extrudePreviewExecution),
            new FilletPreviewExecutionHandler(filletPreviewExecution),
            new RevolvePreviewExecutionHandler(revolvePreviewExecution),
            new ShellPreviewExecutionHandler(shellPreviewExecution),
            new RibPreviewExecutionHandler(ribPreviewExecution),
        ]);
        this.featurePreviewRequestQueue = new FeaturePreviewRequestQueue<ViewerFeaturePreviewInput, ViewerFeaturePreviewResult>({
            delayMs: INTERACTIVE_FEATURE_PREVIEW_DELAY_MS,
            execute: async (input) => this.executeFeaturePreviewNow(input),
        });
        this.featureCommitExecutionRegistry = new FeatureCommitExecutionRegistry([
            new ChamferCommitExecutionHandler(chamferCommitExecution, this.featureExecutionRuntime),
            new ExtrudeCommitExecutionHandler(extrudeCommitExecution, this.featureExecutionRuntime),
            new FilletCommitExecutionHandler(filletCommitExecution, this.featureExecutionRuntime),
            new RevolveCommitExecutionHandler(revolveCommitExecution, this.featureExecutionRuntime),
            new ShellCommitExecutionHandler(shellCommitExecution, this.featureExecutionRuntime),
            new RibCommitExecutionHandler(ribCommitExecution, this.featureExecutionRuntime),
        ]);
        sectionViewRenderController = new SectionViewRenderController(this.sectionableModelRoot);
        this.sectionViewRenderController = sectionViewRenderController;
        this.initializeRenderer();
        this.assemblyComponentEditController = new AssemblyComponentEditController(this);
        // 两个交互管理器会注册全局事件并持有 GPU 资源，必须等所有前置同步初始化成功后再创建。
        patternDirectionArrowManager = new PatternDirectionArrowManager(this);
        this.patternDirectionArrowManager = patternDirectionArrowManager;
        sectionViewRotationHandleManager = new SectionViewRotationHandleManager(this);
        this.sectionViewRotationHandleManager = sectionViewRotationHandleManager;
        sectionViewController = new SectionViewController(
            this,
            this.sectionViewRenderController,
        );
        this.sectionViewController = sectionViewController;
        registerSectionableModelRoot(this.scene, {
            root: this.sectionableModelRoot,
            onContentChanged: () => this.sectionViewController.notifySectionableContentChanged(),
        });
        window.viewer = this;
        } catch (error) {
            // renderer.init() 可能已经排入微任务；构造失败后必须阻止匿名 Viewer 继续异步创建资源。
            this.disposeStarted = true;
            const cleanupErrors: unknown[] = [];
            try {
                unregisterSectionableModelRoot(this.scene);
            } catch (cleanupError) {
                cleanupErrors.push(cleanupError);
            }
            try {
                sectionViewController?.dispose();
            } catch (cleanupError) {
                cleanupErrors.push(cleanupError);
            }
            try {
                sectionViewRotationHandleManager?.dispose();
            } catch (cleanupError) {
                cleanupErrors.push(cleanupError);
            }
            try {
                patternDirectionArrowManager?.dispose();
            } catch (cleanupError) {
                cleanupErrors.push(cleanupError);
            }
            try {
                sectionViewRenderController?.dispose();
            } catch (cleanupError) {
                cleanupErrors.push(cleanupError);
            }
            try {
                sectionableModelRoot?.removeFromParent();
                sectionableModelRoot?.clear();
            } catch (cleanupError) {
                cleanupErrors.push(cleanupError);
            }
            try {
                this.workerRuntimeLease.release(this);
            } catch (cleanupError) {
                cleanupErrors.push(cleanupError);
            }
            this.ownsWorkerRuntimeLease = false;
            if (cleanupErrors.length > 0) {
                throw new AggregateError(
                    [error, ...cleanupErrors],
                    'Viewer 同步初始化失败，且构造期资源补偿释放不完整',
                );
            }
            throw error;
        }
    }

    /**
     * 等待 Viewer 底层渲染链初始化完成。
     *
     * 设计说明：
     * 1. WebGPU 渲染器初始化是异步的，宿主层不能再假设 `new Viewer()` 后立刻可用；
     * 2. 统一由 cad-core 对外暴露就绪门面，避免 web-client 猜测内部时序；
     * 3. Editor / Preview / 未来其它宿主都应复用这一条链。
     *
     * @returns 初始化完成 Promise。
     */
    public whenReady(): Promise<void> {
        return this.readyPromise;
    }

    /**
     * 初始化几何内核。
     *
     * 设计说明：
     * 1. `whenReady()` 只表示 Viewer 渲染链就绪，不等价于 Puppy CAD 内核 Worker 已实例化；
     * 2. 宿主层只能通过 Viewer 门面等待几何内核，不能直接依赖 WorkerManager；
     * 3. WorkerManager 自身已保证重复初始化会复用同一个 Promise。
     *
     * @returns 几何内核初始化完成 Promise。
     */
    public async initializeGeometryKernel(): Promise<void> {
        await this.workerManager.init();
    }

    /**
     * 读取当前渲染统计数据。
     *
     * @returns 当前 Viewer 渲染统计。
     */
    public getRenderStats(): ViewerRenderStats {
        const geometryStats = this.collectVisibleMeshGeometryStats();
        return {
            vertices: geometryStats.vertices,
            triangles: geometryStats.triangles,
        };
    }

    /**
     * 统计当前可见 Mesh 的顶点数和三角面数量。
     *
     * @returns 几何统计数据。
     */
    private collectVisibleMeshGeometryStats(): Pick<ViewerRenderStats, 'vertices' | 'triangles'> {
        const stats = {
            vertices: 0,
            triangles: 0,
        };
        this.scene.traverse((object) => {
            if (!(object instanceof THREE.Mesh) || !this.isObjectVisibleInSceneTree(object)) {
                return;
            }
            if (this.isRenderStatsExcludedMesh(object)) {
                return;
            }
            const geometry = object.geometry;
            if (!(geometry instanceof THREE.BufferGeometry)) {
                return;
            }
            const positionAttribute = geometry.getAttribute('position');
            if (!(positionAttribute instanceof THREE.BufferAttribute)) {
                return;
            }
            stats.vertices += positionAttribute.count;
            const indexAttribute = geometry.getIndex();
            stats.triangles += Math.floor((indexAttribute?.count ?? positionAttribute.count) / 3);
        });
        return stats;
    }

    /**
     * 判断 Mesh 是否应从模型统计中排除。
     *
     * 设计说明：
     * 1. footer 统计表达的是用户模型网格规模，不是交互代理和辅助对象规模；
     * 2. 面代理会复用结果体 position，并把三角面重复计入统计，圆角后会让数字接近翻倍；
     * 3. 因此统计必须从 Viewer 底层排除这些代理对象，避免 UI 层按名称或 CSS 猜测。
     *
     * @param object 目标 Mesh。
     * @returns 是否排除。
     */
    private isRenderStatsExcludedMesh(object: THREE.Mesh): boolean {
        return Reflect.get(object, 'isFeatureFace') === true
            || Reflect.get(object, 'isFeaturePreviewLine') === true;
    }

    /**
     * 判断对象及其父级是否整体可见。
     *
     * @param object 目标对象。
     * @returns 是否可见。
     */
    private isObjectVisibleInSceneTree(object: THREE.Object3D): boolean {
        let cursor: THREE.Object3D | null = object;
        while (cursor) {
            if (!cursor.visible) {
                return false;
            }
            cursor = cursor.parent;
        }
        return true;
    }

    //------ Viewer 构造初始化 开始------
    /**
     * 初始化渲染器。
     * 说明：
     * 1. 该逻辑仅允许由构造函数触发；
     * 2. 初始化失败直接抛错，由调用方决定如何处理中断；
     * 3. 初始化完成后 Viewer 立即处于可用状态。
     */
    private initializeRenderer(): void {
        try {
            //------ 主题视口背景 开始------
            // 当前 WebGPU 视口使用透明画布，最终底色由宿主 render-container 的主题背景提供。
            //------ 主题视口背景 结束------
            const renderer = new THREE.WebGPURenderer({
                alpha: true,
                antialias: this.options.antialias,
                logarithmicDepthBuffer: true
            });
            this.renderer = renderer;
            renderer.setSize(this.container.clientWidth, this.container.clientHeight);
            renderer.setPixelRatio(window.devicePixelRatio);
            // renderer.toneMapping = THREE.NeutralToneMapping;
            // renderer.toneMappingExposure = 1;
            // renderer.outputColorSpace = THREE.SRGBColorSpace;
            //------ 主题视口背景 开始------
            // 保持 canvas 透明，避免 renderer DOM 背景覆盖亮色/暗色主题下的视口底色。
            renderer.domElement.style.background = 'transparent';
            //------ 主题视口背景 结束------
            this.container.appendChild(renderer.domElement);

            // 创建 CSS2D 渲染器（用于渲染基准面标签）。
            this.labelRenderer = new CSS2DRenderer();
            this.labelRenderer.setSize(this.container.clientWidth, this.container.clientHeight);
            this.labelRenderer.domElement.style.position = 'absolute';
            this.labelRenderer.domElement.style.top = '0';
            this.labelRenderer.domElement.style.left = '0';
            this.labelRenderer.domElement.style.pointerEvents = 'none';
            this.labelRenderer.domElement.style.zIndex = '10';
            this.labelRenderer.domElement.style.userSelect = 'none';
            this.container.appendChild(this.labelRenderer.domElement);

            this.resizeObserver = new ResizeObserver(() => {
                this.resize();
                // 连续容器布局变化只绘制当前宿主帧的最终尺寸。
                this.requestRender();
            });
            this.resizeObserver.observe(this.container);

            void renderer.init().then(() => {
                /**
                 * WebGPU 初始化可能晚于 Editor 根文档切换完成。
                 * 已释放 Viewer 禁止继续创建文档级 Controller、启动监听或发布创建完成事件。
                 */
                if (this.disposeStarted) {
                    return;
                }
                this.cameraManager = new CameraManager(this, {
                    cameraPosition: this.options.cameraPosition,
                    fov: this.options.fov,
                });
                this.cameraManager.init(this.container, renderer);
                this.pipelineManager = new PipelineManager(
                    this,
                    renderer,
                    this.sectionViewRenderController,
                );

                this.lightManager = new LightManager(this);

                this.helperManager = new HelperManager(this, {
                    gridSize: this.options.gridSize,
                    gridDivisions: this.options.gridDivisions,
                    gridColor1: 0x111318,
                    gridColor2: 0x7c8092,
                    axesSize: 0.05,
                    showGrid: false,
                });

                this.meshManager = new MeshManager(this);
                this.syncModelDisplayMode();
                this.sketchDrawingManager = new SketchDrawingManager(this);
                this.sketchDrawingManager.setLabelsEnabled(!!this.labelRenderer);
                this.sketchDimensionManager = new SketchDimensionManager(this);

                // development 分支已有点捕捉配置链，这里在 WebGPU 初始化完成后恢复应用。
                this.pointSnapManager = new PointSnapManager(this);
                if (this.options.snapSettings) {
                    this.pointSnapManager.updateSettings(this.options.snapSettings);
                }

                this.eventManager = new EventManager(this);
                this.eventManager.bind();
                this.cameraManager.startViewOrientationInteraction();

                this.viewOrientationPreviewRenderer = new ViewOrientationPreviewRenderer();
                this.viewOrientationPreviewRenderer.start(this, this.container);

                this.sketchDrawingManager.registerBuiltInHandlers();
                this.sketchDrawingManager.start();

                this.entitySelectionManager = new EntitySelectionManager(
                    this,
                    this.meshManager.getSceneObjectMembershipSource(),
                );
                this.entitySelectionManager.start();
                this.contextMenuController = new ContextMenuController(this);
                this.contextMenuController.start();
                this.importedModelSelectionHighlighter = new ImportedModelSelectionHighlighter({
                    scene: this.scene,
                    entityVisualEnabled: false,
                    /** 选中拓扑可以引用已被实例投影移出场景树的 source Mesh。 */
                    resolveSceneObjectById: objectId => this.findSceneObjectById(objectId),
                    /**
                     * 只接管活动实例 source Mesh；普通对象继续由高亮层使用 Three.js 父链读取。
                     */
                    copyObjectWorldMatrix: (object, targetMatrix) => {
                        if (
                            !(object instanceof THREE.Mesh)
                            || !this.prepareImportedModelSourceMeshForSpatialRead(object)
                        ) {
                            return false;
                        }
                        targetMatrix.copy(object.matrixWorld);
                        return true;
                    },
                    /**
                     * Viewer 侧显式接入 imported-model 兼容 bridge，
                     * 避免高亮控制器只能依赖默认构造来补齐旧 SelectionExtension 协议。
                     */
                    selectionExtension: new GlbSelectionExtension(),
                    observeEntityInteraction: (eventType) => this.observeEntityInteraction(eventType),
                    requestRender: () => this.requestRender(),
                    visualUpdateScheduler: this,
                });
                this.importedModelSelectionHighlighter.start();
                this.interactionStateManager = new InteractionStateManager(this);
                this.interactionStateManager.start();

                //------ 统一相机位置与默认基准面尺寸 开始------
                // 默认基准面长宽初始化为毫米级别（0.18米 和 0.11米），以匹配毫米级建模的比例。
                this.datumPlaneManager = new DatumPlaneManager(this, {
                    defaultWidth: 0.18,
                    defaultHeight: 0.11,
                    createDefaultPlanes: true,
                });
                //------ 统一相机位置与默认基准面尺寸 结束------
                this.datumAxisManager = new DatumAxisManager(this);
                this.datumPointManager = new DatumPointManager(this);
                this.datumPlaneManager.setCameraManager(this.cameraManager);

                this.sketchGroupManager = new SketchGroupManager(this);
                this.sketchSessionReferencePointManager = new SketchSessionReferencePointManager({
                    getScene: () => this.scene,
                    getReferencePlane: () => (
                        this.sketchGroupManager?.activeSketchGroup?.referencePlane
                        ?? this.currentReferencePlane
                    ),
                    getCamera: () => this.camera,
                    getViewportSize: (target) => this.renderer.getSize(target),
                    collectVisibleModelEdgeProjectionSources: () => this.visibleModelEdgeProjectionSourceCollector.collect(),
                    requestRender: () => this.requestRender(),
                    onBeforeDestroy: (points) => {
                        this.sketchDrawingManager?.clearSessionReferencePointSnapState(points);
                    },
                });
                this.initializeSketchEntityReferenceDocumentRuntime();
                this.sketchSessionTerminator = new SketchSessionTerminator({
                    sketchGroupManager: this.sketchGroupManager,
                    commitSketchDefinitionChanges: (sketchId) => this.commitSketchDefinitionChanges(sketchId),
                    finalizeEndedGroupBeforeLifecycle: async (endResult) => this.finalizeEndedSketchDisplayState(endResult),
                    removeDatumPlane: (planeId) => this.removeDatumPlane(planeId),
                    isTemporaryDatumPlane: (planeId) => this.isTemporaryDatumPlane(planeId),
                    getCurrentReferencePlane: () => this.currentReferencePlane,
                    exitDatumPlaneView: () => this.exitDatumPlaneView(),
                });
                this.featureRebuildOrchestrator = new FeatureRebuildOrchestrator(this, this.featureExecutionRuntime);
                this.initializeSketchFeaturePrefixEditRuntime();
                this.initializeFeatureTimelineRebuildRuntime();
                this.featureEditOrchestrator = new FeatureEditOrchestrator(this, this.featureExecutionRuntime);

                this.sketchCornerController = new SketchCornerDrawingController(this);
                this.sketchTrimController = new SketchTrimController(this);
                //------ 智能尺寸 开始------
                this.sketchSmartDimensionController = new SketchSmartDimensionController(
                    this.createSmartDimensionCommandHost(),
                );
                // 命令快照 → 引用选中高亮同步（选择视觉与控制器真相单向对齐）。
                this.sketchSmartDimensionController.observe((snapshot) => {
                    this.syncSmartDimensionSelectionHighlight(snapshot);
                });
                // 持久尺寸标注：仓库事件驱动的增量渲染（创建/撤销/重做/加载自动跟随）。
                if (this.scene && this.sketchGroupManager) {
                    this.sketchDimensionAnnotationRenderer.attach({
                        scene: this.scene,
                        repository: this.sketchGroupManager.dimensionRepository,
                        resolveSketchGroup: (sketchId) => this.resolveSketchGroupByPersistentId(sketchId),
                        hitCallbacks: this.createSketchDimensionHitCallbacks(),
                        resolveArrowWorldLength: () => this.resolveSketchDimensionArrowWorldLength(),
                    });
                    /**
                     * 尺寸关系视图对账：尺寸与其几何关系是同一条约束的两个视图，
                     * 仓库事件（创建/改值/删除/角色切换/撤销重做恢复/加载）后重放即可收敛，
                     * 拖拽求解链路据此不能把几何拖到违反已标注尺寸。
                     */
                    this.disposeSketchDimensionRelationSubscription = this.sketchGroupManager.dimensionRepository
                        .observe((event) => {
                            this.synchronizeSketchDimensionRelations(event.sketchId);
                            // 尺寸值修改没有几何写回，等距样条需按新名义距离主动重算
                            if (event.type === 'updated') {
                                this.curveOffsetDerivedSyncService
                                    ?.synchronizeAfterDimensionChange(event.sketchId);
                            }
                        });
                    //------ 等距样条派生同步 开始------
                    /**
                     * 等距样条通过 curve-offset-distance 尺寸与源样条成对；
                     * 每次参数求解写回后，由同步服务把被编辑侧按名义距离重算到对侧
                     * （源 ↔ 等距双向），并在 Worker 权威重采样落地后二次同步。
                     */
                    this.curveOffsetDerivedSyncService = new CurveOffsetDerivedSyncService({
                        repository: this.sketchGroupManager.dimensionRepository,
                        resolveSketchGroup: (sketchId) => this.resolveSketchGroupByPersistentId(sketchId),
                        notifyNotice: (message, level) => this.drawingNoticeCallback?.(message, level),
                        requestRender: () => this.requestRender(),
                    });
                    this.sketchParametricSolveCoordinator.configureCurveOffsetDerivedSync(
                        this.curveOffsetDerivedSyncService,
                    );
                    //------ 等距样条派生同步 结束------
                    //------ 等距链派生同步 开始------
                    /**
                     * 组合图形等距实体只给链首创建距离尺寸；链记录挂在尺寸 metadata
                     * 上，改尺寸值时由本服务整链重算（源链为权威侧、等距链按新名义
                     * 距离偏移 + 延伸/相交处理并静默写回），保证整条轮廓一起移动。
                     */
                    this.offsetChainDerivedSyncService = new OffsetChainDerivedSyncService({
                        repository: this.sketchGroupManager.dimensionRepository,
                        resolveSketchGroup: (sketchId) => this.resolveSketchGroupByPersistentId(sketchId),
                        notifyNotice: (message, level) => this.drawingNoticeCallback?.(message, level),
                        requestRender: () => this.requestRender(),
                    });
                    //------ 等距链派生同步 结束------
                    // 草图定义提交（绘制/拖拽/剪裁等几何变更）后重测 driven 值并重算尺寸布局。
                    this.sketchDimensionRefreshSubscription = this.sketchGroupManager.definitionChangeHub
                        .observeCommitted()
                        .subscribe((event) => {
                            const sketchId = this.sketchGroupManager?.getGroupById(event.sketchId)?.persistentId
                                ?? event.sketchId;

                            this.remeasureSketchDrivenDimensions(sketchId);
                            this.sketchDimensionAnnotationRenderer.refreshSketch(sketchId);
                        });
                }
                //------ 智能尺寸 结束------
                this.sketchOffsetController = new SketchOffsetController(this);
                this.sketchMoveEntitiesController = new SketchMoveEntitiesController(
                    this,
                    this.options.sketchEntityReferenceMoveDetachPort
                        ?? this.sketchEntityReferenceDocumentRuntime?.moveDetachPort
                        ?? undefined,
                );
                this.sketchCopyEntitiesController = new SketchCopyEntitiesController(this);
                this.sketchRotateEntitiesController = new SketchRotateEntitiesController(this);
                this.sketchCircularPatternController = new SketchCircularPatternController(this);
                this.sketchMirrorEntitiesController = new SketchMirrorEntitiesController(this);
                this.sketchEntityClipboardController = new SketchEntityClipboardController(this);

                this.requestRender();
                this.renderer.setAnimationLoop(this.animate).then()
                this.readySettled = true;
                this.resolveReadyPromise?.();
                this.resolveReadyPromise = null;
                this.rejectReadyPromise = null;
                Viewer.viewerCreated$.next(this);
            }).catch((error) => {
                if (this.disposeStarted) {
                    return;
                }
                this.readySettled = true;
                this.rejectReadyPromise?.(error);
                this.resolveReadyPromise = null;
                this.rejectReadyPromise = null;
                console.log('THREE.js 渲染器初始化失败:', error);
            });
        } catch (error) {
            this.readySettled = true;
            this.rejectReadyPromise?.(error);
            this.resolveReadyPromise = null;
            this.rejectReadyPromise = null;
            console.log('THREE.js 渲染器初始化失败:', error);
            throw error;
        }
    }
    //------ Viewer 构造初始化 结束------

    //------ WebGPU Inspector 接入 开始------
    /**
     * 动态创建 WebGPU Inspector。
     *
     * 说明：
     * 1. 默认初始化不创建 Inspector，避免常规 Viewer 启动时挂载调试 UI；
     * 2. 重复调用会返回已存在的 Inspector，避免重复注册 renderer inspector；
     * 3. 创建后同时写入 `renderer.inspector` 与 `viewer.inspector`，前者服务 three.js，后者作为 PuppyCAD 全局调试入口。
     *
     * @returns 当前 Viewer 使用的 Inspector。
     * @throws Viewer 渲染器尚未初始化时无法创建 Inspector。
     */
    public createInspector(): Inspector {
        if (this.inspector) {
            return this.inspector;
        }

        if (!this.renderer) {
            throw new Error('创建 WebGPU Inspector 失败：Viewer 渲染器尚未初始化。');
        }

        const inspector = new Inspector();
        this.renderer.inspector = inspector;
        this.inspector = inspector;
        inspector.init()
        return inspector;
    }

    /**
     * 动态移除 WebGPU Inspector。
     *
     * 说明：
     * 1. 移除时会清理 Inspector 面板 DOM，避免调试 UI 残留在 Viewer 容器中；
     * 2. renderer 侧恢复为 three.js 默认 InspectorBase，保持 renderer 内部 inspector 调用链有效；
     * 3. Viewer 侧清空 `inspector`，后续可再次通过 `createInspector()` 创建。
     */
    public removeInspector(): void {
        const inspector = this.inspector;
        if (!inspector) {
            return;
        }

        inspector.domElement.parentElement?.removeChild(inspector.domElement);
        if (this.renderer) {
            this.renderer.inspector = new THREE.InspectorBase();
        }
        this.inspector = null;
    }
    //------ WebGPU Inspector 接入 结束------

    /**
     * 消费宿主动画帧：先推进派生状态，再按需提交一次完整画面。
     */
    public animate = (): void => {
        this.frameScheduler.flushFrame();
    };

    /** 推进一帧相机与屏幕尺寸派生状态，不在静止时提交 GPU 画面。 */
    private prepareRenderFrame(): void {
        if (!this.renderer || !this.camera || !this.pipelineManager) return;
        this.cameraManager?.updateFrame();
        this.entitySelectionManager?.flushPendingHoverInput();
        this.refreshSketchDimensionArrowScaleOnZoomChange();
    }

    /** 相机缩放跨阈值变化时重算尺寸箭头屏幕尺寸。 */
    private refreshSketchDimensionArrowScaleOnZoomChange(): void {
        const camera = this.camera;

        if (!(camera instanceof THREE.OrthographicCamera)) {
            return;
        }
        const zoom = camera.zoom || 1;

        // 相对变化 >0.5% 才重建（缩放静止时零开销）。
        if (Math.abs(zoom - this.lastSketchDimensionArrowZoom) / this.lastSketchDimensionArrowZoom > 0.005) {
            this.lastSketchDimensionArrowZoom = zoom;
            this.sketchDimensionAnnotationRenderer.refreshArrowScale();
            this.requestRender();
        }
    }

    /**
     * 登记普通重绘请求，由同一动画帧统一提交；不立即执行渲染管线。
     */
    public requestRender(): void {
        this.frameScheduler.requestRender();
    }

    /**
     * 合并同一视觉所有者的待处理状态，保证提交画面前只应用最后一次变化。
     * @param owner 高亮或派生视觉的生命周期所有者。
     * @param update 读取最终状态的稳定回调。
     */
    public scheduleVisualUpdate(owner: object, update: () => void): void {
        this.frameScheduler.scheduleVisualUpdate(owner, update);
    }

    /**
     * 在命令取消、运行时替换或资源释放时移除未消费的派生视觉更新。
     * @param owner 即将失效的视觉所有者。
     */
    public cancelVisualUpdate(owner: object): void {
        this.frameScheduler.cancelVisualUpdate(owner);
    }

    /** @param owner 派生资源所有者。 @param task 仅在无前台绘制请求的帧执行的预热事务。 */
    public scheduleIdleRender(owner: object, task: ViewerIdleRenderTask): void {
        this.frameScheduler.scheduleIdleRender(owner, task);
    }

    /** @param owner 运行时替换或销毁时取消该所有者的预热，不触发重绘。 */
    public cancelIdleRender(owner: object): void {
        this.frameScheduler.cancelIdleRender(owner);
    }

    /**
     * 同步结算当前视觉并提交画面，保留截图、导出和 SDK 显式同步调用的既有语义。
     * 普通交互应调用 requestRender；管线执行中重入会保留为下一帧请求，避免递归渲染。
     */
    public requestImmediateRender(): void {
        this.frameScheduler.renderImmediately();
    }

    /**
     * 提交完整画面；相机状态已在调度准备阶段推进，不能再次消耗阻尼或动画采样。
     * @returns 管线可用且实际完成提交时返回 true。
     */
    private renderFrame(): boolean {
        if (!this.renderer || !this.scene || !this.camera || !this.pipelineManager) {
            return false;
        }

        //------ 调试Box禁用 开始------
        // 临时禁用 DebugTool 的每帧同步，避免创建与刷新调试 Box/BVH Helper。
        // this.debugTool.syncFeatureResultMeshBVHHelpers();
        //------ 调试Box禁用 结束------

        this.cameraManager?.render(false);
        this.pipelineManager.render();
        this.labelRenderer?.render(this.scene, this.camera);
        return true;
    }

    //------ 模型显示模式 开始------
    /**
     * 设置模型显示模式。
     *
     * 设计说明：
     * 1. web-client 只能通过 Viewer 门面切换显示模式；
     * 2. 具体场景对象遍历和材质规则由 ModelDisplayModeController 统一处理；
     * 3. 该显示状态只影响当前 Viewer 会话，不写入 CAD 主模型历史。
     *
     * @param displayMode 目标显示模式。
     */
    public setModelDisplayMode(displayMode: ModelDisplayMode): void {
        this.importedModelInstancedRenderCoordinator.executeWithBusinessSourceMeshes(() => {
            this.modelDisplayModeController.setDisplayMode(displayMode);
        });
        // 材质投影键随显示模式变化，必须原子重建实例组，不能原地污染同批其它 occurrence。
        this.importedModelInstancedRenderCoordinator.rebuild();
        // 实例组和显示边发布完成后再失效捕捉缓存，避免沿用旧表面的可见边投影。
        this.sketchSessionReferencePointManager?.invalidateVisibleModelEdgeProjection();
    }

    /**
     * 读取当前模型显示模式。
     *
     * @returns 当前显示模式。
     */
    public getModelDisplayMode(): ModelDisplayMode {
        return this.modelDisplayModeController.getDisplayMode();
    }

    /**
     * 同步场景对象到当前模型显示模式。
     *
     * 说明：该方法供 MeshManager 在新增正式结果体后调用，也供导入模型加载完成后统一创建拓扑显示边。
     */
    public syncModelDisplayMode(): void {
        this.modelDisplayModeController.syncSceneDisplayMode();
        this.sketchSessionReferencePointManager?.invalidateVisibleModelEdgeProjection();
    }

    //------ 导入模型实例化投影 开始------
    /**
     * 在组合装配完成场景挂载后自动建立 InstancedMesh 派生投影。
     *
     * @param result 已完成最终 occurrence 身份与拓扑注册的组合装配结果。
     * @returns 当前实例化统计；失败时返回 fallback 状态且普通 Mesh 保持可用。
     */
    public activateImportedModelInstancing(
        result: ComposedAssemblyResult,
    ): PuppyCAD.ImportedModelInstancingStats {
        this.importedModelInstancedRenderCoordinator.activate(result, this.scene);
        // 实例分桶已同步发布；显示交给宿主帧与后续加载投影一起结算。
        this.requestRender();
        return this.importedModelInstancedRenderCoordinator.getStats();
    }

    /**
     * 在当前显示模式或装配成员集合变化后重建实例投影。
     *
     * @returns 重建后的实例化统计。
     */
    public rebuildImportedModelInstancing(): PuppyCAD.ImportedModelInstancingStats {
        this.importedModelInstancedRenderCoordinator.rebuild();
        return this.importedModelInstancedRenderCoordinator.getStats();
    }

    /**
     * 替换 cad-core 领域贡献的正式运行时 occurrence，并纳入统一实例化投影。
     *
     * 该门面供线性零部件阵列等领域在业务对象创建、编辑、删除或恢复后调用；领域层不接触
     * Registry、Unit 或 GPU instanceId，全部分桶、抑制和回映射仍由 Coordinator 负责。
     *
     * @param ownerToken 领域生命周期稳定 owner。
     * @param occurrences 当前 owner 的完整运行时 occurrence 集合。
     * @returns 重建后的实例化统计。
     */
    public replaceImportedModelInstancingRuntimeOccurrences(
        ownerToken: string,
        occurrences: readonly PuppyCAD.ImportedModelInstancingOccurrence[],
    ): PuppyCAD.ImportedModelInstancingStats {
        this.importedModelInstancedRenderCoordinator.replaceRuntimeOccurrences(
            ownerToken,
            occurrences,
        );
        return this.importedModelInstancedRenderCoordinator.getStats();
    }

    /**
     * 解析组件对象当前所属的共享实例化 definition 身份。
     *
     * @param object 组件根或其后代对象。
     * @returns definitionKey；当前对象不属于活动 occurrence 时返回 null。
     */
    public resolveImportedModelInstancingDefinitionKey(
        object: THREE.Object3D | null | undefined,
    ): string | null {
        return this.importedModelInstancedRenderCoordinator.resolveDefinitionKey(object);
    }

    /**
     * 在标签切换或挂载非组合模型前恢复普通 Mesh 并结束当前实例投影。
     */
    public deactivateImportedModelInstancing(): void {
        this.importedModelInstancedRenderCoordinator.deactivate();
    }

    /**
     * 将实际发生变换的组件对象直接同步到关联实例矩阵槽。
     *
     * 该入口只供移动、旋转、Mate 和历史恢复等明确变换出口调用；普通相机、hover、选择或
     * 标签重绘不会触发实例矩阵扫描。
     *
     * @param changedObjects 单个变化对象或同一操作联动变化的对象集合。
     * @returns 实际写入 GPU 的实例矩阵槽数量。
     */
    public updateImportedModelInstanceMatrices(
        changedObjects: THREE.Object3D | readonly THREE.Object3D[],
    ): number {
        const objects = Array.isArray(changedObjects) ? changedObjects : [changedObjects];
        const assemblySlotCount = this.importedModelInstancedRenderCoordinator.updateObjectTransforms(objects);
        const patternSlotCount = this.linearComponentPatternManager?.updateInstancedTransforms(objects) ?? 0;
        if (objects.length > 0) this.entitySelectionManager?.invalidateHoverPick();
        return assemblySlotCount + patternSlotCount;
    }

    /**
     * 统一同步装配组件位姿变化产生的全部派生副作用。
     *
     * 该入口由 Mate、Move/Rotate 和历史恢复等业务位姿出口复用；业务对象已在调用前完成写回，
     * 本方法只负责重算装配体基准、更新实例矩阵并在批次末尾请求渲染。
     *
     * @param changedObjects 本批实际变化的组件 transform 根。
     * @param options 派生副作用配置。
     */
    public synchronizeAssemblyComponentTransforms(
        changedObjects: readonly THREE.Object3D[],
        options?: PuppyCAD.AssemblyComponentTransformEffectsOptions,
    ): void {
        this.assemblyComponentTransformEffectsCoordinator.synchronize(changedObjects, options);
    }

    /**
     * 更新临时外观 owner 对单个 occurrence 的 source fallback 请求。
     *
     * @param ownerToken 临时外观生命周期 owner。
     * @param occurrenceId 目标组件 occurrence；为空时清除该 owner。
     * @param componentObject 当前运行时组件根，用于稳定 ID 编码形式不一致时精确回映射 occurrence。
     * @returns 实际改变实例投影成员集合时返回 true。
     */
    public setImportedModelInstanceSourceFallback(
        ownerToken: string,
        occurrenceId: string | null,
        componentObject: THREE.Object3D | null = null,
    ): boolean {
        return this.importedModelInstancedRenderCoordinator.setTemporarySourceFallback(
            ownerToken,
            occurrenceId,
            componentObject,
        );
    }

    /** @returns 当前导入模型实例化运行时统计。 */
    public getImportedModelInstancingStats(): PuppyCAD.ImportedModelInstancingStats {
        return this.importedModelInstancedRenderCoordinator.getStats();
    }

    /**
     * 判断场景对象是否为当前 Viewer 正式发布的导入模型实例组。
     *
     * @param object 待判断场景对象。
     * @returns 是否存在活动 Unit 映射。
     */
    public isImportedModelInstancedMesh(object: THREE.Object3D | null | undefined): boolean {
        return this.importedModelInstancedMeshRegistry.has(object);
    }

    /**
     * 更新单组件的实例临时透明显示；原 Unit 和 source 脱离状态保持。
     * @param owner 命令外观 owner。
     * @param component 当前组件根；null 清除该 owner。
     * @param opacity 临时透明度。
     * @returns 是否更新显示状态。
     */
    public setImportedModelInstanceTemporaryOpacity(owner: string, component: THREE.Object3D | null, opacity: number): boolean {
        const changed = this.importedModelInstancedMeshRegistry.setTemporaryOpacity(owner, component, opacity);
        if (changed) { this.entitySelectionManager?.invalidateHoverPick(); this.requestRender(); }
        return changed;
    }

    /**
     * 读取当前 imported 物理候选的共享只读快照。
     *
     * 候选源与 MeshManager 共用场景成员监听；显隐和语义资格在读取时按当前状态筛选。
     * @returns MeshManager 未就绪时为 null；就绪后返回不允许调用方修改的候选快照。
     */
    public getImportedModelRaycastCandidates(): readonly THREE.Object3D[] | null {
        if (!this.meshManager) return null;
        if (!this.importedModelRaycastCandidateIndex) {
            this.importedModelRaycastCandidateIndex = new ImportedModelRaycastCandidateIndex(
                this.meshManager.getSceneObjectMembershipSource(),
                object => this.isImportedModelInstancedMesh(object),
                {
                    getMetadataRevision: ImportedModelObjectMetadataService.getSemanticRevision,
                    getTopologyRevision: getImportedModelTopologySemanticRevision,
                    getInstancingRevision: () => this.importedModelInstancedMeshRegistry.getSemanticRevision(),
                },
            );
        }
        return this.importedModelRaycastCandidateIndex.collect();
    }

    /**
     * 读取 imported 物理候选索引统计。
     *
     * @returns 索引尚未初始化时返回 null；普通 Hover 不自动创建统计对象。
     */
    public getImportedModelRaycastCandidateStats(): ImportedModelRaycastCandidateStats | null {
        return this.importedModelRaycastCandidateIndex?.getStats() ?? null;
    }

    /**
     * 读取交互性能的统一低扰动统计快照。
     *
     * 该门面只汇总各运行时对象已经维护的数值计数，不创建候选索引、不扫描 Scene、
     * 不消费 Hover，也不重置计数。调用方可在采样前后取差值，避免安装高频方法包装。
     *
     * @returns 帧、Hover、候选、拓扑缓存和实例化投影的当前累计快照。
     */
    public getInteractionPerformanceStats(): ViewerInteractionPerformanceStats {
        return {
            frame: this.frameScheduler.getStats(),
            hover: this.entitySelectionManager.getHoverInputStats(),
            selectionCandidates: this.entitySelectionManager.getSelectionCandidateIndexStats(),
            selectionPartition: this.entitySelectionManager.getSelectionQueryPartitionStats(),
            importedCandidates: this.importedModelRaycastCandidateIndex?.getStats() ?? null,
            topologyRetention: this.entitySelectionManager.getImportedTopologyProxyRetentionStats(),
            instancing: this.importedModelInstancedRenderCoordinator.getStats(),
        };
    }

    /**
     * 为既有选择策略提供普通 Mesh 粗筛，保持原顺序和所有特殊拾取对象。
     * @param candidates 选择策略已生成的候选。
     * @param ray 当前世界射线。
     * @param target 输出缓冲；可与调用方自有的临时候选数组相同。
     */
    public filterMeshRaycastCandidates<T extends THREE.Object3D>(candidates: readonly T[], ray: THREE.Ray, target: T[]): void {
        if (candidates.length === 0) { target.length = 0; return; }
        if (!this.meshManager) {
            for (let index = 0; index < candidates.length; index++) target[index] = candidates[index];
            target.length = candidates.length;
            return;
        }
        this.sceneMeshRaycastIndex ??= new SceneMeshRaycastIndex(this.meshManager.getSceneObjectMembershipSource());
        this.sceneMeshRaycastIndex.filterCandidates(candidates, ray, target);
    }

    /**
     * 完成一次普通 Mesh 候选查询，根据实际成本选择是否先粗筛。
     * @param candidates 当前策略允许的候选，输入不会被修改。
     * @param raycaster 当前射线及精确求交配置。
     * @param owner 选择查询所有者。
     * @param channel 所有者内的独立选择通道。
     * @returns 原始精确命中，剖面/拓扑优先级由调用方继续处理。
     */
    public intersectMeshRaycastCandidates(
        candidates: THREE.Object3D[], raycaster: THREE.Raycaster, owner: object, channel: MeshRaycastQueryChannel,
    ): THREE.Intersection[] {
        this.meshRaycastQuery ??= new MeshRaycastQuery({
            filterCandidates: (objects, ray, target) => this.filterMeshRaycastCandidates(objects, ray, target),
        });
        return this.meshRaycastQuery.intersect(candidates, raycaster, owner, channel);
    }

    /**
     * 判断原始导入 Mesh 是否因活动实例投影而被运行时抑制。
     *
     * 该查询只表达 Viewer 当前派生渲染所有权，不读取或修改业务显隐状态，供特征树 Overlay
     * 等既有视觉链路区分“实例投影抑制”与“用户主动隐藏”。
     *
     * @param sourceMesh 待判断的导入模型原始 Mesh。
     * @returns source Mesh 正由活动 InstancedMeshUnit 接管时返回 true。
     */
    public isImportedModelSourceMeshInstanced(sourceMesh: THREE.Mesh | null | undefined): boolean {
        return this.importedModelInstancedMeshRegistry.hasSourceMesh(sourceMesh);
    }

    /**
     * 解析 source Mesh 当前对应的正式实例渲染 Unit。
     *
     * 该查询只供 cad-core 内部交互视觉适配复用正式 geometry 与实例槽矩阵，不转移 Unit
     * 生命周期，也不允许调用方直接修改正式 InstancedMesh。
     *
     * @param sourceMesh 导入模型原始显示 Mesh。
     * @returns 当前活动 Unit；普通或业务隐藏 Mesh 返回 null。
     */
    public resolveImportedModelInstancedMeshUnit(
        sourceMesh: THREE.Mesh | null | undefined,
    ): ImportedModelInstancedMeshUnit | null {
        return this.importedModelInstancedMeshRegistry.resolveSourceMesh(sourceMesh);
    }

    /** @returns 只读 Unit 成员端口，包含装配加载及独立阵列；不暴露注册表的写接口。 */
    public getImportedModelInstancedUnitSource(): ImportedModelInstancedUnitSource {
        return this.importedModelInstancedMeshRegistry;
    }

    /**
     * 注册由独立 cad-core 领域拥有的导入模型实例 Unit。
     *
     * 线性阵列通过该入口加入统一射线回映射表，但 Unit 的创建、矩阵和销毁仍由阵列组负责，
     * 不进入装配体加载 Coordinator，也不会触发原有 InstancedMesh 重建。
     *
     * @param unit 待注册的外部领域实例 Unit。
     */
    public registerImportedModelExternalInstancedMeshUnit(unit: ImportedModelInstancedMeshUnit): void {
        this.importedModelInstancedMeshRegistry.register(unit);
    }

    /**
     * 解除独立 cad-core 领域实例 Unit 的射线回映射。
     *
     * @param unit 待解除注册的外部领域实例 Unit。
     */
    public unregisterImportedModelExternalInstancedMeshUnit(unit: ImportedModelInstancedMeshUnit): void {
        this.importedModelInstancedMeshRegistry.unregister(unit);
    }

    /**
     * 将当前显示模式同步到全部 imported-model 实例 Unit 的默认拓扑边线。
     *
     * @param enabled 是否显示默认拓扑边线。
     * @param color 当前主题要求的边线颜色。
     * @returns 成功接管实例边线的 Unit 数量。
     */
    public syncImportedModelInstancedTopologyDisplayEdges(
        enabled: boolean,
        color: THREE.ColorRepresentation,
    ): number {
        return this.importedModelInstancedMeshRegistry.syncTopologyDisplayEdges(enabled, color);
    }

    /**
     * 注册独立实例领域拥有的 source Mesh 脱离控制器。
     *
     * 该注册只扩展业务逻辑子树读取，不向装配体 Coordinator 注入 occurrence，也不转移
     * 控制器的释放职责。
     *
     * @param controller 由独立实例领域持有的 source Mesh 脱离控制器。
     */
    public registerImportedModelExternalSourceMeshVisibilityController(
        controller: ImportedModelSourceMeshVisibilityController,
    ): void {
        this.importedModelExternalSourceMeshVisibilityControllers.add(controller);
    }

    /**
     * 解除独立实例领域 source Mesh 脱离控制器的逻辑子树注册。
     *
     * @param controller 即将释放的 source Mesh 脱离控制器。
     */
    public unregisterImportedModelExternalSourceMeshVisibilityController(
        controller: ImportedModelSourceMeshVisibilityController,
    ): void {
        this.importedModelExternalSourceMeshVisibilityControllers.delete(controller);
    }

    /**
     * 读取当前 Viewer 的 topology host 实例变换投影端口。
     *
     * @returns 只依赖统一 Unit Registry 的投影端口。
     */
    public getImportedModelTopologyHostTransformProjectionPort(
    ): ImportedModelTopologyHostTransformProjectionPort {
        return this.importedModelInstancedRenderCoordinator.getTopologyHostTransformProjectionPort();
    }

    /**
     * 把 InstancedMesh raw hit 还原为现有拓扑链可消费的 source Mesh hit。
     *
     * @param hit Three.js 原始射线命中。
     * @returns source Mesh 命中；失效 GPU 下标返回 `null`。
     */
    public resolveImportedModelInstancedHit(
        hit: THREE.Intersection<THREE.Object3D>,
    ): THREE.Intersection<THREE.Object3D> | null {
        return this.importedModelInstancedHitResolver.resolve(hit);
    }
    //------ 导入模型实例化投影 结束------

    /**
     * 将单个正式特征结果体同步到当前模型显示模式。
     *
     * 说明：该入口供 MeshManager 在新增或替换正式结果体后调用，避免整场景遍历。
     *
     * @param mesh 正式特征结果体。
     */
    public syncFeatureResultMeshModelDisplayMode(mesh: FeatureResultMesh): void {
        this.modelDisplayModeController.syncFeatureResultMesh(mesh);
        this.sketchSessionReferencePointManager?.invalidateVisibleModelEdgeProjection();
    }

    /**
     * 恢复单个正式特征结果体被模型显示模式覆盖的临时材质。
     *
     * 说明：该入口供 MeshManager 删除或替换正式结果体前调用，确保材质释放仍落到原始材质槽。
     *
     * @param mesh 正式特征结果体。
     */
    public restoreFeatureResultMeshModelDisplayModeMaterial(mesh: FeatureResultMesh): void {
        this.modelDisplayModeController.restoreFeatureResultMeshMaterial(mesh);
    }
    //------ 模型显示模式 结束------

    /**
     * 在场景中查找对象。
     *
     * 设计说明：
     * 1. 非模型交互对象仍可使用 `THREE.Object3D.uuid`；模型实体必须使用 Worker 权威几何修订 ID；
     * 2. 模型体注册表统一维护“权威修订 ID → 当前显示 Mesh”，覆盖纯导入 GLB 表面与特征结果体；
     * 3. `Object3D.name` 仅保留给未进入模型体注册表的既有几何对象，不能充当实体所有权判断来源。
     *
     * @param objectId 场景对象标识。
     * @returns 场景对象；未命中时返回 null。
     */
    public findSceneObjectById(objectId: string): THREE.Object3D | null {
        if (!this.scene) {
            return null;
        }
        const normalizedObjectId = objectId.trim();
        if (!normalizedObjectId) {
            return null;
        }
        // MeshManager 已维护场景物理成员索引；UUID 热查询不再重复遍历整棵 Scene。
        const target = this.meshManager?.resolveSceneObjectByUuid(normalizedObjectId)
            ?? this.scene.getObjectByProperty('uuid', normalizedObjectId)
            ?? null;
        if (target) {
            return target;
        }
        /**
         * 实例投影激活后 source Mesh 不再挂在主场景树中，但其 UUID 仍是拓扑、阵列和测量
         * 使用的业务身份。对象查询门面必须先恢复这条业务引用，不能退化为 GPU 实例对象。
         */
        const detachedImportedSourceMesh = this.importedModelInstancedMeshRegistry
            .resolveDetachedSourceMeshById(normalizedObjectId);
        if (detachedImportedSourceMesh) {
            return detachedImportedSourceMesh;
        }
        const modelBodyDisplayMesh = this.getModelBodySceneRegistry()
            .resolveActiveDisplayMesh(normalizedObjectId);
        if (modelBodyDisplayMesh) {
            return modelBodyDisplayMesh;
        }
        return this.scene.getObjectByName(normalizedObjectId) ?? null;
    }

    /**
     * 在场景中查找对象。
     * @param objectId 场景对象标识。
     * @returns 场景对象；未命中时返回 null。
     */
    public getSceneObjectById(objectId: string): THREE.Object3D | null {
        return this.findSceneObjectById(objectId);
    }

    /**
     * 按稳定 occurrence 身份或运行时 UUID 解析可写组件根。
     *
     * 历史、外部 SDK 调用必须经过业务逻辑层级，不能因原 Mesh 已脱离物理场景而遗漏组件。
     * 索引借用场景成员源，并按拓扑身份/实例注册版本失效，不扫描每帧位姿。
     * @param componentId 完整组件身份或 UUID，不以显示名称猜测同源实例。
     * @returns 当前可写组件根；缺失或歧义时返回 null。
     */
    public resolveAssemblyComponentObject(componentId: string): THREE.Object3D | null {
        this.assemblyComponentObjectIndex ??= new ComponentInstanceTransformRootIndex({
            getScene: () => this.scene,
            listLogicalChildren: object => this.listImportedModelSourceHierarchyChildren(object),
            getSemanticRevision: () => ImportedModelObjectMetadataService.getComponentIdentityRevision()
                + getImportedModelTopologySemanticRevision()
                + this.importedModelInstancedMeshRegistry.getSemanticRevision(),
            membershipSource: this.meshManager?.getSceneObjectMembershipSource(),
        });
        return this.assemblyComponentObjectIndex.resolveByObjectId(componentId);
    }

    /**
     * 将复制命令的拓扑选择或设计树 ID 解析成当前所属零件。
     *
     * 直接消费选择对象，避免按 UUID 重新查场景时丢失离线拓扑代理；组件归属复用移动、配合
     * 使用的权威解析器，不能把 FeatureFace 或整个实例渲染批次当作复制源。
     * @param sourceObjectId 设计树或剪贴板保存的身份；缺省时消费当前视口选择。
     * @returns 可复制的业务组件根；无有效归属时返回 null。
     */
    public resolveAssemblyReferenceCopySourceObject(sourceObjectId?: string | null): THREE.Object3D | null {
        const selected = this.getSelectedObjects();
        const objectId = sourceObjectId?.trim();
        const candidates = objectId
            ? [selected.find(object => object.uuid === objectId)
                ?? this.getSceneObjectById(objectId)
                ?? this.resolveAssemblyComponentObject(objectId)]
            : selected;
        for (const candidate of candidates) {
            const root = resolveComponentInstanceTransformTarget(candidate, {
                scene: this.scene,
                // 逻辑索引包含已脱离渲染树的 source Mesh 与 occurrence 根。
                getSceneObjectById: id => this.resolveAssemblyComponentObject(id) ?? this.getSceneObjectById(id),
            });
            if (root && root !== this.scene) return root;
        }
        return null;
    }

    /**
     * 解析实例 source Mesh 对应的权威 occurrence 变换根。
     *
     * @param sourceMesh 实例射线命中回映射后的业务 Mesh。
     * @returns 当前 occurrence 根；普通 Mesh 返回 null。
     */
    public resolveImportedModelInstancedOccurrenceRoot(
        sourceMesh: THREE.Mesh,
    ): THREE.Object3D | null {
        return this.importedModelInstancedMeshRegistry.resolveOccurrenceRoot(sourceMesh);
    }

    /**
     * 收集业务对象范围内已脱离主场景树的实例 source Mesh。
     *
     * @param rootObject occurrence 根、装配祖先或 source Mesh 自身。
     * @returns 当前活动业务 Mesh 集合。
     */
    public listImportedModelInstancedSourceMeshes(
        rootObject: THREE.Object3D,
    ): readonly THREE.Mesh[] {
        return this.importedModelInstancedMeshRegistry.listSourceMeshesForObject(rootObject);
    }

    /**
     * 读取导入模型渲染来源的身份/实例注册版本，供派生选择范围缓存使用。
     * 不包含位姿和拓扑代理变化，避免纯交互触发范围重建。
     * @returns 当前 Viewer 可查询来源的单调语义版本。
     */
    public getImportedModelRenderSourceRevision(): number {
        return ImportedModelObjectMetadataService.getSemanticRevision()
            + this.importedModelInstancedMeshRegistry.getSemanticRevision();
    }

    /**
     * 读取导入模型对象在实例投影接管前的业务逻辑子节点。
     *
     * 阵列预览和正式提交必须通过该门面读取脱离 source Mesh，禁止依赖物理渲染树。
     *
     * @param parentObject 待读取的业务父对象。
     * @returns 当前 revision 的逻辑子节点快照。
     */
    public listImportedModelSourceHierarchyChildren(
        parentObject: THREE.Object3D,
    ): readonly THREE.Object3D[] {
        let logicalChildren = this.importedModelInstancedRenderCoordinator
            .listSourceHierarchyChildren(parentObject);
        this.importedModelExternalSourceMeshVisibilityControllers.forEach((controller) => {
            logicalChildren = controller.mergeLogicalChildren(parentObject, logicalChildren);
        });
        return Object.freeze([...logicalChildren]);
    }

    /**
     * 刷新实例 source Mesh 的有效世界矩阵，供空间只读算法使用。
     *
     * @param sourceMesh 当前业务 Mesh。
     * @returns 当前 Mesh 由活动实例投影接管且刷新成功时返回 true。
     */
    public prepareImportedModelSourceMeshForSpatialRead(sourceMesh: THREE.Mesh): boolean {
        return this.importedModelInstancedMeshRegistry.prepareSourceMeshForSpatialRead(sourceMesh);
    }

    /**
     * 解析实例 source Mesh 脱离渲染树前的业务父对象。
     *
     * 装配体加载投影和线性阵列投影都可能移除 source Mesh；组件变换算法通过该门面恢复
     * 稳定的父级坐标系，禁止直接把物理 `parent=null` 当作业务世界根。
     *
     * @param sourceMesh 当前业务 source Mesh。
     * @returns 原业务父对象；普通 Mesh 或未被实例投影接管时返回 null。
     */
    public resolveImportedModelSourceMeshOriginalParent(
        sourceMesh: THREE.Mesh,
    ): THREE.Object3D | null {
        const assemblyParent = this.importedModelInstancedRenderCoordinator
            .resolveSourceMeshOriginalParent(sourceMesh);
        if (assemblyParent) {
            return assemblyParent;
        }

        for (const controller of this.importedModelExternalSourceMeshVisibilityControllers) {
            const externalParent = controller.resolveOriginalParent(sourceMesh);
            if (externalParent) {
                return externalParent;
            }
        }
        return null;
    }

    /**
     * 重算依赖装配体组件位姿的基准引用。
     *
     * 设计说明：
     * undo / redo 应用 component transform patch 后，装配体基准面、基准轴和基准点必须和当前位姿同步。
     */
    public recomputeAssemblyDatumReferences(): void {
        recomputeAssemblyDatumReferences(this);
        // 基于装配实体面的剖面必须在零部件位姿变化后同步刷新世界坐标系。
        this.notifySectionableModelContentChanged();
    }

    /**
     * 判断剖面视图当前是否实际裁剪模型。
     *
     * @returns 至少一个合法剖面已启用时返回 true。
     */
    public hasActiveSectionView(): boolean {
        return this.sectionViewController.isActive();
    }

    /**
     * 判断世界空间命中点是否仍位于剖切后的可见半空间。
     *
     * @param worldPoint Raycaster 世界坐标命中点。
     * @returns 命中仍可见、允许进入实体选择链时返回 true。
     */
    public isSectionPointVisible(worldPoint: THREE.Vector3): boolean {
        return this.sectionViewController.isPointVisible(worldPoint);
    }

    /**
     * 通知剖面视图正式模型内容、可见性或静态几何已经变化。
     *
     * 该门面统一触发实体面重绑定、包围盒刷新和盖面代理重建；调用方不得直接接触
     * `SectionViewRenderController` 的 GPU 资源。
     */
    public notifySectionableModelContentChanged(): void {
        this.entitySelectionManager?.invalidateHoverPick();
        notifySectionableModelRootContentChanged(this.scene);
    }

    /**
     * 重算全部草图中由外部模型边、基准面或平面模型面驱动的镜向关系。
     * @returns 成功关系、悬空 provider 与结构化诊断。
     */
    public reconcileSketchMirrorAxisReferences() {
        if (!this.sketchGroupManager?.hasSketchReferenceTargets()) {
            return SketchMirrorAxisReferenceReconcileService.emptyResult;
        }
        const result = this.sketchMirrorAxisReferenceReconcileService.reconcile(
            this.sketchGroupManager.getAllGroups(),
            input => this.solveActiveSketchParametricState(input),
        );
        if (result.reconciledConstraintIds.length > 0) {
            this.requestRender();
        }
        return result;
    }

    /**
     * 推进历史相关的 Worker epoch。
     *
     * 设计说明：
     * undo/redo 会让主状态回到旧快照，但异步 Worker 响应可能稍后返回。宿主层在应用历史事务前
     * 必须推进 historySeq，让旧 preview/rebuild/mesh 响应被 WorkerRevisionGuard 丢弃。
     *
     */
    public advanceHistoryWorkerEpoch(): void {
        /**
         * Viewer 是 web-client 接触 worker epoch 的唯一表层入口，这里刻意不接收外部 historySeq。
         * undo / redo 可能重放较小的事务 seq，如果把旧 seq 传入 WorkerRevisionGuard 会触发“历史序号回退”
         * 或让旧 worker 响应错误覆盖当前主状态，因此表层操作只允许推进一个独立单调 epoch。
         */
        this.featureExecutionRuntime.advanceWorkerHistorySeq();
    }

    /**
     * 将对象局部坐标点转换为世界坐标。
     *
     * 设计说明：
     * 1. 预览引导锚点由 cad-core 权威求解链输出的是对象局部坐标；
     * 2. web-client 只负责屏幕投影，因此坐标系转换必须由 Viewer 统一提供；
     * 3. 这样可以避免 UI 直接依赖结果体内部层级或自行猜测 matrixWorld 生命周期。
     *
     * @param objectId 场景对象 ID。
     * @param point 局部坐标点。
     * @returns 世界坐标点；对象不存在时返回 `null`。
     */
    public resolveObjectWorldPoint(
        objectId: string,
        point: [number, number, number],
    ): [number, number, number] | null {
        const sceneObject = this.getSceneObjectById(objectId);
        if (!sceneObject) {
            return null;
        }
        sceneObject.updateWorldMatrix(true, false);
        const worldPoint = new THREE.Vector3(point[0], point[1], point[2]);
        sceneObject.localToWorld(worldPoint);
        return [worldPoint.x, worldPoint.y, worldPoint.z];
    }

    /**
     * 读取结果体某条边的世界坐标线段。
     *
     * @param objectId 结果体对象 ID。
     * @param edgeIndex 边索引。
     * @returns 世界坐标线段；不存在时返回 `null`。
     */
    public getTopologyEdgeSegment(
        objectId: string,
        edgeIndex: number,
    ): {
        start: [number, number, number];
        end: [number, number, number];
    } | null {
        const sceneObject = this.getSceneObjectById(objectId);
        if (!sceneObject || !isFeatureResultMesh(sceneObject)) {
            return null;
        }

        const topologySnapshot = sceneObject.getTopologySnapshot();
        const edge = topologySnapshot?.edges.find((candidate) => candidate.index === edgeIndex);
        const vertexA = typeof edge?.vertexAIndex === 'number'
            ? topologySnapshot?.vertices[edge.vertexAIndex]
            : null;
        const vertexB = typeof edge?.vertexBIndex === 'number'
            ? topologySnapshot?.vertices[edge.vertexBIndex]
            : null;
        if (!edge || !vertexA || !vertexB) {
            return null;
        }

        sceneObject.updateWorldMatrix(true, false);
        const start = sceneObject.localToWorld(new THREE.Vector3(
            vertexA.position[0],
            vertexA.position[1],
            vertexA.position[2],
        ));
        const end = sceneObject.localToWorld(new THREE.Vector3(
            vertexB.position[0],
            vertexB.position[1],
            vertexB.position[2],
        ));
        return {
            start: [start.x, start.y, start.z],
            end: [end.x, end.y, end.z],
        };
    }

    /**
     * 读取场景对象。
     * @returns 当前场景。
     */
    public getScene(): THREE.Scene | null {
        return this.scene ?? null;
    }

    /**
     * 读取当前相机。
     * @returns 当前相机。
     */
    public getCamera(): THREE.Camera | null {
        return this.camera ?? null;
    }

    /**
     * 读取宿主容器。
     * @returns 当前容器。
     */
    public getContainer(): HTMLElement | null {
        return this.container ?? null;
    }

    /**
     * 读取当前视图模式。
     * @returns 当前视图模式。
     */
    public getCurrentViewMode(): ViewMode {
        return this.viewMode;
    }

    /**
     * 获取全部基准面。
     * @returns 基准面集合。
     */
    public getAllDatumPlanes(){
        return this.datumPlaneManager?.getAllPlanes() ?? [];
    }

    /**
     * 读取全部基准面摘要。
     * @returns 基准面摘要集合。
     */
    public listDatumPlaneSummaries() {
        return this.datumPlaneManager?.getAllPersistentPlanes() ?? [];
    }

    /**
     * 获取指定基准面。
     * @param planeId 基准面 ID。
     * @returns 基准面实例；未命中时返回 null。
     */
    public getDatumPlane(planeId: number) {
        return this.datumPlaneManager?.getPlane(planeId) ?? null;
    }

    /**
     * 获取指定基准轴。
     * @param axisId 基准轴 ID。
     * @returns 基准轴实例；未命中时返回 null。
     */
    public getDatumAxis(axisId: number) {
        return this.datumAxisManager?.getAxis(axisId) ?? null;
    }

    /**
     * 获取指定基准点。
     * @param pointId 基准点 ID。
     * @returns 基准点实例；未命中时返回 null。
     */
    public getDatumPoint(pointId: number) {
        return this.datumPointManager?.getPoint(pointId) ?? null;
    }

    //------ 离线包基准扩展 开始------
    /**
     * 创建 `.puppy` 离线包使用的文档级基准数据。
     *
     * 设计说明：
     * 1. 离线包协议字段使用 `datumPointList / datumAxisList / datumPlaneList`，不暴露 Undo/Redo Snapshot 命名；
     * 2. 点和轴必须优先从 ReferenceController 读取，因为控制器持有可复算 feature 定义；
     * 3. 基准面当前的可恢复真相源在 DatumPlaneManager 中，计算来源随 plane 记录一起保存。
     *
     * @returns 可直接传给 `exportPuppyPackage()` 的基准扩展输入。
     */
    public createPuppyDatumExtensionInput(): PuppyDatumExtensionInput {
        const datumPointState = this.referencePointController?.createHistorySnapshot()
            ?? this.datumPointManager?.createHistorySnapshot()
            ?? {
                managerId: DATUM_POINT_HISTORY_ENTITY_ID,
                points: [],
                features: [],
            };
        const datumAxisState = this.referenceAxisController?.createHistorySnapshot()
            ?? this.datumAxisManager?.createHistorySnapshot()
            ?? {
                managerId: DATUM_AXIS_HISTORY_ENTITY_ID,
                axes: [],
                features: [],
            };
        const datumPlaneState = this.datumPlaneManager?.createHistorySnapshot()
            ?? {
                managerId: DATUM_PLANE_HISTORY_ENTITY_ID,
                planes: [],
            };

        /**
         * 只返回列表字段，让离线包协议层统一决定是否真正写入 datums 扩展。
         */
        return {
            datumPointList: datumPointState.points,
            datumPointFeatureList: datumPointState.features,
            datumAxisList: datumAxisState.axes,
            datumAxisFeatureList: datumAxisState.features,
            datumPlaneList: datumPlaneState.planes,
        };
    }

    /**
     * 从 `.puppy` 离线包基准扩展恢复文档级基准对象。
     *
     * 设计说明：
     * 1. 调用方必须先完成 display.glb、BRep 与 topology runtime 挂载，保证可复算定义后续能解析装配拓扑；
     * 2. 恢复顺序先放回正式对象列表，再恢复点/轴控制器 feature 记录，避免丢失后续重算能力；
     * 3. 离线包导入是文档加载行为，不写入模型 Undo/Redo 历史。
     *
     * @param datumExtension `.puppy/assembly.json.extensions.datums` 解析出的基准扩展。
     */
    public restorePuppyDatumExtension(datumExtension: PuppyDatumExtension | null | undefined): void {
        if (!datumExtension) {
            return;
        }

        this.datumPlaneManager?.restoreHistorySnapshot({
            managerId: DATUM_PLANE_HISTORY_ENTITY_ID,
            planes: datumExtension.datumPlaneList,
        });
        this.referencePointController?.restoreHistorySnapshot({
            managerId: DATUM_POINT_HISTORY_ENTITY_ID,
            points: datumExtension.datumPointList,
            features: datumExtension.datumPointFeatureList,
        });
        if (!this.referencePointController) {
            this.datumPointManager?.restoreHistorySnapshot({
                managerId: DATUM_POINT_HISTORY_ENTITY_ID,
                points: datumExtension.datumPointList,
                features: datumExtension.datumPointFeatureList,
            });
        }
        this.referenceAxisController?.restoreHistorySnapshot({
            managerId: DATUM_AXIS_HISTORY_ENTITY_ID,
            axes: datumExtension.datumAxisList,
            features: datumExtension.datumAxisFeatureList,
        });
        if (!this.referenceAxisController) {
            this.datumAxisManager?.restoreHistorySnapshot({
                managerId: DATUM_AXIS_HISTORY_ENTITY_ID,
                axes: datumExtension.datumAxisList,
                features: datumExtension.datumAxisFeatureList,
            });
        }
        this.requestRender();
    }
    //------ 离线包基准扩展 结束------

    //------ 剖面视图离线包扩展 开始------
    /**
     * 创建 `.puppy` 离线包使用的剖面视图快照。
     *
     * 设计说明：剖面视图属于文档显示状态，不改变 B-Rep，也不写入建模 Undo/Redo；
     * 没有有效剖面定义时返回 `undefined`，由协议层省略对应扩展字段。
     *
     * @returns 文档级剖面视图快照；尚未定义剖面时返回 `undefined`。
     */
    public createPuppySectionViewSnapshot(): SectionViewDocumentSnapshot | undefined {
        return this.sectionViewController.createDocumentSnapshot() ?? undefined;
    }

    /**
     * 从 `.puppy` 离线包恢复文档级剖面视图。
     *
     * 恢复会优先按稳定实体面身份重新绑定；模型暂不可解析时仍保留已保存的世界坐标平面，
     * 因而文档加载不会因为单个面引用失效而中断。该加载行为不进入建模 Undo/Redo。
     *
     * @param snapshot 离线包解析出的剖面视图快照；缺失时恢复为完整视图。
     */
    public restorePuppySectionViewSnapshot(
        snapshot: SectionViewDocumentSnapshot | null | undefined,
    ): void {
        this.sectionViewController.restoreDocumentSnapshot(snapshot);
    }
    //------ 剖面视图离线包扩展 结束------

    //------ 草图离线包定义持久化 开始------
    /**
     * 采集 `.puppy` 离线包使用的草图扩展记录。
     *
     * Viewer 只提供 cad-core 的统一外观入口；序列化与展示态归一职责由 SketchGroupManager 承担。
     *
     * @returns 可直接传给 `exportPuppyPackage()` 的草图扩展记录列表。
     */
    public createPuppySketchExtensionInput(): PuppySketchExtensionEntry[] {
        return this.sketchGroupManager?.createPuppySketchExtensionEntries() ?? [];
    }

    /**
     * 获取草图尺寸仓库。
     *
     * Viewer 只提供统一外观入口；仓库生命周期由 SketchGroupManager 承担。
     *
     * @returns 尺寸仓库；管理器未初始化时返回 null。
     */
    public getSketchDimensionRepository(): SketchDimensionRepository | null {
        return this.sketchGroupManager?.dimensionRepository ?? null;
    }

    /**
     * 在草图实体发生持久拓扑变化后刷新智能尺寸运行时视图。
     *
     * 说明：尺寸仓库是领域真相；裁剪、圆角等命令完成引用迁移后，由 Viewer 统一重建
     * 几何关系视图、补测 driven 数值并刷新标注，调用方不得直接依赖渲染器具体实现。
     *
     * @param sketchId 发生拓扑变化的草图持久 ID。
     */
    public refreshSketchDimensionsAfterTopologyChange(sketchId: string): void {
        this.synchronizeSketchDimensionRelations(sketchId);
        this.remeasureSketchDrivenDimensions(sketchId);
        this.sketchDimensionAnnotationRenderer.refreshSketch(sketchId);
        // 关系、测量值和标注先完整对账；与上层命令的普通请求在同一宿主帧结算。
        this.requestRender();
    }

    /**
     * 从 `.puppy` 离线包草图扩展恢复草图组。
     *
     * 设计说明：
     * 1. 恢复经 SketchGroupManager 统一接管场景挂载、索引、显示态与生命周期事件；
     * 2. 离线包导入是文档加载行为，不写入模型 Undo/Redo 历史。
     *
     * @param sketchExtension `.puppy/assembly.json.extensions.sketches` 解析出的草图扩展。
     */
    public restorePuppySketchExtension(sketchExtension: PuppySketchExtension | null | undefined): void {
        if (!sketchExtension) {
            return;
        }
        this.sketchGroupManager?.restorePuppySketchExtension(sketchExtension);
        /**
         * 尺寸关系视图补投影：恢复期尺寸先于草图组进入场景，仓库事件那一轮无法解析引用对象，
         * 这里在草图组就位后按最终状态统一对账（关系视图不入盘，一律由尺寸重新投影）。
         */
        this.listSketchGroupSummaries().forEach((summary) => {
            this.synchronizeSketchDimensionRelations(summary.persistentId);
        });
        this.requestRender();
    }

    /**
     * 采集 `.puppy` 离线包使用的转换实体引用 Record。
     *
     * Viewer 只从当前文档唯一 Runtime 的冻结快照整理完整 Record；排序与协议校验由扩展
     * 编码器负责，不给 Repository 增加第二套持久化读取接口。
     *
     * @returns 当前文档全部转换实体引用 Record 深拷贝。
     */
    public createPuppySketchEntityReferenceExtensionInput(): SketchEntityReferenceRecord[] {
        const targetSketchIds = this.listSketchGroupSummaries()
            .map((summary) => summary.persistentId);
        return this.captureSketchEntityReferenceTimelineSnapshot(targetSketchIds)
            .items
            .map((item) => structuredClone(item.reference));
    }

    /**
     * 从 `.puppy` 引用扩展整体恢复文档级转换实体引用 Record。
     *
     * 草图实体必须先恢复；OnEdge 关系不在这里直接创建，而是在后续 FeatureTimeline
     * checkpoint 中由现有重建链派生。加载初始化不写入模型 Undo/Redo。
     *
     * @param extension 已解析的转换实体引用扩展；缺省表示清空当前文档引用。
     */
    public restorePuppySketchEntityReferenceExtension(
        extension: PuppySketchEntityReferenceExtension | null | undefined,
    ): void {
        this.ensureSketchEntityReferenceDocumentRuntimeInitialized();
        const targetSketchIds = this.listSketchGroupSummaries()
            .map((summary) => summary.persistentId);
        this.sketchEntityReferenceDocumentRuntime?.replacePersistentRecords(
            extension?.records ?? [],
            targetSketchIds,
        );
        this.requestRender();
    }
    //------ 草图离线包定义持久化 结束------

    //------ 特征离线包定义持久化 开始------
    /**
     * 采集 `.puppy` 离线包使用的特征时间线快照。
     *
     * 设计说明：
     * 1. 数据源是 FeatureRollbackController 的完整命令时间线 + 退回控制棒状态（plain data）；
     * 2. 命令内的草图引用由绑定层原生保存持久 ID，这里不做任何 ID 重映射；
     * 3. `documentId` 固定为协议常量，避免会话值破坏“场景无变化 → 连续保存一致”。
     *
     * @returns 可直接传给 `exportPuppyPackage()` 的特征扩展输入；无特征时返回 null。
     */
    public createPuppyFeatureExtensionInput(): PuppyFeatureExtensionInput | null {
        const snapshot = this.captureFeatureRollbackHistorySnapshot('puppy-package');
        if (snapshot.commands.length === 0) {
            return null;
        }
        /**
         * structuredClone 快照可能保留显式 undefined 属性，按 JSON 语义净化为纯 JSON，
         * 保证扩展载荷可被规范 JSON 管线序列化。
         */
        return JSON.parse(JSON.stringify(snapshot)) as PuppyFeatureExtensionInput;
    }

    /**
     * 从 `.puppy` 离线包特征扩展恢复特征时间线（设计树/退回控制棒）。
     *
     * 设计说明：
     * 1. 必须在草图恢复完成后调用：命令绑定按草图持久 ID 直接命中已重建的草图实体；
     * 2. 恢复经既有 Undo/Redo 路径 replaceTimeline + Worker 按激活前缀重放；
     * 3. 离线包导入是文档加载行为，不写入模型 Undo/Redo 历史。
     *
     * @param featureExtension `.puppy/assembly.json.extensions.features` 解析出的特征扩展。
     * @param documentType 当前打开项目的权威文档类型；只有零件项目建立导入 replay 基线。
     */
    public async restorePuppyFeatureExtension(
        featureExtension: PuppyFeatureExtension | null | undefined,
        documentType?: PuppyPackageDocumentType,
    ): Promise<void> {
        const restoreGeneration = this.advancePuppyFeatureRestoreGeneration();
        const isCurrentRestore = (): boolean => (
            this.puppyFeatureRestoreGeneration === restoreGeneration
        );
        const hasFeatureCommands = Boolean(featureExtension?.snapshot.commands.length);
        const snapshot = hasFeatureCommands && featureExtension
            ? featureExtension.snapshot as unknown as FeatureRollbackHistorySnapshot
            : null;
        let didLoadedTimelineStop = false;

        /**
         * 复用现有权威时间线替换与 Worker 重放能力。
         * 说明：
         * 1. 重放期间设置保护标记，阻止并发的“按 Worker 摘要对账”把刚恢复的时间线清空；
         * 2. 重放会推进 Worker 历史版本，调用方必须 await 本方法后再刷新设计树，
         *    否则并发刷新请求会以 history-seq-stale 作废；
         * 3. 恢复失败只记录错误，不阻断文档加载（结果实体仍来自离线包 BRep 基线）。
         */
        this.isPuppyFeatureTimelineReplayPending = true;
        try {
            const didCaptureCurrentDocument = await this.captureImportedModelFeatureReplayBaselines(
                documentType,
                !hasFeatureCommands,
                restoreGeneration,
            );
            /** await 交接边界必须再次校验，防止旧纯导入任务清空新文档时间线。 */
            if (!didCaptureCurrentDocument || !isCurrentRestore()) {
                return;
            }
            const hasSketchEntityReferences =
                this.createPuppySketchEntityReferenceExtensionInput().length > 0;
            if (!snapshot) {
                if (documentType === 'part') {
                    /** 纯导入零件的权威时间线为空，不能让旧零件的异步恢复留下历史节点。 */
                    this.featureRollbackController.replaceTimeline([], 0, null, []);
                    this.syncFeatureRollbackManagerTimeline();
                    this.isPuppyFeatureTimelineDegraded = false;
                }
                if (hasSketchEntityReferences) {
                    const result = await this.featureRebuildOrchestrator?.restoreLoadedTimeline(
                        isCurrentRestore,
                    );
                    if (!result || (result.status !== 'success' && result.status !== 'stopped')) {
                        throw new Error(
                            `离线包转换实体引用时间线恢复失败：${result?.status ?? 'orchestrator-missing'}`,
                        );
                    }
                    didLoadedTimelineStop = result.status === 'stopped';
                    this.isPuppyFeatureTimelineDegraded = didLoadedTimelineStop;
                }
                return;
            }
            if (hasSketchEntityReferences) {
                /**
                 * Record 已在草图恢复后就位；先安装离线包权威时间线，再让现有阶段 B
                 * 按 checkpoint 顺序同时重放 Feature 与派生 OnEdge。
                 */
                this.featureRollbackController.replaceTimeline(
                    snapshot.commands,
                    snapshot.cursorIndex,
                    snapshot.previousCursorIndex,
                    snapshot.timelineItems,
                );
                this.syncFeatureRollbackManagerTimeline();
                const result = await this.featureRebuildOrchestrator?.restoreLoadedTimeline(
                    isCurrentRestore,
                );
                if (!result || (result.status !== 'success' && result.status !== 'stopped')) {
                    throw new Error(
                        `离线包转换实体引用时间线恢复失败：${result?.status ?? 'orchestrator-missing'}`,
                    );
                }
                didLoadedTimelineStop = result.status === 'stopped';
            } else {
                await this.restoreFeatureRollbackHistorySnapshot(snapshot, isCurrentRestore);
            }
            if (!isCurrentRestore()) {
                return;
            }
            const didHandOverCurrentDocument = await this.handOverFeatureOwnedImportedBodies(
                isCurrentRestore,
            );
            if (!didHandOverCurrentDocument || !isCurrentRestore()) {
                return;
            }
            // stopped 只提交成功前缀，必须保留降级保护；完整 success 才解除。
            this.isPuppyFeatureTimelineDegraded = didLoadedTimelineStop;
        } catch (error) {
            if (!isCurrentRestore()) {
                return;
            }
            /**
             * 容错语义修正：`restoreFeatureRollbackHistorySnapshot` 面向 Undo/Redo，恢复失败会回滚到
             * 操作前状态；但文档加载的“操作前”是空文档，直接沿用会让一条特征重放失败就清空整棵设计树。
             * 文档加载中，离线包时间线本身就是权威内容：这里无条件重新落盘时间线，
             * 使全部特征仍在设计树上；重放失败的实体保留离线包 GLB 基线显示（降级但可见）。
             */
            console.error('[Viewer] 离线包导入基线或特征重放失败，保留权威时间线并降级显示', error);
            /**
             * 失败降级时只暂停显示补建基线，避免它与离线包最终快照形成双体；基线身份和网格
             * 必须保留，以便用户修复失败特征后再次重放，或回到首个草图时重新激活原始导入体。
             */
            this.meshManager?.deactivateFeatureReplayBaselineMeshes();
            if (snapshot) {
                try {
                    this.featureRollbackController.replaceTimeline(
                        snapshot.commands,
                        snapshot.cursorIndex,
                        snapshot.previousCursorIndex,
                        snapshot.timelineItems,
                    );
                    /**
                     * 标记降级：此后 Worker history 只是“成功执行的前缀”，不再是完整时间线的真相。
                     * 若不持久标记，重放窗口结束后的首次设计树刷新就会用空/残缺 history 对账，
                     * 把失败特征从时间线中抹除，用户再保存即造成特征永久丢失。
                     */
                    this.isPuppyFeatureTimelineDegraded = true;
                    this.syncFeatureRollbackManagerTimeline();
                } catch (timelineError) {
                    console.error('[Viewer] 离线包特征时间线降级恢复失败', timelineError);
                }
            } else if (documentType === 'part') {
                /** 即使纯导入基线物化失败，新零件的权威 Feature 时间线仍必须清空。 */
                this.featureRollbackController.replaceTimeline([], 0, null, []);
                this.syncFeatureRollbackManagerTimeline();
                this.isPuppyFeatureTimelineDegraded = false;
            }
        } finally {
            if (isCurrentRestore()) {
                this.isPuppyFeatureTimelineReplayPending = false;
                // 只绘制本代恢复的最终状态，过期恢复不会获得此请求。
                this.requestRender();
            }
        }
    }

    /**
     * 推进 Feature 恢复任务代次并解除旧任务的 pending 所有权。
     *
     * @returns 新任务代次。
     */
    private advancePuppyFeatureRestoreGeneration(): number {
        this.puppyFeatureRestoreGeneration += 1;
        this.isPuppyFeatureTimelineReplayPending = false;
        return this.puppyFeatureRestoreGeneration;
    }

    /**
     * 在特征时间线重放前冻结当前零件项目的原始导入基线。
     *
     * 设计说明：
     * 1. 捕获时机位于项目 BRep 注册完成、Feature 命令重放之前，此时 Worker 能区分原始导入体与最终快照；
     * 2. `isImportedBaseline` 与 imported-brep 解析器共同确认原始导入身份，禁止按场景仓库类型猜测；
     * 3. 携带 `commandId` 的对象属于离线 Feature 最终快照，不得在较早历史位置恢复；
     * 4. 装配项目直接清空集合，避免共享 revision ID 的 occurrence 互相影响。
     *
     * @param documentType 当前打开项目的权威文档类型。
     * @param activateCapturedBaselines 捕获完成后是否立即按当前 Worker 状态激活基线。
     * @param restoreGeneration 当前恢复任务代次。
     * @returns 捕获结果是否仍属于当前文档。
     */
    private async captureImportedModelFeatureReplayBaselines(
        documentType: PuppyPackageDocumentType | undefined,
        activateCapturedBaselines: boolean,
        restoreGeneration: number,
    ): Promise<boolean> {
        const isCurrentRestore = (): boolean => (
            this.puppyFeatureRestoreGeneration === restoreGeneration
        );
        this.importedModelFeatureReplayBaselineRevisionIds.clear();
        this.meshManager?.clearFeatureReplayBaselineMeshes();
        if (documentType !== 'part') {
            return true;
        }

        try {
            const summary = await this.getGeometryStateSummary({syncHistoryProjection: false});
            if (!isCurrentRestore()) {
                return false;
            }
            const baselineRevisionIds = summary.objects
                .filter(isOriginalImportedBrepBaseline)
                .map((object) => object.id);
            baselineRevisionIds.forEach((revisionId) => {
                this.importedModelFeatureReplayBaselineRevisionIds.add(revisionId);
            });
            const modelBodySceneRegistry = this.getModelBodySceneRegistry();
            for (const baselineRevisionId of this.importedModelFeatureReplayBaselineRevisionIds) {
                if (modelBodySceneRegistry.hasRepresentationRevision(baselineRevisionId)) {
                    continue;
                }
                if (!this.meshManager) {
                    throw new Error('零件项目导入 replay 基线物化失败：MeshManager 尚未初始化');
                }
                const meshData = await this.featureExecutionRuntime.generateMeshData(baselineRevisionId);
                if (!isCurrentRestore()) {
                    return false;
                }
                await this.meshManager.upsertFeatureReplayBaselineMesh(meshData);
                if (!isCurrentRestore()) {
                    return false;
                }
            }
            if (activateCapturedBaselines) {
                /**
                 * 无 Feature 命令的纯导入零件不会进入后续重放同步，必须在捕获完成后显式应用
                 * 当前 Worker 摘要；带 Feature 历史的文档则由重放结果统一决定可见版本。
                 */
                modelBodySceneRegistry.applyFeatureReplayState(
                    summary.objects,
                    this.importedModelFeatureReplayBaselineRevisionIds,
                );
            }
            return true;
        } catch (error) {
            if (!isCurrentRestore()) {
                return false;
            }
            this.importedModelFeatureReplayBaselineRevisionIds.clear();
            this.meshManager?.clearFeatureReplayBaselineMeshes();
            throw new Error('零件项目导入 replay 基线建立失败', {cause: error});
        }
    }

    /**
     * 把“已由特征重放产出”的实体显示从导入 GLB 交接给 Worker 结果体。
     *
     * 设计说明（方案 A）：
     * 1. 重放后 Worker 权威状态中，参数化实体带有 commandId；这些实体的显示所有权必须归结果体，
     *    否则导入 GLB 与结果体共用同一 objectId，编辑预览与提交都无法替换可见几何；
     * 2. 纯导入实体（无 commandId）继续由 GLB 承载，不受影响；
     * 3. 交接后按权威状态重建结果网格，使重开文档回到与原生建模完全一致的运行时结构。
     */
    private async handOverFeatureOwnedImportedBodies(
        isCurrentRestore: () => boolean = () => true,
    ): Promise<boolean> {
        const summary = await this.getGeometryStateSummary({syncHistoryProjection: false});
        if (!isCurrentRestore()) {
            return false;
        }
        const featureOwnedObjectIds = new Set(
            summary.objects
                .filter((object) => typeof object.commandId === 'string' && object.commandId.length > 0)
                .map((object) => object.id),
        );
        if (featureOwnedObjectIds.size === 0) {
            return true;
        }

        const releasedCount = releaseFeatureOwnedImportedBodies(this.scene, featureOwnedObjectIds);
        /**
         * 场景侧 GLB 显示交接后，Worker 侧的同 id 导入基线必须同步彻底移除。
         *
         * 二者是同一实体在两个空间的两个所有者：只清场景不清 Worker 基线时，重放产出的工作态结果虽在初始
         * 显示中遮蔽了基线，但一旦编辑/退回触发 `clearWorkingState`，被保留的基线会重新暴露，与预览体双体并存。
         * 因此这里以“带 commandId 的产出实体”为权威集合，令 Worker 释放被遮蔽的基线；释放后按最新权威状态重建网格。
         */
        const releaseResult = await this.featureExecutionRuntime.releaseFeatureOwnedBaselines(
            [...featureOwnedObjectIds],
        );
        if (!isCurrentRestore()) {
            return false;
        }
        if (releasedCount <= 0 && releaseResult.releasedCount <= 0) {
            return true;
        }
        await this.syncFeatureMeshesFromState(releaseResult.summary, {
            isStateCurrent: isCurrentRestore,
        });
        return isCurrentRestore();
    }
    //------ 特征离线包定义持久化 结束------

    //------ 线性零部件阵列离线包定义持久化 开始------
    /**
     * 创建当前文档的线性零部件阵列定义持久化文档。
     *
     * Viewer 只提供 cad-core 的统一外观入口；定义筛选与克隆职责仍由 Manager 承担。
     *
     * @returns 仅包含可重新求解特征定义的文档。
     */
    public createPuppyLinearComponentPatternDocument(): LinearComponentPatternPersistenceDocument {
        return this.linearComponentPatternManager.createPersistenceDocument();
    }

    /**
     * 从离线包定义重新求解线性零部件阵列。
     *
     * 调用方必须先挂载基础显示模型、BRep 与拓扑运行时；加载恢复不写入 Undo/Redo 历史。
     *
     * @param document 离线包解析并物化后的线性阵列定义文档。
     */
    public restorePuppyLinearComponentPatternDocument(
        document: LinearComponentPatternPersistenceDocument | null | undefined,
    ): void {
        if (!document) {
            return;
        }
        this.linearComponentPatternManager.restorePersistenceDocument(document);
        this.requestRender();
    }

    /**
     * 在项目节点局部切换后增量应用线性零部件阵列定义。
     *
     * 该入口保持 Manager 为唯一运行时写入者；调用方只提供 Finalizer 文档和确认阶段冻结的旧 Group。
     *
     * @param input 局部阵列替换输入。
     */
    public applyScopedPuppyLinearComponentPatternDocuments(
        input: PuppyCAD.ApplyScopedLinearComponentPatternDocumentsInput,
    ): void {
        this.linearComponentPatternManager.applyScopedPersistenceDocuments(input);
        this.requestRender();
    }
    //------ 线性零部件阵列离线包定义持久化 结束------

    /**
     * 解析装配体基准引用的派生依赖。
     *
     * 设计说明：
     * 1. 选择层只知道基准稳定引用，不能直接读取具体 ReferenceController；
     * 2. Viewer 作为运行时宿主只提供记录读取能力，真正依赖归一化仍由 cad-core assembly 模块完成；
     * 3. 返回 null 表示默认固定基准或当前基准没有可移动父组件。
     *
     * @param referenceRef 基准稳定引用。
     * @returns 派生依赖快照；默认固定基准返回 null。
     */
    public resolveAssemblyReferenceDependency(
        referenceRef: AssemblyReferenceSelectionRef,
    ): AssemblyReferenceDependencySnapshot | null {
        const host: AssemblyReferenceDependencyResolverHost = {
            getDatumPlane: (planeId) => this.getDatumPlane(planeId),
            getDatumAxisFeatureRecords: () => this.referenceAxisController.getViewModel().featureRecords,
            getDatumPointFeatureRecords: () => this.referencePointController.getViewModel().featureRecords,
        };
        return resolveAssemblyReferenceDependencySnapshot(referenceRef, host);
    }

    /**
     * 创建基准面。
     * @param options 基准面参数。
     * @returns 基准面摘要；管理器未就绪时返回 null。
     */
    public createDatumPlane(options: DatumPlaneOptions) {
        return this.datumPlaneManager?.createPlane(options) ?? null;
    }



    /**
     * 删除基准面。
     * @param planeId 基准面 ID。
     */
    public removeDatumPlane(planeId: number): void {
        this.datumPlaneManager?.removePlane(planeId);
    }

    /**
     * 判断指定基准面是否为临时平面。
     * @param planeId 基准面 ID。
     * @returns 是否为临时平面。
     */
    public isTemporaryDatumPlane(planeId: number): boolean {
        const plane = this.datumPlaneManager?.getPlane(planeId);
        return plane?.isTemporary ?? false;
    }

    /**
     * 读取单个基准面摘要。
     * @param planeId 基准面 ID。
     * @returns 基准面摘要；未命中时返回 null。
     */
    public getDatumPlaneSummary(planeId: number){
        return this.getDatumPlane(planeId);
    }

    /**
     * 读取单个基准点摘要。
     * @param pointId 基准点 ID。
     * @returns 基准点摘要；未命中时返回 null。
     */
    public getDatumPointSummary(pointId: number) {
        return this.getDatumPoint(pointId);
    }

    /**
     * 设置指定基准面的可见性。
     * @param planeId 基准面 ID。
     * @param visible 是否可见。
     */
    public setDatumPlaneVisible(planeId: number, visible: boolean): void {
        this.datumPlaneManager?.setPlaneVisible(planeId, visible);
    }

    /**
     * 临时显示基准面。
     *
     * 设计说明：
     * 退回控制棒下方的用户基准面已从 FeatureManager 语义上被抑制，
     * 设计树悬停不得再把它临时显示出来，否则会破坏退回态不可用语义。
     *
     * @param planeId 基准面 ID。
     */
    public showDatumPlaneTemporarily(planeId: number): void {
        this.syncFeatureRollbackManagerTimeline();
        if (this.featureRollbackController.isDatumPlaneRolledBack(String(planeId))) {
            return;
        }
        this.datumPlaneManager?.showPlaneTemporarily(planeId);
    }

    /**
     * 隐藏指定临时基准面。
     * @param planeId 基准面 ID。
     */
    public hideTemporaryDatumPlane(planeId: number): void {
        this.datumPlaneManager?.hideTemporaryPlane(planeId);
    }

    /**
     * 隐藏全部基准面。
     */
    public hideAllDatumPlanes(): void {
        this.datumPlaneManager?.hideAllPlanes();
    }

    /**
     * 处理窗口resize
     */
    public resize = (): void => {
        if (!this.container || !this.renderer) return;
        this.entitySelectionManager?.invalidateHoverPick();

        const width = this.container.clientWidth;
        const height = this.container.clientHeight;

        this.renderer.setSize(width, height);
        this.cameraManager?.resize(width, height);
        this.pipelineManager?.resize();
        this.labelRenderer?.setSize(width, height);
    };


    /**
     * 便捷访问当前活动相机。
     */
    get camera(): THREE.Camera | null {
        return this.cameraManager?.camera ?? null;
    }

    /**
     * 便捷访问轨道控制器。
     */
    get controls() {
        return this.cameraManager?.controls ?? null;
    }

    /**
     * 重置相机。
     */
    resetCamera(): void {
        this.cameraManager?.resetCamera();
    }

    /**
     * 将相机聚焦到指定对象的包围盒。
     *
     * 设计说明：上层 UI 只应依赖 Viewer 统一出口，避免直接触达 CameraManager 的内部职责。
     *
     * @param object 需要进入视口焦点的三维对象。
     */
    focusOnObjectBounds(object: THREE.Object3D): void {
        this.cameraManager?.focusOnObjectBounds(object);
    }

    /**
     * 启用/禁用相机控制器交互。
     * @param enabled 是否启用。
     */
    setControlsEnabled(enabled: boolean): void {
        this.cameraManager?.setControlsEnabled(enabled);
    }

    //------ 视图定向 开始------
    /**
     * 读取当前相机投影类型。
     *
     * @returns 当前投影类型；相机管理器未就绪时返回 null。
     */
    public getCameraProjectionType(): CameraProjectionType | null {
        return this.cameraManager?.getCameraProjectionType() ?? null;
    }

    /**
     * 设置当前相机投影类型。
     *
     * @param projectionType 目标投影类型。
     */
    public setCameraProjectionType(projectionType: CameraProjectionType): void {
        this.cameraManager?.setCameraProjectionType(projectionType);
    }

    /**
     * 构建标准视图预览帧。
     *
     * 设计说明：
     * 1. 透传当前相机 up，确保预览画面的 up 与主视口当前旋转姿态一致；
     * 2. 预览帧由 CameraManager 按可见模型包围盒独立 fit，不复用主相机物理距离；
     * 3. 预览图呈现的即是“点击后视角会变成什么样”，同时保证毫米级模型在右上角也能看清。
     *
     * @param viewType 标准视图类型。
     * @param options 预览相机参数。
     * @returns 标准视图预览帧；相机管理器未就绪时返回 null。
     */
    //------ 视图定向预览独立包围盒适配 开始------
    public buildStandardViewPreviewFrame(
        viewType: StandardViewType,
        options?: StandardViewPreviewFrameOptions,
    ) {
        // 获取当前相机 up，供预览帧推导感知一致的屏幕朝上方向。
        const currentCameraUp = this.cameraManager?.camera?.up?.clone();
        return this.cameraManager?.buildStandardViewPreviewCameraFrame(viewType, currentCameraUp, options) ?? null;
    }
    //------ 视图定向预览独立包围盒适配 结束------

    /**
     * 读取视图定向器 hover 变更事件流。
     *
     * @returns 标准视图 hover 事件流；`null` 表示结束预览，摄像机管理器未就绪时返回不发出事件的空流。
     */
    public observeViewOrientationHover(): Observable<StandardViewType | null> {
        return this.cameraManager?.observeViewOrientationHover() ?? EMPTY;
    }

    //------ 视图定向工具栏预览 开始------
    /**
     * 请求显示或关闭视图定向预览。
     *
     * @param viewType 目标标准视图；为 null 时关闭预览。
     */
    public setViewOrientationPreview(viewType: StandardViewType | null): void {
        this.cameraManager?.setViewOrientationPreview(viewType);
    }
    //------ 视图定向工具栏预览 结束------

    /**
     * 切换到指定标准视图。
     *
     * @param viewType 标准视图类型。
     */
    public setStandardView(viewType: StandardViewType): void {
        this.cameraManager?.setStandardView(viewType);
    }

    /**
     * 读取当前标准视图类型。
     *
     * @returns 当前标准视图类型；未匹配时返回 null。
     */
    public getCurrentStandardViewType(): StandardViewType | null {
        return this.cameraManager?.getCurrentStandardViewType() ?? null;
    }

    /**
     * 设置场景内视图定向器可见性。
     *
     * @param visible 是否显示。
     */
    public setViewOrientationMeshVisible(visible: boolean): void {
        this.cameraManager?.setViewOrientationMeshVisible(visible);
    }

    /**
     * 读取场景内视图定向器可见性。
     *
     * @returns 是否显示。
     */
    public isViewOrientationMeshVisible(): boolean {
        return this.cameraManager?.isViewOrientationMeshVisible() ?? false;
    }
    //------ 视图定向 结束------


    /**
     * 便捷访问当前草图参考面。
     */
    get currentReferencePlane(): ReferencePlane | null {
        return this.datumPlaneManager?.currentReferencePlane ?? null;
    }

    /**
     * 便捷访问当前视图模式。
     */
    get viewMode(): ViewMode {
        return this.datumPlaneManager?.viewMode ?? ViewMode.VIEW_3D;
    }

    /**
     * 进入基准面视图。
     * @param planeId 基准面 ID。
     * @param cameraFrame 可选相机帧覆盖。
     */
    enterDatumPlaneView(
        planeId: number,
        cameraFrame?: {
            cameraPosition: THREE.Vector3;
            target: THREE.Vector3;
            up: THREE.Vector3;
        },
    ): void {
        this.datumPlaneManager?.enterView({
            planeId,
            cameraFrame,
        });
        //------ 草图会话参考点 开始------
        // 当前参考面更新后重建原点与特征面边界投影点，保证全部参考点使用同一投影基准。
        if (this.sketchGroupManager?.activeSketchGroup) {
            this.sketchSessionReferencePointManager?.refresh();
        }
        //------ 草图会话参考点 结束------
    }

    /**
     * 退出基准面视图。
     */
    exitDatumPlaneView(): void {
        this.datumPlaneManager?.exitView();
    }


    /**
     * 设置网格可见性。
     * @param visible 是否可见。
     */
    setGridVisible(visible: boolean): void {
        if (visible) {
            this.helperManager?.showGrid();
        } else {
            this.helperManager?.hideGrid();
        }
    }

    /**
     * 设置 Viewer 模式。
     * 说明：
     * 1. Viewer 作为外部唯一状态入口；
     * 2. 同时显式推送给选择管理器和交互视觉状态管理器，保证两者同步；
     * 3. 非法模式不做兜底，直接抛错。
     * @param mode 目标 Viewer 模式。
     */
    setMode(mode: ViewerMode): void {
        this.modeContext = mode;
        // 先通知选择管理器清除 hover 残留，再切换视觉状态，顺序不可颠倒。
        this.entitySelectionManager?.setMode(mode);
        // 直接传入 ViewerMode，由 InteractionStateManager.setMode 统一处理状态切换。
        this.interactionStateManager?.setMode(mode);
    }

    /**
     * 获取 Viewer 当前模式。
     * @returns 当前模式上下文副本。
     */
    public getMode(): ViewerMode {
        return this.modeContext;
    }

    /**
     * 启动绘图模式
     * @param toolType 工具类型
     */
    startDrawing(toolType: SketchDrawingTool): void {
        if (!this.sketchDrawingManager || !this.camera || !this.scene) {
            console.log('渲染器未完全初始化，无法启动绘图');
            return;
        }
        const drawingTools = Object.values(SketchDrawingTool);
        if (!drawingTools.includes(toolType)) {
            console.warn(`不支持的绘图工具类型: ${toolType}`);
            return;
        }
        this.sketchDrawingManager.startDrawing(toolType);
    }

    /**
     * 按工具分发绘图提示。
     * @param tool 当前工具类型。
     * @returns 提示文本。
     */
    public resolveDrawingHintByTool(tool: string): string {
        if (!this.sketchDrawingManager) {
            return '绘制中... | ESC取消';
        }
        return this.sketchDrawingManager.resolveDrawingHintByTool(tool);
    }

    /**
     * 设置绘图完成回调
     *
     * @param callback 当前绘图命令结束时调用的宿主回调；注册后替换此前的回调。
     */
    onDrawingFinished(callback: () => void): void {
        this.drawingFinishedCallback = callback;
    }

    /**
     * 注册 cad-core 操作提示回调，并替换此前注册的提示接收方。
     *
     * 草图、尺寸、测量和选择链路会通过该回调发布面向用户的状态或失败原因；注册动作
     * 不会立即触发回调，也不会转移 Viewer 资源所有权。
     *
     * @param callback 提示接收函数；`message` 为中文提示，`level` 为可选提示级别。
     */
    onDrawingNotice(callback: (message: string, level?: 'info' | 'warning' | 'error') => void): void {
        this.drawingNoticeCallback = callback;
    }

    /**
     * 在指定基准面上开始或恢复草图编辑会话。
     *
     * 已存在活动草图时直接返回该草图并恢复草图模式；否则校验退回控制棒与基准面，
     * 创建新的草图组并同步编辑视觉层级。调用前必须等待 `whenReady()`。
     *
     * @param options 草图会话输入。
     * @param options.planeId 当前 Viewer 中的基准面运行时数字 ID。
     * @returns 活动或新建的草图组；场景未就绪、基准面不存在或基准面已被退回时返回 `null`。
     */
    startSketchSession(options: {  planeId: number }):  SketchGroup | null{
        if (!this.scene || !this.sketchGroupManager) {
            console.log('场景未初始化，无法创建草图分组');
            return null;
        }

        // 已有活跃草图组时直接返回
        const activeSketchGroup = this.sketchGroupManager.activeSketchGroup;
        if (activeSketchGroup) {
            this.setMode(ViewerMode.Sketch);
            this.sketchSessionReferencePointManager?.refresh();
            this.syncSketchEditingVisualLayering();
            return activeSketchGroup;
        }

        /**
         * 退回控制棒下方的用户基准面不得再作为草图宿主面。
         * 临时宿主面不会进入退回序列，因此这里只拦截真正被 rolled-back 的正式基准面。
         */
        this.syncFeatureRollbackManagerTimeline();
        if (this.featureRollbackController.isDatumPlaneRolledBack(String(options.planeId))) {
            this.drawingNoticeCallback?.('该基准面位于退回控制棒下方，无法创建草图', 'warning');
            return null;
        }

        // 获取基准面实例
        const referencePlane = this.datumPlaneManager?.getPlane(options.planeId);
        if (!referencePlane) {
            console.log(`基准面 ${options.planeId} 未找到，无法创建草图分组`);
            return null;
        }

        // 委托给 SketchGroupManager 创建草图组
        const group = this.sketchGroupManager.startGroup({
            referencePlane,
        });

        if (group) {
            this.setMode(ViewerMode.Sketch);
            this.sketchSessionReferencePointManager?.refresh();
            this.syncSketchEditingVisualLayering();
        }

        return group;
    }

    /**
     * 恢复对已有草图的编辑。
     *
     * 设计说明：
     * 1. 与 `startSketchSession` / `endSketchSession` 构成 start / resume / end 三方法对称；
     * 2. 场景未初始化、目标草图不存在、已有其他活动草图时返回 null，由上层 workflow 负责提示 UI；
     * 3. 已有活动草图且就是目标草图时，直接幂等返回，不重复发送生命周期事件；
     * 4. `ViewerMode.Sketch` 切换仅在本层收口，`resumeGroup` 只管草图状态真相。
     * 5. 上色草图轮廓是派生显示代理，恢复编辑时必须按当前 Viewer 配置重新生成。
     *
     * @param options 恢复选项。
     * @returns 草图组实例；场景未就绪或目标不可恢复时返回 null。
     */
    public resumeSketchSession(options: { sketchGroupId: string }): SketchGroup | null {
        if (!this.scene || !this.sketchGroupManager) {
            console.log('场景未初始化，无法恢复草图编辑');
            return null;
        }

        const existingGroup = this.sketchGroupManager.getGroupById(options.sketchGroupId);
        if (!existingGroup) {
            return null;
        }

        const activeSketchGroup = this.sketchGroupManager.activeSketchGroup;
        if (activeSketchGroup) {
            if (activeSketchGroup.uuid !== options.sketchGroupId) {
                return null;
            }
        }

        const resumed = this.sketchGroupManager.resumeGroup(options.sketchGroupId);
        if (!resumed) {
            return null;
        }

        //------ 实体面草图宿主面保留 开始------
        /**
         * 设计树“编辑草图”和 SDK 直接恢复已有草图时，活动草图组不能替代绘制投影参考面。
         * 这里只恢复当前参考面与 DATUM_PLANE 视图语义，不调用正视相机入口；宿主层仍可按入口需要再决定是否调整相机。
         */
        const restoredReferencePlane = this.datumPlaneManager?.restoreSketchReferencePlaneFromHistory(
            resumed.referencePlane,
        ) ?? false;
        if (!restoredReferencePlane && this.datumPlaneManager) {
            console.error('恢复已有草图参考面失败：草图宿主平面不存在。', {
                sketchGroupId: options.sketchGroupId,
                planeId: resumed.referencePlane.id,
            });
        }
        this.sketchSessionReferencePointManager?.refresh();
        //------ 实体面草图宿主面保留 结束------

        this.setMode(ViewerMode.Sketch);
        this.syncSketchEditingVisualLayering();
        //------ 草图重入上色轮廓恢复 开始------
        /**
         * 退出草图会清理轮廓填充面，避免展示态轮廓抢占后续特征面命中。
         * 重新编辑草图时，闭合 profile 仍然存在于草图对象中，必须按当前开关恢复显示代理。
         */
        this.syncSketchContourVisibility(resumed, this.shadedSketchContoursEnabled);
        this.requestRender();
        //------ 草图重入上色轮廓恢复 结束------
        return resumed;
    }

    /**
     * 结束当前草图会话。
     *
     * 设计说明：
     * 1. 草图会话的结束必须由 `cad-core` 底层统一收口，宿主层只负责声明“何时结束”；
     * 2. 是否丢弃空草图由这里集中判定，避免 UI 侧自行猜测草图组内容；
     * 3. 结束过程中会统一提交草图定义、回收临时基准面，并在全部完成后再发布生命周期事件。
     *
     * @param options 结束选项。
     * @returns 会话结束结果。
     */
    public async endSketchSession(options: {
        discardEmpty?: boolean;
    } = {}): Promise<SketchSessionTerminationResult> {
        this.sketchDimensionInstant2dController?.reset();
        if (!this.sketchSessionTerminator) {
            // 草图实体剪贴板属于当前编辑会话；终止器缺失时也必须释放会话级快照。
            this.sketchEntityClipboardController?.clear();
            //------ 草图会话参考点 开始------
            // 即使终止器不可用，也要回收原点和模型投影参考点，避免残留在场景中。
            this.sketchSessionReferencePointManager?.destroy();
            //------ 草图会话参考点 结束------
            this.clearSketchEditingVisualLayering();
            return {
                endedGroupId: null,
                removed: false,
                committed: false,
                removedTemporaryPlaneId: null,
            };
        }
        const result = await this.sketchSessionTerminator.terminate(options);
        // 草图会话结束后清空剪贴板，禁止旧实体快照跨草图会话继续粘贴。
        this.sketchEntityClipboardController?.clear();
        //------ 草图会话参考点 开始------
        // 草图会话结束时统一回收红色原点投影和绿色特征面参考点。
        this.sketchSessionReferencePointManager?.destroy();
        //------ 草图会话参考点 结束------
        this.clearSketchEditingVisualLayering();
        return result;
    }

    /**
     * 同步草图编辑视觉层级。
     *
     * 说明：
     * 1. Viewer 自己负责计算当前编辑草图和其他草图，避免上层重复遍历草图组；
     * 2. `previewRoots` 与 `highlightedExternalRoots` 暂时作为可选入口保留，不在 Viewer 内写死业务来源；
     * 3. 该方法同步处理视觉层级，不更改草图业务状态；只将画面提交合并到 Viewer 帧循环。
     *
     * @param options 可选的额外视觉层级根对象。
     */
    public syncSketchEditingVisualLayering(options: {
        previewRoots?: readonly THREE.Object3D[];
        highlightedExternalRoots?: readonly THREE.Object3D[];
    } = {}): void {
        if (Object.prototype.hasOwnProperty.call(options, 'previewRoots')) {
            this.sketchEditingVisualLayeringPreviewRoots = [...(options.previewRoots ?? [])];
            this.rebuildSketchEditingVisualPreviewObjectIndex();
        }
        if (Object.prototype.hasOwnProperty.call(options, 'highlightedExternalRoots')) {
            this.sketchEditingVisualLayeringHighlightedExternalRoots = [...(options.highlightedExternalRoots ?? [])];
            this.entitySelectionManager?.setExternalImportedTopologyProxyRetainers(
                this.sketchEditingVisualLayeringHighlightedExternalRoots,
            );
        }

        //------ 尺寸随草图会话显隐 开始------
        /**
         * SW 语义：尺寸只在编辑对应草图时显示。会话进入/恢复都会走到这里，
         * 以活动草图为准亮起它的尺寸并重排布局（几何可能在隐藏期间被改过）。
         */
        const activeSketchPersistentId = this.sketchGroupManager?.activeSketchGroup?.persistentId ?? null;

        this.sketchDimensionAnnotationRenderer.setVisibleSketch(activeSketchPersistentId);
        if (activeSketchPersistentId) {
            this.sketchDimensionAnnotationRenderer.refreshSketch(activeSketchPersistentId);
        }
        //------ 尺寸随草图会话显隐 结束------

        const { currentSketchRoots, otherSketchRoots } = this.collectSketchEditingVisualLayeringRoots();
        this.sketchEditingVisualLayering.sync({
            currentSketchRoots,
            otherSketchRoots,
            previewRoots: this.sketchEditingVisualLayeringPreviewRoots,
            highlightedExternalRoots: this.sketchEditingVisualLayeringHighlightedExternalRoots,
        });
        this.requestRender();
    }

    /**
     * 清理草图编辑视觉层级并恢复原始状态。
     */
    public clearSketchEditingVisualLayering(): void {
        this.sketchEditingVisualLayeringPreviewRoots = [];
        this.sketchEditingVisualPreviewObjectIds.clear();
        this.sketchEditingVisualLayeringHighlightedExternalRoots = [];
        this.entitySelectionManager?.setExternalImportedTopologyProxyRetainers([]);
        this.sketchEditingVisualLayering.clear();
        // 退出草图会话即隐藏全部尺寸（SW 语义：尺寸只在编辑草图时显示）。
        this.sketchDimensionAnnotationRenderer.setVisibleSketch(null);
        this.requestRender();
    }

    /**
     * 判断对象是否属于当前草图编辑命令态预览子树。
     *
     * 命令预览会主动管理黄色候选、变换留影等业务材质，
     * 全局交互状态刷新不得将它们恢复为普通草图默认材质。
     *
     * @param object 待判断场景对象。
     * @returns 对象属于当前预览子树时返回 true。
     */
    public isSketchEditingVisualPreviewObject(object: THREE.Object3D): boolean {
        return this.sketchEditingVisualPreviewObjectIds.has(object.uuid);
    }

    /**
     * 按当前预览根对象重建命令态预览子树索引。
     *
     * 索引只在命令预览根发生变化时重建，鼠标移动热路径只做 Set 查询，
     * 避免每次交互视觉刷新都重复遍历预览根。
     */
    private rebuildSketchEditingVisualPreviewObjectIndex(): void {
        this.sketchEditingVisualPreviewObjectIds.clear();
        this.sketchEditingVisualLayeringPreviewRoots.forEach((root) => {
            root.traverse((object) => {
                this.sketchEditingVisualPreviewObjectIds.add(object.uuid);
            });
        });
    }

    /**
     * 读取当前草图编辑视觉层级所需的默认根对象。
     *
     * 说明：
     * 1. 当前编辑草图直接取活动草图组；
     * 2. 其他草图则取草图组管理器中的其余所有草图；
     * 3. 这样上层只需要补充预览和外部高亮对象，无需自己判断当前草图是谁。
     *
     * @returns 当前与其他草图根对象。
     */
    private collectSketchEditingVisualLayeringRoots(): {
        currentSketchRoots: THREE.Object3D[];
        otherSketchRoots: THREE.Object3D[];
    } {
        const sketchGroupManager = this.sketchGroupManager;
        if (!sketchGroupManager) {
            return {
                currentSketchRoots: [],
                otherSketchRoots: [],
            };
        }

        const currentSketchGroup = sketchGroupManager.activeSketchGroup;
        const sketchGroups = sketchGroupManager.getAllGroups();
        return {
            currentSketchRoots: currentSketchGroup ? [currentSketchGroup] : [],
            otherSketchRoots: sketchGroups.filter((group) => group.uuid !== currentSketchGroup?.uuid),
        };
    }

    /**
     * 判断当前是否处于需要应用草图编辑视觉层级的模式。
     *
     * @returns 当当前模式属于草图编辑链路时返回 true。
     */
    private isSketchEditingVisualLayeringEnabled(): boolean {
        return this.modeContext === ViewerMode.Sketch
            || this.modeContext === ViewerMode.SketchDrawing
            || this.modeContext === ViewerMode.SketchMoveEntities
            || this.modeContext === ViewerMode.SketchCopyEntities
            || this.modeContext === ViewerMode.SketchRotateEntities
            || this.modeContext === ViewerMode.SketchCircularPattern
            || this.modeContext === ViewerMode.SketchMirrorEntities
            || this.modeContext === ViewerMode.SketchTrim
            || this.modeContext === ViewerMode.SketchDimension
            || this.modeContext === ViewerMode.SketchOffset;
    }

    /**
     * 在草图结束生命周期事件发布前收口最终显示态。
     *
     * 设计说明：
     * 1. `Ended` 事件必须表达最终终态，而不是“结束后还要再补一次 auto-hide”的中间态；
     * 2. 因此这里在 `SketchSessionTerminator` 发布生命周期事件前，先按消费关系把显示态收口；
     * 3. 若最终态发生变化，需要同步回写 `SketchGroupEndResult`，让 `Ended` 事件携带正确显示态。
     *
     * @param endResult 草图结束结果。
     */
    private async finalizeEndedSketchDisplayState(endResult: SketchGroupEndResult): Promise<void> {
        if (!endResult.endedGroupId) {
            this.clearEditSketchFeaturePreview();
            return;
        }

        const endedSketchGroup = endResult.endedGroup
            ?? this.sketchGroupManager?.getGroupById(endResult.endedGroupId)
            ?? null;
        const restoredPrefix = await this.restoreSketchFeaturePrefixOnSessionEnd({
            sketchId: endResult.endedGroupId,
            expectedDefinitionRevision: endedSketchGroup?.definitionRevision ?? null,
        });
        if (!restoredPrefix) {
            if (endResult.removed) {
                this.clearEditSketchFeaturePreview();
                return;
            }
            await this.commitEditSketchFeaturePreviewForSketch(endResult.endedGroupId);
        }
        if (endResult.removed) {
            return;
        }

        const previousDisplayState = endResult.displayState;
        const changed = await this.reapplyAutoHideOnSessionEnd(endResult.endedGroupId, {
            publishLifecycleEvent: false,
        });
        if (!changed) {
            return;
        }
        endResult.displayState = this.getSketchGroupDisplayState(endResult.endedGroupId);
        endResult.displayStateChanged = endResult.displayStateChanged
            || endResult.displayState !== previousDisplayState;
    }

    /**
     * 在草图会话结束边界恢复严格前缀之前捕获的完整 Feature 命令链。
     *
     * 该方法只负责编排 Viewer 临时状态生命周期；命令重写、revision 校验、
     * Worker replay 和 Mesh 同步全部由 `FeatureRebuildOrchestrator` 收口。
     *
     * @param input 已结束草图及其最终定义版本。
     * @returns 当前结束草图是否属于活动严格前缀会话。
     */
    private async restoreSketchFeaturePrefixOnSessionEnd(input: {
        /** 已结束草图运行时 ID。 */
        readonly sketchId: string;
        /** 草图结束时的最终定义版本；无法解析时为空。 */
        readonly expectedDefinitionRevision: number | null;
    }): Promise<boolean> {
        return this.sketchFeaturePrefixEditRuntime?.restoreOnSessionEnd(input) ?? false;
    }

    /**
     * 会话结束后按消费关系重新应用自动隐藏。
     *
     * 设计说明：
     * 1. 与 `autoHideConsumedSketchAfterFeatureCommit` 保持同一语义——自动隐藏是
     *    "源草图被消费"这一消费关系的派生状态；
     * 2. 编辑期间 selection handler 会逐帧通过 `commitDefinitionChanges` 触发 rebuild-event 订阅，
     *    这条路径 bypass `Viewer.commitSketchDefinitionChanges`，因此不能挂在 commit 链路；
     * 3. 若用于会话结束收口，可关闭 `DisplayStateChanged` 即时事件，改由 `Ended` 生命周期统一承载终态。
     *
     * @param sketchId 刚结束会话的草图 uuid。
     * @param options 显示态事件选项。
     * @returns 是否真的把草图重新收口到隐藏态。
     */
    private async reapplyAutoHideOnSessionEnd(
        sketchId: string,
        options: {
            publishLifecycleEvent?: boolean;
        } = {},
    ): Promise<boolean> {
        if (!this.featureRebuildOrchestrator) {
            return false;
        }
        const dependentFeatureIds = this.featureRebuildOrchestrator.getDependentFeatureIds(sketchId);
        for (const featureId of dependentFeatureIds) {
            const prototype = await this.getFeaturePrototypeById(featureId);
            if (!prototype) {
                continue;
            }
            if (!AUTO_HIDE_SOURCE_SKETCH_FEATURE_KINDS.has(prototype.getFeatureKind())) {
                continue;
            }
            /**
             * 特征原型记录的来源草图是持久 ID，会话结束回调传入的是运行时 uuid。
             * 两者必须经草图组归一后再比对，直接 `!==` 会让自动隐藏对还原草图整体失效。
             */
            if (this.resolveRuntimeSketchId(prototype.getSourceSketchId()) !== sketchId) {
                continue;
            }
            if (this.getSketchGroupDisplayState(sketchId) === SketchDisplayState.Hidden) {
                return false;
            }
            const sourceSketchGroup = this.sketchGroupManager?.getGroupById(sketchId) ?? null;
            if (sourceSketchGroup) {
                clearSketchContourDisplays(sourceSketchGroup);
            }
            this.setSketchGroupDisplayState(sketchId, SketchDisplayState.Hidden, options);
            return true;
        }
        return false;
    }

    /**
     * 读取当前活动草图组 ID。
     *
     * @returns 活动草图组 ID；不存在时返回 null。
     */
    public getActiveSketchGroupId(): string | null {
        return this.sketchGroupManager?.activeSketchGroup?.uuid ?? null;
    }

    /**
     * 捕获当前活动草图组运行时历史快照。
     *
     * @param label 快照标签。
     * @returns 当前活动草图组快照；没有活动草图时返回 null。
     */
    public captureActiveSketchRuntimeHistorySnapshot(label: string): SketchGroupRuntimeHistorySnapshot | null {
        const activeSketchGroup = this.sketchGroupManager?.activeSketchGroup ?? null;

        if (!activeSketchGroup) {
            return null;
        }

        return this.sketchRuntimeSnapshotRegistry.capture({
            sketchGroup: activeSketchGroup,
            label,
        });
    }

    /**
     * 捕获指定草图组运行时历史快照。
     *
     * 设计说明：
     * 设计树删除、隐藏、显示和复制草图时，目标草图不一定处于编辑态；
     * 因此不能复用 active sketch 专用入口，必须按 sketchId 精确捕获。
     *
     * @param sketchId 草图组 ID。
     * @param label 快照标签。
     * @returns 草图组快照；目标不存在时返回 null。
     */
    public captureSketchGroupRuntimeHistorySnapshot(
        sketchId: string,
        label: string,
    ): SketchGroupRuntimeHistorySnapshot | null {
        const sketchGroup = this.sketchGroupManager?.getGroupById(sketchId) ?? null;

        if (!sketchGroup) {
            return null;
        }

        return this.sketchRuntimeSnapshotRegistry.capture({
            sketchGroup,
            label,
        });
    }

    /**
     * 恢复草图组运行时历史快照。
     *
     * @param snapshot 草图组运行时历史快照。
     */
    public restoreSketchGroupRuntimeHistorySnapshot(snapshot: SketchGroupRuntimeHistorySnapshot): void {
        // 必须在替换草图对象树之前恢复预览，旧坐标不能落到历史恢复后的新对象上。
        this.sketchDimensionInstant2dController?.reset();
        this.sketchDrawingManager?.prepareForModelHistoryRestore();
        this.entitySelectionManager?.resetTransientInteractionStateForModelRestore();
        this.interactionStateManager?.prepareForRuntimeObjectTreeReplacement();
        this.sketchRuntimeSnapshotRegistry.restore(snapshot);
        if (snapshot.displayState) {
            this.sketchGroupManager?.restoreDisplayStateFromHistory(snapshot.sketchId, snapshot.displayState as SketchDisplayState, {
                publishLifecycleEvent: false,
            });
            if (snapshot.displayState === SketchDisplayState.Edit) {
                this.restoreSketchReferencePlaneFromHistory(snapshot.sketchId);
            }
        }
        this.finalizeSketchRuntimeHistoryRestoreVisualState(
            this.sketchGroupManager?.getGroupById(snapshot.sketchId) ?? null,
        );
    }

    /**
     * 收口草图运行时历史恢复后的视觉状态。
     *
     * 设计说明：
     * 1. 历史恢复会整体替换草图子树，必须先让端点/中点/象限点回到拓扑默认显示规则；
     * 2. 再通过交互状态管理器重建新对象树的默认材质与缩放，避免复用已离场对象的缓存；
     * 3. 最后同步草图编辑层级并置脏下一帧，保证连续 undo 后端点自动刷新，不依赖下一次鼠标输入。
     *
     * @param restoredSketchGroup 刚恢复到运行时的草图组。
     */
    private finalizeSketchRuntimeHistoryRestoreVisualState(restoredSketchGroup: SketchGroup | null): void {
        if (!restoredSketchGroup) {
            this.requestRender();
            return;
        }

        restoredSketchGroup.traverse((object) => {
            if (isSketchPointObject(object)) {
                object.refreshDefaultVisibilityByTopologyRole();
            }
        });
        this.syncShadedContoursAfterSketchEntityCreated(restoredSketchGroup);
        this.interactionStateManager?.requestVisualRefresh();
        this.syncSketchEditingVisualLayering();
    }

    /**
     * 判断草图组运行时快照是否仍为当前状态。
     *
     * @param snapshot 草图组运行时历史快照。
     * @returns 当前草图组是否仍匹配该快照。
     */
    public isSketchGroupRuntimeHistorySnapshotCurrent(snapshot: SketchGroupRuntimeHistorySnapshot): boolean {
        return this.sketchRuntimeSnapshotRegistry.matchesCurrent(snapshot);
    }

    /**
     * 释放草图组运行时历史快照。
     *
     * @param snapshot 草图组运行时历史快照。
     */
    public releaseSketchGroupRuntimeHistorySnapshot(snapshot: SketchGroupRuntimeHistorySnapshot): void {
        this.sketchRuntimeSnapshotRegistry.release(snapshot);
    }

    /**
     * 开始一个可跨多次事件的草图运行时历史会话。
     *
     * 设计说明：
     * 强力裁剪拖拽会在 mousemove 中连续改写实体，不能让每次移动都进入模型历史；
     * 会话模式在 mousedown 捕获 before，在 mouseup 捕获 after，最终只提交一个用户可感知事务。
     *
     * @param input 会话输入。
     * @returns 历史会话；没有活动草图时返回 null。
     */
    public beginActiveSketchRuntimeHistorySession(
        input: SketchRuntimeHistorySessionInput,
    ): SketchRuntimeHistorySession | null {
        const beforeSnapshot = this.captureActiveSketchRuntimeHistorySnapshot(input.label);

        if (!beforeSnapshot) {
            return null;
        }

        let closed = false;
        const releaseBeforeSnapshot = (): void => {
            this.releaseSketchGroupRuntimeHistorySnapshot(beforeSnapshot);
        };

        return {
            commit: async (options) => {
                if (closed) {
                    return;
                }
                closed = true;

                const afterSnapshot = this.captureActiveSketchRuntimeHistorySnapshot(input.label);
                const extraPatches = options?.extraPatches ?? [];

                if (
                    !afterSnapshot
                    || (
                        areSketchGroupRuntimeSnapshotsEqual(beforeSnapshot, afterSnapshot)
                        && extraPatches.length === 0
                    )
                ) {
                    if (afterSnapshot) {
                        this.releaseSketchGroupRuntimeHistorySnapshot(afterSnapshot);
                    }
                    releaseBeforeSnapshot();
                    return;
                }

                const commitHistoryRecord = this.options.onSketchRuntimeHistoryCommitted;
                if (!commitHistoryRecord) {
                    this.releaseSketchGroupRuntimeHistorySnapshot(afterSnapshot);
                    releaseBeforeSnapshot();
                    return;
                }

                try {
                    await commitHistoryRecord({
                        sketchId: afterSnapshot.sketchId,
                        label: input.label,
                        commandType: input.commandType,
                        before: beforeSnapshot,
                        after: afterSnapshot,
                        ...(extraPatches.length > 0 ? {extraPatches} : {}),
                    });
                } catch (error) {
                    if (this.isSketchGroupRuntimeHistorySnapshotCurrent(afterSnapshot)) {
                        this.restoreSketchGroupRuntimeHistorySnapshot(beforeSnapshot);
                    }
                    this.releaseSketchGroupRuntimeHistorySnapshot(afterSnapshot);
                    releaseBeforeSnapshot();
                    throw error;
                }
            },
            cancel: () => {
                if (closed) {
                    return;
                }
                closed = true;
                this.restoreSketchGroupRuntimeHistorySnapshot(beforeSnapshot);
                releaseBeforeSnapshot();
            },
            discard: () => {
                if (closed) {
                    return;
                }
                closed = true;
                releaseBeforeSnapshot();
            },
        };
    }

    /**
     * 在当前活动草图组上执行运行时历史事务。
     *
     * 设计说明：
     * 草图绘制、删除、裁剪的联动范围暂时无法通过 normalized entity patch 完整表达，
     * 因此统一在 cad-core 捕获整组 before/after，保证关联控制点、几何约束和轮廓能原样撤销。
     *
     * @param input 草图运行时历史事务输入。
     * @returns work 的执行结果。
     * @throws 输入回调或同步历史提交回调抛出错误时，在恢复可恢复的草图快照后原样抛出。
     */
    public executeActiveSketchRuntimeHistoryTransaction<TResult>(
        input: SketchRuntimeHistoryTransactionInput<TResult>,
    ): TResult {
        const beforeSnapshot = this.captureActiveSketchRuntimeHistorySnapshot(input.label);
        let result: TResult;

        try {
            result = input.work();
        } catch (error) {
            if (beforeSnapshot) {
                this.restoreSketchGroupRuntimeHistorySnapshot(beforeSnapshot);
                this.releaseSketchGroupRuntimeHistorySnapshot(beforeSnapshot);
            }
            throw error;
        }

        const shouldCommit = input.shouldCommit?.(result) ?? true;

        if (!beforeSnapshot || !shouldCommit) {
            if (beforeSnapshot) {
                this.releaseSketchGroupRuntimeHistorySnapshot(beforeSnapshot);
            }
            input.commitExtraState?.(result);
            return result;
        }

        const afterSnapshot = this.captureActiveSketchRuntimeHistorySnapshot(input.label);
        /**
         * 纯参考尺寸没有运行时约束视图，删除它时草图组 before/after 可能完全相同；
         * 只要存在尺寸等领域补丁，事务仍必须提交，不能被运行时快照相等短路。
         */
        const extraPatches = input.resolveExtraPatches?.(result) ?? [];

        if (
            !afterSnapshot
            || (
                areSketchGroupRuntimeSnapshotsEqual(beforeSnapshot, afterSnapshot)
                && extraPatches.length === 0
            )
        ) {
            if (afterSnapshot) {
                this.releaseSketchGroupRuntimeHistorySnapshot(afterSnapshot);
            }
            this.releaseSketchGroupRuntimeHistorySnapshot(beforeSnapshot);
            input.commitExtraState?.(result);
            return result;
        }

        try {
            const commitHistoryRecord = this.options.onSketchRuntimeHistoryCommitted;

            if (!commitHistoryRecord) {
                this.releaseSketchGroupRuntimeHistorySnapshot(afterSnapshot);
                this.releaseSketchGroupRuntimeHistorySnapshot(beforeSnapshot);
                input.commitExtraState?.(result);
                return result;
            }

            const commitResult = commitHistoryRecord({
                sketchId: afterSnapshot.sketchId,
                label: input.label,
                commandType: input.commandType,
                before: beforeSnapshot,
                after: afterSnapshot,
                ...(extraPatches.length > 0 ? {extraPatches} : {}),
            });

            if (isPromiseLike(commitResult)) {
                void Promise.resolve(commitResult).then(() => {
                    input.commitExtraState?.(result);
                }).catch(() => {
                    // 先恢复几何再回滚尺寸，额外领域状态才能基于 before 对象树重建关系与标注。
                    const shouldRestoreRuntime = this.isSketchGroupRuntimeHistorySnapshotCurrent(afterSnapshot);
                    try {
                        if (shouldRestoreRuntime) {
                            this.restoreSketchGroupRuntimeHistorySnapshot(beforeSnapshot);
                        }
                    } finally {
                        try {
                            if (shouldRestoreRuntime) {
                                input.rollbackExtraState?.(result);
                            }
                        } finally {
                            this.releaseSketchGroupRuntimeHistorySnapshot(afterSnapshot);
                            this.releaseSketchGroupRuntimeHistorySnapshot(beforeSnapshot);
                        }
                    }
                });
            } else {
                input.commitExtraState?.(result);
            }
        } catch (error) {
            try {
                this.restoreSketchGroupRuntimeHistorySnapshot(beforeSnapshot);
            } finally {
                try {
                    input.rollbackExtraState?.(result);
                } finally {
                    this.releaseSketchGroupRuntimeHistorySnapshot(afterSnapshot);
                    this.releaseSketchGroupRuntimeHistorySnapshot(beforeSnapshot);
                }
            }
            throw error;
        }

        return result;
    }

    /**
     * 把草图场景对象加入当前活动草图组，并立即同步草图编辑视觉层级。
     *
     * 调用前必须等待 `whenReady()` 且已经通过 `startSketchSession()` 建立活动草图；
     * 当前没有活动草图组时，底层管理器会抛出错误。
     *
     * @param object 要加入活动草图组的 Three.js 对象；对象后续由草图组场景生命周期管理。
     */
    public addSketchObject(object: THREE.Object3D): void {
        this.sketchGroupManager!.addSketchObject(object);
    }
    //------ 移除废弃草图组兼容入口 结束------

    /**
     * 提交指定草图的定义变更，并触发依赖特征自动重建。
     * @param sketchId 草图 ID。
     * @returns 是否重建成功。
     */
    public async commitSketchDefinitionChanges(sketchId: string): Promise<boolean> {
        if (await this.refreshEditSketchFeaturePreviewForSketch(sketchId)) {
            return true;
        }
        if (!this.featureRebuildOrchestrator) {
            return false;
        }
        const result = await this.featureRebuildOrchestrator.commitSketchDefinitionChanges(sketchId);
        return result.success;
    }

    /**
     * 列出当前受管理的草图组摘要。
     * @returns 草图组摘要列表。
     */
    public listSketchGroupSummaries(): Array<{
        id: string;
        persistentId: string;
        name: string;
        order: number;
        createdAt?: number;
        displayState: SketchDisplayState;
        referenceWarning: SketchEntityReferenceWarningSummary;
    }> {
        if (!this.sketchGroupManager) {
            return [];
        }
        const groups = this.sketchGroupManager.getAllGroups();
        this.sketchEntityReferenceDocumentRuntime?.refreshDiagnostics(
            groups.map((group) => group.persistentId),
        );
        return groups.map((group, index) => ({
            id: group.uuid,
            /**
             * 特征绑定与离线包记录的草图身份是持久 ID，摘要必须同时暴露两把 ID，
             * 设计树投影与退回时间线才能把“特征来源草图”归一回场景句柄。
             */
            persistentId: group.persistentId,
            name: group.name,
            order: this.sketchGroupManager?.getGroupOrder(group.uuid) ?? index,
            createdAt: this.sketchGroupManager?.getGroupCreatedAt(group.uuid),
            displayState: this.sketchGroupManager?.getDisplayState(group.uuid) ?? SketchDisplayState.Shown,
            referenceWarning: this.sketchEntityReferenceDocumentRuntime
                ?.getWarningSummary(group.persistentId) ?? {
                    status: 'ok',
                    danglingReferenceCount: 0,
                    failedReferenceCount: 0,
                    referenceIds: [],
                    targetEntityIds: [],
                    diagnostics: [],
                },
        }));
    }

    //------ 特征消费草图自动隐藏 开始------
    /**
     * 设置指定草图组的显示态。
     *
     * 设计说明：
     * 1. 宿主层只能通过 Viewer 公开门面驱动草图显隐，不能直接耦合内部管理器；
     * 2. 返回布尔值便于上层判断目标草图是否真实存在；
     * 3. 所有显隐事件仍由 `SketchGroupManager` 统一发布。
     *
     * @param sketchId 草图 ID。
     * @param displayState 目标显示态。
     * @param options 生命周期事件选项。
     * @returns 是否命中并完成设置。
     */
    public setSketchGroupDisplayState(
        sketchId: string,
        displayState: SketchDisplayState,
        options: {
            publishLifecycleEvent?: boolean;
        } = {},
    ): boolean {
        const sketchGroupManager = this.sketchGroupManager;
        const sketchGroup = sketchGroupManager?.getGroupById(sketchId);
        if (!sketchGroup) {
            return false;
        }

        /**
         * 严格前缀后的草图在当前求值上下文中尚未存在，不能被设计树或其它 UI 入口重新显示。
         * 顶级草图直接来自混合时间线；被 Feature 吸收的草图则按后缀命令保存的 persistent ID 判定。
         * 这里只约束 Shown，隐藏与退出前缀后的显示态恢复仍沿用现有 Manager 链路。
         */
        if (displayState === SketchDisplayState.Shown && this.sketchFeaturePrefixEditRuntime?.state) {
            const {plan} = this.sketchFeaturePrefixEditRuntime.state;
            const isInactiveTopLevelSketch = plan.inactiveTimelineItems.some((item) => (
                item.kind === FeatureRollbackTimelineItemKind.Sketch
                && item.id === sketchGroup.uuid
            ));
            const isConsumedByInactiveFeature = plan.fullCommands
                .slice(plan.cutoffFeatureIndex)
                .some((command) => (
                    featureCommandAdapterRegistry
                        .createPrototypeFromCommand(command)
                        ?.getSourceSketchId() === sketchGroup.persistentId
                ));
            if (isInactiveTopLevelSketch || isConsumedByInactiveFeature) {
                return false;
            }
        }

        sketchGroupManager.setDisplayState(sketchId, displayState, options);
        return true;
    }

    /**
     * 按历史记录恢复草图组显示态。
     *
     * 设计说明：
     * 1. 普通 `setSketchGroupDisplayState` 只表达显隐切换，不负责处理历史恢复中的编辑态收口；
     * 2. feature undo/redo 恢复源草图显示态时，本质是在恢复历史主状态的派生显示语义；
     * 3. 通过 Viewer 门面统一下沉到 `SketchGroupManager.restoreDisplayStateFromHistory`，
     *    避免 web-client 直接触碰草图运行时对象或管理器内部状态。
     *
     * @param sketchId 草图 ID。
     * @param displayState 历史目标显示态。
     * @param options 生命周期事件选项。
     * @returns 是否命中并完成恢复。
     */
    public restoreSketchGroupDisplayStateFromHistory(
        sketchId: string,
        displayState: SketchDisplayState,
        options: {
            publishLifecycleEvent?: boolean;
        } = {},
    ): boolean {
        this.sketchDimensionInstant2dController?.reset();
        const restored = this.sketchGroupManager?.restoreDisplayStateFromHistory(sketchId, displayState, options) ?? false;
        if (restored && displayState === SketchDisplayState.Edit) {
            this.restoreSketchReferencePlaneFromHistory(sketchId);
        }
        return restored;
    }

    /**
     * 读取指定草图组的显示态。
     *
     * 设计说明：
     * 1. 设计树右键菜单与后续状态同步都应通过 Viewer 公开门面读取草图显隐；
     * 2. 未命中草图时返回 null，避免宿主层误把 undefined 当成有效状态；
     * 3. 读取逻辑保持只读，不引入额外副作用。
     *
     * @param sketchId 草图 ID。
     * @returns 草图显示态；未命中时返回 null。
     */
    public getSketchGroupDisplayState(sketchId: string): SketchDisplayState | null {
        return this.sketchGroupManager?.getDisplayState(sketchId) ?? null;
    }

    /**
     * 复制指定草图组。
     *
     * 设计说明：
     * 1. 宿主层只能通过 Viewer 公开门面发起草图复制；
     * 2. 返回新草图 ID，便于上层在必要时刷新后重新定位节点；
     * 3. 复制失败时返回 null，由宿主层决定提示策略。
     *
     * @param sketchId 源草图 ID。
     * @returns 新草图 ID；复制失败时返回 null。
     */
    public duplicateSketchGroup(sketchId: string): string | null {
        return this.duplicateSketchGroupWithDimensions(sketchId)?.group.uuid ?? null;
    }

    /**
     * 复制指定草图组及其全部智能尺寸。
     *
     * 设计说明：
     * 1. Viewer 只作为 cad-core 对外门面，具体身份换新和关系重建由 SketchGroupManager 完成；
     * 2. 返回尺寸历史补丁，供宿主把草图与尺寸合并为同一个 Model Undo/Redo 事务；
     * 3. 复制完成后统一刷新草图编辑视觉分层，避免新草图沿用错误渲染层级。
     *
     * @param sketchId 源草图 ID。
     * @returns 草图组与尺寸创建补丁；复制失败时返回 null。
     */
    public duplicateSketchGroupWithDimensions(sketchId: string): SketchGroupDuplicationResult | null {
        const duplication = this.sketchGroupManager?.duplicateGroupWithDimensions(sketchId) ?? null;

        if (duplication && this.isSketchEditingVisualLayeringEnabled()) {
            this.syncSketchEditingVisualLayering();
        }
        return duplication;
    }

    /**
     * 丢弃尚未进入历史栈的草图副本及其智能尺寸。
     *
     * @param sketchId 待丢弃草图副本 ID。
     * @returns 是否成功移除副本。
     */
    public discardDuplicatedSketchGroup(sketchId: string): boolean {
        const removed = this.sketchGroupManager?.discardDuplicatedGroup(sketchId) ?? false;

        if (removed && this.isSketchEditingVisualLayeringEnabled()) {
            this.syncSketchEditingVisualLayering();
        }
        return removed;
    }

    /**
     * 删除指定草图组。
     *
     * 设计说明：
     * 1. 宿主层只能通过 Viewer 公开门面发起草图删除；
     * 2. 删除逻辑内部会统一回收场景对象与生命周期监听；
     * 3. 返回布尔值便于特征树动作层决定提示与刷新时机。
     *
     * @param sketchId 草图 ID。
     * @returns 是否删除成功。
     */
    public removeSketchGroup(sketchId: string): boolean {
        const removed = this.sketchGroupManager?.removeGroup(sketchId) ?? false;
        if (removed && this.isSketchEditingVisualLayeringEnabled()) {
            this.syncSketchEditingVisualLayering();
        }
        return removed;
    }
    //------ 特征消费草图自动隐藏 结束------

    /**
     * 启动草图缩放控制。
     * @param sketchId 草图 ID。
     */
    public startSketchScaleControl(sketchId: string): void {
        this.sketchGroupManager?.startSketchScaleControl(sketchId);
    }

    /**
     * 停止草图缩放控制。
     */
    public stopSketchScaleControl(): void {
        this.sketchGroupManager?.stopSketchScaleControl();
    }

    /**
     * 获取当前场景中的草图边对象集合。
     * @returns 草图边对象集合。
     */
    public getSketchEdgeObjects(): ReadonlySet<AbstractSketchEdgeObject> {
        return this.meshManager?.getSketchEdgeObjects() ?? new Set<AbstractSketchEdgeObject>();
    }

    /**
     * 获取当前场景中的草图点对象集合。
     * @returns 草图点对象集合。
     */
    public getSketchPointObjects(): ReadonlySet<SketchPointObject> {
        return this.meshManager?.getSketchPointObjects() ?? new Set<SketchPointObject>();
    }

    /**
     * 获取指定草图组名称。
     * @param sketchId 草图 ID。
     * @returns 草图名称。
     */
    public getSketchGroupName(sketchId: string): string | null {
        return this.sketchGroupManager?.getGroupById(sketchId)?.name ?? null;
    }

    /**
     * 获取指定草图组关联的基准面 ID。
     *
     * 设计说明：
     * Undo/Redo 恢复到草图编辑态时，宿主层需要同步当前草图会话上下文；
     * 该入口只暴露稳定的基准面 ID，避免 web-client 直接读取 `SketchGroup` 或 `ReferencePlane` 实例。
     *
     * @param sketchId 草图组 ID。
     * @returns 关联基准面 ID；目标草图不存在时返回 null。
     */
    public getSketchGroupReferencePlaneId(sketchId: string): number | null {
        return this.sketchGroupManager?.getGroupById(sketchId)?.referencePlane.id ?? null;
    }

    //------ Undo恢复草图参考面 开始------
    /**
     * 按模型历史恢复草图参考面上下文。
     *
     * 设计说明：
     * 1. 草图显示态恢复为 `Edit` 时，活动草图只代表“编辑哪张草图”，不能替代绘制参考面；
     * 2. `enterDatumPlaneView` 会改变相机，应避免在模型 Undo/Redo 中调用；
     * 3. 这里通过 DatumPlaneManager 的无相机恢复入口重建投影上下文，并刷新绘制期参考点。
     *
     * @param sketchId 需要恢复参考面上下文的草图 ID。
     * @returns 是否成功恢复参考面。
     */
    public restoreSketchReferencePlaneFromHistory(sketchId: string): boolean {
        const sketchGroup = this.sketchGroupManager?.getGroupById(sketchId) ?? null;
        if (!sketchGroup) {
            console.error('草图历史恢复参考面失败：未找到草图组。', {
                sketchId,
            });
            return false;
        }

        const restored = this.datumPlaneManager?.restoreSketchReferencePlaneFromHistory(
            sketchGroup.referencePlane,
        ) ?? false;
        if (!restored) {
            console.error('草图历史恢复参考面失败：未找到草图所属基准面。', {
                sketchId,
                planeId: sketchGroup.referencePlane.id,
            });
            return false;
        }

        // 只恢复草图交互上下文，不修改相机；绘制工具可在随后继续切换到 sketch-drawing。
        this.setMode(ViewerMode.Sketch);
        this.sketchSessionReferencePointManager?.refresh();
        this.syncSketchEditingVisualLayering();
        this.requestRender();
        return true;
    }
    //------ Undo恢复草图参考面 结束------

    /**
     * 重命名指定草图组。
     * @param sketchId 草图 ID。
     * @param nextName 新名称。
     * @returns 是否重命名成功。
     */
    public renameSketchGroup(sketchId: string, nextName: string): boolean {
        const sketchGroup = this.sketchGroupManager?.getGroupById(sketchId) ?? null;
        if (!sketchGroup) {
            return false;
        }
        sketchGroup.name = nextName;
        return true;
    }

    /**
     * 读取指定特征原型。
     * @param featureId 特征 ID。
     * @returns 特征原型。
     */
    public async getFeaturePrototypeById(featureId: string): Promise<FeaturePrototype | null> {
        if (!this.featureEditOrchestrator) {
            return null;
        }
        return this.featureEditOrchestrator.getFeaturePrototypeById(featureId);
    }

    /**
     * 启动指定特征的编辑会话。
     * @param featureId 特征 ID。
     * @returns 编辑会话。
     * @throws 编辑会话创建、Worker 预演或结果体同步失败时抛出；已创建的失败会话会先关闭。
     */
    public async startFeatureEditSession(featureId: string): Promise<FeatureEditSession | null> {
        if (!this.featureEditOrchestrator) {
            return null;
        }
        const session = await this.featureEditOrchestrator.startFeatureEditSession(featureId);
        if (session) {
            try {
                const beforeSummary = await this.featureExecutionRuntime.getGeometryStateSummary();
                const replayResult = await this.featureExecutionRuntime.replayFeatureCommands({
                    commands: session.getPreviewCommands(),
                    clearFirst: true,
                    rollbackCommands: session.getOriginalCommands(),
                    syncHistoryProjection: false,
                });
                await this.syncAffectedFeatureMeshesFromState({
                    beforeSummary,
                    afterSummary: replayResult.summary,
                    affectedCommandIds: session.getAffectedFeatureIds(),
                });
            } catch (error) {
                this.featureEditOrchestrator.closeFeatureEditSession(featureId);
                throw error;
            }
        }
        return session;
    }

    /**
     * 读取指定特征的活动编辑会话。
     * @param featureId 特征 ID。
     * @returns 编辑会话。
     */
    public getFeatureEditSession(featureId: string): FeatureEditSession | null {
        return this.featureEditOrchestrator?.getFeatureEditSession(featureId) ?? null;
    }

    /**
     * 关闭指定特征的编辑会话。
     * @param featureId 特征 ID。
     */
    public closeFeatureEditSession(featureId: string): void {
        const session = this.featureEditOrchestrator?.getFeatureEditSession(featureId) ?? null;
        this.featureEditOrchestrator?.closeFeatureEditSession(featureId);
        if (!session) {
            return;
        }
        void this.featureExecutionRuntime.getGeometryStateSummary().then((beforeSummary) => (
            this.featureExecutionRuntime.replayFeatureCommands({
                commands: session.getOriginalCommands(),
                clearFirst: true,
                rollbackCommands: session.getPreviewCommands(),
                syncHistoryProjection: true,
            }).then(async (replayResult) => {
                await this.syncAffectedFeatureMeshesFromState({
                    beforeSummary,
                    afterSummary: replayResult.summary,
                    affectedCommandIds: session.getAffectedFeatureIds(),
                });
            })
        )).catch((error) => {
            /**
             * 关闭 PropertyManager 后的后台 replay 只负责恢复派生几何。
             * 如果历史 epoch 已推进，说明该 replay 已经被 Undo/Redo 或新操作淘汰，不能再抛出到全局未处理队列。
             */
            if (isStaleWorkerResponseError(error)) {
                return;
            }
            console.error('关闭特征编辑会话后的特征重建失败', error);
        });
    }

    /**
     * 从活动编辑会话提交特征命令。
     *
     * 设计说明：
     * 1. 编辑沙箱只从 Worker 历史移除目标及其真实下游，不移动全局退回控制棒；
     * 2. 提交由编辑会话持有的完整历史重建，因此无关实体和 datum 时间线不被连带改写；
     * 3. 用户真实移动退回控制棒时仍由 development 的统一副作用链路管理非特征显示状态。
     *
     * @param featureId 目标特征 ID。
     * @param command 替换后的命令。
     * @returns 是否替换成功。
     */
    private async replaceFeatureCommandFromEditSession(featureId: string, command: CadCommand): Promise<boolean> {
        return this.featureEditOrchestrator?.replaceFeatureCommand(featureId, command) ?? false;
    }

    /**
     * 解析场景对象对应的拉伸轮廓。
     * @param objectId 场景对象 ID。
     * @returns 轮廓解析结果。
     */
    public resolveBossExtrudeSelectionContours(objectId: string): BossExtrudeSelectionResolution {
        return this.bossExtrudeSceneResolver.resolveContoursFromSelection(this.scene, objectId);
    }

    /**
     * 解析场景对象对应的旋转轮廓。
     * @param objectId 场景对象 ID。
     * @returns 轮廓解析结果。
     */
    public resolveBossRevolveSelectionContours(objectId: string): BossRevolveSelectionResolution {
        const regionContour = this.revolveRegionSelectionManager.resolveContourFromObjectId(objectId);
        if (regionContour) {
            return [regionContour];
        }
        return this.bossRevolveSceneResolver.resolveContoursFromSelection(this.scene, objectId);
    }

    /**
     * 开启旋转闭合区域选择。
     * @param sketchId 草图 ID。
     */
    public async enableRevolveRegionSelection(sketchId: string): Promise<void> {
        await this.revolveRegionSelectionManager.enable(sketchId);
    }

    /**
     * 禁用旋转闭合区域选择。
     */
    public disableRevolveRegionSelection(): void {
        this.revolveRegionSelectionManager.disable();
    }

    /**
     * 刷新指定草图的旋转闭合区域。
     * @param sketchId 草图 ID。
     */
    public async refreshRevolveRegionsForSketch(sketchId: string): Promise<void> {
        await this.revolveRegionSelectionManager.refresh(sketchId);
    }

    /**
     * 获取指定草图的旋转闭合区域。
     * @param sketchId 草图 ID。
     * @returns 区域集合。
     */
    public getRevolveRegions(sketchId: string): SketchRegionPayload[] {
        return this.revolveRegionSelectionManager.getRegions(sketchId);
    }

    /**
     * 获取草图区域解析运行时输入。
     * @param sketchId 草图 ID。
     * @returns Worker 载荷。
     */
    public getRevolveSketchRuntimeInput(sketchId: string): FindSketchRegionsPayload | null {
        return this.revolveRegionSelectionManager.getRuntimeInput(sketchId);
    }

    /**
     * 设置旋转区域 hover 状态。
     * @param regionId 区域 ID。
     */
    public setRevolveRegionHover(regionId: string | null): void {
        this.revolveRegionSelectionManager.setHover(regionId);
    }

    /**
     * 设置旋转区域 selected 状态。
     * @param regionIds 区域 ID 集合。
     */
    public setRevolveRegionSelections(regionIds: string[]): void {
        this.revolveRegionSelectionManager.setSelections(regionIds);
    }

    /**
     * 设置旋转区域填充是否可见。
     * @param visible 是否显示区域填充。
     */
    public setRevolveRegionFillVisible(visible: boolean): void {
        this.revolveRegionSelectionManager.setFillVisible(visible);
    }

    /**
     * 从鼠标事件拾取旋转区域。
     * @param event 鼠标事件。
     * @returns 区域数据。
     */
    public pickRevolveRegionFromEvent(event: MouseEvent): SketchRegionPayload | null {
        return this.revolveRegionSelectionManager.pickRegionFromEvent(event);
    }

    /**
     * 解析场景对象对应的旋转轴。
     *
     * 设计说明：
     * 1. web-client 只能传入当前选择快照，不直接读取草图对象或拓扑代理细节；
     * 2. 模型边轴通过权威 topologyPick 与 Viewer 的边线段查询能力解析；
     * 3. 草图轴通过 cad-core 场景对象解析器读取稳定草图线对象。
     *
     * @param input 轴选择输入。
     * @returns 旋转轴解析结果；无法解析时返回 null。
     */
    public resolveBossRevolveAxisSelection(input: {
        objectId: string;
        topologyPick?: ResolvedTopologyPick | null;
    }): BossRevolveAxisResolution | null {
        return this.bossRevolveSceneResolver.resolveAxisFromSelection({
            scene: this.scene,
            objectId: input.objectId,
            topologyPick: input.topologyPick ?? null,
            resolveModelEdgeSegment: (objectId, edgeIndex) => this.getTopologyEdgeSegment(objectId, edgeIndex),
        });
    }

    /**
     * 解析拉伸切除的默认预览种子。
     *
     * @param input 解析输入。
     * @returns 预览种子；当前选择无法恢复为切除目标时返回 null。
     */
    public resolveCutExtrudePreviewSeed(input: {
        selection: BossExtrudeSessionSelection;
        definition: ExtrudeFeatureEditDefinition;
    }): CutExtrudePreviewSeed | null {
        const target = this.cutExtrudeFeatureSessionBridge.resolveTargetFromSelection(this.scene, input.selection);
        if (!target) {
            return null;
        }
        return this.cutExtrudePreviewSeedResolver.resolve({
            scene: this.scene,
            target,
            definition: input.definition,
        });
    }

    /**
     * 获取当前主选面的最小信息。
     * @returns 面信息；未命中时返回 null。
     */
    public getSelectedFaceInfo(): { objectId: string; faceIndex: number } | null {
        return this.entitySelectionManager?.getSelectedFaceInfo() ?? null;
    }

    /**
     * 读取当前选中的平面面快照。
     * @returns 面快照；当前主选不是实体面时返回 null。
     */
    public getSelectedPlanarFace(): SketchPlanarFaceSelection | null {
        const selectedObject = this.entitySelectionManager?.getPrimarySelectedObject() ?? null;
        return this.sketchPlanarFaceResolver.resolveFromSelectionObject(selectedObject);
    }

    /**
     * 按稳定结果体与面索引重新解析剖切参考。
     *
     * 该入口复用草图平面面解析器，确保剖面、面上草图和“正视于”使用同一套
     * 平面性、世界法向与代表点语义。
     *
     * @param objectId 结果体稳定对象 ID。
     * @param faceIndex 权威面索引。
     * @returns 当前场景中的平面面快照；对象或面已失效时返回 null。
     */
    public resolveSectionPlanarFaceReference(
        objectId: string,
        faceIndex: number,
    ): SketchPlanarFaceSelection | null {
        const sceneObject = this.findSceneObjectById(objectId);
        const featureResultMesh = sceneObject && isFeatureResultMesh(sceneObject)
            ? sceneObject
            : resolveImportedModelTopologyPickHost(this.scene, objectId);
        const featureFace = sceneObject && isFeatureFace(sceneObject)
            ? sceneObject
            : featureResultMesh
                ? featureResultMesh.attachFeatureFaceByIndex(faceIndex)
                : null;
        if (!featureFace
            || featureFace.objectId !== objectId
            || featureFace.faceIndex !== faceIndex) {
            return null;
        }
        return this.sketchPlanarFaceResolver.resolveFromSelectionObject(featureFace);
    }

    /**
     * 读取当前主选对象对应的基准面 ID。
     *
     * 设计说明：
     * 1. 与 `getSelectedPlanarFace` 对称，供宿主层在 armed 态统一消费选择层拾取结果；
     * 2. 仅当主选对象属于正式基准面层级时返回 ID，避免 workflow 直接依赖 EntitySelectionManager。
     *
     * @returns 基准面 ID；当前主选不是基准面时返回 null。
     */
    public getSelectedDatumPlaneId(): number | null {
        const selectedObject = this.entitySelectionManager?.getPrimarySelectedObject() ?? null;
        if (!selectedObject) {
            return null;
        }
        return getDatumPlaneIdFromObject(selectedObject);
    }

    /**
     * 基于平面面启动草图会话。
     *
     * 设计说明：
     * 1. `cad-core` 统一负责“选中面 -> 临时平面 -> 草图会话”的底层链路；
     * 2. 宿主层只消费结果，不再自己拼法向、坐标轴和相机姿态；
     * 3. 临时平面创建后立即隐藏，只保留引用与生命周期。
     *
     * @param input 输入参数。
     * @returns 启动结果；失败时返回 null。
     */
    public beginSketchOnPlanarFace(input: {
        face?: SketchPlanarFaceSelection | null;
        planeName?: string;
    } = {}): SketchPlanarFaceSessionStartResult | null {
        if (!this.camera || !this.datumPlaneManager) {
            return null;
        }

        const face = input.face ?? this.getSelectedPlanarFace();
        if (!face || !face.isPlanar) {
            return null;
        }

        const viewportAspect = this.container.clientHeight > 0
            ? this.container.clientWidth / this.container.clientHeight
            : 1;
        const viewFrame = this.sketchFaceViewFrameBuilder.build({
            face,
            camera: this.camera,
            viewportAspect,
        });
        const plane = this.datumPlaneManager.createPlane({
            creationContext: this.getMode() === ViewerMode.Assembly || this.getMode() === ViewerMode.MoveComponent
                ? ReferenceCreationContext.Assembly
                : ReferenceCreationContext.PartFeature,
            name: input.planeName ?? '草图面',
            originWorld: viewFrame.geometryFrame.origin.clone(),
            normal: viewFrame.geometryFrame.geometricNormal.clone(),
            xAxis: viewFrame.geometryFrame.xAxis.clone(),
            yAxis: viewFrame.geometryFrame.yAxis.clone(),
            hostFace: {
                objectId: face.objectId,
                faceIndex: face.faceIndex,
            },
            visible: true,
            opacity: 0.25,
            isTemporary: true,
        });
        const sketchGroup = this.startSketchSession({planeId: plane.id});

        this.enterDatumPlaneView(plane.id, {
            cameraPosition: viewFrame.cameraPosition.clone(),
            target: viewFrame.target.clone(),
            up: viewFrame.up.clone(),
        });
        //------ 特征面正视完整入框 开始------
        /**
         * 特征面进入草图时需要同时满足“正视”和“完整入框”。
         * enterDatumPlaneView 只知道临时草图面，不知道真实特征面边界，因此这里用选中面的顶点包围盒再次聚焦。
         */
        const faceBounds = new THREE.Box3().setFromPoints(face.worldVertices);
        if (!faceBounds.isEmpty()) {
            this.cameraManager?.focusOnBounds(faceBounds, {
                direction: viewFrame.displayNormal.clone(),
                up: viewFrame.up.clone(),
            });
        }
        //------ 特征面正视完整入框 结束------
        this.hideTemporaryDatumPlane(plane.id);
        this.setDatumPlaneVisible(plane.id, false);

        return {
            planeId: plane.id,
            sketchId: sketchGroup?.uuid ?? null,
            face,
            viewFrame,
        };
    }

    /**
     * 启动拉伸特征编辑会话，并恢复拉伸编辑定义与轮廓。
     * @param featureId 特征 ID。
     * @returns 编辑会话与恢复后的载荷。
     */
    public async startExtrudeEditSession(featureId: string): Promise<{
        session: FeatureEditSession | null;
        definition: ExtrudeFeatureEditDefinition | null;
        contours: BossExtrudeSceneContour[];
    }> {
        /**
         * 拉伸特征预览只面向显式特征编辑，并从当前 Worker 正式历史创建 Session。
         * 普通草图编辑使用严格前缀，不允许借此入口恢复或预览被回滚的消费特征。
         */
        const session = await this.startFeatureEditSession(featureId);
        if (!session) {
            return {
                session: null,
                definition: null,
                contours: [],
            };
        }

        const prototype = session.getPrototype();
        if (
            !(prototype instanceof BossExtrudeFeaturePrototype)
            && !(prototype instanceof CutExtrudeFeaturePrototype)
            && prototype.getFeatureKind() !== 'boss-extrude'
            && prototype.getFeatureKind() !== 'cut-extrude'
        ) {
            return {
                session,
                definition: null,
                contours: [],
            };
        }

        const extrudePrototype = prototype as BossExtrudeFeaturePrototype | CutExtrudeFeaturePrototype;
        const sessionBridge = extrudePrototype.getFeatureKind() === 'cut-extrude'
            ? this.cutExtrudeFeatureSessionBridge
            : this.bossExtrudeFeatureSessionBridge;
        return {
            session,
            definition: sessionBridge.restoreDefinitionFromPrototype(extrudePrototype),
            contours: this.bossExtrudeSceneResolver.resolveContoursFromPrototype(this.scene, extrudePrototype),
        };
    }

    /**
     * 启动拉伸凸台编辑会话。
     * @param featureId 特征 ID。
     * @returns 编辑会话载荷。
     */
    public async startBossExtrudeEditSession(featureId: string): ReturnType<Viewer['startExtrudeEditSession']> {
        return this.startExtrudeEditSession(featureId);
    }

    /**
     * 执行拉伸特征预览。
     * @param input 预览输入。
     * @returns 预览结果。
     */
    public async previewExtrudeFeature(input: {
        featureId?: string | null;
        selection: BossExtrudeSessionSelection;
        definition: ExtrudeFeatureEditDefinition;
    }): ReturnType<Viewer['executeFeaturePreview']> {
        /**
         * sessionBridge 变量用于根据不同拉伸特征类型（如 “cut-extrude” 或 “boss-extrude”）
         * 动态选择对应的特征会话桥接器(BossExtrudeFeatureSessionBridge 或 CutExtrudeFeatureSessionBridge)。
         * 这个“桥”层的设计目的是隔离具体特征类型的实现细节，将特征的预览、提交等输入构建等通用交互抽象封装为统一接口，
         * 从而满足依赖倒置原则，避免高层Viewer逻辑直接依赖底层的具体特征处理实现。
         * 这种桥接模式便于后续扩展（如新增更多拉伸特征类型），只需扩充桥接器实现，无需修改调用方逻辑。
         */
        const sessionBridge = input.definition.featureKind === 'cut-extrude'
            ? this.cutExtrudeFeatureSessionBridge
            : this.bossExtrudeFeatureSessionBridge;
        const previewInput = sessionBridge.buildPreviewInput({
            featureId: input.featureId,
            selection: input.selection,
            definition: input.definition,
            scene: this.scene,
        });
        const featureId = input.featureId ?? null;
        /**
         * “指定特征的活动编辑会话”是指当前已经启动、并处于编辑状态的特征编辑会话对象（FeatureEditSession）。
         * 例如，用户正在对一个特征（如拉伸/切除等）进行属性修改、预览或提交操作时，会有一个与该特征ID对应、尚未关闭的编辑会话。
         * 如果目标特征尚未进入编辑（即未启动编辑会话），此处获取会返回 null。
         */
        const session = featureId ? this.getFeatureEditSession(featureId) : null;
  
        // 这段代码的逻辑是：如果当前存在该特征的编辑会话（即正在编辑该特征），
        // 就实时同步（更新）会话对象中的“草稿定义”为用户当前预览操作产生的新定义（previewInput.definition）。
        // 通常用于支持交互式预览和属性面板输入与草稿状态的绑定，使未提交前的所有编辑都暂存于草稿对象而不影响已保存的特征本体。
        if (session) {
            session.updateDraftDefinition(previewInput.definition);
        }
        return this.executeFeaturePreview(previewInput);
    }

    /**
     * 执行拉伸凸台预览。
     * @param input 预览输入。
     * @returns 预览结果。
     */
    public async previewBossExtrudeFeature(input: {
        featureId?: string | null;
        selection: BossExtrudeSessionSelection;
        definition: ExtrudeFeatureEditDefinition;
    }): ReturnType<Viewer['previewExtrudeFeature']> {
        return this.previewExtrudeFeature(input);
    }

    /**
     * 提交拉伸特征。
     * @param input 提交输入。
     * @returns 提交结果。
     */
    public async commitExtrudeFeature(input: {
        featureId?: string | null;
        selection: BossExtrudeSessionSelection;
        definition: ExtrudeFeatureEditDefinition;
    }): ReturnType<Viewer['commitFeature']> {
        const sessionBridge = input.definition.featureKind === 'cut-extrude'
            ? this.cutExtrudeFeatureSessionBridge
            : this.bossExtrudeFeatureSessionBridge;
        const commitInput = sessionBridge.buildCommitInput({
            featureId: input.featureId,
            selection: input.selection,
            definition: input.definition,
            scene: this.scene,
        });
        const featureId = input.featureId ?? null;
        const session = featureId ? this.getFeatureEditSession(featureId) : null;
        if (session) {
            session.updateDraftDefinition(commitInput.definition);
        }
        return this.commitFeature(commitInput);
    }

    /**
     * 提交拉伸凸台。
     * @param input 提交输入。
     * @returns 提交结果。
     */
    public async commitBossExtrudeFeature(input: {
        featureId?: string | null;
        selection: BossExtrudeSessionSelection;
        definition: ExtrudeFeatureEditDefinition;
    }): ReturnType<Viewer['commitExtrudeFeature']> {
        return this.commitExtrudeFeature(input);
    }

    /**
     * 启动旋转特征编辑会话，并恢复旋转编辑载荷。
     * @param featureId 特征 ID。
     * @returns 旋转编辑会话载荷。
     */
    public async startBossRevolveEditSession(featureId: string): Promise<BossRevolveEditSessionPayload> {
        const session = await this.startFeatureEditSession(featureId);
        return this.bossRevolveFeatureSessionBridge.buildEditSessionPayload(session);
    }

    /**
     * 启动旋转切除编辑会话，并恢复旋转编辑载荷。
     * @param featureId 特征 ID。
     * @returns 旋转编辑会话载荷。
     */
    public async startCutRevolveEditSession(featureId: string): Promise<BossRevolveEditSessionPayload> {
        const session = await this.startFeatureEditSession(featureId);
        return this.bossRevolveFeatureSessionBridge.buildEditSessionPayload(session, 'cut-revolve');
    }

    /**
     * 更新旋转凸台角度拖拽手柄。
     *
     * 设计说明：
     * 1. Viewer 持有 Three.js 场景、相机和 DOM 容器，因此由 Viewer 托管手柄生命周期；
     * 2. web-client 只传入选择草稿与编辑定义，不直接创建 Object3D 或监听 DOM；
     * 3. 当选择条件不足时统一停止手柄，避免残留交互对象。
     *
     * @param input 手柄更新输入。
     * @returns 是否成功展示手柄。
     * @throws 传入特征 ID，但对应编辑会话不存在或不是旋转凸台、旋转切除原型时抛出。
     */
    public updateBossRevolveAngleDrag(input: {
        featureId?: string | null;
        selection?: BossRevolveSessionSelection;
        definition: RevolveFeatureEditDefinition;
        onAnglesChange?: (payload: RevolveAngleDragChangePayload) => void;
    }): boolean {
        const featureId = input.featureId ?? null;
        const session = featureId ? this.getFeatureEditSession(featureId) : null;
        const prototype = session?.getPrototype();
        if (featureId && !(prototype instanceof BossRevolveFeaturePrototype) && !(prototype instanceof CutRevolveFeaturePrototype)) {
            this.bossRevolveAngleDragController.stop();
            throw new Error('当前未找到可用于旋转手柄的特征会话原型');
        }

        const target = this.bossRevolveFeatureSessionBridge.buildDragTarget({
            prototype: prototype instanceof BossRevolveFeaturePrototype || prototype instanceof CutRevolveFeaturePrototype
                ? prototype
                : undefined,
            selection: input.selection,
            definition: input.definition,
            scene: this.scene,
            resolveModelEdgeSegment: (objectId, edgeIndex) => this.getTopologyEdgeSegment(objectId, edgeIndex),
        });
        if (!target) {
            this.bossRevolveAngleDragController.updateTarget({
                context: this.buildBossRevolveAngleDragRuntimeContext(),
                target: null,
                onAnglesChange: input.onAnglesChange,
            });
            return false;
        }

        return this.bossRevolveAngleDragController.updateTarget({
            context: this.buildBossRevolveAngleDragRuntimeContext(),
            target,
            onAnglesChange: input.onAnglesChange,
        });
    }

    /**
     * 停止旋转凸台角度拖拽手柄。
     */
    public stopBossRevolveAngleDrag(): void {
        this.bossRevolveAngleDragController.stop();
    }

    /**
     * 构建旋转手柄运行时上下文。
     *
     * @returns 手柄运行时上下文。
     */
    private buildBossRevolveAngleDragRuntimeContext(): {
        scene: THREE.Scene;
        camera: THREE.Camera;
        container: HTMLElement;
        requestRender: () => void;
    } {
        return {
            scene: this.scene,
            camera: this.camera,
            container: this.container,
            requestRender: () => this.requestRender(),
        };
    }

    /**
     * 执行旋转特征预览。
     * @param input 预览输入。
     * @returns 预览结果。
     * @throws 传入特征 ID，但对应编辑会话不存在或不是旋转凸台原型时抛出。
     */
    public async previewBossRevolveFeature(input: {
        featureId?: string | null;
        selection?: BossRevolveSessionSelection;
        definition: RevolveFeatureEditDefinition;
    }): ReturnType<Viewer['executeFeaturePreview']> {
        const featureId = input.featureId ?? null;
        const session = featureId ? this.getFeatureEditSession(featureId) : null;
        const prototype = session?.getPrototype();
        if (featureId && !(prototype instanceof BossRevolveFeaturePrototype)) {
            throw new Error('当前未找到可用于旋转预览的特征会话原型');
        }
        const previewInput = this.bossRevolveFeatureSessionBridge.buildPreviewInput({
            featureId,
            prototype: prototype instanceof BossRevolveFeaturePrototype ? prototype : undefined,
            selection: input.selection,
            definition: input.definition,
            scene: this.scene,
            resolveModelEdgeSegment: (objectId, edgeIndex) => this.getTopologyEdgeSegment(objectId, edgeIndex),
        });
        session?.updateDraftDefinition(previewInput.definition);
        return this.executeFeaturePreview(previewInput);
    }

    /**
     * 提交旋转特征。
     * @param input 提交输入。
     * @returns 提交结果。
     * @throws 传入特征 ID，但对应编辑会话不存在或不是旋转凸台原型时抛出。
     */
    public async commitBossRevolveFeature(input: {
        featureId?: string | null;
        selection?: BossRevolveSessionSelection;
        definition: RevolveFeatureEditDefinition;
    }): ReturnType<Viewer['commitFeature']> {
        const featureId = input.featureId ?? null;
        const session = featureId ? this.getFeatureEditSession(featureId) : null;
        const prototype = session?.getPrototype();
        if (featureId && !(prototype instanceof BossRevolveFeaturePrototype)) {
            throw new Error('当前未找到可用于旋转提交的特征会话原型');
        }
        const commitInput = this.bossRevolveFeatureSessionBridge.buildCommitInput({
            featureId,
            prototype: prototype instanceof BossRevolveFeaturePrototype ? prototype : undefined,
            selection: input.selection,
            definition: input.definition,
            scene: this.scene,
            resolveModelEdgeSegment: (objectId, edgeIndex) => this.getTopologyEdgeSegment(objectId, edgeIndex),
        });
        session?.updateDraftDefinition(commitInput.definition);
        return this.commitFeature(commitInput);
    }

    /**
     * 执行旋转切除预览。
     * @param input 预览输入。
     * @returns 预览结果。
     * @throws 传入特征 ID，但对应编辑会话不存在或不是旋转切除原型时抛出。
     */
    public async previewCutRevolveFeature(input: {
        featureId?: string | null;
        selection?: BossRevolveSessionSelection;
        definition: RevolveFeatureEditDefinition;
    }): ReturnType<Viewer['executeFeaturePreview']> {
        const featureId = input.featureId ?? null;
        const session = featureId ? this.getFeatureEditSession(featureId) : null;
        const prototype = session?.getPrototype();
        if (featureId && !(prototype instanceof CutRevolveFeaturePrototype)) {
            throw new Error('当前未找到可用于旋转切除预览的特征会话原型');
        }
        const previewInput = this.bossRevolveFeatureSessionBridge.buildPreviewInput({
            featureId,
            prototype: prototype instanceof CutRevolveFeaturePrototype ? prototype : undefined,
            featureKind: 'cut-revolve',
            selection: input.selection,
            definition: input.definition,
            scene: this.scene,
            resolveModelEdgeSegment: (objectId, edgeIndex) => this.getTopologyEdgeSegment(objectId, edgeIndex),
        });
        session?.updateDraftDefinition(previewInput.definition);
        return this.executeFeaturePreview(previewInput);
    }

    /**
     * 提交旋转切除。
     * @param input 提交输入。
     * @returns 提交结果。
     * @throws 传入特征 ID，但对应编辑会话不存在或不是旋转切除原型时抛出。
     */
    public async commitCutRevolveFeature(input: {
        featureId?: string | null;
        selection?: BossRevolveSessionSelection;
        definition: RevolveFeatureEditDefinition;
    }): ReturnType<Viewer['commitFeature']> {
        const featureId = input.featureId ?? null;
        const session = featureId ? this.getFeatureEditSession(featureId) : null;
        const prototype = session?.getPrototype();
        if (featureId && !(prototype instanceof CutRevolveFeaturePrototype)) {
            throw new Error('当前未找到可用于旋转切除提交的特征会话原型');
        }
        const commitInput = this.bossRevolveFeatureSessionBridge.buildCommitInput({
            featureId,
            prototype: prototype instanceof CutRevolveFeaturePrototype ? prototype : undefined,
            featureKind: 'cut-revolve',
            selection: input.selection,
            definition: input.definition,
            scene: this.scene,
            resolveModelEdgeSegment: (objectId, edgeIndex) => this.getTopologyEdgeSegment(objectId, edgeIndex),
        });
        session?.updateDraftDefinition(commitInput.definition);
        return this.commitFeature(commitInput);
    }

    /**
     * 启动圆角特征编辑会话。
     *
     * @param featureId 特征 ID。
     * @returns 圆角编辑会话载荷。
     */
    public async startFilletEditSession(featureId: string): Promise<FilletEditSessionPayload> {
        const session = await this.startFeatureEditSession(featureId);
        return this.filletFeatureSessionBridge.buildEditSessionPayload(session);
    }

    /**
     * 执行圆角特征预览。
     *
     * @param input 预览输入。
     * @returns 预览结果。
     * @throws 对应编辑会话不存在或不是圆角特征原型时抛出。
     */
    public async previewFilletFeature(input: {
        featureId?: string | null;
        definition: FilletFeatureDefinition;
    }): ReturnType<Viewer['executeFeaturePreview']> {
        const featureId = input.featureId ?? null;
        const session = featureId ? this.getFeatureEditSession(featureId) : null;
        const prototype = session?.getPrototype();
        if (!(prototype instanceof FilletFeaturePrototype)) {
            throw new Error('当前未找到可用于圆角预览的特征会话原型');
        }
        const previewInput = this.filletFeatureSessionBridge.buildPreviewInput({
            featureId,
            prototype,
            definition: input.definition,
        });
        session.updateDraftDefinition(previewInput.definition);
        return this.executeFeaturePreview({
            ...previewInput,
            bindings: previewInput.bindings.map((binding) => binding.serialize()),
        });
    }

    /**
     * 提交圆角特征。
     *
     * @param input 提交输入。
     * @returns 提交结果。
     * @throws 对应编辑会话不存在或不是圆角特征原型时抛出。
     */
    public async commitFilletFeature(input: {
        featureId?: string | null;
        definition: FilletFeatureDefinition;
    }): ReturnType<Viewer['commitFeature']> {
        const featureId = input.featureId ?? null;
        const session = featureId ? this.getFeatureEditSession(featureId) : null;
        const prototype = session?.getPrototype();
        if (!(prototype instanceof FilletFeaturePrototype)) {
            throw new Error('当前未找到可用于圆角提交的特征会话原型');
        }
        const commitInput = this.filletFeatureSessionBridge.buildCommitInput({
            featureId,
            prototype,
            definition: input.definition,
        });
        session.updateDraftDefinition(commitInput.definition);
        return this.commitFeature({
            ...commitInput,
            bindings: commitInput.bindings.map((binding) => binding.serialize()),
        });
    }

    /**
     * 启动倒角特征编辑会话。
     *
     * @param featureId 特征 ID。
     * @returns 倒角编辑会话载荷。
     */
    public async startChamferEditSession(featureId: string): Promise<ChamferEditSessionPayload> {
        const session = await this.startFeatureEditSession(featureId);
        return this.chamferFeatureSessionBridge.buildEditSessionPayload(session);
    }

    /**
     * 执行倒角特征预览。
     *
     * @param input 预览输入。
     * @returns 预览结果。
     * @throws 对应编辑会话不存在或不是倒角特征原型时抛出。
     */
    public async previewChamferFeature(input: {
        featureId?: string | null;
        definition: ChamferFeatureDefinition;
    }): ReturnType<Viewer['executeFeaturePreview']> {
        const featureId = input.featureId ?? null;
        const session = featureId ? this.getFeatureEditSession(featureId) : null;
        const prototype = session?.getPrototype();
        if (!(prototype instanceof ChamferFeaturePrototype)) {
            throw new Error('当前未找到可用于倒角预览的特征会话原型');
        }
        const previewInput = this.chamferFeatureSessionBridge.buildPreviewInput({
            featureId,
            prototype,
            definition: input.definition,
        });
        session.updateDraftDefinition(previewInput.definition);
        return this.executeFeaturePreview({
            ...previewInput,
            bindings: previewInput.bindings.map((binding) => binding.serialize()),
        });
    }

    /**
     * 提交倒角特征。
     *
     * @param input 提交输入。
     * @returns 提交结果。
     * @throws 对应编辑会话不存在或不是倒角特征原型时抛出。
     */
    public async commitChamferFeature(input: {
        featureId?: string | null;
        definition: ChamferFeatureDefinition;
    }): ReturnType<Viewer['commitFeature']> {
        const featureId = input.featureId ?? null;
        const session = featureId ? this.getFeatureEditSession(featureId) : null;
        const prototype = session?.getPrototype();
        if (!(prototype instanceof ChamferFeaturePrototype)) {
            throw new Error('当前未找到可用于倒角提交的特征会话原型');
        }
        const commitInput = this.chamferFeatureSessionBridge.buildCommitInput({
            featureId,
            prototype,
            definition: input.definition,
        });
        session.updateDraftDefinition(commitInput.definition);
        return this.commitFeature({
            ...commitInput,
            bindings: commitInput.bindings.map((binding) => binding.serialize()),
        });
    }

    /**
     * 订阅指定草图的定义 dirty 事件。
     *
     * 设计说明：
     * 1. web-client 只能通过 Viewer 订阅草图定义变化，不能直接耦合 SketchGroupManager 或事件中心实现；
     * 2. 返回值统一为取消订阅函数，便于交互会话在结束时释放监听；
     * 3. 订阅键可使用场景内 runtime UUID 或跨运行时 persistent ID；
     * 4. 仅转发目标草图的 dirty 事件，避免上层收到全量草图噪声。
     *
     * @param sketchId 草图 runtime UUID 或 persistent ID。
     * @param callback 事件回调。
     * @returns 取消订阅函数。
     */
    public observeSketchDefinitionDirty(
        sketchId: string,
        callback: (event: {
            sketchId: string;
            revision: number;
            reason: string;
            sourceObjectId?: string;
        }) => void,
    ): () => void {
        const definitionChangeHub = this.sketchGroupManager?.definitionChangeHub;
        if (!definitionChangeHub) {
            return () => {};
        }

        const subscription = definitionChangeHub.observeDirty().subscribe((event) => {
            if (event.sketchId !== sketchId && event.persistentSketchId !== sketchId) {
                return;
            }
            callback({
                sketchId: event.sketchId,
                revision: event.revision,
                reason: event.reason,
                sourceObjectId: event.sourceObjectId,
            });
        });

        return () => {
            subscription.unsubscribe();
        };
    }

    /**
     * 监听草图组生命周期事件。
     *
     * 设计说明：
     * 1. 设计树等宿主状态只允许通过 `Viewer` 观察草图组集合变化；
     * 2. 这样可以把 `SketchGroupManager` 继续保留为内部实现细节；
     * 3. 上层可据此刷新设计树，而不再依赖零散按钮回调时序。
     *
     * @returns 生命周期事件流；管理器未就绪时返回空事件流。
     */
    public observeSketchGroupLifecycle(): Observable<SketchGroupLifecycleEvent> {
        return this.sketchGroupManager?.observeLifecycle() ?? EMPTY;
    }

    /**
     * 更新点捕捉设置。
     * @param settings 点捕捉设置。
     */
    public updatePointSnapSettings(settings: PointSnapSettings): void {
        //------ 点捕捉设置覆盖写入 开始------
        // 更新入口与初始化入口复用同一归一化策略，避免宿主在运行中关闭自动几何关系。
        const normalizedSettings = normalizePointSnapSettings(settings);
        // 无论 pointSnapManager 是否已经创建，都要先把最新配置回写到 Viewer.options，
        // 这样异步初始化完成后的 pointSnapManager 仍能读取到正确的项目设置。
        this.options.snapSettings = normalizedSettings;
        //------ 点捕捉设置覆盖写入 结束------
        this.pointSnapManager?.updateSettings(normalizedSettings);
    }

    /**
     * 设置快速捕捉模式。
     * @param mode 快速捕捉模式。
     */
    public setQuickSnapMode(mode: QuickSnapMode | null): void {
        this.pointSnapManager?.setQuickSnapMode(mode);
    }

    /**
     * 读取快速捕捉模式。
     * @returns 当前快速捕捉模式。
     */
    public getQuickSnapMode(): QuickSnapMode | null {
        return this.pointSnapManager?.quickSnapMode ?? null;
    }

    /**
     * 订阅快速捕捉模式变化。
     * @param callback 模式变化回调。
     * @returns 取消订阅函数。
     */
    public observeQuickSnapModeChange(callback: (mode: QuickSnapMode | null) => void): () => void {
        if (!this.pointSnapManager) {
            return () => {};
        }
        const subscription = this.pointSnapManager.quickSnapModeChange$.subscribe((mode) => {
            callback(mode);
        });
        return () => {
            subscription.unsubscribe();
        };
    }

    /**
     * 订阅实体交互事件。
     * @param eventType 交互事件类型。
     * @returns 事件流。
     */
    public observeEntityInteraction(eventType: PuppyCAD.EntityInteractionEventType): Observable<any> | null {
        return this.entitySelectionManager?.observe(eventType) ?? null;
    }

    /**
     * 创建转换实体引用 Controller 消费的当前文档宿主能力。
     *
     * 说明：
     * 1. 该方法属于 Viewer 组合根，只映射现有文档能力；
     * 2. topology owner 的场景解析交给独立 Resolver，避免 Viewer 吸收边候选构造规则；
     * 3. 返回对象不保存第二份业务状态，也不创建 Repository、Service 或 Coordinator。
     *
     * @returns 当前 Viewer 文档的转换实体引用宿主。
     */
    private createSketchEntityReferenceControllerHost(): SketchEntityReferenceControllerHost {
        const topologyResolver = new SceneTopologyOwnerRuntimeResolver({
            sceneRoot: this.scene,
            findSceneObjectById: objectId => this.findSceneObjectById(objectId),
        });

        return {
            getSketchGroup: sketchId => this.sketchGroupManager?.getGroupById(sketchId) ?? null,
            getSketchObject: (sketchId, entityId) => {
                const sketchGroup = this.sketchGroupManager?.getGroupById(sketchId) ?? null;
                if (!sketchGroup) {
                    return null;
                }
                return sketchGroup.getObjectByPersistentId(entityId);
            },
            getFeatureLine: (ownerId, edgeIndex) => (
                this.resolveSketchEntityReferenceFeatureLine(ownerId, edgeIndex)
            ),
            queryTopologyFaceBoundaries: payload => (
                this.featureExecutionRuntime.queryConvertEntitiesFaceBoundaries({
                    ...payload,
                    importedBodyBrep: this.resolveImportedBodyBrepQuerySource(payload.owner),
                })
            ),
            queryTopologyInnerLoopByEdge: payload => (
                this.featureExecutionRuntime.queryConvertEntitiesInnerLoopByEdge({
                    ...payload,
                    importedBodyBrep: this.resolveImportedBodyBrepQuerySource(payload.owner),
                })
            ),
            resolveTopologyOwnerHost: descriptor => topologyResolver.resolve(descriptor),
            resolveExactTopologyOwnerHost: descriptor => (
                topologyResolver.resolveExactMember(descriptor)
            ),
            getDefinitionChangeHub: () => this.sketchGroupManager?.definitionChangeHub ?? null,
            observeAssemblyTransformCommitted: () => (
                this.moveComponentController?.observeAssemblyTransformCommitted() ?? null
            ),
            resolveTopologyOwnersByAssemblyTransform: record => (
                this.resolveTopologyOwnerQueryInputsFromAssemblyTransformRecord(record)
            ),
            observeSketchEntitiesRemoved: () => (
                this.sketchGroupManager?.observeEntityRemoved() ?? null
            ),
            observeSourceSketchRemoved: () => this.observeSketchGroupLifecycle().pipe(
                filter(event => event.type === SketchGroupLifecycleEventType.Removed),
                map(event => ({
                    type: 'source-sketch-removed' as const,
                    sketchId: event.sketchId,
                    persistentSketchId: event.persistentSketchId,
                })),
            ),
            observeTopologyOwnerRemoved: () => this.observeTopologyOwnerRemoved(),
            reportReferenceLifecycleResult: result => (
                this.reportSketchEntityReferenceLifecycleResult(result)
            ),
            shouldSyncShadedSketchContours: () => this.shadedSketchContoursEnabled,
            solveTargetSketchParametricState: sketchId => (
                this.solveSketchParametricReferences(sketchId)
            ),
        };
    }

    /**
     * 创建当前 Viewer 文档唯一的转换实体引用 Controller。
     *
     * 重复调用保持幂等，确保一个 Repository 只被一个文档级 Controller 操作。
     */
    private initializeSketchEntityReferenceDocumentRuntime(): void {
        if (this.disposeStarted) {
            return;
        }
        if (this.sketchEntityReferenceDocumentRuntime) {
            return;
        }
        if (!this.sketchGroupManager) {
            throw new Error('草图管理器尚未就绪，不能创建转换实体引用文档运行时');
        }
        this.sketchEntityReferenceDocumentRuntime = new SketchEntityReferenceDocumentRuntime({
            createControllerHost: () => this.createSketchEntityReferenceControllerHost(),
            beginHistorySession: input => this.beginActiveSketchRuntimeHistorySession(input),
            solveActiveSketchParametricRelations: () => (
                this.sketchRelationCreationService.solveActiveSketchParametricRelations()
            ),
        });
    }

    /**
     * 按 owner 身份和边索引解析转换实体引用使用的 FeatureLine。
     *
     * @param ownerId 特征结果体或导入体 owner ID。
     * @param edgeIndex 当前 topology 边索引。
     * @returns 当前运行时边代理；未命中时返回 `null`。
     */
    private resolveSketchEntityReferenceFeatureLine(
        ownerId: string,
        edgeIndex: number,
    ): FeatureLine | null {
        const owner = this.findSceneObjectById(ownerId);
        if (owner instanceof FeatureResultMesh) {
            return owner.getFeatureLineByIndex(edgeIndex)
                ?? owner.getAllFeatureLines().find(line => line.edgeIndex === edgeIndex)
                ?? null;
        }
        return resolveImportedModelTopologyFeatureLine(this.scene, ownerId, edgeIndex);
    }

    /**
     * 记录自动生命周期中需要关注的结构化结果。
     *
     * 正常完成且没有诊断时保持静默；dangling 表现层将在后续独立阶段消费影响集合。
     *
     * @param result 自动生命周期处理结果。
     */
    private reportSketchEntityReferenceLifecycleResult(
        result: SketchEntityReferenceAutomaticLifecycleResult,
    ): void {
        this.sketchEntityReferenceDocumentRuntime?.refreshDiagnostics(
            result.affectedTargetSketchIds,
        );
    }

    /**
     * 创建一次 Convert Entities 命令独占的 Preview 会话。
     *
     * @returns Viewer 尚未完成初始化时返回 `null`，否则返回独立命令会话。
     */
    public createSketchEntityReferencePreviewSession(): SketchEntityReferencePreviewSession | null {
        this.ensureSketchEntityReferenceDocumentRuntimeInitialized();
        return this.sketchEntityReferenceDocumentRuntime?.createPreviewSession() ?? null;
    }

    /**
     * 解析一次 Convert Entities 选择候选。
     *
     * @param input 命中对象、目标草图和当前选择模式。
     * @returns Controller 未就绪时返回 null，否则返回异步候选。
     */
    public resolveSketchEntityReferenceSelectionCandidate(
        input: SketchEntityReferenceSelectionResolveInput,
    ): Promise<SketchEntityReferenceSelectionCandidate | null> {
        this.ensureSketchEntityReferenceDocumentRuntimeInitialized();
        return this.sketchEntityReferenceDocumentRuntime?.resolveSelectionCandidate(input)
            ?? Promise.resolve(null);
    }

    /**
     * 解析当前全部已选 Face 的 inner Wire 候选。
     *
     * @param input 当前目标草图和已选 Face。
     * @returns Controller 未就绪时返回空集合。
     */
    public resolveSketchEntityReferenceAllInnerLoopCandidates(
        input: SketchEntityReferenceAllInnerLoopsResolveInput,
    ): Promise<readonly SketchEntityReferenceSelectionCandidate[]> {
        this.ensureSketchEntityReferenceDocumentRuntimeInitialized();
        return this.sketchEntityReferenceDocumentRuntime?.resolveAllInnerLoopCandidates(input)
            ?? Promise.resolve([]);
    }

    /**
     * 提交一次转换实体引用创建任务。
     *
     * @param input 创建任务输入。
     * @returns Viewer 尚未完成初始化时返回 `null`，否则返回创建结果 Promise。
     */
    public commitSketchEntityReferences(
        input: CreateGeometryReferencesInput,
    ): Promise<CreateSketchEntityReferencesResult> | null {
        this.ensureSketchEntityReferenceDocumentRuntimeInitialized();
        return this.sketchEntityReferenceDocumentRuntime?.commitReferences(input) ?? null;
    }

    /**
     * 删除目标实体上的指定转换实体引用关系。
     *
     * @param input 关系删除任务输入。
     * @returns Viewer 尚未完成初始化时返回 `null`，否则返回删除结果 Promise。
     */
    public removeSketchEntityReferenceRelation(
        input: SketchEntityReferenceRemoveRelationInput,
    ): Promise<SketchEntityReferenceRemoveRelationResult> | null {
        this.ensureSketchEntityReferenceDocumentRuntimeInitialized();
        return this.sketchEntityReferenceDocumentRuntime?.removeRelation(input) ?? null;
    }

    /**
     * 同步恢复 Undo/Redo 补丁中的转换实体引用状态。
     *
     * @param input 目标记录与应用前记录。
     * @returns Controller 已就绪并完成恢复时返回 `true`。
     */
    public restoreSketchEntityReferenceFromHistorySync(
        input: SketchEntityReferenceHistoryRestoreInput,
    ): boolean {
        this.ensureSketchEntityReferenceDocumentRuntimeInitialized();
        return this.sketchEntityReferenceDocumentRuntime?.restoreFromHistorySync(input) ?? false;
    }

    /**
     * 同步删除 Undo/Redo 补丁中的转换实体引用状态。
     *
     * @param input 引用 ID 与应用前记录。
     * @returns Controller 已就绪并完成删除时返回 `true`。
     */
    public deleteSketchEntityReferenceFromHistorySync(
        input: SketchEntityReferenceHistoryDeleteInput,
    ): boolean {
        this.ensureSketchEntityReferenceDocumentRuntimeInitialized();
        return this.sketchEntityReferenceDocumentRuntime?.deleteFromHistorySync(input) ?? false;
    }

    /**
     * 回滚已经创建完成、但未能提交历史事务的转换实体引用。
     *
     * @param referenceIds 已落地的引用记录 ID。
     * @returns Controller 尚未就绪时返回 `null`，否则返回统一 Removal 结果。
     */
    public rollbackCreatedSketchEntityReferences(
        referenceIds: readonly string[],
    ): Promise<RemoveGeometryReferencesResult> | null {
        this.ensureSketchEntityReferenceDocumentRuntimeInitialized();
        return this.sketchEntityReferenceDocumentRuntime?.rollbackCreated(referenceIds) ?? null;
    }

    /**
     * 幂等激活当前文档的转换实体引用自动监听。
     *
     * @returns Controller 已就绪并完成激活时返回 `true`。
     */
    public ensureSketchEntityReferenceRuntime(): boolean {
        this.ensureSketchEntityReferenceDocumentRuntimeInitialized();
        return this.sketchEntityReferenceDocumentRuntime?.ensureRuntimeActive() ?? false;
    }

    /**
     * 冻结阶段 B 本轮需要处理的转换实体引用。
     *
     * @param targetSketchIds 当前文档中可参与时间线求值的草图持久 ID。
     * @returns 与后续异步 Worker 求值隔离的引用快照。
     */
    public captureSketchEntityReferenceTimelineSnapshot(
        targetSketchIds: readonly string[],
    ): TimelineReferenceSnapshot {
        return this.sketchEntityReferenceDocumentRuntime
            ?.captureTimelineSnapshot(targetSketchIds) ?? {items: []};
    }

    /**
     * 使用当前历史位置的轻量 topology 更新正式草图引用实体。
     *
     * checkpoint runtime 只替换 topology 来源，正式草图、Repository 与提交链仍复用
     * 当前文档唯一的转换实体引用 Controller。
     *
     * @param input 时间线 checkpoint 输入。
     * @returns 本次正式草图引用更新结果。
     */
    public rebuildSketchEntityReferenceTimelineCheckpoint(
        input: FeatureTimelineCheckpointRebuildInput,
    ): Promise<TimelineReferenceCheckpointResult> {
        if (!this.sketchEntityReferenceDocumentRuntime) {
            return Promise.reject(new Error('转换实体引用 Controller 尚未就绪'));
        }
        return this.sketchEntityReferenceDocumentRuntime.rebuildTimelineCheckpoint(input);
    }

    /**
     * 按 topology owner 重建 Convert Entities topology-edge 引用。
     *
     * @param input owner 级重建输入。
     * @returns 引用重建结果，包含保留、移除和失败的引用 ID 及诊断；无 owner 时返回空结果。
     */
    public async recomputeConvertEntitiesTopologyReferences(
        input: SketchEntityReferenceTopologyOwnerRebuildInput,
    ): Promise<RebuildSketchEntityReferencesResult> {
        if (!this.sketchEntityReferenceDocumentRuntime) {
            return {
                keptReferenceIds: [],
                removedReferenceIds: [],
                failedReferenceIds: [],
                diagnostics: [],
            };
        }
        return this.sketchEntityReferenceDocumentRuntime.rebuildTopologyOwners(input);
    }

    /**
     * 监听 topology owner 显式删除事件。
     *
     * @returns topology owner 删除事件流。
     */
    public observeTopologyOwnerRemoved(): Observable<SketchEntityReferenceTopologyOwnerRemovedEvent> {
        return merge(
            this.topologyOwnerRemovedSubject.asObservable(),
            observeImportedModelTopologyOwnerRemoved().pipe(
                filter((event) => this.isObjectMountedInMainScene(event.root)),
                map((event): SketchEntityReferenceTopologyOwnerRemovedEvent => ({
                    type: event.type,
                    ownerKind: event.ownerKind,
                    ownerId: event.ownerId,
                    bodyBindingId: event.bodyBindingId,
                })),
            ),
        );
    }

    /**
     * 判断对象是否仍挂载在当前 Viewer 主场景内。
     *
     * 设计说明：
     * 1. imported topology runtime 会同时释放场景实例与缓存模板；
     * 2. 只有仍归属于当前 Viewer.scene 的 root 才代表用户可见模型被删除；
     * 3. 过滤后可避免缓存清理误删草图转换实体引用。
     *
     * @param object 待判断的场景对象。
     * @returns 对象是否归属于当前 Viewer 主场景。
     */
    private isObjectMountedInMainScene(object: THREE.Object3D): boolean {
        let current: THREE.Object3D | null = object;
        while (current) {
            if (current === this.scene) {
                return true;
            }
            current = current.parent;
        }
        return false;
    }

    /**
     * 订阅原始鼠标事件。
     * @param eventType 原始鼠标事件类型。
     * @returns 事件流。
     */
    public observeRawPointerEvent(eventType: RawPointerEventType): Observable<any> | null {
        return this.eventManager?.observe(eventType) ?? null;
    }

    //------ 视口右键菜单控制器 开始------
    /**
     * 订阅视口右键菜单打开事件。
     *
     * @returns 事件流。
     */
    public observeContextMenuOpen(): Observable<ContextMenuOpenEventPayload> | null {
        return this.contextMenuController?.observeOpen() ?? null;
    }

    /**
     * 执行视口右键菜单动作。
     *
     * @param action 菜单动作。
     * @returns 执行是否成功。
     */
    public executeContextMenuAction(action: ContextMenuActionType): boolean {
        return this.contextMenuController?.executeAction(action) ?? false;
    }
    //------ 视口右键菜单控制器 结束------

    /**
     * 订阅直线绘制点数变化。
     * @returns 事件流。
     */
    public observeLineDrawingPointCount(): Observable<number> | null {
        return this.sketchDrawingManager?.sketchLineDrawingHandler.observeDrawingPointCount() ?? null;
    }

    /**
     * 观察点绘制参数。
     * @returns 参数流。
     */
    public observePointDrawingParameters(): Observable<unknown> | null {
        return this.sketchDrawingManager?.sketchPointDrawingHandler.observePointParameters() ?? null;
    }

    /**
     * 观察线绘制参数。
     * @returns 参数流。
     */
    public observeLineDrawingParameters(): Observable<unknown> | null {
        return this.sketchDrawingManager?.sketchLineDrawingHandler.observeLineParameters() ?? null;
    }

    /**
     * 观察圆绘制参数。
     * @returns 参数流。
     */
    public observeCircleDrawingParameters(): Observable<unknown> | null {
        return this.sketchDrawingManager?.sketchCircleDrawingHandler.observeCircleParameters() ?? null;
    }

    /**
     * 观察矩形绘制参数。
     * @returns 参数流。
     */
    public observeRectangleDrawingParameters(): Observable<unknown> | null {
        return this.sketchDrawingManager?.sketchRectangleDrawingHandler.observeRectangleParameters() ?? null;
    }

    /**
     * 观察多边形绘制参数。
     * @returns 参数流。
     */
    public observePolygonDrawingParameters(): Observable<unknown> | null {
        return this.sketchDrawingManager?.sketchPolygonDrawingHandler.observePolygonParameters() ?? null;
    }

    /**
     * 观察多边形绘制开始/结束事件。
     * @returns 事件流。
     */
    public observePolygonDrawingEvent(): Observable<unknown> | null {
        return this.sketchDrawingManager?.sketchPolygonDrawingHandler.observePolygonDrawingEvent() ?? null;
    }

    /**
     * 观察椭圆绘制参数。
     * @returns 参数流。
     */
    public observeEllipseDrawingParameters(): Observable<unknown> | null {
        return this.sketchDrawingManager?.sketchEllipseDrawingHandler.observeEllipseParameters() ?? null;
    }

    /**
     * 读取圆弧绘制选项。
     * @returns 圆弧绘制选项；绘制器未就绪时返回 null。
     */
    public getArcDrawingOptions(): ArcOptions | null {
        return this.sketchDrawingManager?.sketchArcDrawingHandler.arcOptions ?? null;
    }

    /**
     * 读取圆弧绘制参数。
     * @returns 圆弧绘制参数；绘制器未就绪时返回 null。
     */
    public getArcDrawingParameters(): ArcParameters | null {
        return this.sketchDrawingManager?.sketchArcDrawingHandler.arcParameters ?? null;
    }

    /**
     * 更新圆弧绘制选项。
     * @param options 圆弧绘制选项。
     */
    public setArcDrawingOptions(options: ArcOptions): void {
        if (this.sketchDrawingManager?.sketchArcDrawingHandler) {
            this.sketchDrawingManager.sketchArcDrawingHandler.arcOptions = options;
        }
    }

    /**
     * 更新圆弧绘制参数。
     * @param parameters 圆弧绘制参数。
     */
    public setArcDrawingParameters(parameters: ArcParameters | null): void {
        if (this.sketchDrawingManager?.sketchArcDrawingHandler) {
            this.sketchDrawingManager.sketchArcDrawingHandler.arcParameters = parameters;
        }
    }

    /**
     * 读取圆绘制选项。
     * @returns 圆绘制选项；绘制器未就绪时返回 null。
     */
    public getCircleDrawingOptions(): CircleOptions | null {
        return this.sketchDrawingManager?.sketchCircleDrawingHandler.circleOptions ?? null;
    }

    /**
     * 更新圆绘制选项。
     * @param options 圆绘制选项。
     */
    public setCircleDrawingOptions(options: CircleOptions): void {
        if (this.sketchDrawingManager?.sketchCircleDrawingHandler) {
            this.sketchDrawingManager.sketchCircleDrawingHandler.circleOptions = options;
        }
    }

    /**
     * 读取椭圆绘制选项。
     * @returns 椭圆绘制选项；绘制器未就绪时返回 null。
     */
    public getEllipseDrawingOptions(): EllipseOptions | null {
        return this.sketchDrawingManager?.sketchEllipseDrawingHandler.ellipseOptions ?? null;
    }

    //------ 椭圆绘制选项完整写回 开始------
    /**
     * 更新椭圆绘制选项。
     * @param options 椭圆绘制选项。
     */
    public setEllipseDrawingOptions(options: EllipseOptions): void {
        if (this.sketchDrawingManager?.sketchEllipseDrawingHandler) {
            this.sketchDrawingManager.sketchEllipseDrawingHandler.ellipseOptions = options;
        }
    }
    //------ 椭圆绘制选项完整写回 结束------

    /**
     * 读取椭圆绘制参数。
     * @returns 椭圆绘制参数；绘制器未就绪时返回 null。
     */
    public getEllipseDrawingParameters(): EllipseParameters | null {
        return this.sketchDrawingManager?.sketchEllipseDrawingHandler.ellipseParameters ?? null;
    }

    /**
     * 更新椭圆绘制参数。
     * @param parameters 椭圆绘制参数。
     */
    public setEllipseDrawingParameters(parameters: EllipseParameters | null): void {
        if (this.sketchDrawingManager?.sketchEllipseDrawingHandler) {
            this.sketchDrawingManager.sketchEllipseDrawingHandler.ellipseParameters = parameters;
        }
    }

    /**
     * 读取直线绘制选项。
     * @returns 直线绘制选项；绘制器未就绪时返回 null。
     */
    public getLineDrawingOptions(): LineOptions | null {
        return this.sketchDrawingManager?.sketchLineDrawingHandler.lineOptions ?? null;
    }

    /**
     * 读取直线绘制参数。
     * @returns 直线绘制参数；绘制器未就绪时返回 null。
     */
    public getLineDrawingParameters(): LineParameters | null {
        const previewLineObject = this.sketchDrawingManager?.sketchLineDrawingHandler.getPreviewLineObject() ?? null;
        if (!previewLineObject) {
            return null;
        }
        return {
            length: previewLineObject.getLength(),
            angle: previewLineObject.angleDeg,
        };
    }

    /**
     * 更新直线绘制选项。
     * @param options 直线绘制选项。
     */
    public setLineDrawingOptions(options: LineOptions): void {
        if (this.sketchDrawingManager?.sketchLineDrawingHandler) {
            this.sketchDrawingManager.sketchLineDrawingHandler.lineOptions = options;
        }
    }

    /**
     * 读取多边形绘制选项。
     * @returns 多边形绘制选项；绘制器未就绪时返回 null。
     */
    public getPolygonDrawingOptions(): PolygonOptions | null {
        return this.sketchDrawingManager?.sketchPolygonDrawingHandler.polygonOptions ?? null;
    }

    /**
     * 读取多边形绘制参数。
     * @returns 多边形绘制参数；绘制器未就绪时返回 null。
     */
    public getPolygonDrawingParameters(): PolygonParameters | null {
        return this.sketchDrawingManager?.sketchPolygonDrawingHandler.polygonParameters ?? null;
    }

    /**
     * 获取当前绘制流程最近一次生成的多边形集合对象。
     * @returns 多边形集合对象或 null。
     */
    public getCurrentPolygonDrawingCollectionObject(): SketchCollectionObject | null {
        return this.sketchDrawingManager?.sketchPolygonDrawingHandler.getCurrentPolygonCollectionObject() ?? null;
    }

    /**
     * 更新多边形绘制选项。
     * @param options 多边形绘制选项。
     */
    public setPolygonDrawingOptions(options: PolygonOptions): void {
        if (this.sketchDrawingManager?.sketchPolygonDrawingHandler) {
            this.sketchDrawingManager.sketchPolygonDrawingHandler.polygonOptions = options;
        }
    }

    /**
     * 刷新多边形绘制预览。
     */
    public refreshPolygonDrawingPreview(): void {
        this.sketchDrawingManager?.sketchPolygonDrawingHandler.refreshPreview();
    }

    /**
     * 根据参数同步当前多边形绘制预览。
     * @param parameters 多边形参数。
     * @returns 是否同步成功。
     */
    public syncPolygonDrawingPreviewByParameters(parameters: PolygonParameters): boolean {
        return this.sketchDrawingManager?.sketchPolygonDrawingHandler.syncPolygonDrawingPreviewByParameters(parameters) ?? false;
    }

    /**
     * 根据参数重建多边形集合对象。
     * @param parameters 多边形参数。
     * @param targetCollectionObject 目标集合对象。
     */
    public rebuildPolygonDrawingCollectionByParameters(
        parameters: PolygonParameters,
        targetCollectionObject?: SketchCollectionObject,
    ): void {
        const polygonDrawingHandler = this.sketchDrawingManager?.sketchPolygonDrawingHandler;
        if (!polygonDrawingHandler) {
            throw new Error('多边形绘制处理器未初始化，无法重建多边形参数。');
        }

        // 种子线、阵列实例、辅助圆和几何关系必须在同一个活动草图事务内原子替换。
        this.executeActiveSketchRuntimeHistoryTransaction({
            label: '修改多边形参数',
            commandType: 'sketch.property.polygon.parameters',
            work: () => {
                polygonDrawingHandler.rebuildPolygonCollectionByParameters(
                    parameters,
                    targetCollectionObject,
                );
            },
        });
    }

    /**
     * 读取矩形绘制选项。
     * @returns 矩形绘制选项；绘制器未就绪时返回 null。
     */
    public getRectangleDrawingOptions(): RectangleOptions | null {
        return this.sketchDrawingManager?.sketchRectangleDrawingHandler.rectangleOptions ?? null;
    }

    //------ 矩形构造线会话缓存 开始------
    /**
     * 读取当前矩形类型缓存的附加构造线来源。
     * @returns “从角点/从中点”来源；绘制器未就绪时返回 null。
     */
    public getRectangleDrawingConstructionLineSource(): RectangleConstructionLineSource | null {
        return this.sketchDrawingManager?.sketchRectangleDrawingHandler.getCurrentRectangleConstructionLineSource() ?? null;
    }
    //------ 矩形构造线会话缓存 结束------

    /**
     * 读取矩形绘制参数。
     * @returns 矩形绘制参数；绘制器未就绪时返回 null。
     */
    public getRectangleDrawingParameters(): RectangleParameters | null {
        return this.sketchDrawingManager?.sketchRectangleDrawingHandler.rectangleParameters ?? null;
    }

    /**
     * 更新矩形绘制选项。
     * @param options 矩形绘制选项。
     */
    public setRectangleDrawingOptions(options: RectangleOptions): void {
        const handler = this.sketchDrawingManager?.sketchRectangleDrawingHandler;
        if (handler) {
            //------ 中心矩形强制构造线配置 开始------
            // 由矩形绘制处理器统一处理模式缓存和中心类矩形强制构造线规则。
            handler.applyRectangleOptions(options);
            //------ 中心矩形强制构造线配置 结束------
        }
    }

    /**
     * 更新矩形绘制参数。
     * @param parameters 矩形绘制参数。
     */
    public setRectangleDrawingParameters(parameters: RectangleParameters | null): void {
        if (this.sketchDrawingManager?.sketchRectangleDrawingHandler) {
            this.sketchDrawingManager.sketchRectangleDrawingHandler.rectangleParameters = parameters;
        }
    }

    /**
     * 读取槽口绘制选项。
     * @returns 槽口绘制选项；绘制器未就绪时返回 null。
     */
    public getSlotDrawingOptions(): SlotOptions | null {
        return this.sketchDrawingManager?.sketchSlotDrawingHandler.slotOptions ?? null;
    }

    /**
     * 更新槽口绘制选项。
     * @param options 槽口绘制选项。
     */
    public setSlotDrawingOptions(options: SlotOptions): void {
        if (this.sketchDrawingManager?.sketchSlotDrawingHandler) {
            this.sketchDrawingManager.sketchSlotDrawingHandler.slotOptions = options;
        }
    }

    /**
     * 读取槽口绘制参数。
     * @returns 槽口绘制参数；绘制器未就绪时返回 null。
     */
    public getSlotDrawingParameters(): SlotParameters | null {
        return this.sketchDrawingManager?.sketchSlotDrawingHandler.slotParameters ?? null;
    }

    /**
     * 监听槽口绘制参数变化。
     * @returns 槽口绘制参数流；绘制器未就绪时返回 `null`。
     */
    public observeSlotDrawingParameters(): Observable<SlotParameters | null> | null {
        return this.sketchDrawingManager?.sketchSlotDrawingHandler.observeSlotParameters() ?? null;
    }

    /**
     * 更新槽口绘制参数。
     * @param parameters 槽口绘制参数。
     */
    public setSlotDrawingParameters(parameters: SlotParameters | null): void {
        if (this.sketchDrawingManager?.sketchSlotDrawingHandler) {
            this.sketchDrawingManager.sketchSlotDrawingHandler.slotParameters = parameters;
        }
    }

    /**
     * 读取样条绘制选项。
     * @returns 样条绘制选项；绘制器未就绪时返回 null。
     */
    public getSplineDrawingOptions(): SplineOptions | null {
        return this.sketchDrawingManager?.sketchSplineDrawingHandler.splineOptions ?? null;
    }

    /**
     * 读取样式样条绘制选项。
     * @returns 样式样条绘制选项；绘制器未就绪时返回 null。
     */
    public getStyleSplineDrawingOptions(): StyleSplineOptions | null {
        return this.sketchDrawingManager?.sketchStyleSplineDrawingHandler.styleSplineOptions ?? null;
    }

    /**
     * 读取当前草图绘制工具。
     * @returns 当前工具；未绘制时返回 null。
     */
    public getCurrentSketchDrawingTool(): string | null {
        return this.sketchDrawingManager?.getCurrentTool() ?? null;
    }

    /**
     * 停止当前草图绘制。
     */
    public stopSketchDrawing(): void {
        this.sketchDrawingManager?.stopDrawing();
    }

    /**
     * 完成当前草图绘制命令。
     * 说明：该入口保持 web-client 不感知具体绘制工具，由 cad-core 统一收口提交或退出语义。
     */
    public async finishCurrentDrawing(): Promise<void> {
        await this.sketchDrawingManager?.finishCurrentDrawing();
    }

    /**
     * 设置椭圆绘制模式。
     * @param mode 椭圆模式。
     */
    public setEllipseDrawingMode(mode: string): void {
        const handler = this.sketchDrawingManager?.sketchEllipseDrawingHandler;
        if (!handler) {
            return;
        }
        handler.ellipseOptions = {
            ...handler.ellipseOptions,
            mode: mode as never,
        };
    }

    /**
     * 设置槽口绘制模式。
     * @param mode 槽口模式。
     */
    public setSlotDrawingMode(mode: string): void {
        const handler = this.sketchDrawingManager?.sketchSlotDrawingHandler;
        if (!handler) {
            return;
        }
        handler.slotOptions = {
            ...handler.slotOptions,
            mode: mode as never,
        };
    }

    /**
     * 解析槽口长度类型。
     * @param mode 槽口模式。
     * @param lengthType 长度类型。
     * @returns 归一化后的长度类型。
     */
    public resolveSlotLengthType(mode: unknown, lengthType: unknown): unknown {
        return this.sketchDrawingManager?.sketchSlotDrawingHandler.resolveSlotLengthType(
            mode as never,
            lengthType as never,
        ) ?? lengthType;
    }

    /**
     * 设置样式样条绘制模式。
     * @param mode 样式样条模式。
     */
    public setStyleSplineDrawingMode(mode: string): void {
        const handler = this.sketchDrawingManager?.sketchStyleSplineDrawingHandler;
        if (!handler) {
            return;
        }
        handler.styleSplineOptions = {
            ...handler.styleSplineOptions,
            mode: mode as never,
        };
    }

    /**
     * 添加或替换正式结果网格。
     * @param meshData 网格数据。
     * @param context 可选的结果体原型与异步提交所有权上下文。
     * @param context.prototype 显式特征原型；未提供有效值时按 `meshData.metadata.featureId` 查询。
     * @param context.isCommitCurrent 异步准备完成后是否仍允许替换当前场景中的同 ID 结果体。
     * @returns 场景网格；管理器不可用时返回 null。
     */
    public async addResultMesh(
        meshData: MeshData,
        context?: {
            prototype?: FeaturePrototype | null;
            /** 异步准备完成后是否仍允许替换当前场景中的同 ID 结果体。 */
            isCommitCurrent?: () => boolean;
        },
    ): Promise<THREE.Mesh | null> {
        const isCommitCurrent = context?.isCommitCurrent ?? (() => true);
        if (!this.meshManager || !isCommitCurrent()) {
            return null;
        }
        const resolvedPrototype = context?.prototype
            ?? (typeof meshData.metadata?.featureId === 'string'
                ? await this.getFeaturePrototypeById(meshData.metadata.featureId)
                : null);
        if (!this.meshManager || !isCommitCurrent()) {
            return null;
        }
        syncImportedModelBodyRepresentations(this.scene, this.getModelBodySceneRegistry());
        const resultMesh = await this.meshManager.upsertResultMesh(meshData, {
            prototype: resolvedPrototype,
        });

        //------ 调试Box禁用 开始------
        // 临时禁用 DebugTool 自动创建，避免为结果体创建调试 Box/BVH Helper。
        // if (resultMesh) {
        //     this.debugTool.ensureFeatureResultMeshBVHAndHelper(resultMesh);
        // }
        //------ 调试Box禁用 结束------

        return resultMesh;
    }

    /**
     * 设置拉伸预览网格。
     * @param meshData 网格数据；传 null 表示移除预览。
     * @param context 可选的预览上下文。
     * @param context.prototype 预览特征原型；省略时向网格管理器传入 `null`。
     * @param context.draftDefinition 尚未提交的特征定义；省略时向网格管理器传入 `null`。
     * @returns 预览网格；管理器不可用时返回 null。
     */
    public async setExtrudePreviewMesh(
        meshData: MeshData | null,
        context?: {
            prototype?: FeaturePrototype | null;
            draftDefinition?: FeatureDefinition | null;
        },
    ): Promise<THREE.Mesh | null> {
        if (!this.meshManager) {
            return null;
        }
        syncImportedModelBodyRepresentations(this.scene, this.getModelBodySceneRegistry());
        return this.meshManager.setPreviewMesh('extrude-preview', meshData, {
            prototype: context?.prototype ?? null,
            draftDefinition: context?.draftDefinition ?? null,
        });
    }

    /**
     * 从正式结果体解析 topology owner 查询输入。
     *
     * 设计说明：
     * 1. FeatureResultMesh 是结果体 topology metadata 的统一封装入口；
     * 2. 回灌触发和删除事件都基于同一份 owner 身份，避免两条链路的匹配语义漂移；
     * 3. 当结果体缺少 bodyBindingId 时，回灌重建可选择 owner-wide 匹配，确保旧数据或单 body 结果体仍能被重建。
     *
     * @param mesh 结果体对象。
     * @param options 解析选项。
     * @returns topology owner 查询输入；缺少稳定 owner 时返回 null。
     */
    private resolveTopologyOwnerQueryInputFromResultMesh(
        mesh: THREE.Mesh | null,
        options: {
            /** 缺少 bodyBindingId 时是否按 ownerId 全量匹配。 */
            matchAnyBodyBindingWhenMissing?: boolean;
        } = {},
    ): SketchEntityReferenceTopologyOwnerQueryInput | null {
        return this.featureTimelineRebuildRuntime
            ?.resolveTopologyOwnerFromResultMesh(mesh, options) ?? null;
    }

    /**
     * 按装配体 transform 历史记录解析受影响的 topology owner。
     *
     * @param record 装配体 transform 历史记录。
     * @returns 需要重建的 topology owner 查询输入集合。
     */
    public resolveTopologyOwnerQueryInputsFromAssemblyTransformRecord(
        record: AssemblyTransformHistoryRecord,
    ): SketchEntityReferenceTopologyOwnerQueryInput[] {
        return this.featureTimelineRebuildRuntime
            ?.resolveTopologyOwnersFromAssemblyTransform(record) ?? [];
    }

    /**
     * 发布特征删除导致的 topology owner 全量删除事件。
     *
     * 设计说明：
     * 1. 特征树删除入口只稳定知道 featureId，不一定能枚举删除前每个结果体的 bodyBindingId；
     * 2. 因此这里使用 owner-wide 语义，让引用仓储按 feature-result ownerId 清理所有相关 body 的转换实体；
     * 3. 该方法只在删除计划成功 replay 之后调用，普通 mesh 同步/重建刷新不会触发。
     *
     * @param featureIds 已被删除的特征 ID 列表。
     */
    private emitTopologyOwnerRemovedEventsForDeletedFeatures(
        featureIds: readonly string[],
    ): void {
        const emittedFeatureIds = new Set<string>();
        featureIds.forEach((featureId) => {
            if (!featureId || emittedFeatureIds.has(featureId)) {
                return;
            }
            emittedFeatureIds.add(featureId);
            this.topologyOwnerRemovedSubject.next({
                type: 'topology-owner-removed',
                ownerKind: 'feature-result',
                ownerId: featureId,
                matchAnyBodyBinding: true,
            });
        });
    }

    /**
     * 移除正式结果网格。
     *
     * 设计说明：
     * 1. 结果网格是可由 Worker 权威状态重新生成的派生显示资源；
     * 2. 网格卸载或替换不代表 topology owner 被永久删除，不能在此发布领域删除事件；
     * 3. 特征永久删除必须在删除计划成功提交后，通过显式 owner 删除入口发布事件。
     *
     * @param meshId 网格 ID。
     */
    public removeResultMesh(meshId: string): void {
        //------ 调试Box禁用 开始------
        // 调试 Box 已禁用，这里不再执行 Helper 清理链路。
        // const existingMesh = this.meshManager?.getResultMesh(meshId) ?? null;
        // if (existingMesh) {
        //     this.debugTool.removeFeatureResultMeshBVHHelper(existingMesh.uuid);
        // }
        //------ 调试Box禁用 结束------
        this.meshManager?.removeResultMesh(meshId);
    }

    /**
     * 设置指定特征结果体的可见性。
     *
     * @param featureId 特征 ID。
     * @param visible 是否可见。
     */
    public setFeatureResultVisibility(featureId: string, visible: boolean): void {
        this.meshManager?.setResultMeshVisibilityByFeatureId(featureId, visible);
    }

    /**
     * 创建当前文档唯一的严格 Feature 前缀状态机。
     *
     * Viewer 只提供既有底层能力，前缀 single-flight、状态和失败恢复均由 Runtime 持有。
     */
    private initializeSketchFeaturePrefixEditRuntime(): void {
        if (this.sketchFeaturePrefixEditRuntime) {
            return;
        }
        this.sketchFeaturePrefixEditRuntime = new SketchFeaturePrefixEditRuntime({
            isFeatureEditBusy: () => Boolean(
                this.editSketchFeaturePreviewState
                || this.isEditSketchFeaturePreviewStarting
                || this.featureEditOrchestrator?.hasActiveFeatureEditSession()
            ),
            resolveSketchIdentity: sketchGroupId => this.resolveSketchIdentity(sketchGroupId),
            getGeometryStateSummary: () => this.getGeometryStateSummary({
                syncHistoryProjection: false,
            }),
            syncRollbackHistory: summary => (
                this.syncFeatureRollbackHistoryFromWorkerSummaryIfSafe(summary)
            ),
            syncRollbackTimeline: () => (
                this.syncFeatureRollbackManagerTimelineAndDisplayStates()
            ),
            resolvePrefix: input => this.featureRollbackController.resolveSketchFeaturePrefix(input),
            replayPrefix: (commands, beforeSummary) => this.replayFeatureHistoryCommands(commands, {
                rollbackCommands: beforeSummary.history,
                beforeSummary,
                syncHistoryProjection: false,
            }).then(() => undefined),
            restoreWorker: summary => this.restoreWorkerFeatureReplayState(summary),
            applyDisplayProjection: () => this.applyFeatureRollbackNonFeatureDisplayStates(),
            requestRender: () => this.requestRender(),
            rebuildAfterPrefixEdit: (input) => {
                if (!this.featureRebuildOrchestrator) {
                    return Promise.reject(new Error(
                        '无法恢复严格 Feature 前缀：FeatureRebuildOrchestrator 尚未初始化',
                    ));
                }
                return this.featureRebuildOrchestrator.rebuildSketchAfterPrefixEdit(input);
            },
            resumeSketch: sketchId => Boolean(this.sketchGroupManager?.resumeGroup(sketchId)),
        });
    }

    /** 在公共任务入口按需建立文档运行时；初始化条件不足时保持稳定空结果。 */
    private ensureSketchEntityReferenceDocumentRuntimeInitialized(): void {
        if (
            !this.sketchEntityReferenceDocumentRuntime
            && this.sketchGroupManager
            && !this.disposeStarted
        ) {
            this.initializeSketchEntityReferenceDocumentRuntime();
        }
    }

    /**
     * 创建 Feature Timeline 在 Viewer 侧的 topology 与结果应用运行时。
     */
    private initializeFeatureTimelineRebuildRuntime(): void {
        if (this.featureTimelineRebuildRuntime) {
            return;
        }
        this.featureTimelineRebuildRuntime = new FeatureTimelineRebuildRuntime({
            sceneRoot: this.scene,
            getSceneObjectById: objectId => this.getSceneObjectById(objectId),
            replaceRollbackTimeline: input => this.featureRollbackController.replaceTimeline(
                input.result.fullCommands,
                input.rollbackState.cursorIndex,
                input.rollbackState.previousCursorIndex,
                input.rollbackState.timelineItems,
            ),
            syncRollbackTimeline: () => this.syncFeatureRollbackManagerTimeline(),
            syncAffectedMeshes: input => this.syncAffectedFeatureMeshesFromState({
                beforeSummary: input.beforeSummary,
                afterSummary: input.result.summary!,
                affectedCommandIds: input.affectedFeatureIds,
                skipConvertEntitiesTopologyFeedback: true,
            }),
            applyNonFeatureSideEffects: () => this.applyFeatureRollbackNonFeatureSideEffects(),
            setTimelineDegraded: degraded => {
                this.isPuppyFeatureTimelineDegraded = degraded;
            },
            refreshReferenceDiagnostics: sketchIds => (
                this.sketchEntityReferenceDocumentRuntime?.refreshDiagnostics(sketchIds)
            ),
            notifyReferenceDiagnosticsChanged: sketchIds => (
                this.sketchGroupManager?.notifyReferenceDiagnosticChanged(sketchIds)
            ),
        });
    }

    /**
     * 为已有草图编辑建立严格 Feature 前缀运行态。
     *
     * Viewer 只保留稳定任务入口；身份解析、前缀计划、single-flight、状态切换与
     * 失败恢复统一由 SketchFeaturePrefixEditRuntime 管理。
     *
     * @param input 设计树已经解析出的目标草图组。
     * @returns 是否成功建立严格前缀编辑状态。
     */
    public async startSketchFeaturePrefixEdit(
        input: SketchFeaturePrefixEditInput,
    ): Promise<boolean> {
        this.initializeSketchFeaturePrefixEditRuntime();
        return this.sketchFeaturePrefixEditRuntime?.start(input) ?? false;
    }

    /**
     * 显式启动草图驱动的真实特征编辑预览。
     *
     * 设计说明：
     * 1. 本入口只服务显式特征预览，不属于普通“编辑草图”的严格前缀链路；
     * 2. 显式预览复用 FeatureEditSession + FeaturePreviewMesh，不能在正式结果体上伪造材质；
     * 3. 调用方只传入草图和特征语义，preview mesh 与最终提交都由 cad-core 收口。
     *
     * @param input 编辑草图预览上下文。
     * @returns 是否成功进入真实特征预览态。
     */
    public async startEditSketchFeaturePreview(input: {
        /** 正在编辑的草图 ID。 */
        sketchId: string;
        /** 来源特征 ID。 */
        featureId?: string | null;
    }): Promise<boolean> {
        /**
         * 普通草图严格前缀已经独占 Worker 求值环境；显式 Preview 必须等待其结束，
         * 不能创建会在关闭时恢复另一份 history 的 FeatureEditSession。
         */
        if (
            this.sketchFeaturePrefixEditRuntime?.state
            || this.sketchFeaturePrefixEditRuntime?.isStarting
            || this.isEditSketchFeaturePreviewStarting
        ) {
            return false;
        }

        this.isEditSketchFeaturePreviewStarting = true;
        try {
            const featureId = input.featureId
                ?? this.featureRebuildOrchestrator?.getDependentFeatureIds(input.sketchId)[0]
                ?? null;
            if (!featureId) {
                return false;
            }

            const activeState = this.editSketchFeaturePreviewState;
            if (activeState?.featureId === featureId && activeState.sketchId === input.sketchId) {
                await this.refreshEditSketchFeaturePreviewForSketch(input.sketchId);
                return true;
            }

            if (activeState) {
                this.clearEditSketchFeaturePreview();
            }

            const prototype = await this.getFeaturePrototypeById(featureId);
            if (
                prototype instanceof BossExtrudeFeaturePrototype
                || prototype instanceof CutExtrudeFeaturePrototype
                || prototype?.getFeatureKind() === 'boss-extrude'
                || prototype?.getFeatureKind() === 'cut-extrude'
            ) {
                /** 必须等待内部 Session 与 Preview Mesh 完成，互斥标记才能在 finally 中释放。 */
                const started = await this.startExtrudeSketchFeaturePreview({
                    sketchId: input.sketchId,
                    featureId,
                });
                return started;
            }

            return false;
        } finally {
            this.isEditSketchFeaturePreviewStarting = false;
        }
    }

    /**
     * 在草图提交边界刷新当前编辑特征预览。
     *
     * @param sketchId 草图 ID。
     * @returns 是否由编辑草图特征预览会话处理。
     */
    public async refreshEditSketchFeaturePreviewForSketch(sketchId: string): Promise<boolean> {
        const prefixState = this.sketchFeaturePrefixEditRuntime?.state;
        if (prefixState?.runtimeSketchId === sketchId) {
            /**
             * 严格前缀中的草图 committed 只更新草图运行时定义。
             * 普通草图编辑不刷新消费 Feature，也不能落回完整重建，
             * 否则正式后缀会在退出草图之前被提前恢复。
             */
            return true;
        }

        const state = this.editSketchFeaturePreviewState;
        if (!state || state.sketchId !== sketchId) {
            return false;
        }
        if (state.featureKind === 'boss-extrude' || state.featureKind === 'cut-extrude') {
            await this.refreshExtrudeSketchFeaturePreview(state);
            return true;
        }
        return false;
    }

    /**
     * 清理编辑草图期间的特征预览显示态，并按取消语义恢复原特征。
     */
    public clearEditSketchFeaturePreview(): void {
        const state = this.editSketchFeaturePreviewState;
        this.editSketchFeaturePreviewState = null;

        void this.setExtrudePreviewMesh(null);
        if (!state) {
            return;
        }

        this.closeFeatureEditSession(state.featureId);
    }

    /**
     * 启动拉伸族特征的编辑草图预览态。
     *
     * @param input 启动输入。
     * @returns 是否成功启动。
     */
    private async startExtrudeSketchFeaturePreview(input: {
        /** 正在编辑的草图 ID。 */
        sketchId: string;
        /** 来源拉伸特征 ID。 */
        featureId: string;
    }): Promise<boolean> {
        const payload = await this.startExtrudeEditSession(input.featureId);
        if (!payload.session || !payload.definition) {
            if (payload.session) {
                this.closeFeatureEditSession(input.featureId);
            }
            return false;
        }

        const prototype = payload.session.getPrototype();
        const featureKind = prototype.getFeatureKind();
        if (featureKind !== 'boss-extrude' && featureKind !== 'cut-extrude') {
            this.closeFeatureEditSession(input.featureId);
            return false;
        }

        this.editSketchFeaturePreviewState = {
            sketchId: input.sketchId,
            featureId: input.featureId,
            featureKind,
            extrudeDefinition: payload.definition,
        };

        try {
            await this.refreshExtrudeSketchFeaturePreview(this.editSketchFeaturePreviewState);
            return true;
        } catch (error) {
            this.clearEditSketchFeaturePreview();
            throw error;
        }
    }

    /**
     * 根据当前草图轮廓刷新拉伸族特征预览体。
     *
     * @param state 编辑草图特征预览状态。
     */
    private async refreshExtrudeSketchFeaturePreview(state: EditSketchFeaturePreviewState): Promise<void> {
        if (!state.extrudeDefinition) {
            return;
        }

        const session = this.getFeatureEditSession(state.featureId);
        const prototype = session?.getPrototype();
        if (
            !(prototype instanceof BossExtrudeFeaturePrototype)
            && !(prototype instanceof CutExtrudeFeaturePrototype)
        ) {
            await this.setExtrudePreviewMesh(null);
            return;
        }

        const contours = this.bossExtrudeSceneResolver.resolveContoursFromPrototype(this.scene, prototype);
        if (contours.length <= 0) {
            await this.setExtrudePreviewMesh(null);
            return;
        }

        const previewResult = await this.previewExtrudeFeature({
            featureId: state.featureId,
            selection: this.buildExtrudeSelectionFromContours(contours),
            definition: state.extrudeDefinition,
        });

        await this.setExtrudePreviewMesh(previewResult.meshData, {
            prototype: previewResult.prototype,
            draftDefinition: previewResult.draftDefinition,
        });
    }

    /**
     * 将当前草图轮廓提交回正在编辑的拉伸族特征。
     *
     * @param sketchId 草图 ID。
     * @returns 是否完成提交。
     */
    private async commitEditSketchFeaturePreviewForSketch(sketchId: string): Promise<boolean> {
        /**
         * 普通草图严格前缀不拥有消费 Feature Preview。
         * 若外部误把两条链路同时激活，仍禁止在草图退出时提交或改写消费者命令；
         * 正式后缀统一由完整命令重建恢复。
         */
        if (this.sketchFeaturePrefixEditRuntime?.state?.runtimeSketchId === sketchId) {
            return false;
        }

        const state = this.editSketchFeaturePreviewState;
        if (!state || state.sketchId !== sketchId) {
            return false;
        }
        if (
            (
                state.featureKind !== 'boss-extrude'
                && state.featureKind !== 'cut-extrude'
            )
            || !state.extrudeDefinition
        ) {
            this.clearEditSketchFeaturePreview();
            return false;
        }

        const session = this.getFeatureEditSession(state.featureId);
        const prototype = session?.getPrototype();
        if (
            !(prototype instanceof BossExtrudeFeaturePrototype)
            && !(prototype instanceof CutExtrudeFeaturePrototype)
        ) {
            this.clearEditSketchFeaturePreview();
            return false;
        }

        const contours = this.bossExtrudeSceneResolver.resolveContoursFromPrototype(this.scene, prototype);
        if (contours.length <= 0) {
            this.clearEditSketchFeaturePreview();
            return false;
        }

        await this.commitExtrudeFeature({
            featureId: state.featureId,
            selection: this.buildExtrudeSelectionFromContours(contours),
            definition: state.extrudeDefinition,
        });
        this.editSketchFeaturePreviewState = null;
        await this.setExtrudePreviewMesh(null);
        return true;
    }

    /**
     * 将场景轮廓转换为拉伸会话选择快照。
     *
     * @param contours 当前草图轮廓。
     * @returns 拉伸会话选择。
     */
    private buildExtrudeSelectionFromContours(contours: BossExtrudeSceneContour[]): BossExtrudeSessionSelection {
        return {
            kind: 'contours',
            contourIds: contours.map((contour) => contour.id),
            contours: contours.map((contour) => ({
                regionId: contour.id,
                geometry: structuredClone(contour.geometry),
                sketchId: contour.sketchId,
                contourSourceObjectId: contour.sourceObjectId,
                contourKey: contour.contourKey,
                regionIdentity: contour.regionIdentity
                    ? structuredClone(contour.regionIdentity)
                    : undefined,
            })),
        };
    }

    /**
     * 判断指定特征结果体是否隐藏。
     *
     * @param featureId 特征 ID。
     * @returns 是否隐藏。
     */
    public isFeatureResultHidden(featureId: string): boolean {
        return this.meshManager?.isFeatureResultHidden(featureId) ?? false;
    }

    /**
     * 读取当前隐藏的特征 ID。
     *
     * @returns 隐藏特征 ID 列表。
     */
    public getHiddenFeatureIds(): string[] {
        return this.meshManager?.getHiddenFeatureIds() ?? [];
    }

    /**
     * 判断 Worker 摘要对象是否需要在图形区显示为正式结果体。
     *
     * @param object Worker 摘要对象。
     * @returns 是否为可渲染结果体。
     */
    private isRenderableFeatureSummaryObject(
        object: WorkerGeometryStateSummary['objects'][number],
    ): boolean {
        return object.type === GeometryType.SOLID || object.type === GeometryType.SURFACE;
    }

    /**
     * 读取当前场景的统一实体生命周期注册表。
     *
     * 部分单元测试通过 `Viewer.prototype` 创建最小替身，因此这里按场景惰性解析，避免依赖构造器字段。
     *
     * @returns 当前 scene 唯一的实体注册表。
     */
    private getModelBodySceneRegistry(): ModelBodySceneRegistry {
        return resolveModelBodySceneRegistry(this.scene);
    }

    /**
     * 按统一修订 ID 或导入边界精确别名解析场景模型体身份。
     *
     * UI 与特征树只消费该统一身份，不允许继续根据 bodyBindingId 规则推断 Worker 对象 ID。
     *
     * @param referenceId 当前场景表示的修订 ID 或精确别名。
     * @returns 唯一模型体身份；未登记时返回 null。
     */
    public resolveModelBodySceneIdentity(referenceId: string): ModelBodySceneIdentity | null {
        return this.getModelBodySceneRegistry().resolveSceneIdentity(referenceId);
    }

    /**
     * 从网格回灌结果中收集转换实体引用重建 owner。
     *
     * @param resultMesh 新增或替换后的结果体。
     * @param ownerMap 当前回灌批次的 owner 去重表。
     */
    private collectFeatureResultFeedbackOwner(
        resultMesh: THREE.Mesh | null,
        ownerMap: Map<string, SketchEntityReferenceTopologyOwnerQueryInput>,
    ): void {
        const topologyOwnerQueryInput = this.resolveTopologyOwnerQueryInputFromResultMesh(resultMesh, {
            matchAnyBodyBindingWhenMissing: true,
        });
        if (!topologyOwnerQueryInput) {
            return;
        }
        const normalizedTopologyOwnerQueryInput =
            SketchEntityReferenceTopologyRebuildOwnerPolicy.normalizeFeatureResultFeedbackOwner(
                topologyOwnerQueryInput,
            );
        this.initializeFeatureTimelineRebuildRuntime();
        ownerMap.set(
            this.featureTimelineRebuildRuntime!.buildTopologyOwnerKey(
                normalizedTopologyOwnerQueryInput,
            ),
            normalizedTopologyOwnerQueryInput,
        );
    }

    /**
     * 重算正式结果体回灌后依赖的派生引用。
     *
     * @param owners 本次回灌涉及的 topology owner。
     * @param recomputeConvertEntitiesTopology 是否同步重算转换实体引用。
     * @param isStateCurrent 当前异步任务是否仍拥有场景提交权。
     */
    private async recomputeFeatureResultFeedbackReferences(
        owners: SketchEntityReferenceTopologyOwnerQueryInput[],
        recomputeConvertEntitiesTopology = true,
        isStateCurrent: () => boolean = () => true,
    ): Promise<void> {
        if (!isStateCurrent()) {
            return;
        }
        //------ 特征模式基准面重建后重算 开始------
        // 特征结果体回灌完成后，再刷新依赖特征面、边、点的基准面，确保求解器解析到最新场景对象。
        this.referencePlaneController.recomputePartFeatureDatumPlanes();
        //------ 特征模式基准面重建后重算 结束------

        //------ 特征模式基准轴重建后重算 开始------
        // 特征结果体回灌完成后，再刷新依赖特征面、边、点的基准轴，确保求解器解析到最新场景对象。
        this.referenceAxisController.recomputePartFeatureDatumAxes();
        //------ 特征模式基准轴重建后重算 结束------

        //------ 特征模式基准点重建后重算 开始------
        // 特征结果体回灌完成后，再刷新依赖特征面、边、点的基准点，确保求解器解析到最新场景对象。
        this.referencePointController.recomputePartFeatureDatumPoints();
        if (recomputeConvertEntitiesTopology && isStateCurrent()) {
            await this.recomputeConvertEntitiesTopologyReferences({
                owners,
                trigger: 'feature-result-feedback',
                isInputCurrent: isStateCurrent,
            });
        }
        if (isStateCurrent()) {
            // 镜向关系只能消费本轮确认仍有效的 topology/datum 结果，过期任务不得回写草图。
            this.reconcileSketchMirrorAxisReferences();
        }
        //------ 特征模式基准点重建后重算 结束------
    }

    /**
     * 根据 Worker 权威状态只同步受影响特征的正式结果网格。
     *
     * 设计说明：
     * 1. 编辑替换和 rollback sandbox 只会影响目标特征及其下游，不能清空整场景后全量回灌；
     * 2. 这里以命令 ID 作为稳定边界，先移除旧摘要中这些命令生成的结果体，再按新摘要添加对应结果体；
     * 3. 派生基准面、基准轴、基准点与转换实体引用仍走统一回灌后刷新链，避免增量路径和全量路径语义分叉。
     *
     * @param input 增量同步输入。
     */
    public async syncAffectedFeatureMeshesFromState(input: {
        /** 同步前 Worker 摘要。 */
        beforeSummary: WorkerGeometryStateSummary;
        /** 同步后 Worker 摘要。 */
        afterSummary: WorkerGeometryStateSummary;
        /** 需要替换的命令 ID 集合。 */
        affectedCommandIds: readonly string[];
        /**
         * 是否跳过 Convert Entities 的结果体 topology 回写。
         *
         * 阶段 B 已在历史 checkpoint 更新过引用，只跳过该派生回写；
         * 基准面、基准轴和基准点仍必须按新 Mesh 刷新。
         */
        skipConvertEntitiesTopologyFeedback?: boolean;
        /** 文档恢复任务是否仍拥有当前 Viewer；过期后禁止继续提交异步结果。 */
        isStateCurrent?: () => boolean;
    }): Promise<void> {
        const isStateCurrent = input.isStateCurrent ?? (() => true);
        if (!isStateCurrent()) {
            return;
        }
        const modelBodySceneRegistry = this.getModelBodySceneRegistry();
        syncImportedModelBodyRepresentations(this.scene, modelBodySceneRegistry);
        /**
         * 零件项目打开时冻结的纯导入 baseline 不属于 FeatureManager 命令时间线。
         * 严格前缀 replay 必须把摘要解释为“导入基线 + 活动 Feature 前缀”；
         * 集合为空时自然退化为 Worker 完整集合语义，因此装配项目不会启用该适配。
         */
        modelBodySceneRegistry.applyFeatureReplayState(
            input.afterSummary.objects,
            this.importedModelFeatureReplayBaselineRevisionIds,
        );
        const renderableBeforeObjects = input.beforeSummary.objects
            .filter((object) => this.isRenderableFeatureSummaryObject(object));
        const renderableAfterObjects = input.afterSummary.objects
            .filter((object) => this.isRenderableFeatureSummaryObject(object));
        const affectedCommandIdSet = new Set(resolveFeatureReplayAffectedCommandIdsFromSummaries({
            beforeCommands: input.beforeSummary.history,
            afterCommands: input.afterSummary.history,
            beforeObjects: renderableBeforeObjects,
            afterObjects: renderableAfterObjects,
            seedCommandIds: input.affectedCommandIds,
        }).filter((commandId) => commandId.length > 0));
        const afterObjectIds = new Set(renderableAfterObjects.map((object) => object.id));
        const beforeObjectsById = new Map(renderableBeforeObjects.map((object) => [object.id, object]));
        const afterObjectSyncMap = new Map<string, WorkerGeometryStateSummary['objects'][number]>();
        const beforeObjectSyncMap = new Map<string, WorkerGeometryStateSummary['objects'][number]>();
        const hasSceneResultMesh = (objectId: string): boolean => {
            const meshManager = this.meshManager;
            if (!meshManager) {
                return false;
            }
            return typeof meshManager.hasResultMeshInScene === 'function'
                ? meshManager.hasResultMeshInScene(objectId)
                : Boolean(meshManager.getResultMesh(objectId));
        };
        const referencesAffectedFeature = (object: WorkerGeometryStateSummary['objects'][number]): boolean => (
            affectedCommandIdSet.size > 0
            && doesGeometryObjectReferenceAnyFeature(object, affectedCommandIdSet)
        );

        renderableBeforeObjects.forEach((object) => {
            const isObjectRemovedByWorker = !afterObjectIds.has(object.id) && hasSceneResultMesh(object.id);
            if (referencesAffectedFeature(object) || isObjectRemovedByWorker) {
                beforeObjectSyncMap.set(object.id, object);
            }
        });

        renderableAfterObjects.forEach((object) => {
            const beforeObject = beforeObjectsById.get(object.id);
            const hasGeneratedResultMesh = hasSceneResultMesh(object.id);
            const hasActiveRepresentation = modelBodySceneRegistry.hasActiveRepresentation(object.id);
            const isNewWorkerObject = !beforeObject;
            const isMissingFromScene = !hasGeneratedResultMesh
                && !hasActiveRepresentation;
            const isCurrentObjectAffected = referencesAffectedFeature(object);
            const isPreviousObjectAffected = beforeObject ? referencesAffectedFeature(beforeObject) : false;
            const shouldMaterializeNewWorkerObject = isNewWorkerObject && !hasActiveRepresentation;
            const shouldRefreshGeneratedResult = hasGeneratedResultMesh
                && (isCurrentObjectAffected || isPreviousObjectAffected);
            if (isMissingFromScene || shouldMaterializeNewWorkerObject || shouldRefreshGeneratedResult) {
                afterObjectSyncMap.set(object.id, object);
            }
        });

        if (beforeObjectSyncMap.size === 0 && afterObjectSyncMap.size === 0) {
            return;
        }

        const generatedMeshEntries: MeshData[] = [];

        for (const object of afterObjectSyncMap.values()) {
            // 先从 Worker 当前权威状态生成新结果，再替换场景旧结果；避免回退相交合并体时失败后清空场景。
            const meshData = await this.featureExecutionRuntime.generateMeshData(object.id);
            if (!isStateCurrent()) {
                return;
            }
            generatedMeshEntries.push(meshData);
        }

        if (!isStateCurrent()) {
            return;
        }
        beforeObjectSyncMap.forEach((object) => {
            if (!afterObjectIds.has(object.id) || afterObjectSyncMap.has(object.id)) {
                this.removeResultMesh(object.id);
            }
        });

        const topologyOwnerQueryInputMap = new Map<string, SketchEntityReferenceTopologyOwnerQueryInput>();
        for (const meshData of generatedMeshEntries) {
            if (!isStateCurrent()) {
                return;
            }
            const resultMesh = await this.addResultMesh(meshData, {
                isCommitCurrent: isStateCurrent,
            });
            if (!isStateCurrent()) {
                if (this.meshManager?.getResultMesh(meshData.id) === resultMesh) {
                    this.removeResultMesh(meshData.id);
                }
                return;
            }
            this.collectFeatureResultFeedbackOwner(resultMesh, topologyOwnerQueryInputMap);
        }

        if (!isStateCurrent()) {
            return;
        }
        await this.recomputeFeatureResultFeedbackReferences(
            [...topologyOwnerQueryInputMap.values()],
            input.skipConvertEntitiesTopologyFeedback !== true,
            isStateCurrent,
        );
    }

    /**
     * 根据 Worker 权威状态重建正式结果网格。
     *
     * 设计说明：
     * 1. 特征编辑、依赖重建等命令历史回放场景，都必须统一通过该入口同步 Viewer；
     * 2. 当前先采用“清空后全量回灌”的稳定策略，保证 Viewer 与 Worker 状态一致；
     * 3. 若后续要做增量 mesh 同步，只需要在这里替换策略，不影响上层编辑链。
     *
     * @param summary Worker 几何状态摘要。
     * @param options 同步选项。
     * @param options.preservePreviewMeshes 为 `true` 时，清理正式结果体期间保留当前预览网格。
     */
    public async syncFeatureMeshesFromState(
        summary: WorkerGeometryStateSummary,
        options: {
            /** 是否在重建正式结果体时临时保留当前预览体。 */
            preservePreviewMeshes?: boolean;
            /** 文档恢复任务是否仍拥有当前 Viewer；过期后禁止继续提交异步结果。 */
            isStateCurrent?: () => boolean;
        } = {},
    ): Promise<void> {
        const isStateCurrent = options.isStateCurrent ?? (() => true);
        if (!isStateCurrent()) {
            return;
        }
        this.entitySelectionManager?.clearSelection();
        this.meshManager?.clearFeatureMeshes({
            preservePreviewMeshes: options.preservePreviewMeshes === true,
        });

        const modelBodySceneRegistry = this.getModelBodySceneRegistry();
        syncImportedModelBodyRepresentations(this.scene, modelBodySceneRegistry);
        modelBodySceneRegistry.applyFeatureReplayState(
            summary.objects,
            this.importedModelFeatureReplayBaselineRevisionIds,
        );

        // 所有来源的实体都先查询统一注册表；已有活动显示表示时无需再次网格化。
        const renderableObjects = summary.objects
            .filter((object) => this.isRenderableFeatureSummaryObject(object))
            .filter((object) => !modelBodySceneRegistry.hasActiveRepresentation(object.id));
        const topologyOwnerQueryInputMap = new Map<string, SketchEntityReferenceTopologyOwnerQueryInput>();

        for (const object of renderableObjects) {
            const meshData = await this.featureExecutionRuntime.generateMeshData(object.id);
            if (!isStateCurrent()) {
                return;
            }
            const resultMesh = await this.addResultMesh(meshData, {
                isCommitCurrent: isStateCurrent,
            });
            if (!isStateCurrent()) {
                if (this.meshManager?.getResultMesh(meshData.id) === resultMesh) {
                    this.removeResultMesh(meshData.id);
                }
                return;
            }
            this.collectFeatureResultFeedbackOwner(resultMesh, topologyOwnerQueryInputMap);
        }

        if (!isStateCurrent()) {
            return;
        }
        await this.recomputeFeatureResultFeedbackReferences(
            [...topologyOwnerQueryInputMap.values()],
            true,
            isStateCurrent,
        );

        if (isStateCurrent() && this.sketchGroupManager?.activeSketchGroup) {
            // 正式结果体已经替换完成；活动特征面草图必须从新拓扑重新解析边界投影点。
            this.sketchSessionReferencePointManager?.refresh();
        }

    }

    /**
     * 获取当前几何状态摘要。
     *
     * 设计说明：
     * 该方法只读取 Worker 状态，不会同步 FeatureManager / rollback controller。
     * 需要把 Worker history 作为新权威写回时，应调用显式同步入口，避免普通 UI 刷新污染退回控制棒完整时间线。
     *
     * @param options 读取选项。
     * @returns 几何状态摘要。
     */
    public async getGeometryStateSummary(
        options: { syncHistoryProjection?: boolean } = {},
    ): Promise<WorkerGeometryStateSummary> {
        return this.featureExecutionRuntime.getGeometryStateSummary(options);
    }

    /**
     * 显式从 Worker 同步特征历史投影。
     *
     * @returns 同步使用的 Worker 几何状态摘要。
     */
    public async syncFeatureHistoryProjectionFromWorker(): Promise<WorkerGeometryStateSummary> {
        return this.featureExecutionRuntime.syncFeatureHistoryProjectionFromWorker();
    }

    /**
     * 读取设计树专用几何状态摘要。
     *
     * 设计说明：
     * 1. Worker 在退回状态下只保存当前激活前缀；
     * 2. 设计树必须显示完整 FeatureManager 时间线，因此这里把摘要中的 history 替换为完整命令序列；
     * 3. objects 仍保持 Worker 当前前缀的可见结果体，确保图形区与退回状态一致。
     *
     * @returns 设计树状态摘要。
     */
    public async getFeatureTreeStateSummary(): Promise<WorkerGeometryStateSummary> {
        const summary = await this.getGeometryStateSummary({syncHistoryProjection: false});
        this.syncFeatureRollbackHistoryFromWorkerSummaryIfSafe(summary);
        this.syncFeatureRollbackManagerTimelineAndDisplayStates();
        const fullCommands = this.featureRollbackController.getFullCommands();

        return {
            ...summary,
            commandCount: fullCommands.length,
            history: fullCommands,
        };
    }

    /**
     * 读取设计树投影原子快照。
     *
     * 设计说明：
     * 1. 设计树刷新不能先读取完整 history、再另读一次 rollbackState；
     * 2. 拖动回退控制棒时两次读取之间可能穿插 Worker replay，导致草图释放或特征节点消失；
     * 3. 因此这里在同一次 Manager timeline 同步后返回 state 与 rollbackState，作为 UI 投影的唯一输入。
     *
     * @returns 设计树投影快照。
     */
    public async getFeatureTreeProjectionSnapshot(): Promise<FeatureTreeProjectionSnapshot> {
        const state = await this.getFeatureTreeStateSummary();

        return {
            state,
            rollbackState: this.getEffectiveFeatureRollbackProjectionState(),
        };
    }

    /** 按正式绑定和设计树命名规则投影终止目标；只在目标或会话变化时调用。 */
    public async resolveExtrudeEndTargetDisplay(target: ExtrudeEndTarget): Promise<ExtrudeEndTargetDisplay> {
        const names = target.kind === 'body' || target.kind === 'model-topology'
            ? await this.createResultBodyDisplayNameMap() : new Map<string, string>();
        return new ExtrudeEndTargetResolver().resolveDisplay(target, this, names);
    }

    /**
     * 目标行交互前重新定位当前代理；代理随拓扑选择模式回收，不能依赖显示快照中的UUID。
     * @param target 稳定终止目标；null用于清理悬停。
     * @param mode 选择、悬停或清理。
     */
    public setExtrudeEndTargetVisual(target: ExtrudeEndTarget | null, mode: 'select' | 'hover' | 'clear'): void {
        if (!target || mode === 'clear') {
            this.setProgrammaticHover(null);
            return;
        }
        const current = new ExtrudeEndTargetResolver().resolveDisplay(target, this, undefined, true);
        if (mode === 'hover') this.setProgrammaticHover(current.sceneObjectId);
        else if (current.sceneObjectId) this.selectEntityByObjectId(current.sceneObjectId);
    }

    /**
     * 构建当前结果体导出显示名映射。
     *
     * 设计说明：
     * 1. 离线包导出的可见名称必须复用设计树投影策略，不能读取 `FeatureResultMesh.name`；
     * 2. 该方法只消费 Worker 摘要、完整 FeatureManager 时间线和回退状态，不写入场景或 Worker；
     * 3. 导出端只拿 bodyId -> displayName 映射，几何 ID 与拓扑 ID 仍由 Worker/BRep 链路负责。
     *
     * @param options 显示名映射选项。
     * @returns 当前结果体 ID 到用户可见名称的映射。
     */
    public async createResultBodyDisplayNameMap(
        options: ViewerResultBodyDisplayNameMapOptions = {},
    ): Promise<Map<string, string>> {
        const {state, rollbackState} = await this.getFeatureTreeProjectionSnapshot();
        const sketchSummaries = this.listSketchGroupSummaries();
        const hiddenSketchIds = new Set(
            sketchSummaries
                .filter((sketch) => sketch.displayState === SketchDisplayState.Hidden)
                .map((sketch) => sketch.id),
        );
        const objectNameOverrides = options.objectNameOverrides
            ? new Map(options.objectNameOverrides)
            : new Map<string, string>();
        const document = buildPartFeatureDocument({
            history: state.history,
            objects: state.objects,
            sceneSketches: sketchSummaries.map((sketch) => ({
                id: sketch.id,
                persistentId: sketch.persistentId,
                name: sketch.name,
                order: sketch.order,
                createdAt: sketch.createdAt,
                entityType: 'sketch',
            })),
            hiddenObjectIds: hiddenSketchIds,
            suppressedSketchIds: new Set<string>(),
            objectNameOverrides,
            rollbackState,
        });

        return buildResultBodyDisplayNameMap({
            objects: state.objects,
            features: document.features,
            objectNameOverrides,
        });
    }

    /**
     * 在安全边界内把 Worker 当前 history 同步为完整 FeatureManager 时间线。
     *
     * 设计说明：
     * 1. 首次拖动退回控制棒前，controller 可能尚未接收过 Worker 的完整命令历史；
     * 2. 只有非退回状态下 Worker history 才代表完整模型，退回状态下它只是 active prefix；
     * 3. 因此该方法只在控制棒位于末尾时同步，防止普通树刷新把 active prefix 覆盖成完整历史。
     *
     * @param summary Worker 当前几何状态摘要。
     */
    private syncFeatureRollbackHistoryFromWorkerSummaryIfSafe(summary: WorkerGeometryStateSummary): void {
        //------ 特征离线包定义持久化 开始------
        /**
         * 离线包特征时间线恢复期间，Worker 重放尚未落地（历史为空或不完整），
         * 此时按 Worker 摘要对账会把刚恢复的完整时间线清空，必须跳过。
         */
        if (this.isPuppyFeatureTimelineReplayPending) {
            return;
        }
        if (!this.canWorkerHistoryReplaceFullTimeline(summary.history)) {
            return;
        }
        //------ 特征离线包定义持久化 结束------
        if (this.featureRollbackController.getState().isRolledBack) {
            return;
        }

        this.applyWorkerHistoryProjection(summary.history);
    }

    /**
     * 判定 Worker history 当前是否有资格代表完整特征时间线。
     *
     * 设计说明：
     * 1. 降级态下 Worker history 只包含重放成功的前缀，用它对账会把重建失败的特征
     *    静默移出设计树，用户下次保存即造成特征永久丢失；
     * 2. 自愈条件是 Worker history 已覆盖完整时间线的全部命令，说明重建恢复正常；
     * 3. 特征增删改走 controller 的显式入口（replaceTimeline 等），不依赖被动对账，
     *    因此降级期间跳过对账不会阻塞正常编辑。
     *
     * @param history Worker 当前命令历史。
     * @returns 允许用该历史覆盖完整时间线时返回 true。
     */
    private canWorkerHistoryReplaceFullTimeline(history: readonly CadCommand[]): boolean {
        /**
         * 草图编辑严格前缀是 Viewer 的临时求值环境，不是文档完整 FeatureManager history。
         * starting 与 active 阶段都必须阻止 Worker 前缀反向截短 Controller 主时间线。
         */
        if (this.sketchFeaturePrefixEditRuntime?.state) {
            return false;
        }

        /**
         * 特征编辑会话期间，Worker 历史是"移除目标特征及其下游"的沙箱前缀，不是文档完整时间线。
         * 若允许它对账，完整时间线会被缩短，设计树随即丢失目标特征及其下游节点；
         * 且取消编辑只异步恢复 Worker，不会把已被缩短的时间线补回来，
         * 表现为"编辑期间节点消失"与"取消后尾部特征不恢复"两个症状。
         */
        if (this.featureEditOrchestrator?.hasActiveFeatureEditSession() === true) {
            return false;
        }
        if (!this.isPuppyFeatureTimelineDegraded) {
            return true;
        }
        const workerCommandIds = new Set(history.map((command) => command.id));
        const isWorkerHistoryComplete = this.featureRollbackController
            .getFullCommands()
            .every((command) => workerCommandIds.has(command.id));
        if (!isWorkerHistoryComplete) {
            return false;
        }
        this.isPuppyFeatureTimelineDegraded = false;
        return true;
    }

    /**
     * 用 Worker history 覆盖完整特征时间线并同步重建依赖索引。
     *
     * 说明：该方法是 Worker history 成为时间线真相的唯一执行点，调用前必须已通过降级判定。
     *
     * @param history Worker 当前命令历史。
     */
    private applyWorkerHistoryProjection(history: readonly CadCommand[]): void {
        const activeCommands = this.featureRollbackController.reconcileWorkerHistory(history);
        this.featureRebuildOrchestrator?.syncFromCommandHistory(activeCommands);
    }

    /**
     * 读取退回控制棒完整状态。
     *
     * @returns 当前退回控制棒状态。
     */
    public getFeatureRollbackState(): FeatureRollbackState {
        this.syncFeatureRollbackManagerTimelineAndDisplayStates();
        return this.featureRollbackController.getState();
    }

    /**
     * 将阶段 B 的成功前缀应用到正式 Viewer，同时保留完整 FeatureManager 时间线。
     *
     * 说明：
     * 1. `success` 与 `stopped` 都是可展示的正式模型状态；后者只表示求值停在首个失败 Feature；
     * 2. Worker summary 只描述成功前缀，不能覆盖完整命令、混合时间线或 rollback cursor；
     * 3. 本方法是既有草图编辑的派生恢复，不新增 Undo/Redo 事务。
     *
     * @param input 求值前摘要、执行结果与冻结的 Rollback Bar 状态。
     */
    public async applyFeatureTimelineRebuildResult(
        input: FeatureTimelineRebuildApplyInput,
    ): Promise<void> {
        this.initializeFeatureTimelineRebuildRuntime();
        await this.featureTimelineRebuildRuntime.applyResult(input);
    }

    /**
     * 读取设计树投影所需的退回控制棒状态。
     *
     * @returns 退回控制棒投影状态。
     */
    public getFeatureRollbackProjectionState(): FeatureRollbackProjectionState {
        this.syncFeatureRollbackManagerTimelineAndDisplayStates();
        return this.getEffectiveFeatureRollbackProjectionState();
    }

    /**
     * 读取包含草图严格前缀后缀的设计树有效退回投影。
     *
     * 设计说明：
     * 1. 草图编辑只临时改变 Worker 求值前缀，不得移动或覆写正式 Rollback Bar；
     * 2. 设计树仍需把临时后缀标记为不可用，否则选中动作可能把尚未求值的草图重新显示；
     * 3. 因此仅在投影读取边界合并后缀，Controller 中的正式 cursor 与退回集合保持原样。
     *
     * @returns 正式退回状态与草图严格前缀后缀的并集投影。
     */
    private getEffectiveFeatureRollbackProjectionState(): FeatureRollbackProjectionState {
        const projection = this.featureRollbackController.getProjectionState();
        const prefixState = this.sketchFeaturePrefixEditRuntime?.state;
        if (!prefixState) {
            return projection;
        }

        const rolledBackFeatureIds = new Set(projection.rolledBackFeatureIds);
        const rolledBackSketchIds = new Set(projection.rolledBackSketchIds);
        const rolledBackDatumPlaneIds = new Set(projection.rolledBackDatumPlaneIds);
        const rolledBackDatumAxisIds = new Set(projection.rolledBackDatumAxisIds);
        const rolledBackDatumPointIds = new Set(projection.rolledBackDatumPointIds);

        prefixState.plan.fullCommands
            .slice(prefixState.plan.cutoffFeatureIndex)
            .forEach((command) => rolledBackFeatureIds.add(command.id));

        prefixState.plan.inactiveTimelineItems.forEach((item) => {
            switch (item.kind) {
                case FeatureRollbackTimelineItemKind.Sketch:
                    rolledBackSketchIds.add(item.id);
                    break;
                case FeatureRollbackTimelineItemKind.DatumPlane:
                    rolledBackDatumPlaneIds.add(item.id);
                    break;
                case FeatureRollbackTimelineItemKind.DatumAxis:
                    rolledBackDatumAxisIds.add(item.id);
                    break;
                case FeatureRollbackTimelineItemKind.DatumPoint:
                    rolledBackDatumPointIds.add(item.id);
                    break;
                default:
                    break;
            }
        });

        return {
            ...projection,
            rolledBackFeatureIds: [...rolledBackFeatureIds],
            rolledBackSketchIds: [...rolledBackSketchIds],
            rolledBackDatumPlaneIds: [...rolledBackDatumPlaneIds],
            rolledBackDatumAxisIds: [...rolledBackDatumAxisIds],
            rolledBackDatumPointIds: [...rolledBackDatumPointIds],
        };
    }

    /**
     * 读取项目保存可用的退回控制棒位置。
     *
     * @returns 当前控制棒插入索引。
     */
    public getFeatureRollbackCursorIndex(): number {
        this.syncFeatureRollbackManagerTimelineAndDisplayStates();
        return this.featureRollbackController.getState().cursorIndex;
    }

    /**
     * 捕获退回控制棒模型历史快照。
     *
     * 设计说明：
     * 1. 退回控制棒影响 Worker 激活特征前缀与顶级草图显隐，属于文档主状态；
     * 2. 快照必须包含完整 FeatureManager 退回序列，避免 Undo/Redo 后丢失未吸收顶级草图位置；
     * 3. 这里返回 plain data，供历史事务持久化与回放使用。
     *
     * @param documentId 所属文档 ID。
     * @returns 退回控制棒历史快照。
     */
    public captureFeatureRollbackHistorySnapshot(documentId: string): FeatureRollbackHistorySnapshot {
        const snapshot = this.captureFeatureRollbackTimeline();

        return {
            documentId,
            commands: snapshot.commands.map((command) => structuredClone(command)),
            cursorIndex: snapshot.cursorIndex,
            previousCursorIndex: snapshot.previousCursorIndex,
            timelineItems: snapshot.timelineItems.map((item) => ({...item})),
        };
    }

    /**
     * 从历史快照恢复退回控制棒状态并重建模型。
     *
     * @param snapshot 退回控制棒历史快照。
     * @param isRestoreCurrent 可选的文档恢复代次校验；过期任务不得再写回时间线。
     * @returns 恢复后的 Worker 状态摘要。
     * @throws Worker 状态读取或退回游标重建失败时抛出；已改写的退回时间线会先恢复。
     */
    public async restoreFeatureRollbackHistorySnapshot(
        snapshot: FeatureRollbackHistorySnapshot,
        isRestoreCurrent: () => boolean = () => true,
    ): Promise<WorkerGeometryStateSummary> {
        const rollbackTimelineSnapshot = this.captureFeatureRollbackTimeline();
        const beforeSummary = await this.getGeometryStateSummary({syncHistoryProjection: false});
        if (!isRestoreCurrent()) {
            return beforeSummary;
        }

        try {
            this.featureRollbackController.replaceTimeline(
                snapshot.commands,
                snapshot.cursorIndex,
                snapshot.previousCursorIndex,
                snapshot.timelineItems,
            );
            this.syncFeatureRollbackManagerTimeline();
            return await this.replayFeatureRollbackCursor(
                beforeSummary.history,
                beforeSummary,
                isRestoreCurrent,
            );
        } catch (error) {
            if (isRestoreCurrent()) {
                this.restoreFeatureRollbackTimeline(rollbackTimelineSnapshot);
            }
            throw error;
        }
    }

    /**
     * 解析草图在 FeatureManager 退回序列中的统一 order。
     *
     * 设计说明：
     * 1. 优先使用草图 `createdAt`，与用户基准面/轴、特征 `timestamp` 共用同一时间刻度；
     * 2. 旧数据缺少 `createdAt` 时，用已知时间戳向前锚定，避免退回到纯数组下标后与基准混排错位。
     *
     * @param sketches 草图组摘要。
     * @returns 草图 ID 到 order 的映射。
     */
    private resolveSketchFeatureManagerOrders(
        sketches: ReadonlyArray<{
            id: string;
            order: number;
            createdAt?: number;
        }>,
    ): Map<string, number> {
        const createdAtBySketchId = new Map<string, number>();
        const knownTimestamps = sketches
            .map((sketch) => sketch.createdAt)
            .filter((value): value is number => Number.isFinite(value));
        const minKnownTimestamp = knownTimestamps.length > 0
            ? Math.min(...knownTimestamps)
            : Number.POSITIVE_INFINITY;
        const legacyAnchor = Number.isFinite(minKnownTimestamp)
            ? minKnownTimestamp - sketches.length * 1000
            : Date.now();

        sketches.forEach((sketch) => {
            const createdAt = Number.isFinite(sketch.createdAt)
                ? Number(sketch.createdAt)
                : legacyAnchor + sketch.order * 1000;
            createdAtBySketchId.set(sketch.id, createdAt);
        });
        return createdAtBySketchId;
    }

    /**
     * 列出进入 FeatureManager 退回序列的用户基准面输入。
     *
     * @returns 用户基准面时间线输入；默认三基准面与临时宿主面排除。
     */
    private listUserCreatedDatumPlaneRollbackInputs(): Array<{ id: string; order: number }> {
        const manager = this.datumPlaneManager;
        if (!manager) {
            return [];
        }
        return manager.getAllPersistentPlanes()
            .filter((plane) => plane.defaultPlaneKind == null)
            .map((plane, index) => {
                const createdAt = manager.getUserCreatedPlaneCreatedAt(plane.id);
                return {
                    id: String(plane.id),
                    order: Number.isFinite(createdAt) ? Number(createdAt) : index,
                };
            });
    }

    /**
     * 列出进入 FeatureManager 退回序列的用户基准轴输入。
     *
     * @returns 用户基准轴时间线输入。
     */
    private listUserCreatedDatumAxisRollbackInputs(): Array<{ id: string; order: number }> {
        const manager = this.datumAxisManager;
        if (!manager) {
            return [];
        }
        return manager.getAllAxes()
            .map((axis, index) => {
                const createdAt = manager.getUserCreatedAxisCreatedAt(axis.datumAxisId);
                return {
                    id: String(axis.datumAxisId),
                    order: Number.isFinite(createdAt) ? Number(createdAt) : index,
                };
            });
    }

    /**
     * 列出进入 FeatureManager 退回序列的用户基准点输入。
     *
     * 设计说明：原点属于系统内置基准，不得进入退回序列。
     *
     * @returns 用户基准点时间线输入。
     */
    private listUserCreatedDatumPointRollbackInputs(): Array<{ id: string; order: number }> {
        const manager = this.datumPointManager;
        if (!manager) {
            return [];
        }
        return manager.getAllPoints()
            .filter((point) => !point.isOrigin)
            .map((point, index) => {
                const createdAt = manager.getUserCreatedPointCreatedAt(point.datumPointId);
                return {
                    id: String(point.datumPointId),
                    order: Number.isFinite(createdAt) ? Number(createdAt) : index,
                };
            });
    }

    /**
     * 用当前草图与用户基准几何同步 FeatureManager 退回序列。
     *
     * 设计说明：
     * 1. Worker 只知道 feature command history，不知道独立草图/基准在设计树中的位置；
     * 2. Viewer 持有草图组与基准 Manager，必须在读取或移动退回控制棒前补齐完整 FeatureManager 序列；
     * 3. 这样默认控制棒才能落在最后一个 FeatureManager 节点之后，而不是最后一个特征之后。
     */
    private syncFeatureRollbackManagerTimeline(): void {
        const sketches = this.listSketchGroupSummaries();
        const sketchOrderById = this.resolveSketchFeatureManagerOrders(sketches);
        this.featureRollbackController.syncFeatureManagerTimeline(
            sketches.map((sketch) => ({
                id: sketch.id,
                persistentId: sketch.persistentId,
                order: sketchOrderById.get(sketch.id) ?? sketch.order,
            })),
            {
                datumPlanes: this.listUserCreatedDatumPlaneRollbackInputs(),
                datumAxes: this.listUserCreatedDatumAxisRollbackInputs(),
                datumPoints: this.listUserCreatedDatumPointRollbackInputs(),
            },
        );
    }

    /**
     * 移动退回控制棒并按当前激活前缀重建模型。
     *
     * @param input 移动输入。
     * @returns 退回后的 Worker 状态摘要。
     * @throws 退回重建失败，或重建未产生有效状态摘要时抛出。
     */
    public async moveFeatureRollbackBar(input: FeatureRollbackMoveInput): Promise<WorkerGeometryStateSummary> {
        const summary = await this.executeFeatureRollbackMutation(() => {
            this.featureRollbackController.moveCursor(input.cursorIndex);
            return true;
        });
        if (!summary) {
            throw new Error('退回控制棒移动未产生有效重建结果');
        }
        return summary;
    }

    /**
     * 将控制棒移动到目标特征之前。
     *
     * @param featureId 目标特征 ID。
     * @returns 是否成功退回。
     */
    public async rollbackToFeature(featureId: string): Promise<boolean> {
        const summary = await this.executeFeatureRollbackMutation(() => (
            this.featureRollbackController.moveCursorToFeature(
                featureId,
                FeatureRollbackCursorPlacement.BeforeFeature,
            )
        ));
        return Boolean(summary);
    }

    /**
     * 将控制棒向前推进到目标特征之后。
     *
     * @param featureId 可选目标特征 ID；不传时前滚一个特征。
     * @returns 是否成功前滚。
     */
    public async rollFeatureRollbackBarForward(featureId?: string): Promise<boolean> {
        const summary = await this.executeFeatureRollbackMutation(() => {
            if (featureId) {
                return this.featureRollbackController.moveCursorToFeature(
                    featureId,
                    FeatureRollbackCursorPlacement.AfterFeature,
                );
            }
            const state = this.featureRollbackController.getState();
            if (state.cursorIndex >= state.totalTimelineItemCount) {
                return false;
            }
            this.featureRollbackController.rollForwardOne();
            return true;
        });
        return Boolean(summary);
    }

    /**
     * 将控制棒恢复到上一退回位置。
     *
     * @returns 是否存在上一位置并成功恢复。
     */
    public async rollFeatureRollbackBarToPrevious(): Promise<boolean> {
        const summary = await this.executeFeatureRollbackMutation(() => (
            this.featureRollbackController.rollToPrevious()
        ));
        return Boolean(summary);
    }

    /**
     * 将控制棒移动到完整模型末尾。
     *
     * @returns 是否完成重建。
     */
    public async rollFeatureRollbackBarToEnd(): Promise<boolean> {
        const summary = await this.executeFeatureRollbackMutation(() => {
            this.featureRollbackController.rollToEnd();
            return true;
        });
        return Boolean(summary);
    }

    /**
     * 按退回控制棒当前 cursor 重建 Worker 与 Viewer。
     *
     * @param rollbackCommands replay 失败时用于恢复的当前命令历史。
     * @param beforeSummary replay 前 Worker 摘要；调用方处于 rollback mutation 中时必须传入，避免中途读取摘要重入同步时间线。
     * @param isReplayCurrent 可选的文档恢复代次校验。
     * @returns 重建后的 Worker 状态摘要。
     */
    private async replayFeatureRollbackCursor(
        rollbackCommands: readonly CadCommand[],
        beforeSummary?: WorkerGeometryStateSummary,
        isReplayCurrent: () => boolean = () => true,
    ): Promise<WorkerGeometryStateSummary> {
        const activeCommands = this.featureRollbackController.getActiveCommands();
        const summary = await this.replayFeatureHistoryCommands(activeCommands, {
            rollbackCommands,
            beforeSummary,
            syncHistoryProjection: false,
            isReplayCurrent,
        });
        if (isReplayCurrent()) {
            this.applyFeatureRollbackNonFeatureSideEffects();
        }
        return summary;
    }

    /**
     * 同步 FeatureManager 退回序列，并按最新 cursor 收口非 Worker 显隐旁路。
     *
     * 设计说明：
     * 1. 只读入口（设计树快照、退回状态查询）也会触发增量 sync，可能剪掉棒下 Sketch 槽；
     * 2. 若不同拍更新草图/基准显隐，会出现「时间线已变、图形仍 Hidden、拖拽无处落点」；
     * 3. 因此凡会改写 timeline 的 sync，必须立即复用同一套显隐旁路。
     */
    private syncFeatureRollbackManagerTimelineAndDisplayStates(): void {
        this.syncFeatureRollbackManagerTimeline();
        if (this.applyFeatureRollbackNonFeatureDisplayStates()) {
            this.requestRender();
        }
    }

    /**
     * 同步退回控制棒对非 Worker 对象的旁路副作用。
     *
     * 设计说明：
     * 1. Worker replay 只能收口特征命令前缀对应的实体结果；
     * 2. 顶级草图与用户基准面/轴/点必须在同一入口同步，避免只修一半导致设计树灰显与图形可用态分裂；
     * 3. 各旁路内部自行维护进入退回前的显示态快照，恢复时不覆盖用户主动隐藏。
     */
    private applyFeatureRollbackNonFeatureSideEffects(): void {
        this.syncFeatureRollbackManagerTimelineAndDisplayStates();
    }

    /**
     * 仅同步退回控制棒对草图与用户基准的显隐旁路（不重同步时间线）。
     *
     * @returns 是否发生可见性变更。
     */
    private applyFeatureRollbackNonFeatureDisplayStates(): boolean {
        const sketchChanged = this.applyFeatureRollbackSketchDisplayStates();
        const datumChanged = this.applyFeatureRollbackDatumDisplayStates();
        return sketchChanged || datumChanged;
    }

    /**
     * 按退回控制棒状态同步顶级草图显隐。
     *
     * 设计说明：
     * 1. 顶级草图不属于 Worker feature history，不能通过 replay 自动隐藏；
     * 2. 首次进入退回状态时记录原显示态，退回到尾时按原值恢复，避免覆盖用户主动隐藏；
     * 3. 只使用 SketchGroupManager 的显示态门面，保证生命周期事件与运行时可见性一致。
     *
     * @returns 是否发生可见性变更。
     */
    private applyFeatureRollbackSketchDisplayStates(): boolean {
        const rollbackState = this.featureRollbackController.getState();
        const rolledBackSketchIds = new Set(rollbackState.rolledBackSketchIds);

        /**
         * 正式 Rollback Bar 不会因草图编辑移动，因此其状态不包含临时严格前缀后缀。
         * 前缀规划已保证目标草图不在 inactive 后缀中，这里只需合并并去重。
         */
        this.sketchFeaturePrefixEditRuntime?.state?.plan.inactiveTimelineItems.forEach((item) => {
            if (item.kind === FeatureRollbackTimelineItemKind.Sketch) {
                rolledBackSketchIds.add(item.id);
            }
        });

        let changed = false;

        rolledBackSketchIds.forEach((sketchId) => {
            if (!this.featureRollbackSketchDisplayStateSnapshot.has(sketchId)) {
                this.featureRollbackSketchDisplayStateSnapshot.set(
                    sketchId,
                    this.getSketchGroupDisplayState(sketchId) ?? SketchDisplayState.Shown,
                );
            }
            if (this.setSketchGroupDisplayState(sketchId, SketchDisplayState.Hidden, {
                publishLifecycleEvent: false,
            })) {
                changed = true;
            }
        });

        [...this.featureRollbackSketchDisplayStateSnapshot.entries()].forEach(([sketchId, displayState]) => {
            if (rolledBackSketchIds.has(sketchId)) {
                return;
            }
            if (this.setSketchGroupDisplayState(sketchId, displayState, {
                publishLifecycleEvent: false,
            })) {
                changed = true;
            }
            this.featureRollbackSketchDisplayStateSnapshot.delete(sketchId);
        });

        return changed;
    }

    /**
     * 按退回控制棒状态同步用户基准面/轴/点显隐，并清掉已退回基准的选择与当前参考面。
     *
     * 设计说明：
     * 1. 用户基准面/轴/点不在 Worker history，只能 Viewer 旁路收口；
     * 2. 隐藏后选择层按 `isVisible()` 过滤，自然禁止拾取作参考；
     * 3. 若当前参考面或选择集仍指向棒下基准，必须同步清空，避免后续草图/命令继续引用已退回对象。
     *
     * @returns 是否发生可见性变更。
     */
    private applyFeatureRollbackDatumDisplayStates(): boolean {
        if (
            !(this.featureRollbackDatumPlaneVisibleSnapshot instanceof Map)
            || !(this.featureRollbackDatumAxisVisibleSnapshot instanceof Map)
            || !(this.featureRollbackDatumPointVisibleSnapshot instanceof Map)
        ) {
            /**
             * Viewer.prototype 单测 harness 可能只挂载部分字段。
             * 缺少快照容器时跳过基准旁路，避免把退回主链打崩。
             */
            return false;
        }

        const rollbackState = this.featureRollbackController.getState();
        const rolledBackPlaneIds = new Set(rollbackState.rolledBackDatumPlaneIds ?? []);
        const rolledBackAxisIds = new Set(rollbackState.rolledBackDatumAxisIds ?? []);
        const rolledBackPointIds = new Set(rollbackState.rolledBackDatumPointIds ?? []);

        /**
         * Worker replay 只处理 Feature 命令，临时前缀后的 datum 仍由 Viewer 持有；
         * 将其并入正式 rollback 集合后，复用现有快照机制隐藏并在退出时恢复原可见性。
         */
        this.sketchFeaturePrefixEditRuntime?.state?.plan.inactiveTimelineItems.forEach((item) => {
            switch (item.kind) {
                case FeatureRollbackTimelineItemKind.DatumPlane:
                    rolledBackPlaneIds.add(item.id);
                    break;
                case FeatureRollbackTimelineItemKind.DatumAxis:
                    rolledBackAxisIds.add(item.id);
                    break;
                case FeatureRollbackTimelineItemKind.DatumPoint:
                    rolledBackPointIds.add(item.id);
                    break;
                default:
                    break;
            }
        });

        const changed = syncFeatureRollbackDatumVisibility({
            rolledBackPlaneIds: [...rolledBackPlaneIds],
            rolledBackAxisIds: [...rolledBackAxisIds],
            rolledBackPointIds: [...rolledBackPointIds],
            planeVisibleSnapshot: this.featureRollbackDatumPlaneVisibleSnapshot,
            axisVisibleSnapshot: this.featureRollbackDatumAxisVisibleSnapshot,
            pointVisibleSnapshot: this.featureRollbackDatumPointVisibleSnapshot,
            readPlaneVisible: (planeId) => {
                const plane = this.datumPlaneManager?.getPlane(Number(planeId));
                return plane ? plane.isVisible() : null;
            },
            writePlaneVisible: (planeId, visible) => {
                this.datumPlaneManager?.setPlaneVisible(Number(planeId), visible);
            },
            readAxisVisible: (axisId) => {
                const axis = this.datumAxisManager?.getAxis(Number(axisId));
                return axis ? axis.isVisible() : null;
            },
            writeAxisVisible: (axisId, visible) => {
                this.datumAxisManager?.setAxisVisible(Number(axisId), visible);
            },
            readPointVisible: (pointId) => {
                const point = this.datumPointManager?.getPoint(Number(pointId));
                return point ? point.isVisible() : null;
            },
            writePointVisible: (pointId, visible) => {
                this.datumPointManager?.setPointVisible(Number(pointId), visible);
            },
        });

        this.clearRolledBackDatumSelections(
            rolledBackPlaneIds,
            rolledBackAxisIds,
            rolledBackPointIds,
        );
        this.clearRolledBackCurrentReferencePlane(rolledBackPlaneIds);
        return changed;
    }

    /**
     * 从选择集移除控制棒下方的用户基准面/轴/点。
     *
     * @param rolledBackPlaneIds 退回基准面 ID。
     * @param rolledBackAxisIds 退回基准轴 ID。
     * @param rolledBackPointIds 退回基准点 ID。
     */
    private clearRolledBackDatumSelections(
        rolledBackPlaneIds: ReadonlySet<string>,
        rolledBackAxisIds: ReadonlySet<string>,
        rolledBackPointIds: ReadonlySet<string>,
    ): void {
        if (
            rolledBackPlaneIds.size === 0
            && rolledBackAxisIds.size === 0
            && rolledBackPointIds.size === 0
        ) {
            return;
        }
        const selectedObjects = this.getSelectedObjects();
        selectedObjects.forEach((selectedObject) => {
            const planeId = getDatumPlaneIdFromObject(selectedObject);
            if (planeId !== null && rolledBackPlaneIds.has(String(planeId))) {
                this.entitySelectionManager?.removeObjectTreeFromSelection(selectedObject);
                return;
            }
            const axisId = getDatumAxisIdFromObject(selectedObject);
            if (axisId !== null && rolledBackAxisIds.has(String(axisId))) {
                this.entitySelectionManager?.removeObjectTreeFromSelection(selectedObject);
                return;
            }
            const pointId = getDatumPointIdFromObject(selectedObject);
            if (pointId !== null && rolledBackPointIds.has(String(pointId))) {
                this.entitySelectionManager?.removeObjectTreeFromSelection(selectedObject);
            }
        });
    }

    /**
     * 若当前参考面位于控制棒下方，则清空参考面上下文。
     *
     * @param rolledBackPlaneIds 退回基准面 ID。
     */
    private clearRolledBackCurrentReferencePlane(rolledBackPlaneIds: ReadonlySet<string>): void {
        if (rolledBackPlaneIds.size === 0) {
            return;
        }
        const currentPlane = this.datumPlaneManager?.currentReferencePlane;
        if (!currentPlane) {
            return;
        }
        if (rolledBackPlaneIds.has(String(currentPlane.id))) {
            this.datumPlaneManager?.setCurrentReferencePlaneByPlaneId(null);
        }
    }

    /**
     * 捕获退回控制棒完整时间线快照。
     *
     * @returns 当前完整命令时间线与控制棒位置。
     */
    private captureFeatureRollbackTimeline(): {
        commands: CadCommand[];
        cursorIndex: number;
        previousCursorIndex: number | null;
        timelineItems: FeatureRollbackTimelineItem[];
    } {
        this.syncFeatureRollbackManagerTimelineAndDisplayStates();
        const state = this.featureRollbackController.getState();
        return {
            commands: this.featureRollbackController.getFullCommands(),
            cursorIndex: state.cursorIndex,
            previousCursorIndex: state.previousCursorIndex,
            timelineItems: [...state.timelineItems],
        };
    }

    /**
     * 恢复退回控制棒完整时间线快照。
     *
     * 设计说明：
     * Worker replay 失败时底层几何状态会回滚到传入的 rollbackCommands；
     * Viewer 也必须同步恢复 cursor，否则设计树会显示一个并未成功生效的位置。
     *
     * @param snapshot 退回控制棒时间线快照。
     */
    private restoreFeatureRollbackTimeline(snapshot: {
        readonly commands: readonly CadCommand[];
        readonly cursorIndex: number;
        readonly previousCursorIndex: number | null;
        readonly timelineItems: readonly FeatureRollbackTimelineItem[];
    }): void {
        this.featureRollbackController.replaceTimeline(
            snapshot.commands,
            snapshot.cursorIndex,
            snapshot.previousCursorIndex,
            snapshot.timelineItems,
        );
        this.applyFeatureRollbackNonFeatureSideEffects();
    }

    /**
     * 在 feature replay 的 Viewer 派生同步失败后恢复 Worker 与正式结果网格。
     *
     * 设计说明：
     * 1. Worker replay 的 rollbackCommands 只能覆盖 Worker 内部校验失败；
     * 2. 如果主线程增量 mesh 回灌失败，Worker 已经处于 after 状态，必须显式 replay 回 before；
     * 3. 恢复过程禁止同步 FeatureManager history，避免失败恢复把 rollback active prefix 提升为完整时间线。
     *
     * @param beforeSummary replay 前的 Worker 摘要。
     * @param isStateCurrent 可选的文档恢复代次校验。
     */
    private async restoreWorkerFeatureReplayState(
        beforeSummary: WorkerGeometryStateSummary,
        isStateCurrent: () => boolean = () => true,
    ): Promise<void> {
        const replayResult = await this.featureExecutionRuntime.replayFeatureCommands({
            commands: beforeSummary.history.map((command) => structuredClone(command)),
            clearFirst: true,
            syncHistoryProjection: false,
        });
        if (!isStateCurrent()) {
            return;
        }
        await this.syncFeatureMeshesFromState(replayResult.summary, {isStateCurrent});
    }

    /**
     * 执行退回控制棒 mutation 并在失败时恢复内部时间线。
     *
     * @param mutate 控制棒状态变更函数；返回 false 表示目标不可解析。
     * @returns 重建后的 Worker 状态摘要；目标不可解析时返回 null。
     */
    private async executeFeatureRollbackMutation(
        mutate: () => boolean,
    ): Promise<WorkerGeometryStateSummary | null> {
        const beforeSummary = await this.getGeometryStateSummary({syncHistoryProjection: false});
        this.syncFeatureRollbackHistoryFromWorkerSummaryIfSafe(beforeSummary);
        /**
         * captureFeatureRollbackTimeline 内会 sync+显隐同拍；
         * mutation 前若只 sync 时间线不同拍显隐，剪枝后的棒下草图会卡在 Hidden。
         */
        const rollbackTimelineSnapshot = this.captureFeatureRollbackTimeline();
        if (!mutate()) {
            return null;
        }
        try {
            return await this.replayFeatureRollbackCursor(beforeSummary.history, beforeSummary);
        } catch (error) {
            this.restoreFeatureRollbackTimeline(rollbackTimelineSnapshot);
            throw error;
        }
    }

    /**
     * 构建设计树特征删除计划输入。
     *
     * 设计说明：
     * 1. 删除影响范围必须基于 Worker 权威命令历史与当前场景草图显隐状态；
     * 2. Viewer 只负责把运行时状态归一成 cad-core plain data，不在这里写 UI 确认逻辑；
     * 3. 名称覆盖从调用方注入，保证 web-client 重命名不会反向耦合到 Viewer 内部状态。
     *
     * @param targetFeatureId 待删除特征 ID。
     * @param summary 当前 Worker 几何状态摘要。
     * @param options 删除计划选项。
     * @returns 删除计划输入。
     */
    private createFeatureDeletionPlanInput(
        targetFeatureId: string,
        summary: WorkerGeometryStateSummary,
        options: ViewerFeatureDeletionPlanOptions = {},
    ): ResolveFeatureDeletionPlanInput {
        const sketchSummaries = this.listSketchGroupSummaries();
        const hiddenSketchIds = new Set(
            sketchSummaries
                .filter((sketch) => sketch.displayState === SketchDisplayState.Hidden)
                .map((sketch) => sketch.id),
        );
        const objectNameOverrides = options.objectNameOverrides
            ? new Map(options.objectNameOverrides)
            : new Map<string, string>();

        return {
            targetFeatureId,
            commands: summary.history,
            objects: summary.objects,
            sceneSketches: sketchSummaries.map((sketch) => ({
                id: sketch.id,
                persistentId: sketch.persistentId,
                name: sketch.name,
                order: sketch.order,
                createdAt: sketch.createdAt,
                entityType: 'sketch',
            })),
            hiddenObjectIds: hiddenSketchIds,
            suppressedSketchIds: new Set<string>(),
            objectNameOverrides,
        };
    }

    /**
     * 解析设计树特征删除计划。
     *
     * @param featureId 待删除特征 ID。
     * @param options 删除计划选项。
     * @returns 删除计划；目标不是有效特征时返回 `null`。
     */
    public async resolveFeatureDeletionPlan(
        featureId: string,
        options: ViewerFeatureDeletionPlanOptions = {},
    ): Promise<FeatureDeletionPlan | null> {
        const summary = await this.getFeatureTreeStateSummary();
        return this.featureDeletionPlanner.resolvePlan(
            this.createFeatureDeletionPlanInput(featureId, summary, options),
        );
    }

    /**
     * 删除设计树特征并同步 Worker 与 Viewer 派生网格。
     *
     * 设计说明：
     * 1. 特征删除本质是从权威 feature command history 中移除目标及相关下游命令；
     * 2. 删除后必须通过 replay 全量重建结果体，避免 UI 层直接移除 mesh 造成主状态与派生状态分裂；
     * 3. 目标特征来源草图恢复为顶级草图，下游子特征来源草图随子特征删除，避免残留 orphan sketch。
     *
     * @param featureId 待删除特征 ID。
     * @param options 删除计划选项。
     * @returns 删除执行结果；目标不是有效特征时返回 `null`。
     * @throws 已确认删除计划失效，或状态读取、计划校验及模型重建失败时抛出。
     */
    public async deleteFeatureById(
        featureId: string,
        options: ViewerFeatureDeletionExecuteOptions = {},
    ): Promise<FeatureDeletionExecutionResult | null> {
        const activeBeforeSummary = await this.getGeometryStateSummary({syncHistoryProjection: false});
        const beforeSummary = await this.getFeatureTreeStateSummary();
        const rollbackStateBeforeDelete = this.featureRollbackController.getState();
        const wasRolledBackBeforeDelete = rollbackStateBeforeDelete.isRolledBack;
        const plan = this.featureDeletionPlanner.resolvePlan(
            this.createFeatureDeletionPlanInput(featureId, beforeSummary, options),
        );

        if (!plan) {
            if (options.confirmedPlan) {
                throw new Error(FEATURE_DELETION_PLAN_CHANGED_MESSAGE);
            }
            return null;
        }
        if (options.confirmedPlan) {
            assertFeatureDeletionPlanMatchesConfirmedPlan(plan, options.confirmedPlan);
        }

        let afterSummary: WorkerGeometryStateSummary;
        const rollbackTimelineSnapshot = this.captureFeatureRollbackTimeline();
        try {
            if (wasRolledBackBeforeDelete) {
                this.featureRollbackController.replaceTimelineAfterFeatureDeletion(
                    plan.remainingCommands,
                    plan.deletedFeatureIds,
                );
                this.syncFeatureRollbackManagerTimeline();
                const activeAfterSummary = await this.replayFeatureRollbackCursor(
                    activeBeforeSummary.history,
                    activeBeforeSummary,
                );
                const fullCommands = this.featureRollbackController.getFullCommands();
                afterSummary = {
                    ...activeAfterSummary,
                    commandCount: fullCommands.length,
                    history: fullCommands,
                };
            } else {
                this.featureRollbackController.replaceTimeline(plan.remainingCommands);
                this.syncFeatureRollbackManagerTimeline();
                afterSummary = await this.replayFeatureHistoryCommands(plan.remainingCommands, {
                    rollbackCommands: plan.rollbackCommands,
                });
            }
        } catch (error) {
            this.restoreFeatureRollbackTimeline(rollbackTimelineSnapshot);
            throw error;
        }
        this.applyFeatureRollbackNonFeatureSideEffects();

        plan.restoredSketchIds.forEach((sketchId) => {
            this.setSketchGroupDisplayState(sketchId, SketchDisplayState.Shown);
        });
        plan.deletedSketchIds.forEach((sketchId) => {
            this.removeSketchGroup(sketchId);
        });
        this.emitTopologyOwnerRemovedEventsForDeletedFeatures(plan.deletedFeatureIds);

        return {
            plan,
            beforeSummary,
            afterSummary,
        };
    }

    /**
     * 请求 Worker 计算曲线采样点。
     * @param payload 曲线计算载荷。
     * @returns 曲线计算结果。
     */
    public async compute2DCurvePoints(payload: Compute2DCurvePayload): Promise<Compute2DCurveResponse> {
        return this.workerManager.compute2DCurve(payload);
    }

    /**
     * 从命令历史恢复项目状态并同步结果体。
     * @param commands 项目命令历史。
     * @param options 恢复选项。
     * @param options.rollbackCursorIndex 项目保存时的退回控制棒插入索引；省略时恢复完整命令序列。
     * @returns 恢复后的几何状态摘要。
     * @throws Worker 命令重放或 Viewer 网格同步失败时，在恢复退回时间线后原样抛出。
     */
    public async restoreProjectState(
        commands: CadCommand[],
        options: {
            /** 项目保存时记录的退回控制棒插入索引。 */
            rollbackCursorIndex?: number;
        } = {},
    ): Promise<WorkerGeometryStateSummary> {
        const rollbackTimelineSnapshot = this.captureFeatureRollbackTimeline();
        try {
            this.featureRollbackController.replaceTimeline(commands);
            this.syncFeatureRollbackManagerTimeline();
            if (typeof options.rollbackCursorIndex === 'number') {
                this.featureRollbackController.moveCursor(options.rollbackCursorIndex);
            }
            const replayResult = await this.featureExecutionRuntime.replayFeatureCommands({
                commands: this.featureRollbackController.getActiveCommands(),
                clearFirst: true,
                syncHistoryProjection: false,
            });
            const summary = replayResult.summary;
            await this.syncFeatureMeshesFromState(summary);
            this.applyFeatureRollbackNonFeatureSideEffects();
            return summary;
        } catch (error) {
            this.restoreFeatureRollbackTimeline(rollbackTimelineSnapshot);
            throw error;
        }
    }

    /**
     * 在完整文档 runtime switch 边界内恢复项目状态。
     *
     * 设计说明：
     * 1. 首次页面加载和跨项目上下文切换必须先写 staged Worker runtime，不能让 replay 触碰旧 active runtime；
     * 2. staged replay 通过 FeatureExecutionRuntime 复用命令一致性校验，校验通过后才 commit 为新的 active runtime；
     * 3. commit 前任意失败都必须 abort staged runtime 并恢复退回时间线；commit 后 Worker 已替换，后续错误直接抛出。
     * 4. 该入口只恢复 CAD 文档派生运行时，不写 Undo/Redo 事务；文档加载边界不属于用户可撤销建模操作。
     *
     * @param input 文档运行时切换恢复输入。
     * @returns commit 后的 Worker 几何状态摘要。
     * @throws staged runtime 启动、重放、提交、网格同步或失败后的放弃操作失败时抛出。
     */
    public async restoreProjectStateInDocumentRuntimeSwitch(
        input: ViewerRestoreProjectStateInDocumentRuntimeSwitchInput,
    ): Promise<WorkerGeometryStateSummary> {
        const rollbackTimelineSnapshot = this.captureFeatureRollbackTimeline();
        const runtimeSwitch = await this.beginDocumentRuntimeSwitch({
            runtimeKey: input.runtimeKey,
            reason: input.reason,
            scope: input.scope,
        });
        let committed = false;

        try {
            this.featureRollbackController.replaceTimeline(input.commands);
            this.syncFeatureRollbackManagerTimeline();
            if (typeof input.rollbackCursorIndex === 'number') {
                this.featureRollbackController.moveCursor(input.rollbackCursorIndex);
            }

            await this.featureExecutionRuntime.replayFeatureCommandsInDocumentRuntimeSwitch(
                runtimeSwitch,
                {
                    commands: this.featureRollbackController.getActiveCommands(),
                    clearFirst: true,
                    syncHistoryProjection: false,
                },
            );

            const summary = await runtimeSwitch.commit();
            committed = true;
            await this.syncFeatureMeshesFromState(summary);
            this.applyFeatureRollbackNonFeatureSideEffects();
            return summary;
        } catch (error) {
            if (committed) {
                throw error;
            }

            this.restoreFeatureRollbackTimeline(rollbackTimelineSnapshot);
            try {
                await runtimeSwitch.abort();
            } catch (abortError) {
                const restoreMessage = error instanceof Error ? error.message : String(error);
                const abortMessage = abortError instanceof Error ? abortError.message : String(abortError);
                throw new Error(`文档 runtime switch 恢复失败(${restoreMessage})，且 abort staged runtime 失败：${abortMessage}`);
            }
            throw error;
        }
    }

    /**
     * 从 feature 历史命令恢复 Worker 与 Viewer 派生状态。
     *
     * 设计说明：
     * 1. Undo/Redo 的 ChangeSet 只保存 feature 主状态，BRep / Mesh 必须通过 Worker replay 重新生成；
     * 2. replay 前后通过命令前缀差异解析受影响范围，只替换变化点之后的结果体，避免全量清空场景；
     * 3. rollbackCommands 由调用方传入当前命令历史，replay 校验失败时可恢复到进入 Undo/Redo 前的派生状态。
     *
     * @param commands 目标 feature 命令历史。
     * @param options replay 选项。
     * @returns 恢复后的 Worker 几何状态摘要。
     * @throws Worker 状态读取、历史重放、结果校验、网格同步或失败后的回滚恢复失败时抛出。
     */
    public async replayFeatureHistoryCommands(
        commands: readonly CadCommand[],
        options: {
            /** 需要校验的目标 feature ID。 */
            targetFeatureId?: string;
            /** 期望 replay 结果哈希。 */
            expectedResultHash?: string;
            /** 历史快照中记录的期望命令序列哈希。 */
            expectedCommandSeq?: string;
            /** 历史快照中记录的期望 feature revision。 */
            expectedFeatureRevision?: string;
            /** replay 失败时用于回滚 Worker 的命令历史。 */
            rollbackCommands?: readonly CadCommand[];
            /** replay 前的 Worker 摘要；rollback mutation 内调用时必须传入，避免读取摘要污染退回时间线。 */
            beforeSummary?: WorkerGeometryStateSummary;
            /** 是否把 replay 结果同步为完整特征历史投影。回退控制棒 replay 传 false。 */
            syncHistoryProjection?: boolean;
            /**
             * 是否把 commands 恢复为完整 FeatureManager 主状态，并与 Rollback Bar 一致：
             * 清空 Worker 后从头重放当前正式 active commands。
             */
            restoreFullFeatureHistory?: boolean;
            /** 是否保留“退回到前”使用的上一控制棒位置；严格前缀退出时必须保留。 */
            preserveRollbackNavigationState?: boolean;
            /** 文档恢复任务是否仍拥有当前 Viewer；过期后禁止继续提交 Worker 异步结果。 */
            isReplayCurrent?: () => boolean;
        } = {},
    ): Promise<WorkerGeometryStateSummary> {
        const isReplayCurrent = options.isReplayCurrent ?? (() => true);
        const beforeSummary = options.beforeSummary ?? await this.getGeometryStateSummary({syncHistoryProjection: false});
        if (!isReplayCurrent()) {
            return beforeSummary;
        }
        if (options.restoreFullFeatureHistory === true) {
            this.syncFeatureRollbackHistoryFromWorkerSummaryIfSafe(beforeSummary);
            const rollbackTimelineSnapshot = this.captureFeatureRollbackTimeline();
            try {
                const sketchSummaries = this.listSketchGroupSummaries();
                const sketchOrderById = this.resolveSketchFeatureManagerOrders(sketchSummaries);
                this.featureRollbackController.replaceTimelineForFeatureHistoryRestore(
                    commands,
                    sketchSummaries.map((sketch) => ({
                        id: sketch.id,
                        persistentId: sketch.persistentId,
                        order: sketchOrderById.get(sketch.id) ?? sketch.order,
                    })),
                    {
                        datumPlanes: this.listUserCreatedDatumPlaneRollbackInputs(),
                        datumAxes: this.listUserCreatedDatumAxisRollbackInputs(),
                        datumPoints: this.listUserCreatedDatumPointRollbackInputs(),
                    },
                );
                if (options.preserveRollbackNavigationState === true) {
                    /**
                     * 完整历史恢复会重建混合时间线，并默认清空 previous cursor。
                     * 严格前缀只是临时求值环境，退出时不能改变用户原有“退回到前”位置。
                     */
                    const restoredState = this.featureRollbackController.getState();
                    this.featureRollbackController.replaceTimeline(
                        commands,
                        restoredState.cursorIndex,
                        rollbackTimelineSnapshot.previousCursorIndex,
                        restoredState.timelineItems,
                    );
                }
                const activeCommands = this.featureRollbackController.getActiveCommands();

                /**
                 * 完整历史恢复与正式 Rollback Bar 使用相同的求值规则：
                 * 棒下命令不执行；棒上命令清空 Worker 后从头重放。
                 * 调用方声明的受影响后缀只决定命令重写范围，不改变这里的执行范围。
                 */
                const activeCommandIds = new Set(activeCommands.map((command) => command.id));
                const isTargetFeatureActive = options.targetFeatureId
                    ? activeCommandIds.has(options.targetFeatureId)
                    : false;
                const activeCommandSeq = createFeatureCommandSeq(activeCommands);
                const canUseSequenceExpectations = isTargetFeatureActive
                    && (!options.expectedCommandSeq || options.expectedCommandSeq === activeCommandSeq);
                const replayResult = await this.featureExecutionRuntime.replayFeatureCommands({
                    commands: activeCommands.map((command) => structuredClone(command)),
                    clearFirst: true,
                    targetFeatureId: isTargetFeatureActive
                        ? options.targetFeatureId
                        : undefined,
                    expectedResultHash: canUseSequenceExpectations
                        ? options.expectedResultHash
                        : undefined,
                    expectedCommandSeq: canUseSequenceExpectations
                        ? options.expectedCommandSeq
                        : undefined,
                    expectedFeatureRevision: isTargetFeatureActive
                        ? options.expectedFeatureRevision
                        : undefined,
                    rollbackCommands: beforeSummary.history.map((command) => structuredClone(command)),
                    syncHistoryProjection: false,
                });
                if (!isReplayCurrent()) {
                    return replayResult.summary;
                }

                await this.syncAffectedFeatureMeshesFromState({
                    beforeSummary,
                    afterSummary: replayResult.summary,
                    affectedCommandIds: resolveFeatureReplayAffectedCommandIds(
                        beforeSummary.history,
                        replayResult.summary.history,
                    ),
                    isStateCurrent: isReplayCurrent,
                });
                if (isReplayCurrent()) {
                    this.applyFeatureRollbackNonFeatureSideEffects();
                }
                return replayResult.summary;
            } catch (error) {
                if (isReplayCurrent()) {
                    this.restoreFeatureRollbackTimeline(rollbackTimelineSnapshot);
                    await this.restoreWorkerFeatureReplayState(beforeSummary, isReplayCurrent);
                }
                throw error;
            }
        }

        try {
            const replayResult = await this.featureExecutionRuntime.replayFeatureCommands({
                commands: commands.map((command) => structuredClone(command)),
                clearFirst: true,
                targetFeatureId: options.targetFeatureId,
                expectedResultHash: options.expectedResultHash,
                expectedCommandSeq: options.expectedCommandSeq,
                expectedFeatureRevision: options.expectedFeatureRevision,
                rollbackCommands: options.rollbackCommands?.map((command) => structuredClone(command)),
                syncHistoryProjection: options.syncHistoryProjection ?? true,
            });
            if (!isReplayCurrent()) {
                return replayResult.summary;
            }

            await this.syncAffectedFeatureMeshesFromState({
                beforeSummary,
                afterSummary: replayResult.summary,
                affectedCommandIds: resolveFeatureReplayAffectedCommandIds(
                    beforeSummary.history,
                    replayResult.summary.history,
                ),
                isStateCurrent: isReplayCurrent,
            });

            return replayResult.summary;
        } catch (error) {
            if (isReplayCurrent()) {
                await this.restoreWorkerFeatureReplayState(beforeSummary, isReplayCurrent);
            }
            throw error;
        }
    }

    /**
     * 获取当前已选中的对象 ID 集合。
     *
     * 设计说明：
     * 1. web-client 只能通过 Viewer 读取当前选择结果，不能直接探测 EntitySelectionManager 内部状态；
     * 2. 返回值统一为字符串 ID 数组，便于命令层做一次性预选对象吸收；
     * 3. 未选中任何对象时返回空数组，避免上层分支里出现 null 判空噪声。
     *
     * @returns 当前已选中对象 ID 集合。
     */
    public getSelectedObjectIds(): string[] {
        return this.entitySelectionManager?.getSelectedObjectIds() ?? [];
    }

    /**
     * 设置当前选择模式。
     *
     * @param mode 目标模式。
     * @param options 选项。
     */
    public setSelectionMode(
        mode: 'object' | 'face' | 'edge' | 'vertex' | 'face-edge' | 'topology',
        options: EntitySelectionModeOptions = {},
    ): void {
        this.entitySelectionManager?.setSelectionMode(mode, options);
        this.meshManager?.setFeatureTopologySelectionMode(mode === 'object' ? 'none' : mode);
    }

    /**
     * 获取当前选择模式。
     *
     * @returns 当前模式；选择管理器未就绪时回退为 `object`。
     */
    public getSelectionMode(): 'object' | 'face' | 'edge' | 'vertex' | 'face-edge' | 'topology' {
        return this.entitySelectionManager?.getSelectionMode() ?? 'object';
    }

    /**
     * 读取当前命令态声明的结果体拓扑选择模式。
     *
     * @returns 当前命令态选择模式；`none` 表示由默认浏览态交互代理接管。
     */
    public getFeatureTopologySelectionMode(): 'none' | 'face' | 'edge' | 'vertex' | 'face-edge' | 'topology' {
        return this.meshManager?.getFeatureTopologySelectionMode() ?? 'none';
    }

    /**
     * 设置当前是否允许穿透前景面选择被遮挡的实体拓扑。
     *
     * @param value 是否允许穿透前景面选择。
     */
    public setAllowOccludedFeatureTopologySelection(value: boolean): void {
        this.entitySelectionManager?.setAllowOccludedFeatureTopologySelection(value);
    }

    /**
     * 读取当前是否允许穿透前景面选择被遮挡的实体拓扑。
     *
     * @returns 是否允许穿透前景面选择。
     */
    public getAllowOccludedFeatureTopologySelection(): boolean {
        return this.entitySelectionManager?.getAllowOccludedFeatureTopologySelection() ?? false;
    }

    /**
     * 解析插入零部件预览阶段的装配几何吸附点。
     *
     * 设计说明：
     * 1. 该入口只读取当前场景并返回临时放置坐标，不修改装配体主状态；
     * 2. web-client 通过 Viewer 调用，避免直接依赖 imported-model 的拓扑拾取细节；
     * 3. 解析器按需创建，确保 Viewer 初始化与无插入命令场景不额外承担拾取对象。
     *
     * @param input 放置吸附输入。
     * @returns 吸附结果；未命中可吸附几何时返回 `null`。
     */
    public resolveAssemblyComponentPlacementSnap(
        input: AssemblyComponentPlacementSnapInput,
    ): AssemblyComponentPlacementSnapResult | null {
        return this.getAssemblyComponentPlacementSnapResolver().resolve(input);
    }

    /**
     * 读取插入零部件放置吸附解析器实例。
     *
     * @returns 放置吸附解析器。
     */
    private getAssemblyComponentPlacementSnapResolver(): AssemblyComponentPlacementSnapResolver {
        if (!this.assemblyComponentPlacementSnapResolver) {
            this.assemblyComponentPlacementSnapResolver = new AssemblyComponentPlacementSnapResolver({
                viewer: this,
            });
        }

        return this.assemblyComponentPlacementSnapResolver;
    }

    /**
     * 获取当前全部已选场景对象。
     * @returns 已选对象集合。
     */
    public getSelectedObjects(): THREE.Object3D[] {
        return this.entitySelectionManager?.getSelectedObjects() ?? [];
    }

    /**
     * 按特征拓扑引用同步命令态选中集合。
     *
     * @param items 需要保持选中态的拓扑项。
     */
    public selectFeatureTopologyItems(items: SelectionItemRef[]): void {
        if (!this.entitySelectionManager) {
            return;
        }
        const selectedObjects: THREE.Object3D[] = [];
        const selectedKeys = new Set(items.map((item) => `${item.kind}:${item.bodyObjectId}:${item.persistentRef}`));
        this.scene.traverse((object) => {
            const key = this.resolveFeatureTopologyObjectKey(object);
            if (key && selectedKeys.has(key)) {
                selectedObjects.push(object);
            }
        });
        this.entitySelectionManager.selectEntitiesProgrammatically(selectedObjects);
    }

    /**
     * 获取当前主选对象。
     * @returns 主选对象；未命中时返回 null。
     */
    public getPrimarySelectedObject(): THREE.Object3D | null {
        return this.entitySelectionManager?.getPrimarySelectedObject() ?? null;
    }

    /**
     * 删除当前选中的草图内容。
     *
     * 设计说明：
     * 1. 选择范围同时包含 Three.js 草图实体与尺寸领域聚合，不能由 UI 分两次删除；
     * 2. Delete 键与右键菜单复用同一事务，避免混合多选只删除一个尺寸或遗留悬空标签；
     * 3. 上层 UI 只调用 Viewer 门面，实体级联、尺寸补丁和历史回滚全部由 cad-core 收口。
     *
     * @returns 成功删除至少一个草图实体或智能尺寸时返回 true。
     */
    public deleteCurrentSketchSelection(): boolean {
        //------ Delete 键复用右键删除 开始------
        return this.contextMenuController?.executeDeleteForCurrentSelection() ?? false;
        //------ Delete 键复用右键删除 结束------
    }

    /**
     * 把当前选中的草图实体与显式选中尺寸复制到会话级剪贴板。
     *
     * @returns 成功复制至少一个草图实体时返回 true。
     */
    public copySelectedSketchEntitiesToClipboard(): boolean {
        return this.sketchEntityClipboardController?.copySelectedEntities() ?? false;
    }

    /**
     * 把会话级草图实体与尺寸剪贴板粘贴到最后一次画布内鼠标位置。
     *
     * @returns 成功粘贴至少一个草图实体时返回 true。
     */
    public pasteSketchEntityClipboardAtLastPointerPosition(): boolean {
        return this.sketchEntityClipboardController?.pasteAtLastPointerPosition() ?? false;
    }

    /**
     * 解析特征拓扑代理对象的选择键。
     *
     * @param object 场景对象。
     * @returns 选择键；不是特征拓扑代理时返回 null。
     */
    private resolveFeatureTopologyObjectKey(object: THREE.Object3D): string | null {
        if (isFeatureLine(object) && object.objectId && object.persistentRef) {
            return `edge:${object.objectId}:${object.persistentRef}`;
        }
        if (isFeatureFace(object)) {
            return `face:${object.objectId}:${object.persistentRef}`;
        }
        if (isFeaturePoint(object) && object.objectId && object.persistentRef) {
            return `vertex:${object.objectId}:${object.persistentRef}`;
        }
        return null;
    }

    /**
     * 获取当前选中点。
     * @returns 点对象；未命中时返回 null。
     */
    public getCurrentSelectedPoint(): THREE.Object3D | null {
        return this.entitySelectionManager?.currentSelectedPoint ?? null;
    }

    /**
     * 获取当前选中线。
     * @returns 线对象；未命中时返回 null。
     */
    public getCurrentSelectedLine(): THREE.Object3D | null {
        return this.entitySelectionManager?.currentSelectedLine ?? null;
    }

    /**
     * 获取当前选中圆。
     * @returns 圆对象；未命中时返回 null。
     */
    public getCurrentSelectedCircle(): THREE.Object3D | null {
        return this.entitySelectionManager?.currentSelectedCircle ?? null;
    }

    /**
     * 获取当前选中圆弧。
     * @returns 圆弧对象；未命中时返回 null。
     */
    public getCurrentSelectedArc(): THREE.Object3D | null {
        return this.entitySelectionManager?.currentSelectedArc ?? null;
    }

    /**
     * 获取当前选中椭圆。
     * @returns 椭圆对象；未命中时返回 null。
     */
    public getCurrentSelectedEllipse(): THREE.Object3D | null {
        return this.entitySelectionManager?.currentSelectedEllipse ?? null;
    }

    /**
     * 获取当前选中槽口。
     * @returns 槽口对象；未命中时返回 null。
     */
    public getCurrentSelectedSlot(): THREE.Object3D | null {
        return this.entitySelectionManager?.currentSelectedSlot ?? null;
    }

    /**
     * 获取当前选中样条。
     * @returns 样条对象；未命中时返回 null。
     */
    public getCurrentSelectedSpline(): THREE.Object3D | null {
        return this.entitySelectionManager?.currentSelectedSpline ?? null;
    }

    /**
     * 获取当前选中样式样条。
     * @returns 样式样条对象；未命中时返回 null。
     */
    public getCurrentSelectedStyleSpline(): THREE.Object3D | null {
        return this.entitySelectionManager?.currentSelectedStyleSpline ?? null;
    }

    /**
     * 以程序方式清空当前所有已选对象。
     * 说明：
     * 1. 宿主层只应通过 Viewer 的公开入口驱动选择中心；
     * 2. 由选择中心统一派发 `SelectionCleared` 及相关选择变更事件；
     * 3. 禁止宿主层自行散落重置各类选中状态，避免状态不同步。
     *
     * @param options 清空选项。
     * @param options.scope `auto` 会同时清空活动测量选择，`command` 仅清空命令选择状态。
     * @param options.intent 写入选择更新事件的交互意图；省略时按普通程序化清空处理。
     */
    public clearSelectionProgrammatically(
        options: { scope?: 'command' | 'auto'; intent?: SelectionUpdateIntent } = {},
    ): void {
        this.interactionStateManager?.setSelectedFeatureVisualScope(null, false);
        this.entitySelectionManager?.clearSelectionProgrammatically(options);
    }

    /**
     * 文档上下文切换时重置临时交互态。
     *
     * 设计说明：
     * 1. 供 Tab 切换、模型卸载等场景统一收口 3D 交互残留，包含普通选择与 Mate 命令态高亮；
     * 2. 测量会话保持激活，仅清空选中实体、测量线与属性浮窗；
     * 3. 实体选中始终走选择中心单一出口，避免宿主层散落调用。
     */
    public resetDocumentTransientInteraction(): void {
        this.sketchDimensionInstant2dController?.reset();
        this.meshRaycastQuery?.reset();
        if (this.measureController.isMeasureActive()) {
            this.measureController.clearSelectedEntities();
        }
        this.clearImportedModelDatumSlotHighlight();
        this.clearImportedModelCommandSelectedEntities();
        this.setProgrammaticHover(null);
        this.setFeatureVisualHoverScope(null);
        this.setFeatureVisualSelectionScope(null);
        this.clearSelectionProgrammatically();
        this.requestRender();
    }

    /**
     * 开始 Viewer 文档级 runtime switch。
     *
     * 设计说明：
     * 1. 文档切换首先收口 Viewer 内存交互态，避免旧文档选择、悬停和测量残留落入新运行时；
     * 2. 特征预览请求必须在 Worker staged runtime 开始前清空，避免旧预览异步回写；
     * 3. BRep 注册能力只暴露为 staged Worker handle，调用方不能绕过该会话写入全局 Worker runtime。
     * 4. 该入口只收口文档切换、离线包导入和编辑器销毁的运行时边界，不写入建模历史命令，因此不进入 Undo/Redo 历史。
     *
     * @param input 文档运行时切换输入。
     * @returns Viewer 文档运行时切换句柄。
     */
    public async beginDocumentRuntimeSwitch(
        input: ViewerDocumentRuntimeSwitchInput,
    ): Promise<ViewerDocumentRuntimeSwitchHandle> {
        // 文档切换必须先切断旧文档交互残留，再允许 Worker 进入新的 staged runtime。
        this.resetDocumentTransientInteraction();
        this.clearFeaturePreviewRequests();

        const workerHandle = await this.workerManager.beginGeometryRuntimeSwitch({
            reason: input.reason,
            runtimeKey: input.runtimeKey,
            scope: input.scope,
        });

        // 仅转交 staged Worker handle 的受控能力，保持 BRep 注册、提交和放弃的会话边界一致。
        return {
            id: workerHandle.id,
            brepRegistrar: workerHandle,
            batchExecute: (payload) => workerHandle.batchExecute(payload),
            commit: async (commitInput) => {
                const summary = await workerHandle.commit(commitInput);
                if (input.scope.kind === 'full-document') {
                    /**
                     * staged switch 成功提交才使旧文档任务失效；begin 失败或 abort 时旧文档仍保持所有权。
                     * 新文档随后调用 restore 会再次推进代次，但不会留下 Worker commit 到场景挂载间的旧任务窗口。
                     */
                    this.advancePuppyFeatureRestoreGeneration();
                }
                return summary;
            },
            abort: () => workerHandle.abort(),
        };
    }

    /**
     * 以对象 ID 触发程序化悬停。
     * @param objectId 目标对象 ID；传入 null 时清空当前悬停。
     * @param options 悬停选项。
     */
    public setProgrammaticHover(
        objectId: string | null,
        options: { interactionMeta?: SelectionInteractionMeta } = {},
    ): void {
        if (objectId) {
            this.interactionStateManager?.setProgrammaticHoveredFeatureVisualScope(null, false);
        }
        this.entitySelectionManager?.setProgrammaticHover(objectId, options);
    }

    //------ 线性零部件阵列节点包围盒移入 开始------
    /**
     * 以对象 ID 列表触发程序化多目标悬停。
     *
     * @param objectIds 目标对象 ID 列表；为空时清空当前多目标悬停。
     * @param options 悬停选项。
     */
    public setProgrammaticHoverTargets(
        objectIds: readonly string[],
        options: { interactionMeta?: SelectionInteractionMeta } = {},
    ): void {
        if (objectIds.length > 0) {
            this.interactionStateManager?.setProgrammaticHoveredFeatureVisualScope(null, false);
        }
        this.entitySelectionManager?.setProgrammaticHoverTargets(objectIds, options);
    }

    /**
     * 设置设计树特征节点 hover 的局部视觉范围。
     *
     * @param featureId 特征 ID；传入 null 时清空。
     * @param resultObjectIds 该特征当前关联的结果体 objectId 列表。
     */
    public setFeatureVisualHoverScope(
        featureId: string | null,
        resultObjectIds: readonly string[] = [],
    ): void {
        if (featureId) {
            this.entitySelectionManager?.setProgrammaticHover(null);
        }
        this.interactionStateManager?.setProgrammaticHoveredFeatureVisualScope(
            this.resolveFeatureVisualScope(featureId, resultObjectIds),
        );
    }

    /**
     * 设置设计树特征节点选中的局部视觉范围。
     *
     * @param featureId 特征 ID；传入 null 时清空。
     * @param resultObjectIds 该特征当前关联的结果体 objectId 列表。
     */
    public setFeatureVisualSelectionScope(
        featureId: string | null,
        resultObjectIds: readonly string[] = [],
    ): void {
        const scope = this.resolveFeatureVisualScope(featureId, resultObjectIds);
        if (scope) {
            this.entitySelectionManager?.clearSelectionProgrammatically({
                intent: 'feature-tree-visual-scope',
            });
        }
        this.interactionStateManager?.setSelectedFeatureVisualScope(scope);
    }

    /**
     * 解析设计树特征节点视觉范围。
     *
     * @param featureId 特征 ID。
     * @param resultObjectIds 结果体 objectId 列表。
     * @returns 可被 InteractionStateManager 消费的视觉范围；无法解析时返回 null。
     */
    private resolveFeatureVisualScope(
        featureId: string | null,
        resultObjectIds: readonly string[],
    ): FeatureVisualScope | null {
        const normalizedFeatureId = featureId?.trim() ?? '';
        if (normalizedFeatureId.length <= 0) {
            return null;
        }

        const referencedMeshes = Array.from(new Set(resultObjectIds))
            .map((objectId) => objectId.trim())
            .filter((objectId) => objectId.length > 0)
            .map((objectId) => this.findSceneObjectById(objectId))
            .filter((object): object is FeatureResultMesh => Boolean(object) && isFeatureResultMesh(object));
        const contributionMeshes = referencedMeshes.filter((mesh) => (
            mesh.hasFeatureContribution(normalizedFeatureId)
        ));
        const resultMeshes = contributionMeshes.length > 0
            ? contributionMeshes
            : this.collectFeatureContributionMeshes(normalizedFeatureId);

        if (resultMeshes.length <= 0) {
            return null;
        }

        return {
            featureId: normalizedFeatureId,
            resultMeshes,
        };
    }

    /**
     * 从当前场景收集包含指定特征拓扑贡献的结果体。
     *
     * 设计说明：
     * 1. 相交合并后多个特征会贡献到同一个结果体，设计树 bodyRefs 可能因为投影刷新时序缺失；
     * 2. 运行时局部高亮的最终权威是 FeatureResultMesh.topologySnapshot 的 owningFeatureId；
     * 3. 这里只返回真实包含贡献的结果体，避免把合并实体整体作为某个特征高亮。
     *
     * @param featureId 特征 ID。
     * @returns 包含该特征贡献的结果体列表。
     */
    private collectFeatureContributionMeshes(featureId: string): FeatureResultMesh[] {
        const resultMeshes: FeatureResultMesh[] = [];
        this.scene.traverse((object) => {
            if (!isFeatureResultMesh(object)) {
                return;
            }
            if (object.hasFeatureContribution(featureId)) {
                resultMeshes.push(object);
            }
        });
        return resultMeshes;
    }
    //------ 线性零部件阵列节点包围盒移入 结束------

    /**
     * 以对象 ID 触发程序化选择。
     * @param objectId 目标对象 ID。
     * @param options 选择选项。
     */
    public selectEntityByObjectId(
        objectId: string,
        options: { interactionMeta?: SelectionInteractionMeta } = {},
    ): void {
        this.entitySelectionManager?.selectEntityByObjectId(objectId, options);
    }

    //------ 线性零部件阵列节点选中高亮 开始------
    /**
     * 以对象 ID 列表触发程序化多目标选择。
     *
     * @param objectIds 目标对象 ID 列表。
     * @param options 选择选项。
     */
    public selectEntitiesByObjectIds(
        objectIds: readonly string[],
        options: { interactionMeta?: SelectionInteractionMeta } = {},
    ): void {
        this.entitySelectionManager?.selectEntitiesByObjectIds(objectIds, options);
    }
    //------ 线性零部件阵列节点选中高亮 结束------

    /**
     * 构建导入模型选择摘要。
     * @param objectId 目标对象 ID。
     * @param options 摘要构建选项。
     * @returns 结构化选择摘要；未命中导入模型时返回 null。
     */
    public buildImportedSelectionSummary(
        objectId: string,
        options: { interactionMeta?: SelectionInteractionMeta } = {},
    ): ImportedModelSelectionSummary | null {
        return buildImportedModelSelectionSummary(this.scene, objectId, options.interactionMeta);
    }

    /**
     * 同步对象基础视觉状态。
     * @param object 目标对象。
     */
    public syncObjectBaseVisualState(object: THREE.Object3D): void {
        this.interactionStateManager?.syncObjectBaseVisualState(object);
    }

    /**
     * 设置 imported-model datum-slot hover。
     *
     * @param descriptor datum-slot 描述符；为空时清空 hover。
     */
    public setImportedModelDatumSlotHover(descriptor: GlbDatumSlotTargetDescriptor | null): void {
        this.importedModelSelectionHighlighter?.setDatumSlotHover(descriptor);
    }

    /**
     * 切换 imported-model datum-slot selected。
     *
     * @param descriptor datum-slot 描述符。
     */
    public toggleImportedModelDatumSlotSelection(descriptor: GlbDatumSlotTargetDescriptor): void {
        this.importedModelSelectionHighlighter?.toggleDatumSlotSelection(descriptor);
    }

    /**
     * 设置 imported-model datum-slot selected（非 toggle，供命令态设计树使用）。
     *
     * @param descriptor datum-slot 描述符；为空时清空 selected。
     */
    public setImportedModelDatumSlotSelected(descriptor: GlbDatumSlotTargetDescriptor | null): void {
        this.importedModelSelectionHighlighter?.setDatumSlotSelected(descriptor);
    }

    /**
     * 设置测量模式 datum-slot 选中 overlay（绿色平面线框 / 原点标记）。
     *
     * @param descriptor datum-slot 描述符；为空时清空测量 overlay。
     */
    public setMeasureDatumSlotSelected(descriptor: GlbDatumSlotTargetDescriptor | null): void {
        this.importedModelSelectionHighlighter?.setMeasureDatumSlotSelected(descriptor);
    }

    /**
     * 清空 imported-model datum-slot 高亮。
     */
    public clearImportedModelDatumSlotHighlight(): void {
        this.importedModelSelectionHighlighter?.clearDatumSlotState();
    }

    /**
     * 设置 imported-model 命令态实体选中高亮。
     *
     * @param payloads 需要持续高亮的实体选择事件快照。
     * @param options 同步选项；force 用于配合预览变换后强制刷新同一组拓扑高亮。
     */
    public setImportedModelCommandSelectedEntities(
        payloads: readonly EntitySelectedEventPayload[],
        options: { readonly force?: boolean } = {},
    ): void {
        const commandSelectedObjects = payloads.flatMap((payload) => [
            ...(payload.object ? [payload.object] : []),
            ...payload.selectedObjects,
        ]);
        /**
         * 命令态高亮可能引用按需创建的 FeatureFace/FeatureLine/FeaturePoint。
         * 这些对象不属于普通 selectedPicks，如果不先交给选择管理器保留，
         * 下一次普通选择同步会释放旧代理，导致 overlay 绑定到 parent=null 的对象。
         */
        this.entitySelectionManager?.setCommandSelectedTopologyProxyObjects(commandSelectedObjects);
        this.importedModelSelectionHighlighter?.setCommandSelectedEntities(payloads, options);
    }

    /**
     * 清空 imported-model 命令态实体选中高亮。
     */
    public clearImportedModelCommandSelectedEntities(): void {
        this.entitySelectionManager?.clearCommandSelectedTopologyProxyObjects();
        this.importedModelSelectionHighlighter?.clearCommandSelectedEntities();
    }

    /**
     * 移除对象约束标记。
     * @param object 目标对象。
     * @param constraintId 约束 ID。
     */
    public removeObjectConstraintMarker(object: THREE.Object3D, constraintId: number): void {
        this.interactionStateManager?.removeObjectConstraintMarker(object, constraintId);
    }

    /**
     * 获取圆角/倒角第一条实体快照。
     * @returns 第一条实体快照；未命中时返回 null。
     */
    public getSketchCornerFirstSelection(): unknown | null {
        return this.sketchCornerController?.getFirstSelectedEntitySnapshot() ?? null;
    }

    //------ 草图圆角实体面板快照 开始------
    /**
     * 获取草图圆角/倒角控制器快照。
     * @returns 草图圆角/倒角控制器快照；控制器未就绪时返回 null。
     */
    public getSketchCornerSnapshot(): SketchCornerControllerSnapshot | null {
        return this.sketchCornerController?.getSnapshot() ?? null;
    }

    /**
     * 监听草图圆角/倒角控制器快照变化。
     * @returns 草图圆角/倒角控制器快照事件流。
     */
    public observeSketchCornerSnapshot(): Observable<SketchCornerControllerSnapshot> {
        return this.sketchCornerController?.observeSnapshot() ?? EMPTY;
    }
    //------ 草图圆角实体面板快照 结束------

    /**
     * 获取圆角参数快照。
     * @returns 圆角参数快照；控制器未就绪时返回 null。
     */
    public getSketchCornerFilletOptions(): SketchFilletOptions | null {
        return this.sketchCornerController?.getFilletOptionsSnapshot() ?? null;
    }

    /**
     * 获取倒角参数快照。
     * @returns 倒角参数快照；控制器未就绪时返回 null。
     */
    public getSketchCornerChamferOptions(): SketchChamferOptions | null {
        return this.sketchCornerController?.getChamferOptionsSnapshot() ?? null;
    }

    /**
     * 更新圆角参数。
     * @param options 参数补丁。
     */
    public setSketchCornerFilletOptions(options: Partial<SketchFilletOptions>): void {
        this.sketchCornerController?.setFilletOptions(options);
    }

    /**
     * 判断当前是否存在待确认圆角预览。
     * @returns 存在待确认圆角预览返回 true。
     */
    public hasPendingSketchCornerFilletPreview(): boolean {
        return this.sketchCornerController?.hasPendingFilletPreview() ?? false;
    }

    /**
     * 确认当前待确认圆角预览并提交正式实体。
     * @returns 提交成功返回 true。
     */
    public confirmSketchCornerFilletPreview(): boolean {
        return this.sketchCornerController?.confirmPendingFilletPreview() ?? false;
    }

    /**
     * 取消当前待确认圆角预览。
     */
    public cancelSketchCornerFilletPreview(): void {
        this.sketchCornerController?.cancelPendingFilletPreview();
    }

    /**
     * 更新倒角参数。
     * @param options 参数补丁。
     */
    public setSketchCornerChamferOptions(options: Partial<SketchChamferOptions>): void {
        this.sketchCornerController?.setChamferOptions(options);
    }

    //------ 草图倒角命令内撤消 开始------
    /**
     * 判断当前草图倒角命令是否存在可撤消步骤。
     * @returns 存在可撤消倒角步骤时返回 true。
     */
    public hasSketchCornerChamferUndo(): boolean {
        return this.sketchCornerController?.hasChamferCommandUndo() ?? false;
    }

    /**
     * 撤消当前草图倒角命令内最近一次倒角。
     * @returns 撤消成功返回 true。
     */
    public undoLastSketchCornerChamfer(): boolean {
        return this.sketchCornerController?.undoLastChamferCommandStep() ?? false;
    }
    //------ 草图倒角命令内撤消 结束------

    /**
     * 重置圆角/倒角选择。
     */
    public resetSketchCornerSelection(): void {
        this.sketchCornerController?.resetSelection();
    }

    /**
     * 开启圆角/倒角模式。
     * @param mode 模式。
     */
    public startSketchCornerMode(mode: string): void {
        this.sketchCornerController?.startMode(mode as never);
    }

    /**
     * 清理圆角/倒角模式。
     * @param keepMode 是否保留模式值。
     */
    public clearSketchCornerMode(keepMode = false): void {
        this.sketchCornerController?.clearMode(keepMode);
    }

    /**
     * 获取最近一次 Trim 选中实体快照。
     * @returns Trim 实体快照；未命中时返回 null。
     */
    public getLastSelectedTrimEntitySnapshot(): unknown | null {
        return this.sketchTrimController?.getLastSelectedEntitySnapshot() ?? null;
    }

    /**
     * 获取当前可显示的 Power Trim 剪裁标记快照。
     * @returns 可显示的剪裁标记；Shift、延伸记录或无标记时返回 null，不影响撤销能力。
     */
    public getLastPowerTrimMarkerSnapshot(): unknown | null {
        return this.sketchTrimController?.getLastPowerTrimMarkerSnapshot() ?? null;
    }

    /**
     * 撤销最近一次 Power Trim。
     * @returns 是否撤销成功。
     */
    public undoLastPowerTrim(): boolean {
        return this.sketchTrimController?.undoLastPowerTrim() ?? false;
    }

    /**
     * 刷新当前选择的视觉和事件状态。
     *
     * 恰好选中一个对象时会通过程序化选择入口重新提交该对象，从而重建选中命中并派发
     * 选择事件；其它选择数量只请求重绘。调用前必须等待 `whenReady()`。
     */
    public refreshSelection(): void {
        const selectedObjects = this.entitySelectionManager.getSelectedObjects();
        if (selectedObjects.length === 1) {
            this.entitySelectionManager.selectEntityProgrammatically(selectedObjects[0]);
            return;
        }
        this.requestRender();
    }

    /**
     * 按绘制方向归一化圆弧起止角度。
     *
     * 逆时针圆弧会把终止角调整到不小于起始角的同周期位置，顺时针圆弧会把终止角
     * 调整到不大于起始角的同周期位置；输入和返回角度单位均为弧度。
     *
     * @param startAngle 起始角度，单位为弧度。
     * @param endAngle 终止角度，单位为弧度。
     * @param clockwise 是否按顺时针方向计算角度范围。
     * @returns 归一化后的起始角、终止角和非负角度范围。
     */
    public normalizeArcAngles(startAngle: number, endAngle: number, clockwise: boolean) {
        return normalizeArcAngles(startAngle, endAngle, clockwise);
    }

    /**
     * 撤销当前草图绘制工具最近提交的一个绘制点或步骤。
     *
     * 成功时底层绘制管理器会清理在途预览并请求重绘；没有活动工具、工具不支持逐点撤销
     * 或已经没有可撤销步骤时返回 `false`。调用前必须等待 `whenReady()`。
     *
     * @returns 是否成功撤销一个绘制步骤。
     */
    public undoSketchDrawingPoint(): boolean {
        return this.sketchDrawingManager.undoLastDrawingPoint();
    }

    /**
     * 启动 Trim 模式。
     * @param mode 模式。
     * @param options 参数补丁。
     */
    public startSketchTrimMode(mode: string, options?: Record<string, unknown>): void {
        this.setMode(ViewerMode.SketchTrim);
        this.sketchTrimController?.startMode(mode as never, options as never);
    }

    //------ 智能尺寸 开始------
    /**
     * 创建智能尺寸命令宿主端口。
     *
     * 命令和求解回写同步完成；普通画面交给 Viewer 帧调度器统一结算，避免引用高亮、
     * 预演和预览在同一输入中各自提交一次完整管线。
     *
     * @returns 命令宿主端口。
     */
    private createSmartDimensionCommandHost(): SmartDimensionCommandHost {
        return {
            resolveContext: () => {
                const group = this.sketchGroupManager?.activeSketchGroup ?? null;
                const repository = this.sketchGroupManager?.dimensionRepository ?? null;

                if (!group || !repository) {
                    return null;
                }

                return {
                    sketchId: group.persistentId,
                    referencePlane: group.referencePlane,
                    repository,
                    collectRuntimePoints: () => {
                        const points: SketchPointObject[] = [];

                        group.traverse((object) => {
                            if (isSketchPointObject(object)) {
                                points.push(object);
                            }
                        });

                        return points;
                    },
                    collectSeedObjects: () => [...group.getSketchObjects().values()],
                };
            },
            requestRender: () => {
                this.requestRender();
            },
            onNotice: (message, level) => {
                this.drawingNoticeCallback?.(message, level);
            },
            onExit: () => {
                this.sketchSmartDimensionPreviewRenderer.hide();
                if (this.getMode() === ViewerMode.SketchDimension) {
                    this.setMode(ViewerMode.Sketch);
                }
                // 退出后的最终隐藏态必须有重绘机会，不能依赖下一次鼠标移动。
                this.requestRender();
            },
        };
    }

    /**
     * 启动智能尺寸命令模式（阶段 8：H/V 固定方向、路径长度、chain/baseline 序列共用入口）。
     *
     * 标准智能尺寸启动时消费当前草图预选集合（2018+ Auto Insert 语义：预选实体
     * 由命令直接接管为已选集合）；命令变体不消费预选。
     *
     * @param options 启动选项（continuous 默认 true：完成一个尺寸后继续收集）。
     */
    public startSketchSmartDimensionMode(options?: SmartDimensionStartOptions): void {
        // 预选集合必须在 setMode 清空选择之前捕获。
        const isStandardSmart = !options?.forcedOrientation && !options?.pathLength && !options?.sequence;
        const preselected = isStandardSmart ? this.collectPreselectedSketchDimensionTargets() : [];

        this.setMode(ViewerMode.SketchDimension);
        if (this.scene) {
            this.sketchSmartDimensionPreviewRenderer.attach(this.scene);
        }
        this.sketchSmartDimensionController?.start(options);
        preselected.forEach((object) => {
            this.sketchSmartDimensionController?.handleEntityClick(object);
        });
    }

    /** 收集活动草图内的预选实体（Auto Insert 预选消费）。 */
    private collectPreselectedSketchDimensionTargets(): THREE.Object3D[] {
        const activeGroup = this.sketchGroupManager?.activeSketchGroup ?? null;
        const selected = this.entitySelectionManager?.getSelectedObjects() ?? [];

        if (!activeGroup || selected.length === 0) {
            return [];
        }

        return selected.filter((object) => {
            let parent: THREE.Object3D | null = object.parent;

            while (parent) {
                if (parent === activeGroup) {
                    return true;
                }
                parent = parent.parent;
            }

            return false;
        });
    }

    /**
     * 取消智能尺寸命令模式（不产生历史）。
     */
    public cancelSketchSmartDimensionMode(): void {
        this.sketchSmartDimensionController?.cancelCommand();
        this.sketchSmartDimensionPreviewRenderer.hide();
        this.requestRender();
    }

    /**
     * 智能尺寸命令 Esc 入口（选择阶段撤回上一次选择，空选择退出）。
     */
    public escapeSketchSmartDimension(): void {
        this.sketchSmartDimensionController?.handleEscape();
        this.requestRender();
    }

    /**
     * 注入尺寸历史事务端口（dimension + solver patch 一笔 Model 事务）。
     *
     * @param port 事务端口；传 null 清除。
     */
    public configureSmartDimensionTransactionPort(port: SketchDimensionHistoryTransactionPort | null): void {
        this.sketchSmartDimensionController?.configureTransactionPort(port);
        // 已有尺寸编辑（改值/拖动标注/删除/角色切换）复用同一端口。
        this.sketchDimensionTransactionPort = port;
    }

    /**
     * 批量创建草图尺寸（父事务托管，不提交任何事务）。
     *
     * 说明：
     * 1. 等距实体等复合命令需要在同一笔历史事务内原子提交几何与尺寸，
     *    通过本入口落库并取回领域补丁，再经
     *    `SketchRuntimeHistoryTransactionInput.resolveExtraPatches` 并入父事务；
     * 2. 本入口不做求解预演、不产生 solver 补丁——driving 尺寸由后续参数求解
     *    经尺寸补充端口自动编入求解岛；
     * 3. 调用方必须在父事务失败时调用返回结果的 `rollback` 对称回滚仓库。
     *
     * @param input 尺寸创建输入。
     * @returns 落库快照、领域补丁与对称回滚函数。
     */
    public createSketchDimensionInBatch(input: CreateSketchDimensionInput): SketchDimensionInBatchCreationResult {
        const repository = this.sketchGroupManager?.dimensionRepository;

        if (!repository) {
            throw new Error('当前不存在活动草图的尺寸仓库，无法创建草图尺寸。');
        }

        const service = new SketchDimensionApplicationService({
            repository,
            identityService: new SketchDimensionIdentityService(),
            validationService: new SketchDimensionValidationService(),
        });

        return service.createDimensionInBatch(input);
    }

    /**
     * 注入尺寸编辑 UI 处理器（双击 Modify、选中变化）。
     *
     * @param handlers 处理器集合；传 null 清除。
     */
    public configureSketchDimensionUiHandlers(handlers: {
        readonly onRequestModify?: (payload: {
            readonly sketchId: string;
            readonly dimensionId: string;
            readonly screen: {readonly x: number; readonly y: number};
        }) => void;
        readonly onSelectionChange?: (payload: {readonly sketchId: string; readonly dimensionId: string} | null) => void;
    } | null): void {
        this.sketchDimensionUiHandlers = handlers;
    }

    /** 按持久 ID 解析草图组。 */
    private resolveSketchGroupByPersistentId(sketchId: string): SketchGroup | null {
        return this.sketchGroupManager?.getAllGroups()
            .find((candidate) => candidate.persistentId === sketchId) ?? null;
    }

    /**
     * 计算尺寸箭头的世界长度（屏幕定长：目标像素 × 每像素世界长）。
     *
     * 正交相机下每像素世界长 = 视锥高 / 缩放 / 视口像素高；非正交相机退回
     * 固定几何尺寸（返回 undefined 由渲染器兜底）。
     *
     * @returns 箭头世界长度（米）；无法换算时返回 undefined。
     */
    private resolveSketchDimensionArrowWorldLength(): number | undefined {
        const camera = this.camera;
        const container = this.container;

        if (!container || !(camera instanceof THREE.OrthographicCamera)) {
            return undefined;
        }

        const viewportHeight = container.clientHeight;

        if (viewportHeight <= 0) {
            return undefined;
        }

        const frustumHeight = Math.abs(camera.top - camera.bottom) / (camera.zoom || 1);

        return (frustumHeight / viewportHeight) * SKETCH_DIMENSION_ARROW_PIXELS;
    }

    /** 构造尺寸求解预演的草图上下文（改值/附着切换/Instant2D 共用）。 */
    private createSketchDimensionRehearsalContext(
        sketchId: string,
        group: SketchGroup,
        repository: SketchDimensionRepository,
    ): SmartDimensionSketchContext {
        return {
            sketchId,
            referencePlane: group.referencePlane,
            repository,
            collectRuntimePoints: () => {
                const points: SketchPointObject[] = [];

                group.traverse((object) => {
                    if (isSketchPointObject(object)) {
                        points.push(object);
                    }
                });

                return points;
            },
            collectSeedObjects: () => [...group.getSketchObjects().values()],
            /**
             * 尺寸驱动写回是静默写点（不触发对象派生通知也不进拖拽提交链），
             * 特征重建协调器只认 `sketch-definition-committed`——不补这一笔，
             * 改尺寸后拉伸凸台要等退出草图才重建（拖拽路径由 drag-finished 提交，故正常）。
             */
            notifyGeometryCommitted: () => {
                group.markDefinitionDirty({reason: 'dimension-driven'});
                group.commitDefinitionChanges({reason: 'dimension-driven'});
            },
        };
    }

    /**
     * 使用统一参数方程系统预演并写回一组点目标。
     *
     * 该入口不先修改运行时对象，也不调用对象式 BFS；全部目标点会编译成同一条瞬态
     * `point-targets` 意图，与活动关系、driving 尺寸和外部边界在同一个 island 中整体求解。
     * 求解失败或存在未迁移关系时保持当前几何，不回退旧求解器。
     *
     * @param targets 目标点到世界坐标的映射。
     * @param intentId 本次交互内稳定的意图 ID。
     * @param dragSession
     * @returns 是否完成参数解写回。
     */
    public previewSketchParametricPointTargets(
        targets: ReadonlyMap<SketchPointObject, THREE.Vector3>,
        intentId: string,
        dragSession?: SketchParametricDragSession,
    ): boolean {
        const sketchId = this.sketchGroupManager?.activeSketchGroup?.persistentId;
        if (!sketchId) {
            return false;
        }
        return this.previewSketchParametricPointTargetsForSketch(
            sketchId,
            targets,
            intentId,
            dragSession,
        );
    }

    /**
     * 对指定草图使用统一参数方程系统预演并写回一组点目标。
     *
     * 该入口供非编辑态草图的缩放控制复用；目标草图按稳定 ID 解析，所有点目标、
     * 关系、driving 尺寸和实体结构方程仍在同一参数岛内求解。
     *
     * @param sketchId 目标草图稳定 ID。
     * @param targets 目标点到世界坐标的映射。
     * @param intentId 本次交互内稳定的意图 ID。
     * @returns 是否完成参数解写回。
     */
    public previewSketchParametricPointTargetsForSketch(
        sketchId: string,
        targets: ReadonlyMap<SketchPointObject, THREE.Vector3>,
        intentId: string,
        dragSession?: SketchParametricDragSession,
    ): boolean {
        if (targets.size === 0) {
            return false;
        }
        const localTargets = Array.from(targets, ([point, targetWorldPosition]) => {
            const targetLocal = point.referencePlane.worldToLocal(targetWorldPosition);

            return {
                pointId: point.uuid,
                localPosition: [targetLocal.x, targetLocal.y] as const,
            };
        });
        const priorityAliases = new Map<string, string>();

        targets.forEach((_targetWorldPosition, point) => {
            priorityAliases.set(point.persistentId, point.uuid);
        });

        const operationIntents = [{
            kind: 'point-targets' as const,
            intentId,
            targets: localTargets,
        }];

        if (dragSession) {
            return dragSession.preview(operationIntents).applied;
        }

        return this.solveActiveSketchParametricState({
            targetSketchId: sketchId,
            operationIntents,
            priorityAliases,
            reason: 'parametric-point-targets',
        }).applied;
    }

    /**
     * 开始 FreeCAD 式活动草图参数拖拽会话。
     *
     * pointerdown 时捕获不可变 solver island；之后每帧仍经过 Viewer 的版本守卫、
     * 尺寸过滤和原子写回链路，但纯数据求解始终从该基线开始。
     *
     * @param seedObjects 拖拽直接对象；用于把基线与每帧求解范围收窄到真实关系连通岛。
     * @returns 拖拽会话；当前无活动草图或无法采集基线时返回 null。
     */
    public beginActiveSketchParametricDragSession(
        seedObjects: readonly unknown[] = [],
    ): SketchParametricDragSession | null {
        const group = this.sketchGroupManager?.activeSketchGroup ?? null;
        const repository = this.sketchGroupManager?.dimensionRepository ?? null;

        if (!group || !repository) {
            return null;
        }

        const sketchId = group.persistentId;
        const dragSeedObjects = seedObjects.length > 0
            ? [...seedObjects]
            : [...group.getSketchObjects().values()];
        const supplementProvider = createSketchDimensionSolverSupplementProvider(repository);
        const baselineIsland = this.sketchParametricSolveCoordinator.captureBaseline({
            seedObjects: dragSeedObjects,
            supplementProvider,
        });

        if (!baselineIsland) {
            return null;
        }

        return new SketchParametricDragSession({
            baselineIsland,
            getDefinitionRevision: () => {
                const currentGroup = this.sketchGroupManager?.getGroupById(sketchId) ?? null;

                // Undo/Redo 可能以相同持久 ID、相同版本号替换运行时组，旧基线不得写入新实例。
                return currentGroup === group ? group.definitionRevision : null;
            },
            solve: input => this.solveActiveSketchParametricState({
                targetSketchId: sketchId,
                seedObjects: dragSeedObjects,
                operationIntents: input.operationIntents,
                baselineIsland: input.baselineIsland,
                priorityAliases: new Map(),
                reason: 'parametric-drag-preview',
            }),
        });
    }

    /**
     * 抑制或恢复一条草图几何关系，并在同一历史事务中重解完整活动草图。
     *
     * @param targetEntity 持有该关系的任一草图对象。
     * @param relationId 关系当前会话数字 ID。
     * @param suppressed 目标抑制状态。
     * @returns 状态和参数解完成提交时返回 true；关系无效或求解拒绝时返回 false。
     */
    public async setSketchRelationSuppressed(
        targetEntity: unknown,
        relationId: number,
        suppressed: boolean,
    ): Promise<boolean> {
        return this.sketchRelationStateService.setSuppressed({
            targetEntity,
            relationId,
            suppressed,
            beginHistorySession: input => this.beginActiveSketchRuntimeHistorySession(input),
            solveParametricRelations: () => (
                this.sketchRelationCreationService.solveActiveSketchParametricRelations()
            ),
            requestRender: () => this.requestRender(),
        });
    }

    /**
     * 对 PropertyManager 已写入的候选属性或声明式属性目标执行统一参数求解。
     *
     * 本入口只完成求解和 dirty 标记；草图定义提交与候选失败回滚由
     * `SketchPropertyEditService` 和属性历史基线统一处理。
     *
     * @param target 当前被编辑的草图对象。
     * @param operationIntents 本次属性输入必须优先保持的瞬态参数目标。
     * @returns 候选属性是否通过完整活动草图参数求解。
     */
    public solveActiveSketchParametricProperties(
        target: THREE.Object3D,
        operationIntents: readonly SketchParametricOperationIntent[] = [],
    ): boolean {
        if (!isSketchObject(target)) {
            return false;
        }
        const priorityAliases = new Map([[target.persistentId, target.uuid]]);
        /**
         * 直线长度是端点坐标的派生量，声明式长度目标必须拥有调整端点的自由度。
         * 若同时把同一条线登记为权威实体，参数构建器会固定其全部端点，使长度偏好
         * 只能保留旧值；其它已经通过 setter 写入候选几何的属性仍沿用权威实体语义。
         */
        const hasDeclarativeLineLengthTarget = operationIntents.some((intent) => (
            (intent.kind === 'entity-line-length-target' && intent.entityId === target.uuid)
            || intent.temporaryConstraints?.some((constraint) => (
                constraint.kind === 'line-length'
                && constraint.entityId === target.uuid
            )) === true
        ));
        return this.solveActiveSketchParametricState({
            seedObjects: [target],
            operationIntents,
            ...(isSketchPointObject(target)
                ? {authoritativePointIds: [target.uuid]}
                : hasDeclarativeLineLengthTarget
                    ? {}
                    : {authoritativeEntityIds: [target.uuid]}),
            priorityAliases,
            reason: 'parametric-property-edit',
        }).applied;
    }

    /**
     * 对圆角/倒角已经挂载完成的候选实体、关系与现有尺寸执行统一参数求解。
     *
     * 本入口只求解并标记草图定义 dirty；命令控制器负责候选失败回滚、自动尺寸创建
     * 和整组历史提交，禁止在角点上下文内逐对象调用 `solveConstraints()`。
     *
     * @returns 圆角/倒角候选是否通过完整活动草图参数求解。
     */
    public solveActiveSketchParametricCornerOperation(): boolean {
        return this.solveActiveSketchParametricState({
            operationIntents: [],
            priorityAliases: new Map(),
            reason: 'parametric-corner-operation',
        }).applied;
    }

    /**
     * 对裁剪完成后的候选拓扑、剩余关系与已迁移尺寸执行统一参数求解。
     *
     * 裁剪控制器负责在调用前完成关系/尺寸拓扑迁移，并在返回 false 时通过当前
     * 运行时历史事务恢复 before；本入口不回退对象级传播。
     *
     * @returns 裁剪候选是否通过完整活动草图参数求解。
     */
    public solveActiveSketchParametricTrimOperation(): boolean {
        return this.solveActiveSketchParametricState({
            operationIntents: [],
            priorityAliases: new Map(),
            reason: 'parametric-trim-operation',
        }).applied;
    }

    /**
     * 对指定草图的外部引用候选状态执行统一参数求解。
     *
     * 引用创建、实时重建和时间线 checkpoint 重建都通过该稳定端口按目标草图 ID
     * 装配完整参数岛；调用方负责定义提交、引用状态降级及事务补偿。
     *
     * @param sketchId 目标草图稳定 ID。
     * @returns 外部引用候选状态是否通过完整参数求解。
     */
    public solveSketchParametricReferences(sketchId: string): boolean {
        return this.solveActiveSketchParametricState({
            targetSketchId: sketchId,
            operationIntents: [],
            priorityAliases: new Map(),
            reason: 'parametric-reference-rebuild',
            // 引用求解只产生候选几何；成功批次由创建协调器或目标变化提交器统一推进 definition revision。
            markDefinitionDirty: false,
        }).applied;
    }

    /**
     * 校验并收口持久化加载后的完整草图参数状态。
     *
     * 加载文档记录的是已求解位形，因此该入口不创建历史、也不把草图标记为用户编辑；
     * 但关系、driving 尺寸和结构方程仍必须在同一参数岛中可收敛，否则加载方应撤销
     * 已挂载的草图并显式报错，禁止留下部分恢复状态。
     *
     * @param sketchId 目标草图稳定标识。
     * @returns 持久化状态是否通过统一参数方程验证并完成原子写回。
     */
    public validateLoadedSketchParametricState(sketchId: string): boolean {
        return this.solveActiveSketchParametricState({
            targetSketchId: sketchId,
            operationIntents: [],
            priorityAliases: new Map(),
            reason: 'parametric-document-load',
            markDefinitionDirty: false,
        }).applied;
    }

    /**
     * 装配并求解活动草图的完整参数 island。
     *
     * @param input 本次瞬态操作输入、身份别名与 dirty 原因。
     * @returns 包含写回状态、求解快照、回滚入口和失败诊断的协调器结果。
     */
    public solveActiveSketchParametricState(
        input: SketchActiveParametricStateSolveInput,
    ): SketchParametricSolveCoordinatorResult {
        const group = input.targetSketchId
            ? this.resolveSketchGroupByPersistentId(input.targetSketchId)
            : this.sketchGroupManager?.activeSketchGroup ?? null;
        const repository = this.sketchGroupManager?.dimensionRepository ?? null;

        if (!group || !repository) {
            return {
                applied: false,
                beforeIsland: null,
                solveResult: null,
                rollback: () => undefined,
                message: !group
                    ? '目标草图不存在，无法执行统一参数求解。'
                    : '草图尺寸仓库尚未就绪，无法执行统一参数求解。',
            };
        }

        const sketchId = group.persistentId;
        const solveRevision = (this.sketchParametricSolveRevisionBySketchId.get(sketchId) ?? 0) + 1;

        // 新请求先推进版本；任何较早求解在写回前都会观察到自身已过期。
        this.sketchParametricSolveRevisionBySketchId.set(sketchId, solveRevision);
        const dimensions = repository.listBySketch(sketchId).map((dimension) => dimension.toSnapshot());
        const rehearsalContext = this.createSketchDimensionRehearsalContext(sketchId, group, repository);
        const allSketchObjects = [...rehearsalContext.collectSeedObjects()];
        const seedObjects = input.seedObjects && input.seedObjects.length > 0
            ? [...input.seedObjects]
            : allSketchObjects;
        const outcome = this.sketchParametricSolveCoordinator.solveAndApply({
            sketchId,
            seedObjects,
            dimensions,
            supplementProvider: createSketchDimensionSolverSupplementProvider(repository),
            ...(input.operationIntents.length > 0 ? {operationIntents: input.operationIntents} : {}),
            ...(input.authoritativePointIds && input.authoritativePointIds.length > 0
                ? {authoritativePointIds: input.authoritativePointIds}
                : {}),
            ...(input.authoritativeEntityIds && input.authoritativeEntityIds.length > 0
                ? {authoritativeEntityIds: input.authoritativeEntityIds}
                : {}),
            ...(input.baselineIsland ? {baselineIsland: input.baselineIsland} : {}),
            priorityAliases: input.priorityAliases,
            isResultCurrent: () => (
                this.sketchParametricSolveRevisionBySketchId.get(sketchId) === solveRevision
            ),
            requestRender: () => this.requestRender(),
        });

        if (!outcome.applied) {
            // 保留协调器提供的具体诊断，禁止在 Viewer 边界退化为无上下文布尔值。
            return outcome;
        }

        if (input.markDefinitionDirty !== false) {
            group.markDefinitionDirty({reason: input.reason});
        }
        this.remeasureSketchDrivenDimensions(sketchId);
        this.sketchDimensionAnnotationRenderer.refreshSketch(sketchId);
        return outcome;
    }

    /**
     * 使用统一参数方程系统预演并写回普通单点拖拽。
     *
     * @param point 被拖拽点。
     * @param targetWorldPosition 鼠标对应的目标世界坐标。
     * @returns 是否完成参数解写回。
     */
    public previewSketchParametricPointDrag(
        point: SketchPointObject,
        targetWorldPosition: THREE.Vector3,
        dragSession?: SketchParametricDragSession,
    ): boolean {
        return this.previewSketchParametricPointTargets(
            new Map([[point, targetWorldPosition]]),
            `point-drag:${point.uuid}`,
            dragSession,
        );
    }

    /**
     * 按草图运行时几何重测全部 driven 尺寸显示值并写回仓库。
     *
     * 语义：driven 值是几何派生量（缓存），不入历史——几何被 Undo/Redo 恢复后
     * 定义提交/裁决链路会再次经过本方法自愈。曾经重测只活在 solver 驱动路径里，
     * 普通拖拽（无 driving 尺寸参与）后 driven 尺寸一直显示旧值，且跨形状引用
     * 分属两个 island 时连 solver 同岛重测也覆盖不到。
     *
     * @param sketchId 草图持久 ID。
     * @returns 是否有尺寸的测量值实际发生变化。
     */
    private remeasureSketchDrivenDimensions(sketchId: string): boolean {
        const repository = this.sketchGroupManager?.dimensionRepository ?? null;
        const sketchGroup = this.resolveSketchGroupByPersistentId(sketchId);

        if (!repository || !sketchGroup) {
            return false;
        }

        let changed = false;

        repository.listBySketch(sketchId).forEach((dimension) => {
            const snapshot = dimension.toSnapshot();

            if (snapshot.role !== 'driven' || snapshot.suppressed) {
                return;
            }

            const measured = measureSketchDimensionFromSource(
                snapshot,
                createSketchRuntimeMeasureSource(snapshot, sketchGroup),
            );

            if (measured === null || (snapshot.measuredValue !== null
                && Math.abs(snapshot.measuredValue - measured) <= 1e-12)) {
                return;
            }
            repository.mutate(sketchId, snapshot.dimensionId, (target) => {
                target.updateMeasuredValue(measured);
            });
            changed = true;
        });

        return changed;
    }

    /** 解析尺寸所属草图持久 ID（标签命中只带 dimensionId）。 */
    private resolveSketchIdOfDimension(dimensionId: string): string | null {
        const repository = this.sketchGroupManager?.dimensionRepository;

        if (!repository) {
            return null;
        }

        for (const group of this.sketchGroupManager?.getAllGroups() ?? []) {
            if (repository.get(group.persistentId, dimensionId)) {
                return group.persistentId;
            }
        }

        return null;
    }

    /** 把屏幕坐标换算为指定草图参考面局部坐标。 */
    private clientToSketchLocal(sketchId: string, client: {readonly x: number; readonly y: number}):
        | readonly [number, number]
        | null {
        const referencePlane = this.resolveSketchGroupByPersistentId(sketchId)?.referencePlane ?? null;
        const container = this.container;
        const camera = this.camera;

        if (!referencePlane || !container || !camera) {
            return null;
        }

        const rect = container.getBoundingClientRect();
        const ndc = new THREE.Vector2(
            ((client.x - rect.left) / rect.width) * 2 - 1,
            -((client.y - rect.top) / rect.height) * 2 + 1,
        );
        const raycaster = new THREE.Raycaster();

        raycaster.setFromCamera(ndc, camera);

        const hit = new THREE.Vector3();

        if (!raycaster.ray.intersectPlane(referencePlane, hit)) {
            return null;
        }

        const local = referencePlane.worldToLocal(hit);

        return [local.x, local.y];
    }

    /** 构造已有尺寸编辑用的应用服务（复用注入的事务端口）。 */
    private createSketchDimensionEditService(): SketchDimensionApplicationService {
        const repository = this.sketchGroupManager?.dimensionRepository;
        const transactionPort = this.sketchDimensionTransactionPort;

        if (!repository || !transactionPort) {
            throw new Error('尺寸编辑依赖仓库与历史事务端口，当前未就绪。');
        }

        return new SketchDimensionApplicationService({
            repository,
            identityService: new SketchDimensionIdentityService(),
            validationService: new SketchDimensionValidationService(),
            transactionPort,
        });
    }

    /**
     * 解析被拖尺寸所在链组的全部成员快照。
     *
     * @param sketchId 草图持久 ID；null 直接返回 null。
     * @param dimensionId 被拖尺寸持久 ID。
     * @returns 链组成员（含被拖条）；非链成员或组内孤条时返回 null。
     */
    private resolveChainDimensionMembers(
        sketchId: string | null,
        dimensionId: string,
    ): readonly SketchDimensionSnapshot[] | null {
        const repository = this.sketchGroupManager?.dimensionRepository;

        if (!sketchId || !repository) {
            return null;
        }

        const chainGroupId = repository.get(sketchId, dimensionId)?.toSnapshot().branch.chainGroupId;

        if (!chainGroupId) {
            return null;
        }

        const members = repository.listBySketch(sketchId)
            .map((dimension) => dimension.toSnapshot())
            .filter((snapshot) => snapshot.branch.chainGroupId === chainGroupId);

        return members.length >= 2 ? members : null;
    }

    /**
     * 计算链组拖动预览的整组标注位置。
     *
     * @param sketchId 草图持久 ID。
     * @param dimensionId 被拖尺寸持久 ID。
     * @param local 被拖标注目标位置（草图局部）。
     * @returns 尺寸 ID → 预览位置；非链成员时返回 null（走单条预览）。
     */
    private resolveChainAlignedLabelPreview(
        sketchId: string,
        dimensionId: string,
        local: readonly [number, number],
    ): ReadonlyMap<string, readonly [number, number]> | null {
        const members = this.resolveChainDimensionMembers(sketchId, dimensionId);

        return members ? resolveChainAlignedLabelPositions(members, dimensionId, local) : null;
    }

    /** 创建标签命中回调；选择、预览和事务入口同步执行，画面由统一帧循环合并提交。 */
    private createSketchDimensionHitCallbacks() {
        return {
            onSelect: (dimensionId: string) => {
                const sketchId = this.resolveSketchIdOfDimension(dimensionId);

                this.sketchDimensionAnnotationRenderer.setSelectedDimension(dimensionId);
                this.requestRender();
                this.sketchDimensionUiHandlers?.onSelectionChange?.(
                    sketchId ? {sketchId, dimensionId} : null,
                );
            },
            onRequestModify: (dimensionId: string, client: {readonly x: number; readonly y: number}) => {
                const sketchId = this.resolveSketchIdOfDimension(dimensionId);

                if (sketchId) {
                    this.sketchDimensionUiHandlers?.onRequestModify?.({sketchId, dimensionId, screen: client});
                }
            },
            onDragMove: (dimensionId: string, client: {readonly x: number; readonly y: number}) => {
                const sketchId = this.resolveSketchIdOfDimension(dimensionId);
                const local = sketchId ? this.clientToSketchLocal(sketchId, client) : null;

                if (!local || !sketchId) {
                    return;
                }

                /** 链尺寸组：拖动过程整组共线联动预览（SW——移动任意一条全组跟着动）。 */
                const chainPositions = this.resolveChainAlignedLabelPreview(sketchId, dimensionId, local);

                if (chainPositions) {
                    chainPositions.forEach((position, memberId) => {
                        this.sketchDimensionAnnotationRenderer.previewLabelPosition(memberId, position);
                    });
                } else {
                    this.sketchDimensionAnnotationRenderer.previewLabelPosition(dimensionId, local);
                }
                this.requestRender();
            },
            onDragCommit: (dimensionId: string, client: {readonly x: number; readonly y: number}) => {
                const sketchId = this.resolveSketchIdOfDimension(dimensionId);
                const local = sketchId ? this.clientToSketchLocal(sketchId, client) : null;

                // 预览覆盖先清除（链组逐条清）；提交成功后仓库事件驱动正式刷新，失败则回到原位置。
                this.resolveChainDimensionMembers(sketchId, dimensionId)?.forEach((member) => {
                    this.sketchDimensionAnnotationRenderer.previewLabelPosition(member.dimensionId, null);
                });
                this.sketchDimensionAnnotationRenderer.previewLabelPosition(dimensionId, null);
                if (!sketchId || !local) {
                    this.requestRender();
                    return;
                }
                void this.createSketchDimensionEditService()
                    .updateDisplay(sketchId, dimensionId, {labelPosition: local})
                    .catch(() => {
                        this.sketchDimensionAnnotationRenderer.refreshSketch(sketchId);
                    })
                    .finally(() => {
                        this.requestRender();
                    });
            },
            onValueHandleDragMove: (dimensionId: string, client: {readonly x: number; readonly y: number}) => {
                this.sketchDimensionInstant2dController ??= this.createSketchDimensionInstant2dController();
                this.sketchDimensionInstant2dController.move(dimensionId, client);
            },
            onValueHandleDragCommit: (dimensionId: string, client: {readonly x: number; readonly y: number}) => {
                void this.sketchDimensionInstant2dController?.commit(dimensionId, client).catch((error) => {
                    console.error('[Viewer] Instant2D 尺寸提交失败', {dimensionId, error});
                    this.drawingNoticeCallback?.(error instanceof Error ? error.message : '尺寸提交失败，已恢复预览。', 'warning');
                });
            },
            onValueHandleDragCancel: (dimensionId: string) => {
                this.sketchDimensionInstant2dController?.cancel(dimensionId);
            },
        };
    }

    /**
     * 装配 Instant2D 的求解与渲染端口；输入、会话和提交边界由独立控制器拥有。
     * @returns 不另建 RAF 的拖动控制器。
     */
    private createSketchDimensionInstant2dController(): SketchDimensionInstant2dController {
        return new SketchDimensionInstant2dController({
            scheduler: this.frameScheduler,
            resolveTarget: (dimensionId) => {
                const sketchId = this.resolveSketchIdOfDimension(dimensionId);
                const group = sketchId ? this.resolveSketchGroupByPersistentId(sketchId) : null;
                const snapshot = sketchId ? this.sketchGroupManager?.dimensionRepository?.get(sketchId, dimensionId)?.toSnapshot() : null;
                return sketchId && group && snapshot?.role === 'driving'
                    ? {sketchId, dimensionId, owner: group, nominalValue: snapshot.parameter.nominalValue}
                    : null;
            },
            rehearse: (target, client) => {
                const repository = this.sketchGroupManager?.dimensionRepository;
                const group = this.resolveSketchGroupByPersistentId(target.sketchId);
                const snapshot = repository?.get(target.sketchId, target.dimensionId)?.toSnapshot();
                if (!repository || !group || group !== target.owner || !snapshot) return {kind: 'blocked'};
                const local = this.clientToSketchLocal(target.sketchId, client);
                const anchors = this.sketchDimensionAnnotationRenderer.resolveAnchorsOf(target.sketchId, target.dimensionId);
                const value = local && anchors ? resolveSketchDimensionInstant2dValue(snapshot, anchors, local) : null;
                if (value === null) return {kind: 'blocked'};
                const dimensions = repository.listBySketch(target.sketchId).map((dimension) => {
                    const candidate = dimension.toSnapshot();
                    return candidate.dimensionId === target.dimensionId
                        ? {...candidate, parameter: {...candidate.parameter, nominalValue: value}}
                        : candidate;
                });
                // 沿用已有起点预演、稳定侧偏好和正式提交服务，不在输入合帧层复制求解规则。
                const result = rehearseSketchDimensionDrivingSolve({
                    context: this.createSketchDimensionRehearsalContext(target.sketchId, group, repository),
                    dimensions,
                    stabilityDimensionId: target.dimensionId,
                    requestRender: () => this.requestRender(),
                });
                return result.kind === 'ready' ? {kind: 'ready', value, session: result.session} : {kind: 'blocked'};
            },
            previewValue: (dimensionId, value) => this.sketchDimensionAnnotationRenderer.previewNominalValue(dimensionId, value),
            refreshSketch: (sketchId) => this.sketchDimensionAnnotationRenderer.refreshSketch(sketchId),
            commitValue: (sketchId, dimensionId, value) => this.commitSketchDimensionValue(sketchId, dimensionId, value),
            requestRender: () => this.requestRender(),
        });
    }

    /**
     * 按屏幕坐标选中尺寸线/界线（ARC-003：非标签区域的 WebGL 命中）。
     *
     * 命中阈值取"4 个屏幕像素对应的草图平面世界长度"（相邻像素射线与平面交点
     * 距离换算，正交/透视相机通吃）。命中后走与标签单击相同的选中链。
     *
     * @param client 屏幕坐标。
     * @returns 是否命中并选中了尺寸。
     */
    public selectSketchDimensionAtClient(client: {readonly x: number; readonly y: number}): boolean {
        const activeGroup = this.sketchGroupManager?.activeSketchGroup ?? null;
        const camera = this.camera;
        const container = this.container;

        if (!activeGroup || !camera || !container) {
            return false;
        }

        const sketchId = activeGroup.persistentId;
        const origin = this.clientToSketchLocal(sketchId, client);
        const offset = this.clientToSketchLocal(sketchId, {x: client.x + 4, y: client.y});

        if (!origin || !offset) {
            return false;
        }

        const threshold = Math.max(Math.hypot(offset[0] - origin[0], offset[1] - origin[1]), 1e-9);
        const rect = container.getBoundingClientRect();
        const ndc = new THREE.Vector2(
            ((client.x - rect.left) / rect.width) * 2 - 1,
            -((client.y - rect.top) / rect.height) * 2 + 1,
        );
        const raycaster = new THREE.Raycaster();

        raycaster.setFromCamera(ndc, camera);
        raycaster.params.Line = {threshold};

        const dimensionId = this.sketchDimensionAnnotationRenderer.hitTestLines(raycaster);

        if (!dimensionId) {
            return false;
        }

        const dimensionSketchId = this.resolveSketchIdOfDimension(dimensionId);

        this.sketchDimensionAnnotationRenderer.setSelectedDimension(dimensionId);
        this.requestRender();
        this.sketchDimensionUiHandlers?.onSelectionChange?.(
            dimensionSketchId ? {sketchId: dimensionSketchId, dimensionId} : null,
        );

        return true;
    }

    /**
     * 按框选矩形同步当前活动草图的尺寸选择集合。
     *
     * @param input 框选矩形与方向语义。
     * @returns 本次命中的尺寸持久 ID 列表。
     */
    public selectSketchDimensionsByScreenRect(input: {
        readonly selectionRect: {
            readonly minX: number;
            readonly minY: number;
            readonly maxX: number;
            readonly maxY: number;
        };
        readonly isCrossing: boolean;
    }): readonly string[] {
        const camera = this.camera;
        const container = this.container;
        if (!camera || !container || !this.sketchGroupManager?.activeSketchGroup) {
            return [];
        }

        const dimensionIds = this.sketchDimensionAnnotationRenderer.collectDimensionsByScreenRect(
            input.selectionRect,
            input.isCrossing,
            camera,
            container.getBoundingClientRect(),
        );

        this.sketchDimensionAnnotationRenderer.setSelectedDimensions(dimensionIds);
        const primaryDimensionId = dimensionIds.length === 1 ? dimensionIds[0] : null;
        const primarySketchId = primaryDimensionId
            ? this.resolveSketchIdOfDimension(primaryDimensionId)
            : null;

        // 单尺寸且没有实体时继续显示尺寸属性；混合/多尺寸选择交给普通多选面板。
        this.sketchDimensionUiHandlers?.onSelectionChange?.(
            primaryDimensionId && primarySketchId && this.getSelectedObjects().length === 0
                ? {sketchId: primarySketchId, dimensionId: primaryDimensionId}
                : null,
        );
        this.requestRender();
        return dimensionIds;
    }

    /**
     * 以程序方式替换尺寸选择集合。
     *
     * @param dimensionIds 尺寸持久 ID 列表。
     */
    public selectSketchDimensionsProgrammatically(dimensionIds: readonly string[]): void {
        this.sketchDimensionAnnotationRenderer.setSelectedDimensions(dimensionIds);
        const primaryDimensionId = dimensionIds.length === 1 ? dimensionIds[0] : null;
        const primarySketchId = primaryDimensionId
            ? this.resolveSketchIdOfDimension(primaryDimensionId)
            : null;

        this.sketchDimensionUiHandlers?.onSelectionChange?.(
            primaryDimensionId && primarySketchId && this.getSelectedObjects().length === 0
                ? {sketchId: primarySketchId, dimensionId: primaryDimensionId}
                : null,
        );
        this.requestRender();
    }

    /**
     * 更新已有尺寸显示状态（精度/公差/单位覆盖/显示样式/覆盖值等，一个 Model step）。
     *
     * 显示字段浅合并且不触碰求解参数（DISP-001/002）；渲染由仓库事件驱动的标注渲染器跟随。
     *
     * @param sketchId 草图持久 ID。
     * @param dimensionId 尺寸持久 ID。
     * @param display 待合并显示字段。
     */
    public async updateSketchDimensionDisplay(
        sketchId: string,
        dimensionId: string,
        display: SketchDimensionDisplayState,
    ): Promise<void> {
        await this.createSketchDimensionEditService().updateDisplay(sketchId, dimensionId, display);
        this.requestRender();
    }

    /**
     * 修改已有尺寸数值（双击 Modify 确认；driving 尺寸预演求解并驱动几何）。
     *
     * @param sketchId 草图持久 ID。
     * @param dimensionId 尺寸持久 ID。
     * @param nominalValue 新名义值（核心单位：米/弧度）。
     * @param options 可选项：`flipDirectionSign` 翻转分支方向符号（负值输入语义，VAL-003）。
     */
    /**
     * 按尺寸仓库当前状态对账草图的尺寸关系视图。
     *
     * 说明：草图组尚未进入场景（文档加载早期）时跳过，待 `restorePuppySketchExtension`
     * 收尾时统一补投影。
     *
     * @param sketchId 草图持久 ID。
     */
    private synchronizeSketchDimensionRelations(sketchId: string): void {
        const repository = this.sketchGroupManager?.dimensionRepository;
        const sketchGroup = this.resolveSketchGroupByPersistentId(sketchId);

        if (!repository || !sketchGroup) {
            return;
        }
        this.sketchDimensionRelationSynchronizer.synchronize(sketchId, sketchGroup, repository);
    }

    /**
     * 提示"尺寸未驱动几何"。
     *
     * 说明：island 含参数内核暂不支持的关系/尺寸时，尺寸值仍会落库但几何不动。
     * 这种情况必须显式告知用户——否则表现为"改了数值图形毫无反应"，
     * 而后续拖动端点又会被关系视图纠正、突然跳变，用户完全无从判断。
     *
     * @param reason 未驱动原因；缺省表示本次求解正常，不提示。
     */
    private noticeSketchDimensionUndriven(reason: string | undefined): void {
        if (!reason) {
            return;
        }
        this.drawingNoticeCallback?.(reason, 'warning');
    }

    /**
     * 修改现有草图尺寸的标称值，并在同一历史事务中提交相关几何和分支状态。
     *
     * 驱动尺寸会先以尺寸全集进行预演求解；预演通过后才提交尺寸仓库、刷新尺寸标注，
     * 必要时通知草图定义变更并触发依赖特征重建。参考尺寸只更新尺寸记录和标注。
     *
     * @param sketchId 尺寸所属草图的持久 ID。
     * @param dimensionId 要修改的尺寸持久 ID。
     * @param nominalValue 新的标称值；单位与目标尺寸类型的既有契约一致。
     * @param options 修改选项。
     * @param options.flipDirectionSign 为 `true` 时在同一事务中翻转尺寸分支方向符号。
     * @returns 尺寸事务、标注刷新及必要的几何提交全部完成后的 Promise。
     * @throws 草图或尺寸不存在、预演求解被阻止、历史事务端口未注入或底层提交失败时抛出错误。
     */
    public async modifySketchDimensionValue(
        sketchId: string,
        dimensionId: string,
        nominalValue: number,
        options?: {readonly flipDirectionSign?: boolean},
    ): Promise<void> {
        // 外部改值是新的命令边界，不能继承临时预览或随后重放旧手势。
        this.sketchDimensionInstant2dController?.reset();
        await this.commitSketchDimensionValue(sketchId, dimensionId, nominalValue, options);
    }

    /**
     * 通过原尺寸服务提交一次已获得交互写回权的改值。
     * 外部入口先重置旧手势；Instant2D 已独占写回，不能在此取消排队的下一笔。
     * @param sketchId 草图持久 ID。
     * @param dimensionId 尺寸持久 ID。
     * @param nominalValue 本笔冻结的最终值。
     * @param options 同一事务内的方向翻转选项。
     */
    private async commitSketchDimensionValue(
        sketchId: string,
        dimensionId: string,
        nominalValue: number,
        options?: {readonly flipDirectionSign?: boolean},
    ): Promise<void> {
        const repository = this.sketchGroupManager?.dimensionRepository;
        const group = this.resolveSketchGroupByPersistentId(sketchId);

        if (!repository || !group) {
            throw new Error(`草图 ${sketchId} 不在场景中，无法修改尺寸。`);
        }

        const target = repository.assert(sketchId, dimensionId).toSnapshot();
        /** 负值输入方向翻转：与新值同一笔事务写入的分支状态（不翻转时为 undefined）。 */
        const nextBranch = options?.flipDirectionSign === true
            ? {...target.branch, directionSign: (target.branch.directionSign === -1 ? 1 : -1) as 1 | -1}
            : undefined;
        let solverSession: SketchDimensionSolverSession | null = null;

        if (target.role === 'driving') {
            /** 预演：把目标尺寸替换为新值后的全集求解（与创建确认同一入口语义）。 */
            const rehearsalDimensions = repository.listBySketch(sketchId).map((dimension) => {
                const snapshot = dimension.toSnapshot();

                return snapshot.dimensionId === dimensionId
                    ? {
                        ...snapshot,
                        parameter: {...snapshot.parameter, nominalValue},
                        branch: nextBranch ?? snapshot.branch,
                    }
                    : snapshot;
            });
            const rehearsal = rehearseSketchDimensionDrivingSolve({
                context: this.createSketchDimensionRehearsalContext(sketchId, group, repository),
                dimensions: rehearsalDimensions,
                stabilityDimensionId: dimensionId,
                requestRender: () => this.requestRender(),
            });

            if (rehearsal.kind === 'blocked') {
                throw new Error(rehearsal.message);
            }
            solverSession = rehearsal.session;
            this.noticeSketchDimensionUndriven(rehearsal.undrivenReason);
        }

        const transactionPort = this.sketchDimensionTransactionPort;

        if (!transactionPort) {
            throw new Error('尺寸历史事务端口未注入，无法提交修改。');
        }

        const serviceOptions: ConstructorParameters<typeof SketchDimensionApplicationService>[0] = {
            repository,
            identityService: new SketchDimensionIdentityService(),
            validationService: new SketchDimensionValidationService(),
            transactionPort,
        };

        if (solverSession) {
            const readySession = solverSession;

            (serviceOptions as {solverSessionPort?: unknown}).solverSessionPort = {
                begin: () => readySession,
            };
        }

        await new SketchDimensionApplicationService(serviceOptions).changeNominalValue(sketchId, dimensionId, nominalValue, nextBranch);
        // 驱动几何经静默写回（不触发 definitionChangeHub），标注需显式重读新几何重排布局。
        this.sketchDimensionAnnotationRenderer.refreshSketch(sketchId);
        // 几何确实被驱动过才补定义提交，纯改值失败/无 driving 会话不触发特征重建。
        if (solverSession) {
            this.createSketchDimensionRehearsalContext(sketchId, group, repository).notifyGeometryCommitted?.();
            // 等距链派生重算必须在求解器 commit 写回之后执行：
            // 闭合组合图形的预演解只驱动链首实体，若在尺寸仓库 updated 事件（commit 前）
            // 整链写回会被随后的 solver commit 覆盖；这里在 session.commit 完成后重算，
            // 保证闭合/开口链都按新名义距离整链更新。
            this.offsetChainDerivedSyncService?.synchronizeAfterDimensionChange(sketchId);
            // 等距样条/抛物线派生重算与等距链同一条时序纪律：
            // 仓库 updated 事件在 session.commit 之前触发，事件回调里的派生写回
            // 会被随后的 island 点写回覆盖（表现为改值后闪一下又回到旧位置）；
            // 这里在 commit 完成后按新名义距离再统一重算一次，保证样条偏移对
            // 最终落在新距离上（重复重算幂等，方向分支以尺寸 directionSign 为准）。
            this.curveOffsetDerivedSyncService?.synchronizeAfterDimensionChange(sketchId);
        }
        this.requestRender();
    }

    /**
     * 修改已有尺寸圆系引用附着条件（Center/Min/Max，一个 Model step；ARC-002）。
     *
     * driving 尺寸附着变更即测量方程变更：与改值同款预演求解并驱动几何。
     *
     * @param sketchId 草图持久 ID。
     * @param dimensionId 尺寸持久 ID。
     * @param selectionOrder 目标引用选择顺序。
     * @param arcCondition 新附着条件。
     * @throws 尺寸仓库、草图组或历史事务端口未就绪，或驱动尺寸预演求解被阻止时抛出。
     */
    public async changeSketchDimensionArcCondition(
        sketchId: string,
        dimensionId: string,
        selectionOrder: number,
        arcCondition: 'center' | 'min' | 'max',
    ): Promise<void> {
        const repository = this.sketchGroupManager?.dimensionRepository;
        const group = this.resolveSketchGroupByPersistentId(sketchId);
        const transactionPort = this.sketchDimensionTransactionPort;

        if (!repository || !group || !transactionPort) {
            throw new Error('尺寸附着条件修改依赖仓库、草图组与事务端口，当前未就绪。');
        }

        const target = repository.assert(sketchId, dimensionId).toSnapshot();
        let solverSession: SketchDimensionSolverSession | null = null;

        if (target.role === 'driving') {
            const rehearsalDimensions = repository.listBySketch(sketchId).map((dimension) => {
                const snapshot = dimension.toSnapshot();

                if (snapshot.dimensionId !== dimensionId) {
                    return snapshot;
                }

                return {
                    ...snapshot,
                    references: snapshot.references.map((reference) => (
                        reference.selectionOrder === selectionOrder
                            ? {...reference, arcCondition}
                            : reference
                    )),
                };
            });
            const rehearsal = rehearseSketchDimensionDrivingSolve({
                context: this.createSketchDimensionRehearsalContext(sketchId, group, repository),
                dimensions: rehearsalDimensions,
                requestRender: () => this.requestRender(),
            });

            if (rehearsal.kind === 'blocked') {
                throw new Error(rehearsal.message);
            }
            solverSession = rehearsal.session;
            this.noticeSketchDimensionUndriven(rehearsal.undrivenReason);
        }

        const serviceOptions: ConstructorParameters<typeof SketchDimensionApplicationService>[0] = {
            repository,
            identityService: new SketchDimensionIdentityService(),
            validationService: new SketchDimensionValidationService(),
            transactionPort,
        };

        if (solverSession) {
            const readySession = solverSession;

            (serviceOptions as {solverSessionPort?: unknown}).solverSessionPort = {
                begin: () => readySession,
            };
        }

        await new SketchDimensionApplicationService(serviceOptions)
            .changeArcCondition(sketchId, dimensionId, selectionOrder, arcCondition);
        // 附着切换驱动几何后标注重读新几何（同改值路径的静默写回刷新纪律）。
        this.sketchDimensionAnnotationRenderer.refreshSketch(sketchId);
        if (solverSession) {
            this.createSketchDimensionRehearsalContext(sketchId, group, repository).notifyGeometryCommitted?.();
        }
        this.requestRender();
    }

    /**
     * 重新附着悬空尺寸（DANG-002 I 级：保留尺寸身份，替换引用，一个 Model step）。
     *
     * 说明：不自动重解几何（reattach 后的 driving 重解语义待实机金标）；
     * 标注布局由仓库事件驱动刷新。
     *
     * @param sketchId 草图持久 ID。
     * @param dimensionId 尺寸持久 ID。
     * @param references 新的有序稳定引用。
     */
    public async reattachSketchDimension(
        sketchId: string,
        dimensionId: string,
        references: readonly SketchDimensionReferenceRecord[],
    ): Promise<void> {
        await this.createSketchDimensionEditService().reattachDimension(sketchId, dimensionId, references);
        this.requestRender();
    }

    /**
     * 删除已有尺寸（一个 Model step）。
     *
     * @param sketchId 草图持久 ID。
     * @param dimensionId 尺寸持久 ID。
     */
    public async deleteSketchDimensionById(sketchId: string, dimensionId: string): Promise<void> {
        await this.createSketchDimensionEditService().deleteDimension(sketchId, dimensionId);
        this.sketchDimensionUiHandlers?.onSelectionChange?.(null);
        this.requestRender();
    }

    /**
     * 切换已有尺寸驱动角色（一个 Model step；driven→driving 的过定义判定属阶段 6+）。
     *
     * @param sketchId 草图持久 ID。
     * @param dimensionId 尺寸持久 ID。
     * @param role 目标角色。
     */
    public async changeSketchDimensionRole(
        sketchId: string,
        dimensionId: string,
        role: 'driving' | 'driven',
    ): Promise<void> {
        await this.createSketchDimensionEditService().changeRole(sketchId, dimensionId, role);
        this.requestRender();
    }

    /**
     * 读取当前选中的尺寸。
     *
     * @returns 选中尺寸的定位与快照；未选中返回 null。
     */
    public getSelectedSketchDimension(): {
        readonly sketchId: string;
        readonly dimensionId: string;
        readonly snapshot: SketchDimensionSnapshot;
    } | null {
        const dimensionId = this.sketchDimensionAnnotationRenderer.getSelectedDimension();
        const sketchId = dimensionId ? this.resolveSketchIdOfDimension(dimensionId) : null;
        const snapshot = dimensionId && sketchId
            ? this.sketchGroupManager?.dimensionRepository.get(sketchId, dimensionId)?.toSnapshot() ?? null
            : null;

        return dimensionId && sketchId && snapshot ? {sketchId, dimensionId, snapshot} : null;
    }

    /**
     * 读取当前全部选中尺寸。
     *
     * @returns 可解析到尺寸仓库快照的选择列表。
     */
    public getSelectedSketchDimensions(): Array<{
        readonly sketchId: string;
        readonly dimensionId: string;
        readonly snapshot: SketchDimensionSnapshot;
    }> {
        return this.sketchDimensionAnnotationRenderer.getSelectedDimensions()
            .map((dimensionId) => {
                const sketchId = this.resolveSketchIdOfDimension(dimensionId);
                const snapshot = sketchId
                    ? this.sketchGroupManager?.dimensionRepository.get(sketchId, dimensionId)?.toSnapshot() ?? null
                    : null;

                return sketchId && snapshot ? {sketchId, dimensionId, snapshot} : null;
            })
            .filter((selection): selection is {
                readonly sketchId: string;
                readonly dimensionId: string;
                readonly snapshot: SketchDimensionSnapshot;
            } => selection !== null);
    }

    /**
     * 清除尺寸选中态。
     */
    public clearSketchDimensionSelection(): void {
        this.sketchDimensionAnnotationRenderer.setSelectedDimension(null);
        this.sketchDimensionUiHandlers?.onSelectionChange?.(null);
        this.requestRender();
    }

    /**
     * 读取智能尺寸命令快照。
     *
     * @returns 命令快照；控制器未初始化时返回 null。
     */
    public getSmartDimensionSnapshot(): SmartDimensionCommandSnapshot | null {
        return this.sketchSmartDimensionController?.getSnapshot() ?? null;
    }

    /**
     * 订阅智能尺寸命令快照。
     *
     * @param listener 快照监听器。
     * @returns 取消订阅函数。
     */
    public observeSmartDimensionSnapshot(listener: SmartDimensionSnapshotListener): () => void {
        return this.sketchSmartDimensionController?.observe(listener) ?? (() => undefined);
    }

    /**
     * 确认智能尺寸数值（Modify 确认；核心单位：米/弧度）。
     *
     * @param nominalValue 名义值。
     */
    public async confirmSmartDimensionValue(nominalValue: number): Promise<void> {
        await this.sketchSmartDimensionController?.confirmValue(nominalValue);
        this.sketchSmartDimensionPreviewRenderer.hide();
        // 创建 driving 尺寸驱动几何后，标注重读新几何（静默写回不触发 definitionChangeHub）。
        this.sketchDimensionAnnotationRenderer.refreshSketch();
        this.requestRender();
    }

    /**
     * 过定义/无解决策：转 driven 创建。
     */
    public async convertSmartDimensionPendingToDriven(): Promise<void> {
        await this.sketchSmartDimensionController?.convertPendingToDriven();
        this.sketchSmartDimensionPreviewRenderer.hide();
        this.sketchDimensionAnnotationRenderer.refreshSketch();
        this.requestRender();
    }

    /**
     * 过定义/无解决策：取消当前尺寸创建。
     */
    public cancelSmartDimensionPending(): void {
        this.sketchSmartDimensionController?.cancelPendingDimension();
        this.requestRender();
    }

    /**
     * 按命令快照同步引用选中高亮。
     *
     * @param snapshot 命令快照。
     */
    private syncSmartDimensionSelectionHighlight(snapshot: SmartDimensionCommandSnapshot): void {
        const selectionManager = this.entitySelectionManager;

        if (!selectionManager) {
            return;
        }
        if (snapshot.state === 'inactive') {
            selectionManager.updateSelectionState([]);
            this.requestRender();
            return;
        }

        const runtimeIds = new Set(snapshot.picks.map((pick) => pick.runtimeId));
        const selectedObjects: THREE.Object3D[] = [];

        this.sketchGroupManager?.activeSketchGroup?.traverse((object) => {
            if (runtimeIds.has(object.uuid)) {
                selectedObjects.push(object);
            }
        });
        selectionManager.updateSelectionState(selectedObjects.map((object) => ({
            distance: 0,
            object,
            point: object.getWorldPosition(new THREE.Vector3()),
        })));
        this.requestRender();
    }

    /**
     * 同步智能尺寸预览显示（选择处理器指针移动/点击后调用）。
     *
     * 预览几何和显隐立即更新，所有退出分支共用一条普通帧请求；不在高亮与预览之间
     * 插入同步画面，也不让无候选、无参考面或命令结束后的隐藏态丢失最后一帧。
     *
     * @param pointerLocal 指针局部坐标；null 时隐藏预览。
     */
    public syncSmartDimensionPreview(pointerLocal: SmartDimensionVec2 | null): void {
        this.requestRender();
        const controller = this.sketchSmartDimensionController;
        const referencePlane = this.sketchGroupManager?.activeSketchGroup?.referencePlane ?? null;

        if (!controller || !controller.isActive() || !referencePlane) {
            this.sketchSmartDimensionPreviewRenderer.hide();
            return;
        }

        //------ 尺寸链专用预览 开始------
        /**
         * 尺寸链（Ordinate）：选基准后是"值为 0 的界线跟随鼠标"（SW——此时还没有
         * 第二点与横向箭头线）；凑对瞬间**不画**常规两点距离预览——序列自动提交,
         * 常规预览会闪现一根蓝色尺寸线与值然后消失（其它尺寸链路侵入的观感）。
         */
        if (controller.isOrdinateCommand()) {
            const datum = controller.resolveOrdinateDatumPreviewPoint();
            /** 0 界线不能自由移动：吸附到草图面绝对水平/竖直（SW），并锁定测量轴。 */
            const snappedPointer = datum && pointerLocal
                ? controller.resolveOrdinateGuideSnappedPointer(pointerLocal)
                : null;

            if (datum && snappedPointer) {
                // 正式灰色样式的 0 界线 + 端头圆圈（不是蓝色预览样式）。
                this.sketchSmartDimensionPreviewRenderer.updateOrdinateDatumGuide(
                    datum, snappedPointer, referencePlane,
                );
                return;
            }
            this.sketchSmartDimensionPreviewRenderer.hide();
            return;
        }
        //------ 尺寸链专用预览 结束------

        const snapshot = controller.getSnapshot();
        const anchor = snapshot.placementWorld
            ? referencePlane.worldToLocal(new THREE.Vector3(...snapshot.placementWorld))
            : null;
        const previewPointer: SmartDimensionVec2 | null = anchor
            ? [anchor.x, anchor.y]
            : pointerLocal;

        if (!snapshot.candidate || !previewPointer) {
            this.sketchSmartDimensionPreviewRenderer.hide();
            return;
        }

        this.sketchSmartDimensionPreviewRenderer.update(snapshot.candidate, referencePlane, previewPointer);
    }
    //------ 智能尺寸 结束------

    /**
     * 启动等距实体模式。
     *
     * 说明：
     * 1. 仅在命令激活时调用一次。后续参数变更应通过
     * {@link updateSketchOffsetOptions} 进行；
     * 2. 与 SolidWorks 一致：启动时自动吸收选择管理器中当前已选中的草图实体，
     *    并立即生成偏移预览（支持先选中实体再点等距实体）。
     *
     * @param options 偏移选项（距离、方向、双向等）。
     */
    public startSketchOffsetMode(options?: Record<string, unknown>): void {
        this.setMode(ViewerMode.SketchOffset);
        this.sketchOffsetController?.startMode(options as never);
    }

    /**
     * 更新等距实体选项（不重置已选实体）。
     *
     * 说明：
     * 1. 属性面板修改参数时调用，仅更新选项并刷新预览；
     * 2. 区别于 {@link startSketchOffsetMode}，不会清空已选实体列表。
     *
     * @param options 偏移选项补丁。
     */
    public updateSketchOffsetOptions(options?: Record<string, unknown>): void {
        this.sketchOffsetController?.updateOptions(options as never);
    }

    /**
     * 清理 等距实体 模式。
     */
    public clearSketchOffsetMode(): void {
        this.sketchOffsetController.clearPreviewSketchEntities();
        this.sketchOffsetController.clearMode();
        if (this.getMode() === ViewerMode.SketchOffset) {
            this.setMode(ViewerMode.Sketch);
        }
        // 清除所有的选中草图实体
        this.clearSelectionProgrammatically();
    }

    /**
     * 生成等距实体，并提交为模型历史事务。
     *
     * 说明：
     * 1. 通过 executeActiveSketchRuntimeHistoryTransaction 捕获活动草图的 before/after 整组快照；
     * 2. 仅当 generateOffsetSketchEntity 成功生成实体时才提交事务；
     * 3. 等距线段创建的平行线距尺寸通过 resolveExtraPatches 并入同一笔事务
     *    （HIST-001：dimension patch 与几何变更不可拆成两步），
     *    提交失败由 rollbackExtraState 对称回滚尺寸仓库；
     * 4. commandType 为 sketch.runtime.offset-entities，对齐其他草图运行时操作命名规范。
     */
    public generateEquidistantEntities() {
        try {
            this.executeActiveSketchRuntimeHistoryTransaction({
                label: '草图等距实体',
                commandType: 'sketch.runtime.offset-entities',
                work: () => this.sketchOffsetController.generateOffsetSketchEntity(),
                shouldCommit: (generated) => generated,
                resolveExtraPatches: () => this.sketchOffsetController.takePendingDimensionPatches(),
                rollbackExtraState: () => this.sketchOffsetController.rollbackPendingDimensions(),
            });
        } catch (error) {
            // work 抛错路径由本层兜底回滚尺寸写入；事务内部失败路径已调用 rollbackExtraState
            // （该方法幂等），此处重复调用无副作用
            this.sketchOffsetController.rollbackPendingDimensions();
            throw error;
        }
    }


    /**
     * 启动移动实体模式。
     *
     * @param options 启动选项。
     */
    public startSketchMoveEntitiesMode(options: SketchEntitiesTransformModeStartOptions = {}): void {
        this.setMode(ViewerMode.SketchMoveEntities);
        this.sketchMoveEntitiesController?.start(options.preselectedObjects ?? []);
    }

    /**
     * 启动复制实体模式。
     *
     * @param options 启动选项。
     */
    public startSketchCopyEntitiesMode(options: SketchEntitiesTransformModeStartOptions = {}): void {
        this.setMode(ViewerMode.SketchCopyEntities);
        this.sketchCopyEntitiesController?.start(options.preselectedObjects ?? []);
    }

    /**
     * 启动旋转实体模式。
     *
     * @param options 启动选项。
     */
    public startSketchRotateEntitiesMode(options: SketchEntitiesTransformModeStartOptions = {}): void {
        this.setMode(ViewerMode.SketchRotateEntities);
        this.sketchRotateEntitiesController?.start(options.preselectedObjects ?? []);
    }

    /**
     * 启动圆周草图阵列模式。
     *
     * @param options 启动选项。
     */
    public startSketchCircularPatternMode(options: SketchEntitiesTransformModeStartOptions = {}): void {
        this.setMode(ViewerMode.SketchCircularPattern);
        this.sketchCircularPatternController?.start(options.preselectedObjects ?? []);
    }

    /**
     * 获取圆周草图阵列控制器快照。
     *
     * @returns 控制器快照。
     */
    public getSketchCircularPatternSnapshot(): SketchCircularPatternControllerSnapshot | null {
        return this.sketchCircularPatternController?.getSnapshot() ?? null;
    }

    /**
     * 监听圆周草图阵列控制器快照。
     *
     * @returns 快照事件流。
     */
    public observeSketchCircularPatternSnapshot(): Observable<SketchCircularPatternControllerSnapshot> {
        return this.sketchCircularPatternController?.observeSnapshot() ?? EMPTY;
    }

    /**
     * 设置圆周草图阵列选择角色。
     *
     * @param role 选择角色。
     */
    public setSketchCircularPatternSelectionRole(role: SketchCircularPatternSelectionRole): void {
        this.sketchCircularPatternController?.setActiveSelectionRole(role);
    }

    /**
     * 更新圆周草图阵列参数。
     *
     * @param patch 参数补丁。
     */
    public updateSketchCircularPatternParameters(patch: Partial<SketchCircularPatternParameters>): void {
        this.sketchCircularPatternController?.updateParameters(patch);
    }

    /**
     * 删除一个圆周草图阵列源实体选择项。
     *
     * @param entityId 草图对象运行时 ID。
     */
    public removeSketchCircularPatternSelection(entityId: string): void {
        this.sketchCircularPatternController?.removeSelectedEntity(entityId);
    }

    /**
     * 清空圆周草图阵列源实体选择。
     */
    public clearSketchCircularPatternSelections(): void {
        this.sketchCircularPatternController?.clearSelectedEntities();
    }

    /**
     * 恢复一个已跳过的圆周草图阵列实例。
     *
     * @param instanceIndex 稳定实例索引。
     */
    public restoreSketchCircularPatternSkippedInstance(instanceIndex: number): void {
        this.sketchCircularPatternController?.restoreSkippedInstance(instanceIndex);
    }

    /**
     * 确认创建圆周草图阵列。
     *
     * @returns 是否成功。
     */
    public confirmSketchCircularPattern(): boolean {
        return this.sketchCircularPatternController?.confirm() ?? false;
    }

    /**
     * 取消圆周草图阵列。
     */
    public cancelSketchCircularPattern(): void {
        this.sketchCircularPatternController?.cancel();
    }

    /**
     * 清理圆周草图阵列模式。
     */
    public clearSketchCircularPatternMode(): void {
        this.sketchCircularPatternController?.cancel();
        if (this.getMode() === ViewerMode.SketchCircularPattern) {
            this.setMode(ViewerMode.Sketch);
        }
    }

    /**
     * 启动镜向实体模式。
     *
     * @param options 启动选项。
     * @returns 当前确实位于活动草图编辑上下文时返回 true。
     * @throws 控制器启动或失败清理异常时，在恢复原 Viewer 模式后原样抛出。
     */
    public startSketchMirrorEntitiesMode(options: SketchEntitiesTransformModeStartOptions = {}): boolean {
        const currentMode = this.getMode();
        if (
            (currentMode !== ViewerMode.Sketch && currentMode !== ViewerMode.SketchDrawing)
            || !this.sketchGroupManager?.activeSketchGroup
            || !this.sketchMirrorEntitiesController
        ) {
            return false;
        }
        try {
            this.setMode(ViewerMode.SketchMirrorEntities);
            this.sketchMirrorEntitiesController.start(options.preselectedObjects ?? []);
        } catch (error) {
            // mode 与控制器是一笔启动事务；控制器半启动时必须先回收预览，再恢复原编辑态。
            try {
                this.sketchMirrorEntitiesController.clear();
            } finally {
                this.setMode(currentMode);
            }
            throw error;
        }
        return true;
    }

    /**
     * 获取移动实体快照。
     * @returns 控制器快照。
     */
    public getSketchMoveEntitiesSnapshot(): SketchEntitiesTransformControllerSnapshot | null {
        return this.sketchMoveEntitiesController?.getSnapshot() ?? null;
    }

    /**
     * 监听移动实体快照变化。
     * @returns 快照事件流。
     */
    public observeSketchMoveEntitiesSnapshot(): Observable<SketchEntitiesTransformControllerSnapshot> {
        return this.sketchMoveEntitiesController?.observeSnapshot() ?? EMPTY;
    }

    /**
     * 获取复制实体快照。
     * @returns 控制器快照。
     */
    public getSketchCopyEntitiesSnapshot(): SketchEntitiesTransformControllerSnapshot | null {
        return this.sketchCopyEntitiesController?.getSnapshot() ?? null;
    }

    /**
     * 监听复制实体快照变化。
     * @returns 快照事件流。
     */
    public observeSketchCopyEntitiesSnapshot(): Observable<SketchEntitiesTransformControllerSnapshot> {
        return this.sketchCopyEntitiesController?.observeSnapshot() ?? EMPTY;
    }

    /**
     * 获取旋转实体快照。
     * @returns 控制器快照。
     */
    public getSketchRotateEntitiesSnapshot(): SketchEntitiesTransformControllerSnapshot | null {
        return this.sketchRotateEntitiesController?.getSnapshot() ?? null;
    }

    /**
     * 监听旋转实体快照变化。
     * @returns 快照事件流。
     */
    public observeSketchRotateEntitiesSnapshot(): Observable<SketchEntitiesTransformControllerSnapshot> {
        return this.sketchRotateEntitiesController?.observeSnapshot() ?? EMPTY;
    }

    /**
     * 获取镜向实体快照。
     * @returns 控制器快照。
     */
    public getSketchMirrorEntitiesSnapshot(): SketchEntitiesTransformControllerSnapshot | null {
        return this.sketchMirrorEntitiesController?.getSnapshot() ?? null;
    }

    /**
     * 监听镜向实体快照变化。
     * @returns 快照事件流。
     */
    public observeSketchMirrorEntitiesSnapshot(): Observable<SketchEntitiesTransformControllerSnapshot> {
        return this.sketchMirrorEntitiesController?.observeSnapshot() ?? EMPTY;
    }

    /**
     * 设置移动实体输入模式。
     * @param mode 输入模式。
     */
    public setSketchMoveEntitiesInputMode(mode: SketchEntitiesTransformInputMode): void {
        this.sketchMoveEntitiesController?.setInputMode(mode);
    }

    //------ 移动实体保留几何关系 开始------
    /**
     * 设置移动实体几何关系保留模式。
     * @param mode 几何关系保留模式。
     */
    public setSketchMoveEntitiesRelationRetentionMode(mode: SketchEntitiesTransformRelationRetentionMode): void {
        this.sketchMoveEntitiesController?.setRelationRetentionMode(mode);
    }
    //------ 移动实体保留几何关系 结束------

    /**
     * 设置复制实体输入模式。
     * @param mode 输入模式。
     */
    public setSketchCopyEntitiesInputMode(mode: SketchEntitiesTransformInputMode): void {
        this.sketchCopyEntitiesController?.setInputMode(mode);
    }

    /**
     * 设置移动实体选择角色。
     * @param role 选择角色。
     */
    public setSketchMoveEntitiesSelectionRole(role: SketchEntitiesTransformSelectionRole): void {
        this.sketchMoveEntitiesController?.setActiveSelectionRole(role);
    }

    /**
     * 设置复制实体选择角色。
     * @param role 选择角色。
     */
    public setSketchCopyEntitiesSelectionRole(role: SketchEntitiesTransformSelectionRole): void {
        this.sketchCopyEntitiesController?.setActiveSelectionRole(role);
    }

    /**
     * 设置旋转实体选择角色。
     * @param role 选择角色。
     */
    public setSketchRotateEntitiesSelectionRole(role: SketchEntitiesTransformSelectionRole): void {
        this.sketchRotateEntitiesController?.setActiveSelectionRole(role);
    }

    /**
     * 设置镜向实体选择角色。
     * @param role 选择角色。
     * @throws 角色不属于镜向实体命令支持范围时原样抛出。
     */
    public setSketchMirrorEntitiesSelectionRole(role: SketchEntitiesTransformSelectionRole): void {
        this.sketchMirrorEntitiesController?.setActiveSelectionRole(role);
    }

    /**
     * 设置镜向实体复制选项。
     * @param enabled 是否创建镜向副本。
     * @throws 旧预览回滚或新预览创建失败时原样抛出。
     */
    public setSketchMirrorEntitiesCopyEnabled(enabled: boolean): void {
        this.sketchMirrorEntitiesController?.setCopyEnabled(enabled);
    }

    /**
     * 设置移动实体目标坐标。
     * @param axis 坐标轴。
     * @param value 目标值。
     */
    public setSketchMoveEntitiesTargetCoordinate(axis: 'x' | 'y', value: number | null): void {
        this.sketchMoveEntitiesController?.setTargetCoordinate(axis, value);
    }

    /**
     * 设置复制实体目标坐标。
     * @param axis 坐标轴。
     * @param value 目标值。
     */
    public setSketchCopyEntitiesTargetCoordinate(axis: 'x' | 'y', value: number | null): void {
        this.sketchCopyEntitiesController?.setTargetCoordinate(axis, value);
    }

    /**
     * 设置旋转实体角度。
     * @param value 角度值。
     */
    public setSketchRotateEntitiesAngleDeg(value: number | null): void {
        this.sketchRotateEntitiesController?.setAngleDeg(value);
    }

    /**
     * 删除移动实体选中项。
     * @param entityId 实体 ID。
     */
    public removeSketchMoveEntitiesSelection(entityId: string): void {
        this.sketchMoveEntitiesController?.removeSelectedEntity(entityId);
    }

    /**
     * 删除复制实体选中项。
     * @param entityId 实体 ID。
     */
    public removeSketchCopyEntitiesSelection(entityId: string): void {
        this.sketchCopyEntitiesController?.removeSelectedEntity(entityId);
    }

    /**
     * 删除旋转实体选中项。
     * @param entityId 实体 ID。
     */
    public removeSketchRotateEntitiesSelection(entityId: string): void {
        this.sketchRotateEntitiesController?.removeSelectedEntity(entityId);
    }

    /**
     * 删除镜向实体选中项。
     * @param entityId 实体或镜向轴运行时 ID。
     */
    public removeSketchMirrorEntitiesSelection(entityId: string): void {
        this.sketchMirrorEntitiesController?.removeSelectedEntity(entityId);
    }

    /** 清除镜向轴。 */
    public clearSketchMirrorEntitiesAxis(): void {
        this.sketchMirrorEntitiesController?.clearMirrorAxis();
    }

    /**
     * 确认移动实体。
     * @returns 是否成功。
     */
    public confirmSketchMoveEntities(): boolean {
        return this.sketchMoveEntitiesController?.confirm() ?? false;
    }

    /**
     * 确认复制实体。
     * @returns 是否成功。
     */
    public confirmSketchCopyEntities(): boolean {
        return this.sketchCopyEntitiesController?.confirm() ?? false;
    }

    /**
     * 确认旋转实体。
     * @returns 是否成功。
     */
    public confirmSketchRotateEntities(): boolean {
        return this.sketchRotateEntitiesController?.confirm() ?? false;
    }

    /**
     * 确认镜向实体。
     * @returns 是否成功。
     * @throws 镜向定义写回、关系迁移、历史提交或失败回滚异常时原样抛出。
     */
    public confirmSketchMirrorEntities(): boolean {
        return this.sketchMirrorEntitiesController?.confirm() ?? false;
    }

    /**
     * 取消移动实体。
     */
    public cancelSketchMoveEntities(): void {
        this.sketchMoveEntitiesController?.cancel();
    }

    /**
     * 取消复制实体。
     */
    public cancelSketchCopyEntities(): void {
        this.sketchCopyEntitiesController?.cancel();
    }

    /**
     * 取消旋转实体。
     */
    public cancelSketchRotateEntities(): void {
        this.sketchRotateEntitiesController?.cancel();
    }

    /** 取消镜向实体。 */
    public cancelSketchMirrorEntities(): void {
        try {
            this.sketchMirrorEntitiesController?.cancel();
        } finally {
            if (this.getMode() === ViewerMode.SketchMirrorEntities) {
                this.setMode(ViewerMode.Sketch);
            }
        }
    }

    /**
     * 清理移动实体模式。
     */
    public clearSketchMoveEntitiesMode(): void {
        this.sketchMoveEntitiesController?.clear();
        if (this.getMode() === ViewerMode.SketchMoveEntities) {
            this.setMode(ViewerMode.Sketch);
        }
    }

    /**
     * 清理复制实体模式。
     */
    public clearSketchCopyEntitiesMode(): void {
        this.sketchCopyEntitiesController?.clear();
        if (this.getMode() === ViewerMode.SketchCopyEntities) {
            this.setMode(ViewerMode.Sketch);
        }
    }

    /**
     * 清理旋转实体模式。
     */
    public clearSketchRotateEntitiesMode(): void {
        this.sketchRotateEntitiesController?.clear();
        if (this.getMode() === ViewerMode.SketchRotateEntities) {
            this.setMode(ViewerMode.Sketch);
        }
    }

    /** 清理镜向实体模式。 */
    public clearSketchMirrorEntitiesMode(): void {
        try {
            this.sketchMirrorEntitiesController?.clear();
        } finally {
            if (this.getMode() === ViewerMode.SketchMirrorEntities) {
                this.setMode(ViewerMode.Sketch);
            }
        }
    }

    /**
     * 重复上一次移动实体。
     * @returns 是否成功。
     */
    public repeatSketchMoveEntities(): boolean {
        return this.sketchMoveEntitiesController?.repeat() ?? false;
    }

    /**
     * 重复上一次复制实体。
     * @returns 是否成功。
     */
    public repeatSketchCopyEntities(): boolean {
        return this.sketchCopyEntitiesController?.repeat() ?? false;
    }

    /**
     * 重复上一次旋转实体。
     * @returns 是否成功。
     */
    public repeatSketchRotateEntities(): boolean {
        return this.sketchRotateEntitiesController?.repeat() ?? false;
    }

    /**
     * 判断当前 Trim 模式是否激活。
     * @returns 是否激活。
     */
    public isSketchTrimModeActive(): boolean {
        return this.sketchTrimController?.isModeActive() ?? false;
    }

    /**
     * 更新 Trim 模式。
     * @param mode 模式。
     */
    public setSketchTrimMode(mode: string): void {
        this.setMode(ViewerMode.SketchTrim);
        this.sketchTrimController?.setMode(mode as never);
    }

    /**
     * 更新 Trim 选项。
     * @param options 选项补丁。
     */
    public setSketchTrimOptions(options: Record<string, unknown>): void {
        this.sketchTrimController?.setOptions(options as never);
    }

    /**
     * 清理 Trim 模式。
     * @param keepMode 是否保留模式值。
     */
    public clearSketchTrimMode(keepMode = false): void {
        this.sketchTrimController?.clearMode(keepMode);
        if (this.getMode() === ViewerMode.SketchTrim) {
            this.setMode(ViewerMode.Sketch);
        }
    }

    /**
     * 立即执行特征预览。
     *
     * @param input 预览输入。
     * @returns 预览结果。
     */
    public async executeFeaturePreview(input: ViewerFeaturePreviewInput): Promise<ViewerFeaturePreviewResult> {
        return this.executeFeaturePreviewNow(input);
    }

    /**
     * 设置旋转切除保留实体片段高亮网格。
     *
     * @param fragmentMeshes 片段网格。
     */
    public setRevolveCutKeepBodyPreviewFragments(fragmentMeshes: RevolveCutPreviewFragmentMesh[]): void {
        this.meshManager.setRevolveCutKeepBodyPreviewFragments(fragmentMeshes);
    }

    /**
     * 更新旋转切除保留实体片段高亮选择。
     *
     * @param selection 草稿选择。
     */
    public updateRevolveCutKeepBodyPreviewSelection(selection: Record<string, string[]>): void {
        this.meshManager.updateRevolveCutKeepBodyPreviewSelection(selection);
    }

    /**
     * 清空旋转切除保留实体片段高亮。
     */
    public clearRevolveCutKeepBodyPreview(): void {
        this.meshManager.clearRevolveCutKeepBodyPreview();
    }

    /**
     * 请求交互式特征预览。
     *
     * 设计说明：
     * 1. 该入口用于属性面板连续变化、选择变化等高频交互；
     * 2. 同一 Viewer 内只允许一个预览求解在途，新请求会覆盖等待中的旧请求；
     * 3. 旧请求完成后如果已经过期，会返回 stale，避免过期结果重新落回场景。
     *
     * @param input 预览输入。
     * @param options 调度参数。
     * @returns 预览请求队列结果。
     */
    public requestFeaturePreview(
        input: ViewerFeaturePreviewInput,
        options: { force?: boolean } = {},
    ): Promise<FeaturePreviewRequestQueueResult<ViewerFeaturePreviewResult>> {
        return this.featurePreviewRequestQueue.request(input, options);
    }

    /**
     * 清空交互式特征预览请求。
     */
    public clearFeaturePreviewRequests(): void {
        this.featurePreviewRequestQueue.clear();
    }

    /**
     * 立即执行特征预览。
     *
     * @param input 预览输入。
     * @returns 预览结果。
     */
    private async executeFeaturePreviewNow(input: ViewerFeaturePreviewInput): Promise<ViewerFeaturePreviewResult> {
        const definition = this.resolveFeatureDocumentDefinition(input.definition, input.featureId);
        return this.featurePreviewExecutionRegistry.execute({
            featureKind: input.featureKind,
            featureId: input.featureId,
            definition,
            bindings: input.bindings.map((binding) => createFeatureReferenceBinding(binding)),
            session: input.featureId ? this.getFeatureEditSession(input.featureId) : null,
            preferredItemRef: input.preferredItemRef ?? null,
        });
    }

    /**
     * 提交特征。
     * @param input 提交输入。
     * @returns 提交结果。
     */
    public async commitFeature(input: {
        featureKind: string;
        featureId?: string | null;
        definition: FeatureDefinition;
        bindings: SerializedFeatureReferenceBinding[];
    }): Promise<{
        replaced: boolean;
        featureId: string;
        prototype: FeaturePrototype;
        meshData: MeshData | null;
        objectId?: string;
        objectIds?: string[];
        meshDataList?: MeshData[];
    }> {
        const definition = this.resolveFeatureDocumentDefinition(input.definition, input.featureId);
        const result = await this.featureCommitExecutionRegistry.execute({
            featureKind: input.featureKind,
            featureId: input.featureId,
            definition,
            bindings: input.bindings.map((binding) => createFeatureReferenceBinding(binding)),
            session: input.featureId ? this.getFeatureEditSession(input.featureId) : null,
        });
        this.autoHideConsumedSketchAfterFeatureCommit(result.prototype);
        return result;
    }

    /**
     * 正式基准面几何变化进入 SDK 依赖重建，初始化和包恢复由其完整重建边界接管。
     * @param planeId 稳定用户基准面 ID。
     */
    public notifyDatumPlaneDefinitionChanged(planeId: number): void {
        if (this.isPuppyFeatureTimelineReplayPending) return;
        const generation = this.puppyFeatureRestoreGeneration;
        this.featureRebuildOrchestrator?.notifyModelReferenceChanged(`datum-plane:${planeId}`,
            () => generation === this.puppyFeatureRestoreGeneration && !this.isPuppyFeatureTimelineReplayPending);
    }

    /** @returns 等待基准面更新、删除或历史恢复引起的特征重建。 */
    public async flushDatumPlaneFeatureRebuild(): Promise<void> {
        await this.featureRebuildOrchestrator?.flushModelReferenceChanges();
    }

    /**
     * 为凸台预览和提交附加同一正式零件范围，编辑时优先沿用原特征的归属。
     * @param definition 本次定义。 @param featureId 编辑目标。 @returns 带范围的定义快照。
     */
    private resolveFeatureDocumentDefinition(definition: FeatureDefinition, featureId?: string | null): FeatureDefinition {
        if (!(definition instanceof ExtrudeFeatureDefinitionModel) || definition.getFeatureKind() !== 'boss-extrude') return definition;
        const original = featureId ? this.getFeatureEditSession(featureId)?.getTargetCommand().params : null;
        const scope = original?.documentScopeNodeKey
            ? { documentScopeNodeKey: original.documentScopeNodeKey, documentObjectIds: original.documentObjectIds ?? [] }
            : featureId ? null : this.assemblyComponentEditController.getDocumentScope();
        return scope ? definition.withCommandParams({ ...definition.getCommandParams(), ...scope }) : definition;
    }

    /**
     * 在草图实体创建后按当前“上色草图轮廓”开关同步目标草图的轮廓显示。
     *
     * `group` 为 `null` 时不产生副作用；同步只更新轮廓显示，不改变草图实体定义。
     *
     * @param group 需要刷新轮廓的草图分组；`null` 表示当前没有可同步分组。
     */
    public syncShadedContoursAfterSketchEntityCreated(group: THREE.Group | null): void {
        if (!group) {
            return;
        }
        this.syncSketchContourVisibility(group, this.shadedSketchContoursEnabled);
    }

    //------ 特征消费草图自动隐藏 开始------
    /**
     * 在特征提交成功后按消费关系自动隐藏来源草图。
     *
     * 设计说明：
     * 1. 该规则属于 CAD 核心领域语义，必须由 cad-core 在提交链路统一收口；
     * 2. 仅当特征种类命中自动隐藏集合且存在来源草图时才生效；
     * 3. 若目标草图不存在于当前场景，则保持静默返回，避免把提交成功链路变成失败。
     *
     * @param prototype 已提交的特征原型。
     */
    private autoHideConsumedSketchAfterFeatureCommit(prototype: FeaturePrototype): void {
        if (!AUTO_HIDE_SOURCE_SKETCH_FEATURE_KINDS.has(prototype.getFeatureKind())) {
            return;
        }
        /**
         * 特征原型记录的来源草图是持久 ID，而显示态、轮廓清理等场景操作只认运行时 uuid，
         * 因此这里先统一归一，再驱动后续场景副作用。
         */
        const sourceSketchId = this.resolveRuntimeSketchId(prototype.getSourceSketchId());
        if (!sourceSketchId) {
            return;
        }
        const sourceSketchGroup = this.sketchGroupManager?.getGroupById(sourceSketchId) ?? null;
        if (sourceSketchGroup) {
            clearSketchContourDisplays(sourceSketchGroup);
        }
        this.setSketchGroupDisplayState(sourceSketchId, SketchDisplayState.Hidden);
    }

    /**
     * 按运行时 ID 或持久 ID 解析当前文档中的统一草图身份。
     *
     * 该入口只复用 SketchGroupManager 的既有双 ID 索引，不维护第二份映射。
     * 缺失持久 ID 的旧草图不能进入依赖持久身份的 Feature/Convert Entities 链路。
     *
     * @param sketchId 待解析的运行时 uuid 或持久 ID。
     * @returns 同一草图的双 ID 身份；草图不存在或持久 ID 无效时返回 null。
     */
    public resolveSketchIdentity(
        sketchId: string | null | undefined,
    ): PuppyCAD.SketchIdentityEntry | null {
        if (typeof sketchId !== 'string' || sketchId.length === 0) {
            return null;
        }
        const sketchGroup = this.sketchGroupManager?.getGroupById(sketchId) ?? null;
        if (
            !sketchGroup
            || typeof sketchGroup.persistentId !== 'string'
            || sketchGroup.persistentId.length === 0
        ) {
            return null;
        }
        return {
            runtimeId: sketchGroup.uuid,
            persistentId: sketchGroup.persistentId,
        };
    }

    /**
     * 把任意来源的草图 ID 归一为场景运行时句柄。
     *
     * 设计说明：
     * 1. 命令、特征绑定与离线包记录的草图身份是持久 ID，场景与显示态只认运行时 uuid；
     * 2. `SketchGroupManager.getGroupById` 是两个 ID 空间的唯一查找入口，这里复用它完成换算；
     * 3. 场景中不存在对应草图组时返回 null，调用方据此静默跳过，不把链路变成失败。
     *
     * @param sketchId 待归一的草图 ID，可能是运行时 uuid 或持久 ID。
     * @returns 场景运行时 uuid；未命中时返回 null。
     */
    public resolveRuntimeSketchId(sketchId: string | null | undefined): string | null {
        return this.resolveSketchIdentity(sketchId)?.runtimeId ?? null;
    }
    //------ 特征消费草图自动隐藏 结束------

    /**
     * 设置上色草图轮廓开关，并把状态同步到当前可见草图对象。
     *
     * 关闭时会清理整个主场景中的草图轮廓显示；开启时只为当前活动草图同步轮廓，
     * 最后请求一次即时渲染。该调用不会修改草图几何定义或持久化特征数据。
     *
     * 说明：开关变化会增删轮廓显示对象，进而改变等距实体预览的虚线/实线判定输入，
     * 因此轮廓同步完成后必须通知等距实体控制器重跑预览，避免旧线型残留。
     *
     * @param enabled 是否启用上色草图轮廓。
     */
    setShadedSketchContoursEnabled(enabled: boolean): void {
        this.shadedSketchContoursEnabled = enabled;
        if (!this.scene) {
            return;
        }
        if (!enabled) {
            this.syncSketchContourVisibility(this.scene, false);
            // 轮廓已从场景移除：通知等距实体控制器重跑预览，
            // 阶段三重新收集轮廓时为空，全部标记 outside，虚线预览恢复为实线
            this.sketchOffsetController?.refreshPreviewAfterShadedSketchContoursChanged();
            this.requestRender();
            return;
        }
        const activeSketchGroup = this.sketchGroupManager?.activeSketchGroup ?? null;
        if (activeSketchGroup) {
            this.syncSketchContourVisibility(activeSketchGroup, true);
        }
        // 轮廓已恢复：通知等距实体控制器重跑预览，
        // 阶段三重新收集到轮廓，位于轮廓内的预览段重新渲染为虚线
        this.sketchOffsetController?.refreshPreviewAfterShadedSketchContoursChanged();
        this.requestRender();
    }

    /**
     * 同步指定对象树下的草图轮廓显示状态。
     * @param root 根对象。
     * @param enabled 是否启用上色草图轮廓。
     */
    private syncSketchContourVisibility(root: THREE.Object3D, enabled: boolean): void {
        if (!enabled) {
            clearSketchContourDisplays(root);
            return;
        }
        const contourOwners: THREE.Object3D[] = [];
        const contours: THREE.Object3D[] = [];

        root.traverse((object) => {
            if (isSketchCollectionObject(object)) {
                if (enabled && object.isClosedGeometry) {
                    contourOwners.push(object);
                }
            } else if (isSketchCircleObject(object)) {
                if (enabled) {
                    contourOwners.push(object);
                }
            } else if (isSketchEllipseObject(object)) {
                // 部分椭圆的轮廓由所属 SketchCollectionObject 统一恢复，避免同一 contour 被重复创建或改挂父级。
                if (enabled && !object.isPartialEllipse() && object.canCreateContour()) {
                    contourOwners.push(object);
                }
            } else if (isSketchSlotObject(object)) {
                if (enabled) {
                    contourOwners.push(object);
                }
            //------ 自闭合样条上色轮廓恢复 开始------
            } else if (isSketchSplineObject(object) || isSketchStyleSplineObject(object)) {
                /**
                 * 混合闭合样条的轮廓由 SketchCollectionObject 承担；这里只恢复单条样条自身闭合的轮廓，
                 * 避免重入草图时把集合轮廓错误挂回子样条对象。
                 */
                if (enabled && object.closedCollection === null && object.isClosedGeometry) {
                    contourOwners.push(object);
                }
            //------ 自闭合样条上色轮廓恢复 结束------
            }

            if (isSketchContour(object)) {
                contours.push(object);
            }
        });
        contourOwners.forEach((object) => {
            if (isSketchCollectionObject(object)
                || isSketchCircleObject(object)
                || isSketchEllipseObject(object)
                || isSketchSlotObject(object)
                //------ 自闭合样条上色轮廓恢复 开始------
                || isSketchSplineObject(object)
                || isSketchStyleSplineObject(object)
                //------ 自闭合样条上色轮廓恢复 结束------
            ) {
                object.syncContour();
            }
        });
        contours.forEach((object) => {
            object.visible = enabled;
        });
    }

    /**
     * 将对象挂入场景树。
     *
     * 设计说明：
     * 1. 这是 Viewer 对外公开的统一场景入场门面；
     * 2. 所有会进入已存在场景树的对象，都应在这里完成 BVH 构建与父子挂载；
     * 3. `parent` 缺省时默认挂到 Viewer 主场景根节点，`index` 用于稳定控制插入顺序。
     *
     * @param object 待挂载对象。
     * @param parent 可选父节点；缺省时挂到主场景。
     * @param index 可选插入位置。
     * @returns 已挂载对象。
     */
    public addObject(
        object: THREE.Object3D,
        parent?: THREE.Object3D,
        index?: number,
    ): THREE.Object3D {
        const resolvedParent = parent ?? this.scene;
        const attachedObject = addObject3DToParent(object, resolvedParent, index);
        syncImportedModelBodyRepresentations(
            this.scene,
            this.getModelBodySceneRegistry(),
            {changedSubtree: attachedObject},
        );
        const sectionableModelRoot = this.sectionableModelRoot;
        if (sectionableModelRoot
            && (attachedObject === sectionableModelRoot
                || sectionableModelRoot.getObjectByProperty('uuid', attachedObject.uuid))) {
            this.notifySectionableModelContentChanged();
        }
        return attachedObject;
    }

    /**
     * 将正式可剖切模型挂入统一局部裁剪根。
     *
     * 特征预览、草图、基准几何和交互辅助对象不得通过此入口挂载；正式结果体进入后会
     * 自动刷新剖切包围盒、实体面引用和剖面盖代理。
     *
     * @param object 待挂载的正式模型对象。
     * @param index 可选稳定插入位置。
     * @returns 已挂载对象。
     */
    public addSectionableObject(
        object: THREE.Object3D,
        index?: number,
    ): THREE.Object3D {
        return this.addObject(object, this.sectionableModelRoot, index);
    }


    /**
     * 从场景中移除指定 ID 的对象。
     * @param objectId 对象标识（THREE.Object3D.name）。
     * @param _options 可选项（保留用于撤销等扩展）。
     */
    public removeSceneObject(objectId: string, _options: { preserveObjectForUndo?: boolean } = {}): void {
        if (!this.scene) return;
        const object = this.scene.getObjectByName(objectId);
        if (object && object.parent) {
            const wasSectionable = object === this.sectionableModelRoot
                || Boolean(this.sectionableModelRoot.getObjectByProperty('uuid', object.uuid));
            object.parent.remove(object);
            if (wasSectionable) {
                this.notifySectionableModelContentChanged();
            }
        }
    }

    /**
     * 隐藏吸附指示器。
     * 子类或外部可重写此方法以实现具体视觉效果。
     */
    public hideSnapIndicator(): void {
        this.pointerNearbyLabelManager.hideLabel(PointerNearbyLabelPosition.RightBottom);
    }

    /**
     * 通知草图实体已创建。
     * @param object 新创建的草图实体。
     */
    public notifySketchEntityCreated(object: THREE.Object3D): void {
        if (this.sketchEntityCreatedCallback) {
            this.sketchEntityCreatedCallback(object);
        }
    }

    /**
     * 进入编辑零部件模式。
     * @param objectId 要编辑的零部件对象 ID。
     * @returns 是否成功进入编辑零部件模式。
     */
    public enterAssemblyComponentEditMode(objectId: string, documentScope?: FeatureDocumentScope): boolean {
        this.assemblyComponentEditController.modifyAllComponentsAppearance(objectId, documentScope);
        return true;
    }

    /**
     * 退出编辑零部件模式。
     */
    public exitAssemblyComponentEditMode(): void {
        this.assemblyComponentEditController.restoreAllComponentsAppearance();
        return;
    }

    /**
     * 进入测量（评估）叠加会话。
     *
     * 设计说明：底层 ViewerMode 保持不变，仅激活 Inspection 会话层。
     *
     * @returns 是否成功启动测量会话。
     */
    public enterMeasureMode(): boolean {
        return this.measureController.start();
    }

    /**
     * 退出测量（评估）叠加会话。
     */
    public exitMeasureMode(): void {
        this.measureController.stop();
    }

    /**
     * 测量叠加会话是否激活。
     *
     * @returns 是否处于测量会话中。
     */
    public isMeasureSessionActive(): boolean {
        return this.measureController.isMeasureActive();
    }

    /**
     * 订阅测量选中集变更事件。
     *
     * @returns 测量选中集变更 Observable。
     */
    public observeMeasureSelectionUpdated(): Observable<MeasureSelectionUpdatedPayload> {
        return this.measureController.observeSelectionUpdated();
    }

    /**
     * 构建测量选中对象对应的设计树投影 hint。
     *
     * @param objectId 测量选中对象 ID。
     * @returns 设计树投影 hint。
     */
    public buildMeasureSelectionTreeHint(objectId: string) {
        return buildMeasureSelectionTreeHint(this, objectId);
    }

    /**
     * 从测量实体引用构建设计树投影 hint。
     *
     * @param entityRef 测量实体引用。
     * @returns 设计树投影 hint。
     */
    public buildMeasureSelectionTreeHintFromEntityRef(entityRef: MeasureEntityRef) {
        return buildMeasureSelectionTreeHintFromEntityRef(this, entityRef);
    }

    /**
     * 应用 GLB datum-slot 无边界测量选中。
     *
     * @param descriptor datum-slot 描述符。
     * @returns 是否成功写入测量选中集。
     */
    public applyMeasureUnboundedGlbDatumSlotSelection(
        descriptor: GlbDatumSlotTargetDescriptor,
    ): boolean {
        return this.measureController.applyUnboundedGlbDatumSlotSelection(descriptor);
    }

    /**
     * 展开设计树零件/实体节点并替换测量选中集。
     *
     * @param descriptor 设计树节点测量展开描述符。
     * @returns 是否至少成功选中一个可测量实体。
     */
    public replaceMeasureSelectionFromTreePartNode(
        descriptor: MeasureTreePartSelectionDescriptor,
    ): boolean {
        return this.measureController.replaceSelectedEntitiesFromTreePart(descriptor);
    }

    /**
     * 应用设计树节点轻量测量选择。
     *
     * @param descriptor 设计树节点轻量选择描述符。
     * @returns 是否成功解析并写入轻量选择。
     */
    public applyMeasureTreeNodeVisualSelection(
        descriptor: MeasureTreeNodeVisualSelectionDescriptor,
    ): boolean {
        return this.measureController.applyTreeNodeVisualSelection(descriptor);
    }


    /**
     * 清理 Viewer 持有的渲染、交互和运行时资源。
     *
     * @param options 资源释放选项；不传时执行页面卸载级完整释放。
     */
    dispose(options: ViewerDisposeOptions = {}): void {
        this.assemblyComponentObjectIndex?.dispose();
        this.assemblyComponentObjectIndex = null;
        this.disposeStarted = true;
        this.frameScheduler.dispose();
        this.advancePuppyFeatureRestoreGeneration();
        if (!this.readySettled) {
            this.readySettled = true;
            const rejectReadyPromise = this.rejectReadyPromise;
            this.resolveReadyPromise = null;
            this.rejectReadyPromise = null;
            rejectReadyPromise?.(new Error('Viewer 已释放，异步初始化已取消'));
        }
        const shouldTerminateWorker = options.terminateWorker ?? true;

        try {
            // 停止渲染循环
            if (this.animationFrameId !== null) {
                cancelAnimationFrame(this.animationFrameId);
                this.animationFrameId = null;
            }

            if (this.resizeObserver) {
                this.resizeObserver.disconnect();
                this.resizeObserver = null;
            }
            // 在草图组和标注资源释放前回滚预览，并解除所有宿主闭包。
            this.sketchDimensionInstant2dController?.dispose();
            this.sketchDimensionInstant2dController = null;
            this.clearSketchEditingVisualLayering();

            this.rectangleRelationMarkerManager?.dispose();
            this.rectangleRelationMarkerManager = null;

            this.revolveRegionSelectionManager.dispose();
            this.bossRevolveAngleDragController.stop();

            this.sketchDimensionManager?.dispose();
            this.sketchDimensionManager = null;
            this.featureRebuildOrchestrator?.dispose();
            this.featureRebuildOrchestrator = null;
            this.sketchFeaturePrefixEditRuntime?.dispose();
            this.sketchFeaturePrefixEditRuntime = null;
            this.featurePreviewRequestQueue.dispose();
            this.featureEditOrchestrator = null;
            this.sketchEntityReferenceDocumentRuntime?.dispose();
            this.sketchEntityReferenceDocumentRuntime = null;
            this.sketchSessionReferencePointManager?.dispose();
            this.sketchSessionReferencePointManager = null;
            this.sketchGroupManager?.dispose();
            this.sketchGroupManager = null;
            this.sketchSessionTerminator = null;
            this.sketchRuntimeSnapshotRegistry.clear();
            this.sketchParametricSolveRevisionBySketchId.clear();
            this.importedModelFeatureReplayBaselineRevisionIds.clear();
            this.topologyOwnerRemovedSubject.complete();

            // 释放新交互管理链路资源。
            this.sketchDrawingManager?.dispose();
            this.sketchDrawingManager = null;
            this.pointSnapManager?.dispose();
            this.pointSnapManager = null;
            this.pointerNearbyLabelManager.dispose();

            this.sketchTrimController?.dispose();
            this.sketchTrimController = null;
            //------ 智能尺寸 开始------
            this.sketchSmartDimensionController?.dispose();
            this.sketchSmartDimensionController = null;
            this.sketchSmartDimensionPreviewRenderer?.dispose();
            this.sketchDimensionAnnotationRenderer?.dispose();
            this.sketchDimensionRefreshSubscription?.unsubscribe();
            this.sketchDimensionRefreshSubscription = null;
            this.disposeSketchDimensionRelationSubscription?.();
            this.disposeSketchDimensionRelationSubscription = null;
            this.curveOffsetDerivedSyncService?.dispose();
            this.curveOffsetDerivedSyncService = null;
            this.offsetChainDerivedSyncService = null;
            this.sketchDimensionUiHandlers = null;
            //------ 智能尺寸 结束------
            this.sketchCornerController?.dispose();
            this.sketchCornerController = null;
            this.sketchMoveEntitiesController?.dispose();
            this.sketchMoveEntitiesController = null;
            this.sketchCopyEntitiesController?.dispose();
            this.sketchCopyEntitiesController = null;
            this.sketchRotateEntitiesController?.dispose();
            this.sketchRotateEntitiesController = null;
            this.sketchCircularPatternController?.dispose();
            this.sketchCircularPatternController = null;
            this.sketchMirrorEntitiesController?.dispose();
            this.sketchMirrorEntitiesController = null;
            this.sketchEntityClipboardController?.dispose();
            this.sketchEntityClipboardController = null;
            this.importedModelSelectionHighlighter?.dispose();
            this.importedModelSelectionHighlighter = null;
            // 必须先恢复 source Mesh 并释放实例 scope，后续显示控制器和导入结果才能安全退役。
            this.importedModelInstancedRenderCoordinator.dispose();
            this.interactionStateManager?.dispose();
            this.interactionStateManager = null;

            this.contextMenuController?.dispose();
            this.contextMenuController = null;

            this.entitySelectionManager?.dispose();
            this.entitySelectionManager = null;
            this.assemblyComponentPlacementSnapResolver?.dispose();
            this.assemblyComponentPlacementSnapResolver = null;
            this.eventManager?.dispose();
            this.eventManager = null;
            this.importedModelRaycastCandidateIndex?.dispose();
            this.importedModelRaycastCandidateIndex = null;
            this.meshRaycastQuery?.dispose();
            this.meshRaycastQuery = null;
            this.sceneMeshRaycastIndex?.dispose();
            this.sceneMeshRaycastIndex = null;
            this.meshManager?.dispose();
            this.meshManager = null;
            disposeModelBodySceneRegistry(this.scene);
            this.sectionViewController.dispose();
            unregisterSectionableModelRoot(this.scene);
            this.sectionableModelRoot.removeFromParent();
            this.sectionableModelRoot.clear();
            this.modelDisplayModeController.dispose();

            // 清理材质库
            this.latestCreatedPointObject = null;
            this.suppressSketchEndpointLayerDuringLineDrawing = false;

            this.cameraManager?.dispose();
            this.cameraManager = null;

            // 清理辅助对象管理器
            this.helperManager?.dispose();
            this.helperManager = null;

            // 清理光照管理器
            this.lightManager?.dispose();
            this.lightManager = null;

            // 清理 WebGPU 管线与辅助场景。
            this.pipelineManager?.dispose();
            this.pipelineManager = null;
            this.sceneHelpers.clear();

            // 清理预览渲染器，释放独立 WebGPU 上下文
            this.viewOrientationPreviewRenderer?.dispose();
            this.viewOrientationPreviewRenderer = null;

            // 清理基准面管理器
            this.referencePlaneController?.dispose();
            this.referenceAxisController.cancel();
            this.referencePointController.cancel();
            this.moveComponentController?.dispose();
            this.assemblyMateController?.dispose();
            this.linearComponentPatternManager?.dispose();
            this.importedModelExternalSourceMeshVisibilityControllers.clear();
            this.sectionViewRotationHandleManager.dispose();
            this.patternDirectionArrowManager.dispose();
            this.cursorManager.dispose();

            this.datumPlaneManager?.dispose();
            this.datumPlaneManager = null;
            this.datumAxisManager?.dispose();
            this.datumAxisManager = null;
            this.datumPointManager?.dispose();
            this.datumPointManager = null;
            // 清理CSS2D渲染器
            if (this.labelRenderer && this.container) {
                this.container.removeChild(this.labelRenderer.domElement);
                this.labelRenderer = null;
            }
            // 清理渲染器
            if (this.renderer) {
                this.removeInspector();
                this.renderer.dispose();
                this.container?.removeChild(this.renderer.domElement);
                this.renderer = null;
            }
        } catch (error) {
            console.log('清理 THREE.js 渲染器失败:', error);
        }

        if (shouldTerminateWorker) {
            try {
                /**
                 * WorkerManager 当前仍是 cad-core 级共享运行时。页面卸载时必须终止 Worker，
                 * 避免 Puppy CAD 内核/WASM、待处理请求或 runtime switch 会话泄漏；根文档
                 * 切换重建 Viewer 会显式保留 Worker，由 document runtime switch 接管新文档。
                 */
                this.workerManager.terminate();
            } catch (error) {
                console.log('清理 Worker runtime 失败:', error);
            }
        }

        try {
            if (this.ownsWorkerRuntimeLease) {
                this.workerRuntimeLease.release(this);
                this.ownsWorkerRuntimeLease = false;
            }
        } catch (error) {
            console.log('释放 Viewer Worker runtime 租约失败:', error);
        }
    }
}
